import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowLeft, ArrowRight, Heart, MessageCircle, Plus, User as UserIcon, Zap, MessageSquareDashed, Rocket, Download,
  Home as HomeIcon, Users, MoreHorizontal, Camera, Send, Check, Bell, Repeat,
  Search, Image as ImageIcon, MapPin, GraduationCap, Calendar, Settings,
  Shield, ChevronRight, X, LogOut, Info, School, Building2, Clock, Lock,
  EyeOff, Mail, KeyRound, Hash, Sparkles, Megaphone, HandHeart, ThumbsUp,
  Copy, Share2, PartyPopper, Home as Apartment, UsersRound, ChevronDown,
  ChevronsUpDown, ImagePlus, Pencil, BellRing, UserPlus, MessageSquare,
  ShieldCheck, Inbox, Trash2, Flag, AlertTriangle, ShieldAlert, Crown,
  UserMinus, MoreVertical, ExternalLink, Ban, Gift, FileText, HelpCircle,
  Smile, ChevronLeft, CalendarDays,
  Code2, Paintbrush, Stethoscope, Briefcase, Scale, Wrench, ChefHat, Car, Hammer, Tractor,
  ZoomIn, ZoomOut, RotateCcw, Link2
} from 'lucide-react';
import Cropper from 'react-easy-crop';
import { signInWithGoogle, signInWithEmail, signUpWithEmail, sendPasswordReset, watchAuth, loadUserDoc, logout as firebaseLogout, markUserOnboarded, updateUserProfile, startPhoneSignIn, confirmPhoneOtp } from './lib/auth';
import { createGroup, loadUserGroups, addGroupEvent, getGroupEvents, deleteGroupEvent, findGroupByConnectId, joinGroupAsPendingMember, addGroupToUserMembership, uploadProfilePhoto, deleteProfilePhoto, uploadGroupCover, updateGroupCoverUrl, updateGroupInfo, uploadPostPhoto, getPendingMembers, subscribeToPendingMembers, approvePendingMember, rejectPendingMember, getMyMembership, removeGroupFromUserMembership, loadUserProfile, createPost, subscribeToGroupPosts, togglePostLike, hasUserLikedPost, addPostComment, editPostComment, subscribeToPostComments, deletePost, loadOlderComments, findOrphanedMemberships, loadUserChats, subscribeToUserChats, subscribeToLatestMessages, sendChatMessage, loadOlderMessages, makeChatId, deleteChat as deleteFirestoreChat, clearChatForUser, markChatRead, subscribeToGroupMembers, subscribeToGroupMemories, uploadMemoryPhoto, addGroupMemory, deleteGroupMemory, getMemoryCount, leaveGroup, isOnlyAdmin, checkPostPhotoLimit, checkProfilePhotoLimit, checkGroupCoverLimit, searchGroupsByCriteria, makeGroupAdmin, removeGroupMember, submitReport, subscribeToGroupReports, resolveReport,subscribeToPendingWarning, acknowledgePendingWarning, openOrCreateSupportChat, blockUser, unblockUser, subscribeToBlockedUsers} from './lib/db';
import { requestNotificationPermission, onForegroundMessage } from './lib/messaging';
import { collection, query, where, orderBy, limit, onSnapshot, getDoc, doc, updateDoc, writeBatch, serverTimestamp, getDocs } from 'firebase/firestore';
import { db } from './lib/firebase';
// Session A — V1 Help & Support now opens a real support chat with the
// Aangan team, reusing the V2 modal built in Session 5a. Cross-tree import
// is deliberate: the modal is self-contained (own CSS, talks only to db.js)
// and duplicating it for V1 would guarantee drift.
import SupportChatModal from './screens/v2/SupportChatModal';
// Phase 2.4 C8 — biz deep link resolution needs these to fetch the
// business doc and auto-connect after auth completes. Existing V2
// surfaces import from the same module; this is the first V1 touch.
import { getBusiness, connectToBusiness, getBusinessesByOwners } from './lib/v2/firestore';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

// Share/invite URL base. Lives in one module now (src/lib/shareBase.js) so V1
// and V2 cannot drift: window.location.origin returns http://localhost on
// Capacitor native, which makes shared links useless to recipients, and the V2
// surfaces had each reinvented that bug independently.
import { AANGAN_SHARE_BASE } from './lib/shareBase';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useV2Flag } from './lib/v2/flags';
import V2App from './screens/v2/V2App';
import MembersTabV2 from './screens/v2/MembersTabV2';
import ProfileTabV2 from './screens/v2/ProfileTabV2';
import HomeConnectListV2 from './screens/v2/HomeConnectListV2';
// Phase 2.4 C9 — Onboarded users with zero batches land here instead
// of V1's empty Home, since V1 Home assumes an active group context.
import BizOnlyHome from './screens/v2/BizOnlyHome';

// Convert plain text with http/https URLs into React fragments where URLs
// are clickable <a> tags. Preserves whitespace/newlines (caller can use
// whiteSpace: 'pre-wrap'). Splits on URL boundaries, wraps each URL in an
// anchor that opens in a new tab with safe rel attributes.
function linkifyText(text) {
  if (!text || typeof text !== 'string') return text;
  const urlRegex = /(https?:\/\/[^\s<>"']+[^\s<>"',.;:!?)\]])/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      urlRegex.lastIndex = 0;
      return React.createElement('a', {
        key: i,
        href: part,
        target: '_blank',
        rel: 'noopener noreferrer',
        onClick: (e) => {
          // Prevent post-card click from firing, AND prevent the default
          // anchor navigation so the PWA doesn't accidentally navigate away.
          // We open the URL programmatically in a new window/tab instead.
          e.stopPropagation();
          e.preventDefault();
          window.open(part, '_blank', 'noopener,noreferrer');
        },
        style: { color: '#2563EB', textDecoration: 'underline', wordBreak: 'break-word' },
      }, part);
    }
    return part;
  });
}

// =============================================================
// CONSTANTS / SEED DATA
// =============================================================
const APP_NAME = 'Aangan';

// Format a Firestore timestamp into a relative string like "2h ago" or "just now"
const formatTimestamp = (ts) => {
  if (!ts) return 'just now';
  const ms = ts.toMillis ? ts.toMillis() : ts;
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(days / 365)}y`;
};

const generateConnectId = () => {
  const cities = ['MUM', 'DEL', 'BLR', 'HYD', 'CHN', 'PUN', 'KOL'];
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let suffix = '';
  for (let i = 0; i < 4; i++) suffix += chars[Math.floor(Math.random() * chars.length)];
  return `${cities[Math.floor(Math.random() * cities.length)]}-${suffix}`;
};





// Membership map: which seeded users are in which seeded groups.
// Newly created groups (g_new_*) start with only the current user.
const GROUP_MEMBERS = {
  g1: ['u1', 'u2', 'u3', 'u4', 'u5', 'u6', 'u7', 'u8', 'u9'], // St. Mary's — all 9 seed users
  g2: ['u2', 'u3', 'u7', 'u9'],                                // St. Xavier's College
  g3: ['u1', 'u2'],                                             // Infosys Bengaluru
};

// Time Capsule seed memories per group. New groups start empty.



// (Time helpers for chat auto-expiry removed — chats now persist with manual clear-for-self.)





// admin moderation queue — pending join requests + reports






const BATCH_TYPES = {
  alma: { label: 'School / College', icon: School, emoji: '🏫', subtitle: 'Classmates from school or college', headerLabel: 'Your school friends' },
  company: { label: 'Work colleagues', icon: Building2, emoji: '💼', subtitle: 'Old colleagues', headerLabel: 'Your colleagues' },
  society: { label: 'Society', icon: Apartment, emoji: '🏘️', subtitle: 'Apartment / building neighbours', headerLabel: 'Your neighbours' },
  family: { label: 'Family/Friends', icon: UsersRound, emoji: '👨‍👩‍👧', subtitle: 'Family or close friends', headerLabel: 'Your family group' },
  business: { label: 'Business Connect', icon: Building2, emoji: '🏢', subtitle: 'Customers, clients & local businesses', headerLabel: 'Your business community', comingSoon: true },
};

// =============================================================
// PROFESSION CATEGORY MATCHER
// Maps free-text profession (e.g. "Senior Frontend Developer") to one of
// 12 broad category icons. Keyword-based; tolerant of typos and variations.
//
// Order matters: more specific keywords checked first.
// Returns: { Icon: LucideComponent, key: string }
// =============================================================
const PROFESSION_CATEGORIES = {
  tech: { Icon: Code2 },
  design: { Icon: Paintbrush },
  medical: { Icon: Stethoscope },
  education: { Icon: GraduationCap },
  legal: { Icon: Scale },
  engineering: { Icon: Wrench },
  food: { Icon: ChefHat },
  transport: { Icon: Car },
  trade: { Icon: Hammer },
  agriculture: { Icon: Tractor },
  business: { Icon: Briefcase },
  other: { Icon: Briefcase }, // default fallback
};

// Keyword → category. Each profession text is checked against these
// in order; first match wins. Keywords are lowercased substrings.
const PROFESSION_KEYWORDS = [
  // Medical (check first — "doctor" must beat "doctorate")
  { match: ['doctor', 'surgeon', 'nurse', 'dentist', 'medical', 'physician', 'pharmacist', 'pharma', 'therapist', 'physio', 'clinic', 'hospital', 'paramedic', 'radiolog', 'veterinar', 'vet '], category: 'medical' },
  // Tech
  { match: ['developer', 'engineer', 'programmer', 'coder', 'sde', 'software', 'frontend', 'backend', 'fullstack', 'devops', 'tech ', 'tech-', 'data scientist', 'ml ', 'ai ', 'cyber', 'qa ', 'tester', 'sysadmin', 'devrel', 'it '], category: 'tech' },
  // Design / Creative
  { match: ['designer', 'design ', 'design-', 'ux', 'ui ', 'graphic', 'illustrator', 'creative', 'art director', 'animator', 'photograph', 'videograph', 'filmmaker', 'editor', 'writer', 'journalist', 'musician', 'singer', 'dancer'], category: 'design' },
  // Education
  { match: ['teacher', 'professor', 'lecturer', 'tutor', 'principal', 'instructor', 'educator', 'coach ', 'trainer', 'librarian', 'researcher', 'phd ', 'student', 'academ'], category: 'education' },
  // Legal
  { match: ['lawyer', 'advocate', 'attorney', 'legal', 'judge', 'paralegal', 'notary', 'counsel '], category: 'legal' },
  // Engineering (non-software) / build
  { match: ['architect', 'civil eng', 'mechanical eng', 'electrical eng', 'chemical eng', 'aerospace', 'project manager', 'consultant'], category: 'engineering' },
  // Food & hospitality
  { match: ['chef', 'cook', 'baker', 'restaurant', 'hotel', 'caterer', 'waiter', 'barista', 'tea seller', 'sommelier'], category: 'food' },
  // Transport
  { match: ['driver', 'pilot', 'sailor', 'captain', 'logistics', 'delivery', 'courier', 'rideshare'], category: 'transport' },
  // Skilled trade
  { match: ['electrician', 'mechanic', 'plumber', 'carpenter', 'welder', 'mason', 'painter', 'tailor', 'cobbler', 'technician', 'fitter', 'wireman'], category: 'trade' },
  // Agriculture
  { match: ['farmer', 'dairy', 'fisherman', 'gardener', 'horticultur', 'agro', 'agriculture', 'florist'], category: 'agriculture' },
  // Business / sales / corporate (broad — checked last among real categories)
  { match: ['founder', 'co-founder', 'cofounder', 'ceo', 'cto', 'cfo', 'coo', 'vp ', 'director', 'manager', 'analyst', 'sales', 'marketing', 'business', 'entrepreneur', 'banker', 'banking', 'finance', 'accountant', 'auditor', 'hr ', 'human resources', 'recruiter', 'consultant', 'realtor', 'real estate', 'trader', 'investor', 'broker', 'shopkeeper', 'merchant'], category: 'business' },
];

/**
 * Match a free-text profession to a category icon.
 * Returns the Lucide Icon component to render.
 *
 * Examples:
 *   "Software Engineer"        → Code2
 *   "Sr. Frontend Developer"   → Code2
 *   "Doctor"                   → Stethoscope
 *   "Pediatric Surgeon"        → Stethoscope
 *   "Graphic Designer"         → Paintbrush
 *   "Founder & CEO"            → Briefcase
 *   "Random unmatched thing"   → Briefcase (default)
 */
const getProfessionIcon = (text) => {
  if (!text || typeof text !== 'string') return PROFESSION_CATEGORIES.other.Icon;
  // Normalize: lowercase, add space padding so word boundaries are findable
  const normalized = ' ' + text.toLowerCase().trim() + ' ';
  for (const rule of PROFESSION_KEYWORDS) {
    for (const keyword of rule.match) {
      if (normalized.includes(keyword)) {
        return PROFESSION_CATEGORIES[rule.category].Icon;
      }
    }
  }
  return PROFESSION_CATEGORIES.other.Icon;
};

// =============================================================
// INDIAN HOLIDAYS — 2026 + 2027
// Posted automatically as system messages on Wall on the day.
// To refresh annually: replace the year arrays below from drikpanchang.com
// =============================================================
const HOLIDAYS = [
  // 2026
  { date: '2026-01-01', name: 'New Year', emoji: '🎊', message: 'Wishing everyone a wonderful new year ahead — may 2026 bring joy, health, and many shared moments.' },
  { date: '2026-01-14', name: 'Makar Sankranti & Pongal', emoji: '🪁', message: 'Happy Makar Sankranti and Pongal to all! May the harvest season bring prosperity to your home.' },
  { date: '2026-01-26', name: 'Republic Day', emoji: '🇮🇳', message: 'Happy Republic Day. Wishing every batchmate a safe and proud 26th January.' },
  { date: '2026-02-15', name: 'Mahashivratri', emoji: '🕉️', message: 'Mahashivratri blessings to everyone — may Lord Shiva bring strength and peace.' },
  { date: '2026-03-04', name: 'Holi', emoji: '🌈', message: 'Happy Holi! Wishing the batch a colourful, joyful, gulaal-filled day with friends and family.' },
  { date: '2026-03-21', name: 'Eid al-Fitr', emoji: '🌙', message: 'Eid Mubarak to all our friends celebrating today — may this Eid bring you peace, joy, and togetherness.' },
  { date: '2026-03-31', name: 'Ram Navami', emoji: '🙏', message: 'Ram Navami greetings to all — wishing courage and devotion in equal measure.' },
  { date: '2026-04-03', name: 'Good Friday', emoji: '✝️', message: 'A peaceful Good Friday to everyone observing today.' },
  { date: '2026-04-14', name: 'Ambedkar Jayanti & Baisakhi', emoji: '🌾', message: 'Happy Baisakhi and Ambedkar Jayanti — wishing harvest joy and a kinder world for all.' },
  { date: '2026-05-01', name: 'Labour Day & Maharashtra Day', emoji: '🛠️', message: 'Salute to every worker and a happy Maharashtra Day.' },
  { date: '2026-05-27', name: 'Eid al-Adha', emoji: '🌙', message: 'Eid Mubarak. May this Bakrid be filled with blessings and happiness for you and your loved ones.' },
  { date: '2026-06-26', name: 'Muharram', emoji: '🕌', message: 'A reflective Muharram to all observing today.' },
  { date: '2026-08-15', name: 'Independence Day', emoji: '🇮🇳', message: 'Happy Independence Day to every Indian. Wishing pride, unity and freedom in every home.' },
  { date: '2026-08-28', name: 'Raksha Bandhan', emoji: '🪢', message: 'Happy Raksha Bandhan! To every brother and sister — may your bond stay forever strong.' },
  { date: '2026-09-04', name: 'Janmashtami', emoji: '🦚', message: 'Janmashtami greetings to everyone celebrating Lord Krishna\'s birth today.' },
  { date: '2026-09-14', name: 'Ganesh Chaturthi', emoji: '🐘', message: 'Ganpati Bappa Morya! Wishing everyone a joyful Ganesh Chaturthi with Bappa\'s blessings.' },
  { date: '2026-10-02', name: 'Gandhi Jayanti', emoji: '🕊️', message: 'Remembering Mahatma Gandhi today. Wishing the spirit of non-violence guides us all.' },
  { date: '2026-10-11', name: 'Navratri Begins', emoji: '💃', message: 'Shubh Navratri! Wishing nine days of joy, dance, and devotion to all.' },
  { date: '2026-10-20', name: 'Dussehra', emoji: '🏹', message: 'Happy Dussehra! May good triumph over evil, today and always.' },
  { date: '2026-10-30', name: 'Karva Chauth', emoji: '🌕', message: 'Karva Chauth blessings to everyone celebrating love today.' },
  { date: '2026-11-01', name: 'Karnataka Rajyotsava', emoji: '🌺', message: 'Happy Karnataka Rajyotsava to all friends from the state of Karnataka.' },
  { date: '2026-11-08', name: 'Diwali', emoji: '🪔', message: 'Wishing you all a joyous Diwali! May the lights bring warmth, prosperity, and many happy moments to your home.' },
  { date: '2026-11-11', name: 'Bhai Dooj', emoji: '🪷', message: 'Happy Bhai Dooj! May the bond between brothers and sisters grow stronger every year.' },
  { date: '2026-11-24', name: 'Guru Nanak Jayanti', emoji: '🙌', message: 'Guru Nanak Jayanti greetings — wishing peace, humility, and seva to all.' },
  { date: '2026-12-25', name: 'Christmas', emoji: '🎄', message: 'Merry Christmas to everyone! May your home be full of warmth, lights, and cheer.' },
  // 2027
  { date: '2027-01-01', name: 'New Year', emoji: '🎊', message: 'Wishing everyone a wonderful 2027 ahead.' },
  { date: '2027-01-14', name: 'Makar Sankranti & Pongal', emoji: '🪁', message: 'Happy Makar Sankranti and Pongal to all!' },
  { date: '2027-01-26', name: 'Republic Day', emoji: '🇮🇳', message: 'Happy Republic Day.' },
  { date: '2027-03-03', name: 'Holi', emoji: '🌈', message: 'Happy Holi! Wishing the batch a colourful day.' },
  { date: '2027-08-15', name: 'Independence Day', emoji: '🇮🇳', message: 'Happy Independence Day to every Indian.' },
  { date: '2027-10-02', name: 'Gandhi Jayanti', emoji: '🕊️', message: 'Remembering Mahatma Gandhi today.' },
  { date: '2027-10-29', name: 'Diwali', emoji: '🪔', message: 'Wishing you all a joyous Diwali!' },
  { date: '2027-12-25', name: 'Christmas', emoji: '🎄', message: 'Merry Christmas to everyone!' },
];

function getTodaysHolidays() {
  const today = new Date();
  const ymd = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  return HOLIDAYS.filter(h => h.date === ymd);
}
// Returns events that are happening today, given a list of events for a batch.
// - 'birthday' / 'anniversary' (recurring=yearly): match by month-day
// - 'event' (recurring=once): match exact date
function getTodaysEvents(events) {
  if (!events || events.length === 0) return [];
  const today = new Date();
  const ymd = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  const monthDay = ymd.slice(5);
  return events.filter(e => {
    if (e.recurring === 'yearly') return e.monthDay === monthDay;
    return e.date === ymd;
  });
}

const SUB_TABS = [
  { key: 'wall', label: 'Wall', icon: MessageCircle, desc: 'Daily chat & updates' },
  { key: 'capsule', label: 'Time Capsule', icon: Heart, desc: 'Saved memories from the batch' },
  { key: 'picks', label: 'Word of Mouth', icon: ThumbsUp, desc: 'Job referrals, recommendations & heads-ups' },
];

// =============================================================
// PRIMITIVE COMPONENTS
// =============================================================
const Logo = ({ size = 32, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    {/* Torana — open arched doorway, the welcome of an Indian home */}
    <path d="M6 28 V14 Q6 6 16 6 Q26 6 26 14 V28"
          stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    {/* Three people gathered inside — the courtyard */}
    <circle cx="12.5" cy="22" r="1.6" fill={color} />
    <circle cx="19.5" cy="22" r="1.6" fill={color} />
    <circle cx="16" cy="17" r="1.6" fill={color} />
  </svg>
);

const Avatar = ({ user, size = 40, ring = false }) => {
  const [imgFailed, setImgFailed] = useState(false);
  // Compute safe initials — always max 2 letters from first+last name
  const safeInitials = (user?.initials && user.initials.length <= 3)
    ? user.initials
    : (user?.name || 'U').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const showImage = user?.photoUrl && !imgFailed;

  if (showImage) {
    return (
      <img
        src={user.photoUrl}
        alt={user.name || 'avatar'}
        onError={() => setImgFailed(true)}
        referrerPolicy="no-referrer"
        className="shrink-0 object-cover"
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          boxShadow: ring ? '0 0 0 3px var(--bg), 0 0 0 5px var(--accent)' : 'none',
        }}
      />
    );
  }
  return (
    <div className="flex items-center justify-center font-medium text-white shrink-0"
         style={{
           width: size,
           height: size,
           borderRadius: '50%',
           background: user?.color || '#C45A3F',
           fontSize: size * 0.36,
           boxShadow: ring ? '0 0 0 3px var(--bg), 0 0 0 5px var(--accent)' : 'none',
           letterSpacing: '0.02em',
         }}>
      {safeInitials}
    </div>
  );
};

// =============================================================
// PROFESSION PILL — small inline badge showing user's profession.
// Renders an icon (matched by getProfessionIcon) + the profession text.
// Returns null if profession is empty/missing — caller doesn't need to check.
// =============================================================
const ProfessionPill = ({ profession, size = 'sm' }) => {
  if (!profession || !profession.trim()) return null;
  const Icon = getProfessionIcon(profession);
  const isLg = size === 'lg';
  return (
    <div
      className="inline-flex items-center gap-1 rounded-full"
      style={{
        background: 'var(--accent-soft)',
        padding: isLg ? '3px 10px' : '2px 8px',
        fontSize: isLg ? 11 : 10,
        color: 'var(--ink)',
        fontWeight: 500,
        maxWidth: '100%',
      }}
    >
      <Icon
        size={isLg ? 12 : 11}
        strokeWidth={1.8}
        style={{ color: 'var(--accent)', flexShrink: 0 }}
      />
      <span className="truncate">{profession.trim()}</span>
    </div>
  );
};

// =============================================================
// PHOTO CROP MODAL — wraps react-easy-crop for in-browser cropping.
// Works for circular profile photos (aspect=1) and rectangular group
// covers (e.g. aspect=16/9). Outputs a Blob that's then compressed +
// uploaded via the existing pipeline. NO firebase cost change —
// crop happens entirely in the browser before upload.
// =============================================================

/**
 * Helper: crops an image to the given pixel rectangle and returns a Blob.
 * Used by PhotoCropModal. Pure client-side — no network, no Firebase.
 */
const getCroppedBlob = (imageSrc, pixelCrop) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas not supported'));
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      // Output as JPEG at 92% quality — small but high quality.
      // The downstream uploadProfilePhoto/uploadGroupCover/uploadMemoryPhoto
      // will WebP-compress this further, so 92% here is fine.
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Cropping failed'));
          // Wrap as File so existing upload code (which expects File) works
          const croppedFile = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
          resolve(croppedFile);
        },
        'image/jpeg',
        0.92
      );
    };
    image.onerror = () => reject(new Error('Could not load image for cropping'));
    image.src = imageSrc;
  });
};

const PhotoCropModal = ({ open, imageSrc, aspect = 1, cropShape = 'round', title = 'Position your photo', onClose, onDone }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  // Reset state every time the modal opens
  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedPixels(null);
      setBusy(false);
    }
  }, [open, imageSrc]);

  if (!open || !imageSrc) return null;

  const handleCropComplete = (_croppedArea, croppedAreaPixels) => {
    setCroppedPixels(croppedAreaPixels);
  };

  const handleDone = async () => {
    if (!croppedPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedPixels);
      onDone(blob);
    } catch (err) {
      console.error('Crop failed:', err);
      alert('Could not crop photo. Try again.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#0A0A0A' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 shrink-0 topbar-safe"
           style={{ background: 'rgba(0,0,0,0.6)', borderBottom: '1px solid rgba(255,255,255,0.1)', minHeight: '56px' }}>
        <button onClick={onClose} disabled={busy}
          className="w-9 h-9 flex items-center justify-center -ml-2 rounded-full active:bg-white/10">
          <X size={20} style={{ color: 'white' }} />
        </button>
        <h1 className="text-[15px] font-medium" style={{ color: 'white', fontFamily: 'var(--font-display)' }}>
          {title}
        </h1>
        <button onClick={handleDone} disabled={busy || !croppedPixels}
          className="px-4 py-1.5 rounded-full text-[13px] font-medium disabled:opacity-40"
          style={{ background: 'var(--accent)', color: 'white' }}>
          {busy ? 'Saving…' : 'Done'}
        </button>
      </div>

      {/* Cropper area */}
      <div className="flex-1 relative" style={{ background: '#000' }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={aspect}
          cropShape={cropShape}
          showGrid={cropShape === 'rect'}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={handleCropComplete}
          objectFit="contain"
          minZoom={1}
          maxZoom={3}
        />
      </div>

      {/* Bottom controls */}
      <div className="px-5 py-4 shrink-0" style={{ background: 'rgba(0,0,0,0.6)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center gap-3">
          <ZoomOut size={16} style={{ color: 'rgba(255,255,255,0.7)' }} />
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1"
            style={{ accentColor: 'var(--accent)' }}
          />
          <ZoomIn size={16} style={{ color: 'rgba(255,255,255,0.7)' }} />
        </div>
        <p className="text-[11px] text-center mt-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
          Drag to position · Pinch or use slider to zoom
        </p>
      </div>
    </div>
  );
};

const GroupAvatar = ({ group, size = 48, rounded = 12, onTap = null }) => {
  if (group.photoUrl) {
    // U7: when onTap is supplied (group header only), the cover becomes
    // tappable and opens the fullscreen photo viewer, matching how post-author
    // avatars already behave. List usages omit onTap, so small avatars stay
    // non-interactive.
    const img = (
      <img src={group.photoUrl} alt={group.name} className="object-cover shrink-0"
           style={{ width: size, height: size, borderRadius: rounded,
                    border: '1px solid var(--border)' }} />
    );
    if (!onTap) return img;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onTap(); }}
        className="shrink-0 p-0 border-0 bg-transparent"
        style={{ lineHeight: 0, cursor: 'pointer' }}
        aria-label={`View ${group.name} photo`}
      >
        {img}
      </button>
    );
  }
  return (
    <div className="flex items-center justify-center shrink-0"
         style={{ width: size, height: size, borderRadius: rounded, fontSize: size * 0.5,
                  background: 'var(--surface)', border: '1px solid var(--border)' }}>
      {group.emoji}
    </div>
  );
};

const PhotoBlock = ({ photo }) => {
  // New shape: { url: '...', aspect: 'square' | 'portrait' | 'landscape' }
  if (photo?.url) {
    // V2 polish #9 — Previously forced photos into Instagram-style 4:5
    // (portrait), 16:9 (landscape), or 1:1 (square) frames with object-
    // cover, which cropped any photo whose true aspect didn't match
    // the bucketed category (most notably tall phone screenshots like
    // 9:16 ending up bin-rounded to 4:5 and losing top/bottom). Render
    // at the photo's true aspect ratio instead, capped to maxHeight so
    // a 4000px-tall portrait doesn't blow up the post card. object-
    // contain ensures no crop within the cap; the background fills
    // any letterbox cleanly.
    return (
      <div className="relative w-full overflow-hidden" style={{ borderRadius: 8, background: 'var(--bg)' }}>
        <img src={photo.url} alt=""
             className="w-full"
             draggable={false}
             onContextMenu={(e) => e.preventDefault()}
             style={{ 
               width: '100%',
               height: 'auto',
               maxHeight: 520,
               objectFit: 'contain',
               display: 'block',
               margin: '0 auto',
               userSelect: 'none',
               WebkitUserSelect: 'none',
               MozUserSelect: 'none',
               msUserSelect: 'none',
               WebkitTouchCallout: 'none', // disable iOS press-and-hold "save image"
               pointerEvents: 'auto',
             }} />
      </div>
    );
  }
 
  // Legacy shape: { tone: 'warm' | 'cool' | 'sepia', label: '...' }
  const tones = {
    warm: 'linear-gradient(135deg, #E8B98A 0%, #C45A3F 60%, #7A2E1F 100%)',
    cool: 'linear-gradient(135deg, #A8C8E8 0%, #4F7AC4 60%, #1F3A7A 100%)',
    sepia: 'linear-gradient(135deg, #D4B896 0%, #8E6E4F 60%, #4A3320 100%)',
  };
  return (
    <div className="relative w-full overflow-hidden"
         style={{ aspectRatio: '4 / 3', background: tones[photo?.tone] || tones.warm, borderRadius: 8 }}>
      <div className="absolute inset-0 mix-blend-overlay opacity-30"
           style={{ backgroundImage: `radial-gradient(circle at 30% 40%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 70% 60%, rgba(0,0,0,0.3) 0%, transparent 50%)` }} />
      <div className="absolute inset-0 opacity-20"
           style={{ backgroundImage: `repeating-linear-gradient(45deg, transparent 0, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 3px)` }} />
      <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
        <Camera size={12} className="text-white/80" />
        <span className="text-[11px] tracking-wide uppercase text-white/90"
              style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.1em' }}>{photo?.label}</span>
      </div>
    </div>
  );
};
// =============================================================
// =============================================================
// Aangan Holiday Card — auto-renders on Wall when today is a holiday
// =============================================================
const AanganHolidayCard = ({ holiday }) => (
  <div className="px-5 py-4 mx-5 my-3 rounded-2xl"
       style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
    <div className="flex items-start gap-3">
      {/* Aangan author avatar — clean A badge with verified checkmark */}
      <div className="relative shrink-0">
        <div className="w-10 h-10 rounded-full flex items-center justify-center"
             style={{ background: 'var(--ink)', color: 'var(--bg)', fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: 500 }}>
          A
        </div>
        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
             style={{ background: 'var(--accent)', border: '1.5px solid var(--accent-soft)' }}>
          <Check size={9} strokeWidth={3} style={{ color: 'white' }} />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium mb-1" style={{ color: 'var(--ink)' }}>Aangan</p>
        <p className="mb-2" style={{ fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: 500, color: 'var(--ink)', lineHeight: 1.35 }}>
          Today we celebrate {holiday.name} {holiday.emoji}
        </p>
        <p className="text-[14px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
          {holiday.message}
        </p>
      </div>
    </div>
  </div>
);

// =============================================================
// Aangan Event Card — auto-renders on Wall when today is an event
// (system post, not stored as Firestore post, doesn't count toward limits)
//
// Accepts EITHER a single event OR an array of events of the same type.
// Multiple events same type → combined card with all names listed.
// =============================================================
const AanganEventCard = ({ event, events }) => {
  // Normalize to an array. If single event passed, wrap it.
  const list = events || [event];
  if (!list.length) return null;
  
  const type = list[0].type;
  // Deduplicate titles in case the same person is added twice
  const uniqueTitles = Array.from(new Set(list.map(e => e.title.trim())));
  
  // Format the names: "A", "A & B", "A, B & C", "A, B, C & 2 others"
  const formatNames = (names) => {
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    if (names.length === 3) return `${names[0]}, ${names[1]} & ${names[2]}`;
    // 4+ — show first 3 then count
    const first = names.slice(0, 3).join(', ');
    return `${first} & ${names.length - 3} ${names.length - 3 === 1 ? 'other' : 'others'}`;
  };
  
  const namesText = formatNames(uniqueTitles);
  const isPlural = uniqueTitles.length > 1;
  
  const meta = {
    birthday: {
      headline: isPlural
        ? `Today we celebrate ${namesText}'s birthdays 🎂`
        : `Today we celebrate ${namesText}'s birthday 🎂`,
      body: `Let's make their day a little more special.`,
    },
    anniversary: {
      headline: isPlural
        ? `Cheers to ${namesText} on their anniversaries 💍`
        : `Cheers to ${namesText} on their anniversary 💍`,
      body: `Wishing many more years of love and laughter.`,
    },
    event: {
      // For events, show as a list since each event is distinct
      headline: isPlural
        ? `Today's events 🎉`
        : `Today: ${uniqueTitles[0]} 🎉`,
      body: isPlural
        ? null // we'll render the list below instead
        : `Something special is happening — see you there.`,
      isList: isPlural,
    },
  }[type] || {
    headline: namesText,
    body: `A small moment worth noting today.`,
  };

  return (
    <div className="px-5 py-4 mx-5 my-3 rounded-2xl"
         style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <div className="w-10 h-10 rounded-full flex items-center justify-center"
               style={{ background: 'var(--ink)', color: 'var(--bg)', fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: 500 }}>
            A
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
               style={{ background: 'var(--accent)', border: '1.5px solid var(--accent-soft)' }}>
            <Check size={9} strokeWidth={3} style={{ color: 'white' }} />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-medium mb-1" style={{ color: 'var(--ink)' }}>Aangan</p>
          <p className="mb-2" style={{ fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: 500, color: 'var(--ink)', lineHeight: 1.35 }}>
            {meta.headline}
          </p>
          {meta.isList ? (
            <ul className="space-y-1 mt-2">
              {uniqueTitles.map((title, i) => (
                <li key={i} className="text-[14px] leading-relaxed" style={{ color: 'var(--ink)' }}>
                  • {title}
                </li>
              ))}
            </ul>
          ) : meta.body && (
            <p className="text-[14px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
              {meta.body}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================
// Calendar List Modal — show all events for a batch
// Sorts by upcoming first (today + future), then past events.
// Yearly recurring events (birthdays, anniversaries) show next occurrence.
// =============================================================
const CalendarListModal = ({ open, onClose, events, isAdmin, currentUserId, onDelete, onAddNew }) => {
  if (!open) return null;
  
  // Compute next occurrence for each event
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const enriched = events.map(e => {
    let nextDate;
    if (e.recurring === 'yearly') {
      // For yearly, find next occurrence based on monthDay
      const [m, d] = (e.monthDay || '01-01').split('-').map(Number);
      const candidate = new Date(today.getFullYear(), m - 1, d);
      candidate.setHours(0, 0, 0, 0);
      // If this year's date already passed, use next year
      if (candidate < today) candidate.setFullYear(today.getFullYear() + 1);
      nextDate = candidate;
    } else {
      nextDate = new Date(e.date);
      nextDate.setHours(0, 0, 0, 0);
    }
    
    const daysFromToday = Math.floor((nextDate - today) / (1000 * 60 * 60 * 24));
    return { ...e, nextDate, daysFromToday };
  });
  
  // Split into upcoming (today + future) vs past (one-time events that already happened)
  const upcoming = enriched.filter(e => e.daysFromToday >= 0).sort((a, b) => a.nextDate - b.nextDate);
  const past = enriched.filter(e => e.daysFromToday < 0).sort((a, b) => b.nextDate - a.nextDate);
  
  const formatDate = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const formatRelative = (days) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days > 0 && days < 7) return `In ${days} days`;
    if (days > 0 && days < 30) return `In ${Math.ceil(days / 7)} weeks`;
    if (days > 0) return `In ${Math.ceil(days / 30)} months`;
    return null;
  };
  
  const typeMeta = {
    birthday: { emoji: '🎂', label: 'Birthday' },
    anniversary: { emoji: '💍', label: 'Anniversary' },
    event: { emoji: '🎉', label: 'Event' },
  };
  
  return (
    <div className="absolute inset-0 z-40 flex items-end" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full rounded-t-3xl flex flex-col" 
           style={{ background: 'var(--bg)', maxHeight: '85%' }}>
        <div className="w-12 h-1 rounded-full mx-auto my-3 shrink-0" style={{ background: 'var(--border)' }} />
        
        <div className="px-5 pb-3 flex items-center justify-between shrink-0">
          <h2 className="text-[20px]" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
            Calendar
          </h2>
          <button onClick={onAddNew}
            className="px-3 py-1.5 rounded-full text-[12px] font-medium flex items-center gap-1"
            style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
            <Plus size={12} /> Add
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto px-5 sheet-bottom-safe">
          {events.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <CalendarDays size={20} style={{ color: 'var(--ink-soft)' }} />
              </div>
              <p className="text-[14px] mb-1" style={{ color: 'var(--ink)' }}>No events yet</p>
              <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
                Add birthdays, anniversaries, or events for your batch.
              </p>
            </div>
          ) : (
            <>
              {upcoming.length > 0 && (
                <>
                  <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>
                    Upcoming
                  </p>
                  <div className="space-y-2 mb-5">
                    {upcoming.map(e => {
                      const meta = typeMeta[e.type] || { emoji: '📌', label: 'Event' };
                      const canDelete = e.createdBy === currentUserId || isAdmin;
                      return (
                        <div key={e.id} className="rounded-2xl p-3 flex items-center gap-3"
                             style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
                               style={{ background: 'var(--accent-soft)' }}>
                            {meta.emoji}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] font-medium truncate" style={{ color: 'var(--ink)' }}>
                              {e.title}
                            </p>
                            <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
                              {formatDate(e.nextDate)}
                              {formatRelative(e.daysFromToday) && (
                                <span style={{ color: 'var(--accent)', fontWeight: 500 }}>
                                  {' · '}{formatRelative(e.daysFromToday)}
                                </span>
                              )}
                              {e.recurring === 'yearly' && ' · yearly'}
                            </p>
                            {e.description && (
                              <p className="text-[11px] italic truncate mt-0.5" style={{ color: 'var(--ink-soft)' }}>
                                {e.description}
                              </p>
                            )}
                          </div>
                          {canDelete && (
                            <button onClick={() => {
                              if (window.confirm(`Delete "${e.title}"?`)) onDelete(e.id);
                            }}
                              className="w-8 h-8 rounded-full flex items-center justify-center active:bg-black/5">
                              <Trash2 size={14} style={{ color: '#B33A3A' }} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              
              {past.length > 0 && (
                <>
                  <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>
                    Past one-time events
                  </p>
                  <div className="space-y-2">
                    {past.map(e => {
                      const meta = typeMeta[e.type] || { emoji: '📌', label: 'Event' };
                      const canDelete = e.createdBy === currentUserId || isAdmin;
                      return (
                        <div key={e.id} className="rounded-2xl p-3 flex items-center gap-3 opacity-60"
                             style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
                               style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            {meta.emoji}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] truncate" style={{ color: 'var(--ink)' }}>
                              {e.title}
                            </p>
                            <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
                              {formatDate(e.nextDate)}
                            </p>
                          </div>
                          {canDelete && (
                            <button onClick={() => {
                              if (window.confirm(`Delete "${e.title}"?`)) onDelete(e.id);
                            }}
                              className="w-8 h-8 rounded-full flex items-center justify-center active:bg-black/5">
                              <Trash2 size={14} style={{ color: '#B33A3A' }} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================
// Add-Event Modal — calendar entry for a batch
// Centered, compact, drag-anywhere-to-dismiss (touch + mouse)
// =============================================================
const AddEventModal = ({ open, onClose, onSubmit, busy }) => {
  const [type, setType] = useState('birthday');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  // Drag-to-dismiss state
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(null);
  const isDragging = useRef(false);

  useEffect(() => {
    if (open) {
      setType('birthday'); setTitle(''); setDate(''); setDescription(''); setError('');
      setDragY(0);
      isDragging.current = false;
      dragStartY.current = null;
    }
  }, [open]);

  if (!open) return null;

  const typeOptions = [
    { value: 'birthday', label: 'Birthday', emoji: '🎂', placeholder: "e.g. Priya's son" },
    { value: 'anniversary', label: 'Anniversary', emoji: '💍', placeholder: 'e.g. Anjali & Rohan' },
    { value: 'event', label: 'Event', emoji: '🎉', placeholder: 'e.g. Diwali party' },
  ];

  const handleSubmit = () => {
    setError('');
    if (!title.trim()) return setError('Please add a name or title.');
    if (!date) return setError('Please pick a date.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return setError('Date format should be YYYY-MM-DD.');
    onSubmit({ type, title: title.trim(), date, description: description.trim() });
  };

  // Drag handlers — work from anywhere on the sheet (except inputs)
  const isInteractive = (el) => {
    if (!el) return false;
    const tag = el.tagName?.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'button' || tag === 'select';
  };

  const startDrag = (clientY, target) => {
    if (isInteractive(target)) return; // don't start drag from form fields
    dragStartY.current = clientY;
    isDragging.current = true;
  };
  const moveDrag = (clientY) => {
    if (!isDragging.current || dragStartY.current === null) return;
    const delta = clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const endDrag = () => {
    if (!isDragging.current) return;
    if (dragY > 80) onClose();
    else setDragY(0);
    dragStartY.current = null;
    isDragging.current = false;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
         style={{ background: 'rgba(0,0,0,0.45)' }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           onTouchStart={(e) => startDrag(e.touches[0].clientY, e.target)}
           onTouchMove={(e) => moveDrag(e.touches[0].clientY)}
           onTouchEnd={endDrag}
           onMouseDown={(e) => startDrag(e.clientY, e.target)}
           onMouseMove={(e) => isDragging.current && moveDrag(e.clientY)}
           onMouseUp={endDrag}
           onMouseLeave={endDrag}
           className="w-full max-w-sm rounded-2xl px-5 pt-2 pb-4 shadow-2xl"
           style={{
             background: 'var(--bg)',
             transform: `translateY(${dragY}px)`,
             transition: !isDragging.current ? 'transform 0.22s ease-out' : 'none',
             touchAction: 'pan-y',
             userSelect: 'none',
           }}>
        {/* Drag handle */}
        <div className="flex justify-center pb-2 cursor-grab active:cursor-grabbing">
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--border)' }} />
        </div>

        {/* Header */}
        <div className="flex items-baseline justify-between mb-3">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: 500, color: 'var(--ink)', lineHeight: 1.2 }}>
            Add to calendar
          </h2>
          <span className="text-[10px]" style={{ color: 'var(--ink-soft)' }}>This batch only</span>
        </div>

        {/* Type picker */}
        <div className="flex gap-1.5 mb-2.5">
          {typeOptions.map(opt => (
            <button key={opt.value} onClick={() => setType(opt.value)}
              className="flex-1 py-1.5 rounded-lg flex items-center justify-center gap-1 active:scale-[0.97] transition-transform"
              style={{
                background: type === opt.value ? 'var(--ink)' : 'var(--surface)',
                border: '1px solid var(--border)',
                color: type === opt.value ? 'var(--bg)' : 'var(--ink)',
              }}>
              <span className="text-[12px]">{opt.emoji}</span>
              <span className="text-[11px] font-medium">{opt.label}</span>
            </button>
          ))}
        </div>

        {/* Title */}
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
               placeholder={typeOptions.find(t => t.value === type)?.placeholder} maxLength={60}
               className="w-full px-3 py-2 rounded-lg text-[13px] outline-none mb-2"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />

        {/* Date + helper */}
        <div className="flex items-center gap-2 mb-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 className="flex-1 px-3 py-2 rounded-lg text-[13px] outline-none"
                 style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
          <span className="text-[10px]" style={{ color: 'var(--ink-soft)' }}>
            {(type === 'birthday' || type === 'anniversary') ? 'yearly' : 'one-time'}
          </span>
        </div>

        {/* Note (optional, single line by default) */}
        <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Note (optional)" maxLength={140} rows={1}
                  className="w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-none"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />

        {error && <p className="text-[11px] mt-2" style={{ color: '#B33A3A' }}>{error}</p>}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-3">
          <button onClick={onClose} disabled={busy}
            className="px-2 py-2 text-[13px] active:opacity-70"
            style={{ color: 'var(--ink-soft)', background: 'transparent', border: 'none' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={busy || !title.trim() || !date}
            className="px-5 py-2 rounded-lg text-[13px] font-medium disabled:opacity-40 active:scale-[0.98] transition-transform"
            style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TopBar = ({ title, onBack, right }) => (
  <div className="flex items-center justify-between px-4 sticky top-0 z-10 shrink-0 topbar-safe"
       style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)', minHeight: '56px' }}>
    {onBack ? (
      <button onClick={onBack} className="w-9 h-9 flex items-center justify-center -ml-2 rounded-full active:bg-black/5">
        <ArrowLeft size={20} style={{ color: 'var(--ink)' }} />
      </button>
    ) : <div className="w-9" />}
    <h1 className="text-base font-medium" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>{title}</h1>
    <div className="w-9 flex items-center justify-end">{right}</div>
  </div>
);

// =============================================================
// WELCOME / AUTH (carried over)
// =============================================================
const Welcome = ({ onStart }) => (
  <div className="flex-1 flex flex-col px-6 pb-8 relative overflow-hidden profile-header-safe">
    <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-30"
         style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)' }} />
    <div className="absolute top-32 -left-16 w-48 h-48 rounded-full opacity-20"
         style={{ background: 'radial-gradient(circle, var(--gold) 0%, transparent 70%)' }} />
    <div className="relative flex items-center justify-between mb-8">
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--ink)' }}>
          <Logo size={22} color="var(--bg)" />
        </div>
        <div>
          <p className="text-[18px] leading-none" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 600 }}>{APP_NAME}</p>
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--ink-soft)' }}>The gathering place</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 opacity-70">
        <p className="text-[9px] uppercase tracking-[0.15em]" style={{ color: 'var(--ink-soft)' }}>From</p>
        <img src="/aap-saathi-logo.png" alt="AapSaathi"
             style={{ height: 16, width: 'auto' }}
             onError={(e) => { e.target.style.display = 'none'; }} />
      </div>
    </div>
    <div className="relative flex-1 flex flex-col justify-center">
      <h1 className="leading-[0.95] mb-5"
          style={{ fontSize: '50px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, letterSpacing: '-0.02em' }}>
        Find the<br />
        <em style={{ color: 'var(--accent)', fontStyle: 'italic' }}>people</em><br />
        you grew up<br />with.
      </h1>
      <p className="text-[15px] leading-relaxed mb-1" style={{ color: 'var(--ink-soft)', maxWidth: 300 }}>
        Reconnect with your school batch, college mates, old colleagues, neighbours, and family — wherever they are now.
      </p>
    </div>
    <div className="relative space-y-3 pb-2">
      <button onClick={onStart}
        className="w-full h-14 rounded-2xl font-medium text-[15px] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
        Get started <ArrowRight size={18} />
      </button>
      <button onClick={onStart} className="text-center text-[12px] block w-full py-2 active:opacity-70" style={{ color: 'var(--ink-soft)' }}>
        Already have an account? <span style={{ color: 'var(--accent)', fontWeight: 500 }}>Sign in</span>
      </button>
    </div>
  </div>
);

const AuthOptions = ({ onBack, onMethod }) => (
  <div className="flex-1 flex flex-col">
    <TopBar title="" onBack={onBack} />
    <div className="flex-1 flex flex-col px-6 pt-2">
      {/* Aangan logo + AapSaathi tag at top */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--ink)' }}>
            <Logo size={20} color="var(--bg)" />
          </div>
          <p className="text-[16px]" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 600 }}>
            Aangan
          </p>
        </div>
        <div className="flex items-center gap-1.5 opacity-70">
          <p className="text-[9px] uppercase tracking-[0.15em]" style={{ color: 'var(--ink-soft)' }}>From</p>
          <img src="/aap-saathi-logo.png" alt="AapSaathi"
               style={{ height: 16, width: 'auto' }}
               onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
      </div>
      
      <h2 className="mb-2" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.1 }}>
        How would you like to sign in?
      </h2>
      <p className="text-[14px] mb-8" style={{ color: 'var(--ink-soft)' }}>We use the most secure option you choose. Switch any time.</p>
      <div className="space-y-3">
        <button onClick={() => onMethod('google')}
          className="w-full h-14 rounded-2xl flex items-center gap-3 px-5 active:scale-[0.99] transition-transform"
          style={{ background: 'var(--bg)', border: '1.5px solid var(--ink)' }}>
          <svg width="20" height="20" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6,20.1H42V20H24v8h11.3c-1.6,4.7-6.1,8-11.3,8c-6.6,0-12-5.4-12-12s5.4-12,12-12c3.1,0,5.8,1.2,7.9,3.1l5.7-5.7C34.9,6.1,29.7,4,24,4C13,4,4,13,4,24s9,20,20,20c11,0,20-9,20-20C44,22.7,43.9,21.4,43.6,20.1z"/>
            <path fill="#FF3D00" d="M6.3,14.7l6.6,4.8C14.7,15.1,18.9,12,24,12c3.1,0,5.8,1.2,7.9,3.1l5.7-5.7C34.9,6.1,29.7,4,24,4C16.3,4,9.7,8.3,6.3,14.7z"/>
            <path fill="#4CAF50" d="M24,44c5.6,0,10.7-2.1,14.6-5.6l-6.7-5.7c-2,1.5-4.6,2.4-7.9,2.4c-5.2,0-9.6-3.3-11.3-7.9l-6.5,5C9.5,39.6,16.2,44,24,44z"/>
            <path fill="#1976D2" d="M43.6,20.1H42V20H24v8h11.3c-0.8,2.2-2.2,4.2-4.1,5.6c0,0,0,0,0,0l6.7,5.7C37.6,40.1,44,35,44,24C44,22.7,43.9,21.4,43.6,20.1z"/>
          </svg>
          <span className="text-[15px] font-medium flex-1 text-left" style={{ color: 'var(--ink)' }}>Continue with Google</span>
        </button>
        {/*
          Phone OTP deferred for web/PWA launch.
          Reasons:
            - Requires reCAPTCHA Enterprise setup (Path 1) which is fiddly on web
            - SMS costs (~₹5/verification) hard to control without strict abuse protection
            - 95% of Indian web users sign in via Google or email anyway
          Re-enable when:
            - We launch native iOS/Android apps (phone OTP works trivially in mobile SDKs)
            - Or after wiring up SMS abuse protection + cost caps
          The PhoneAuthFlow component + auth.js helpers stay in code, ready to re-enable.
        <button onClick={() => onMethod('phone')}
          className="w-full h-14 rounded-2xl flex items-center gap-3 px-5 active:scale-[0.99] transition-transform"
          style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <KeyRound size={14} />
          </div>
          <span className="text-[15px] font-medium flex-1 text-left">Phone OTP (SMS)</span>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: 'var(--accent)', color: 'white' }}>Recommended</span>
        </button>
        */}
        <button onClick={() => onMethod('email')}
          className="w-full h-14 rounded-2xl flex items-center gap-3 px-5 active:scale-[0.99] transition-transform"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <Mail size={18} style={{ color: 'var(--ink)' }} />
          <span className="text-[15px] font-medium flex-1 text-left" style={{ color: 'var(--ink)' }}>Email & password</span>
        </button>
      </div>
      <div className="flex-1" />
      <p className="text-[11px] leading-relaxed sheet-bottom-safe" style={{ color: 'var(--ink-soft)' }}>
        We follow India's DPDPA 2023. Your email stays private — never shown to other members.
      </p>
    </div>
  </div>
);

const PhoneAuthFlow = ({ onBack, onContinue }) => {
  const [step, setStep] = useState('enter-phone'); // 'enter-phone' | 'verify-otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const friendlyError = (err) => {
    const c = err?.code || '';
    if (c === 'auth/invalid-phone-number') return 'That phone number doesn\'t look right.';
    if (c === 'auth/too-many-requests') return 'Too many attempts. Wait a few minutes.';
    if (c === 'auth/quota-exceeded') return 'Daily SMS limit reached. Try tomorrow.';
    if (c === 'auth/invalid-verification-code') return 'Wrong OTP. Try again.';
    if (c === 'auth/code-expired') return 'OTP expired. Request a new one.';
    if (c === 'auth/network-request-failed') return 'Check your internet connection.';
    if (c === 'auth/captcha-check-failed') return 'reCAPTCHA failed. Refresh the page and try again.';
    return 'Something went wrong. Please try again.';
  };

  const formatPhone = (raw) => {
    // If user types only digits and number is 10 long, assume Indian number
    const digits = raw.replace(/\D/g, '');
    if (raw.startsWith('+')) return raw;
    if (digits.length === 10) return '+91' + digits;
    if (digits.length === 12 && digits.startsWith('91')) return '+' + digits;
    return raw; // user entered something explicit; trust them
  };

  const handleSendOtp = async () => {
    setError(''); setBusy(true);
    try {
      const formatted = formatPhone(phone.trim());
      if (!/^\+\d{10,15}$/.test(formatted)) {
        setError('Enter a valid 10-digit number (we\'ll add +91).');
        setBusy(false);
        return;
      }
      const result = await startPhoneSignIn(formatted, 'recaptcha-container');
      setConfirmation(result);
      setStep('verify-otp');
      setBusy(false);
    } catch (err) {
      console.error('Phone OTP send failed:', err);
      setError(friendlyError(err));
      setBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError(''); setBusy(true);
    try {
      if (!/^\d{6}$/.test(otp.trim())) {
        setError('OTP should be 6 digits.');
        setBusy(false);
        return;
      }
      await confirmPhoneOtp(confirmation, otp.trim());
      onContinue();
    } catch (err) {
      console.error('Phone OTP verify failed:', err);
      setError(friendlyError(err));
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <TopBar title="" onBack={onBack} />
      {/* Invisible reCAPTCHA mounts here */}
      <div id="recaptcha-container"></div>

      <div className="flex-1 px-6 pt-2">
        {step === 'enter-phone' && (
          <>
            <h2 className="mb-2" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.1 }}>
              Sign in with phone
            </h2>
            <p className="text-[14px] mb-6" style={{ color: 'var(--ink-soft)' }}>
              We'll send you a 6-digit code by SMS.
            </p>
            <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Phone number</label>
            <input type="tel" placeholder="98765 43210" value={phone}
                   onChange={(e) => setPhone(e.target.value)} autoComplete="tel" inputMode="tel"
                   className="w-full px-4 py-4 rounded-2xl text-[16px] outline-none mb-3"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
            <p className="text-[11px] mb-6" style={{ color: 'var(--ink-soft)' }}>
              Just type your 10-digit number — we'll add +91 automatically. Or include the country code if you're outside India.
            </p>
            {error && <p className="text-[12px] mb-3" style={{ color: '#B33A3A' }}>{error}</p>}
            <button onClick={handleSendOtp} disabled={!phone.trim() || busy}
              className="w-full h-14 rounded-2xl font-medium text-[15px] disabled:opacity-40 active:scale-[0.98] transition-transform"
              style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
              {busy ? 'Sending OTP…' : 'Send OTP'}
            </button>
          </>
        )}

        {step === 'verify-otp' && (
          <>
            <h2 className="mb-2" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.1 }}>
              Enter the OTP
            </h2>
            <p className="text-[14px] mb-6" style={{ color: 'var(--ink-soft)' }}>
              We sent a 6-digit code to <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{formatPhone(phone)}</span>.
              <button onClick={() => { setStep('enter-phone'); setOtp(''); setError(''); }} className="ml-1" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
                Change number
              </button>
            </p>
            <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>6-digit OTP</label>
            <input type="text" placeholder="123456" value={otp} maxLength={6}
                   onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                   autoComplete="one-time-code" inputMode="numeric"
                   className="w-full px-4 py-4 rounded-2xl text-[20px] tracking-widest text-center outline-none mb-3"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)', fontFamily: 'monospace', letterSpacing: '0.4em' }} />
            {error && <p className="text-[12px] mb-3" style={{ color: '#B33A3A' }}>{error}</p>}
            <button onClick={handleVerifyOtp} disabled={otp.length !== 6 || busy}
              className="w-full h-14 rounded-2xl font-medium text-[15px] disabled:opacity-40 active:scale-[0.98] transition-transform"
              style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
              {busy ? 'Verifying…' : 'Verify & continue'}
            </button>
            <p className="text-[11px] mt-4 text-center" style={{ color: 'var(--ink-soft)' }}>
              Didn't get the OTP? Wait 30 seconds, then go back and try again.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const EmailAuthFlow = ({ onBack, onContinue }) => {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot'
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const friendlyError = (err) => {
    const c = err?.code || '';
    if (c === 'aangan/account-exists-different-provider') return err.message;
    if (c === 'auth/invalid-email') return 'That doesn\'t look like a valid email.';
    if (c === 'auth/user-not-found') return 'No account with that email. Tap "Create account" below.';
    if (c === 'auth/wrong-password' || c === 'auth/invalid-credential') return 'Wrong email or password.';
    if (c === 'auth/email-already-in-use') return 'An account already exists with that email. Try signing in.';
    if (c === 'auth/weak-password') return 'Password must be at least 6 characters.';
    if (c === 'auth/too-many-requests') return 'Too many attempts. Wait a few minutes and try again.';
    if (c === 'auth/network-request-failed') return 'Check your internet connection.';
    return 'Something went wrong. Please try again.';
  };

  const handleSubmit = async () => {
    setError(''); setInfo(''); setBusy(true);
    try {
      if (mode === 'forgot') {
        await sendPasswordReset(email.trim());
        setInfo('Password reset email sent. Check your inbox.');
        setBusy(false);
        return;
      }
      if (mode === 'signup') {
        await signUpWithEmail(email.trim(), pwd);
      } else {
        await signInWithEmail(email.trim(), pwd);
      }
      onContinue();
    } catch (err) {
      console.error('Email auth failed:', err);
      setError(friendlyError(err));
      setBusy(false);
    }
  };

  const canSubmit = mode === 'forgot' ? email.trim().length > 3 : (email.trim() && pwd.length >= 6);

  return (
    <div className="flex-1 flex flex-col">
      <TopBar title="" onBack={onBack} />
      <div className="flex-1 overflow-y-auto px-6 pt-2 sheet-bottom-safe">
        <h2 className="mb-2" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.1 }}>
          {mode === 'signup' ? 'Create your account' : mode === 'forgot' ? 'Reset password' : 'Sign in with email'}
        </h2>
        <p className="text-[14px] mb-6" style={{ color: 'var(--ink-soft)' }}>
          {mode === 'forgot'
            ? 'We\'ll email you a link to reset your password.'
            : 'Use your work or personal email. We never share it.'}
        </p>

        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Email</label>
        <input type="email" placeholder="you@example.com" value={email}
               onChange={(e) => setEmail(e.target.value)} autoComplete="email"
               className="w-full px-4 py-4 rounded-2xl text-[15px] outline-none mb-4"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />

        {mode !== 'forgot' && (
          <>
            <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Password</label>
            <input type="password" placeholder="At least 6 characters" value={pwd}
                   onChange={(e) => setPwd(e.target.value)}
                   autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                   className="w-full px-4 py-4 rounded-2xl text-[15px] outline-none mb-2"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
          </>
        )}

        {mode === 'signin' && (
          <button onClick={() => { setMode('forgot'); setError(''); setInfo(''); }}
                  className="text-[12px] mb-3" style={{ color: 'var(--accent)' }}>
            Forgot password?
          </button>
        )}

        {error && <p className="text-[12px] mb-3" style={{ color: '#B33A3A' }}>{error}</p>}
        {info && <p className="text-[12px] mb-3" style={{ color: '#3F8E5C' }}>{info}</p>}

        <button onClick={handleSubmit} disabled={!canSubmit || busy}
          className="w-full h-14 rounded-2xl font-medium text-[15px] disabled:opacity-40 active:scale-[0.98] transition-transform"
          style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
          {busy ? 'Please wait…' :
            mode === 'signup' ? 'Create account' :
            mode === 'forgot' ? 'Send reset email' :
            'Sign in'}
        </button>

        <div className="text-center mt-5">
          {mode === 'signin' && (
            <button onClick={() => { setMode('signup'); setError(''); setInfo(''); }}
                    className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>
              New to Aangan? <span style={{ color: 'var(--accent)', fontWeight: 500 }}>Create account</span>
            </button>
          )}
          {mode === 'signup' && (
            <button onClick={() => { setMode('signin'); setError(''); setInfo(''); }}
                    className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>
              Already have an account? <span style={{ color: 'var(--accent)', fontWeight: 500 }}>Sign in</span>
            </button>
          )}
          {mode === 'forgot' && (
            <button onClick={() => { setMode('signin'); setError(''); setInfo(''); }}
                    className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>
              ← Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const GoogleAuthFlow = ({ onBack, onContinue }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

 const handleGoogleSignIn = async () => {
    setBusy(true);
    setError('');
    try {
      await signInWithGoogle();
      onContinue();
    } catch (err) {
      console.error('Google sign-in failed:', err);
      let msg = 'Sign-in failed. Please try again.';
      if (err.code === 'aangan/account-exists-different-provider') {
        msg = err.message;  // friendly cross-method message from auth.js
      } else if (err.code === 'auth/popup-closed-by-user') {
        msg = 'Sign-in cancelled.';
      } else if (err.code === 'auth/popup-blocked') {
        msg = 'Pop-up was blocked. Please allow pop-ups for this site.';
      } else if (err.code === 'auth/unauthorized-domain') {
        msg = 'This domain is not authorized. Add it in Firebase Console.';
      }
      setError(msg);
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <TopBar title="" onBack={onBack} />
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
             style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <svg width="28" height="28" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6,20.1H42V20H24v8h11.3c-1.6,4.7-6.1,8-11.3,8c-6.6,0-12-5.4-12-12s5.4-12,12-12c3.1,0,5.8,1.2,7.9,3.1l5.7-5.7C34.9,6.1,29.7,4,24,4C13,4,4,13,4,24s9,20,20,20c11,0,20-9,20-20C44,22.7,43.9,21.4,43.6,20.1z"/>
            <path fill="#FF3D00" d="M6.3,14.7l6.6,4.8C14.7,15.1,18.9,12,24,12c3.1,0,5.8,1.2,7.9,3.1l5.7-5.7C34.9,6.1,29.7,4,24,4C16.3,4,9.7,8.3,6.3,14.7z"/>
            <path fill="#4CAF50" d="M24,44c5.6,0,10.7-2.1,14.6-5.6l-6.7-5.7c-2,1.5-4.6,2.4-7.9,2.4c-5.2,0-9.6-3.3-11.3-7.9l-6.5,5C9.5,39.6,16.2,44,24,44z"/>
            <path fill="#1976D2" d="M43.6,20.1H42V20H24v8h11.3c-0.8,2.2-2.2,4.2-4.1,5.6c0,0,0,0,0,0l6.7,5.7C37.6,40.1,44,35,44,24C44,22.7,43.9,21.4,43.6,20.1z"/>
          </svg>
        </div>
        <h2 className="mb-2" style={{ fontSize: '24px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
          Continue with Google
        </h2>
        <p className="text-[13px] mb-8" style={{ color: 'var(--ink-soft)' }}>
          We'll only get your name & email. We won't post or read anything.
        </p>
        <button onClick={handleGoogleSignIn} disabled={busy}
          className="w-full h-14 rounded-2xl font-medium text-[15px] active:scale-[0.98] transition-transform disabled:opacity-60"
          style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
          {busy ? 'Opening Google…' : 'Allow & continue'}
        </button>
        {error && (
          <p className="text-[12px] mt-3" style={{ color: '#B33A3A' }}>{error}</p>
        )}
      </div>
    </div>
  );
};

// =============================================================
// PRIVACY GATE
// =============================================================
const PrivacyGate = ({ onAccept, onBack }) => {
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [agreedRules, setAgreedRules] = useState(false);
  const [agreedAge, setAgreedAge] = useState(false);
  const all = agreedPrivacy && agreedRules && agreedAge;
  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: 0, height: '100%' }}>
      <TopBar title="" onBack={onBack} />
      <div className="flex-1 px-6 pt-2 overflow-y-auto" style={{ minHeight: 0 }}>
        <h2 className="mb-2" style={{ fontSize: '26px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.15 }}>
          A small house keeping note before we start.
        </h2>
        <p className="text-[14px] mb-6" style={{ color: 'var(--ink-soft)' }}>
          {APP_NAME} is for genuine reconnections — not spam, not abuse. Please read & agree.
        </p>
        <div className="space-y-3 mb-4">
          {[
            ['agreedAge', agreedAge, setAgreedAge, "I'm 18 or older.", `${APP_NAME} is for adults only as of now.`],
            ['agreedPrivacy', agreedPrivacy, setAgreedPrivacy, 'I agree to the Privacy Policy.', "We follow India's DPDPA 2023. You can delete your data any time from Settings."],
            ['agreedRules', agreedRules, setAgreedRules, 'I agree to the Community Rules.', 'Be kind. No abuse, harassment, hate speech, scams or impersonation.'],
          ].map(([k, val, setter, title, desc]) => (
            <button key={k} onClick={() => setter(!val)} className="w-full text-left flex items-start gap-3 p-4 rounded-2xl"
                    style={{ background: val ? 'var(--accent-soft)' : 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="w-5 h-5 rounded shrink-0 flex items-center justify-center mt-0.5"
                   style={{ background: val ? 'var(--ink)' : 'var(--bg)', border: '1.5px solid var(--ink)' }}>
                {val && <Check size={12} color="white" strokeWidth={3} />}
              </div>
              <div className="flex-1">
                <p className="text-[13px] font-medium mb-0.5" style={{ color: 'var(--ink)' }}>{title}</p>
                <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="px-6 pt-2 sheet-bottom-safe">
        <button onClick={onAccept} disabled={!all}
          className="w-full h-14 rounded-2xl font-medium text-[15px] disabled:opacity-40 active:scale-[0.98] transition-transform"
          style={{ background: 'var(--ink)', color: 'var(--bg)' }}>Agree & continue</button>
      </div>
    </div>
  );
};

// =============================================================
// PROFILE SETUP
// =============================================================
const ProfileSetup = ({ onBack, onContinue }) => {
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [colorIdx, setColorIdx] = useState(0);
  const [photoUrl, setPhotoUrl] = useState(null);
  const fileInputRef = useRef(null);
  const colors = ['#C45A3F', '#3F6BC4', '#7A4FC4', '#3F8E5C', '#B8893A', '#5A4FC4'];
  const initials = name.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
  const [uploading, setUploading] = useState(false);
  // Crop modal state — holds the local preview URL of the picked photo
  // until user crops + confirms. Then we upload the cropped blob.
  const [cropSrc, setCropSrc] = useState(null);
  // Step 1: User picks a file from their device
  // We don't upload yet — first show the crop modal.
  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-picking same file later
    
    // Open the crop modal with this image
    const localPreview = URL.createObjectURL(file);
    setCropSrc(localPreview);
  };

  // Step 2: User finished cropping — now upload the cropped blob.
  // ProfileSetup runs BEFORE the user has a Firestore profile, so we
  // can't upload to Storage yet. We just hold the cropped image as a
  // local data URL and save it when the user taps Continue.
  const handleCropDone = (croppedFile) => {
    // Read the cropped File as a data URL for local preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoUrl(ev.target.result);
      // Clean up the original picked-file URL
      if (cropSrc) URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    };
    reader.onerror = () => {
      alert('Could not load cropped photo');
      setCropSrc(null);
    };
    reader.readAsDataURL(croppedFile);
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };
  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: 0, height: '100%' }}>
      <TopBar title="" onBack={onBack} />
      <div className="flex-1 px-6 pt-2 overflow-y-auto" style={{ minHeight: 0 }}>
        <h2 className="mb-2" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.1 }}>About you</h2>
        <p className="text-[14px] mb-8" style={{ color: 'var(--ink-soft)' }}>This is what your batchmates will see.</p>
        <div className="flex flex-col items-center mb-6">
          <button onClick={() => fileInputRef.current?.click()} className="relative mb-3 active:scale-[0.97] transition-transform">
            {photoUrl ? (
              <img src={photoUrl} alt="profile" className="w-24 h-24 rounded-full object-cover" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
            ) : (
              <div className="w-24 h-24 rounded-full flex items-center justify-center text-white font-medium"
                   style={{ background: colors[colorIdx], fontSize: 36, fontFamily: 'var(--font-display)' }}>{initials}</div>
            )}
            <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center"
                 style={{ background: 'var(--ink)', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}><Camera size={14} color="white" /></div>
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
          {photoUrl ? (
            <button onClick={() => setPhotoUrl(null)} className="text-[12px]" style={{ color: 'var(--accent)' }}>Remove photo</button>
          ) : (
            <>
              <p className="text-[12px] mb-2" style={{ color: 'var(--ink-soft)' }}>Tap to upload, or pick a color</p>
              <div className="flex gap-2">
                {colors.map((c, i) => (
                  <button key={c} onClick={() => setColorIdx(i)} className="w-7 h-7 rounded-full transition-transform"
                    style={{ background: c, transform: i === colorIdx ? 'scale(1.15)' : 'scale(1)',
                             boxShadow: i === colorIdx ? `0 0 0 2px var(--bg), 0 0 0 4px ${c}` : 'none' }} />
                ))}
              </div>
            </>
          )}
        </div>
        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Full name</label>
        <input type="text" placeholder="Priya Sharma" value={name} onChange={(e) => setName(e.target.value)}
               className="w-full p-4 rounded-2xl text-[15px] outline-none mb-5"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>One line about you</label>
        <textarea placeholder="Designer, dog parent, runs on filter coffee." value={bio} onChange={(e) => setBio(e.target.value)} rows={2}
                  className="w-full p-4 rounded-2xl text-[15px] outline-none resize-none mb-6"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
      </div>
      <div className="px-6 pt-2 sheet-bottom-safe">
        <button onClick={() => onContinue({ name: name || 'You', bio, color: colors[colorIdx], photoUrl, initials, status: '' })}
          disabled={!name}
          className="w-full h-14 rounded-2xl font-medium text-[15px] disabled:opacity-40 active:scale-[0.98] transition-transform"
          style={{ background: 'var(--ink)', color: 'var(--bg)' }}>Continue</button>
      </div>  
      <PhotoCropModal
        open={!!cropSrc}
        imageSrc={cropSrc}
        aspect={1}
        cropShape="round"
        title="Position your profile photo"
        onClose={handleCropCancel}
        onDone={handleCropDone}
      />
    </div>
  );
};

// =============================================================
// UNAUTH BIZ PROFILE REDIRECT (Phase 2.4 C8.5)
// =============================================================
// Tiny landing component shown for the brief moment between detecting
// "unauthenticated user trying to load a V2 biz profile" and the state
// updates that route them through the deep-link onboarding flow.
//
// Why this exists: the V2 intercept at the bottom of App's render
// catches ?v2=biz-profile&id=X for ALL users. For unauth users, V2App
// mounts → BusinessProfile tries to read the biz doc → Firestore rules
// deny → "Couldn't load business" error. We want unauth users to hit
// the celebratory signup ceremony instead, but we can't setState
// during render. This component runs the state transitions in its
// own effect, then the parent re-renders without ?v2= and the regular
// deep-link flow takes over.
//
// Also serves the legacy ?v2=biz-profile&id=X URL format — anyone who
// has an older share link saved will get the right experience.

const UnauthBizProfileRedirect = ({ bizId, setPendingBusinessId, setBizDeepLinkArrivedUnauth }) => {
  useEffect(() => {
    setPendingBusinessId(bizId);
    setBizDeepLinkArrivedUnauth(true);
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', '/');
    }
  }, [bizId, setPendingBusinessId, setBizDeepLinkArrivedUnauth]);

  return (
    <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <p className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Opening your link…</p>
    </div>
  );
};

// =============================================================
// DEEP LINK SUCCESS SCREENS (Phase 2.4 C8)
// =============================================================
// Two screens shown after a user arrives via a share link AND completes
// (or already had) auth + onboarding:
//
//   DeepLinkBizSuccess  — Screen F — "You're connected!" (or "Already
//     connected"). Businesses auto-connect, no admin step. CTA goes
//     straight to the biz profile.
//
//   DeepLinkGroupSuccess — Screen G — "Request to join sent". Groups
//     still need admin approval (V1 policy preserved). CTA returns to
//     home; secondary CTA pivots to the V2 business directory.
//
// Both share the same visual skeleton (top pill, avatar→target visual,
// title, info card, two CTAs, footer) but are kept as separate
// components so each can evolve independently — the prototype hinted
// at biz-specific celebratory polish (confetti?) we might add later.

const DeepLinkBizSuccess = ({ business, alreadyConnected, currentUser, onGoToBiz, onGoHome }) => {
  const userInitial = (currentUser?.name?.[0] || 'U').toUpperCase();
  const userColor = currentUser?.color || '#C45A3F';
  // Business display: prefer uploaded logo, fall back to first-letter
  // tile with the iconBg-style gradient we use across V2.
  const bizName = business?.name || 'this business';
  const bizLogoUrl = business?.logoImageUrl || null;
  const bizInitial = (bizName[0] || 'B').toUpperCase();

  return (
    <div className="flex-1 flex flex-col" style={{ background: 'var(--bg)' }}>
      <div className="flex-1 overflow-y-auto px-6 pt-10 pb-6 flex flex-col items-center">
        {/* Top pill — "Shared business link" */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-7"
             style={{ background: '#E7EEF6', color: '#3E5B7A' }}>
          <Link2 size={13} />
          <span className="text-[12px] font-medium">Shared business link</span>
        </div>

        {/* Avatar → Business visual */}
        <div className="flex items-center gap-3 mb-5">
          {/* User avatar */}
          <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-[22px] font-medium"
               style={{ background: userColor }}>
            {userInitial}
          </div>
          <ArrowRight size={20} style={{ color: '#5B9F6A' }} />
          {/* Business tile */}
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden"
               style={{ background: bizLogoUrl ? 'transparent' : 'linear-gradient(135deg, #C45A3F 0%, #E8A57A 100%)' }}>
            {bizLogoUrl ? (
              <img src={bizLogoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white text-[22px] font-medium">{bizInitial}</span>
            )}
          </div>
        </div>

        {/* Title + body */}
        <h2 className="text-center mb-2.5" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.15 }}>
          {alreadyConnected ? "Already connected!" : "You're connected!"}
        </h2>
        <p className="text-center text-[14px] mb-5 px-2 leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
          {alreadyConnected ? (
            <>You're already connected to <strong style={{ color: 'var(--ink)' }}>{bizName}</strong>.</>
          ) : (
            <>You and <strong style={{ color: 'var(--ink)' }}>{bizName}</strong> are now connected. They can take your orders right away.</>
          )}
        </p>

        {/* Green check pill */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-6"
             style={{ background: '#E8F1E5', color: '#3E6B47' }}>
          <Check size={13} />
          <span className="text-[12px] font-medium">
            {alreadyConnected ? 'In your Connect List' : 'Added to your Connect List'}
          </span>
        </div>

        {/* "What you can do now" card */}
        <div className="w-full max-w-sm rounded-2xl p-5 mb-7" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: 'var(--ink-soft)' }}>
            What you can do now
          </div>
          {[
            ['See ', <strong key="b1" style={{ color: 'var(--ink)' }}>their menu & offers</strong>, ' on the business profile'],
            ['Place an ', <strong key="b2" style={{ color: 'var(--ink)' }}>order</strong>, " when they're open"],
            ['Chat with them — ', <strong key="b3" style={{ color: 'var(--ink)' }}>phone & WhatsApp</strong>, ' visible once their Aangan Connect is active'],
          ].map((parts, i) => (
            <div key={i} className="flex gap-3 mb-2.5 last:mb-0">
              <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[11px] font-medium"
                   style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
                {i + 1}
              </div>
              <p className="text-[13px] leading-snug" style={{ color: 'var(--ink-soft)' }}>
                {parts}
              </p>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="w-full max-w-sm space-y-2.5 mb-5">
          <button onClick={onGoToBiz}
                  className="w-full py-3.5 rounded-full text-[15px] font-medium flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
                  style={{ background: 'var(--ink)', color: '#fff' }}>
            Go to {bizName} <ArrowRight size={16} />
          </button>
          <button onClick={onGoHome}
                  className="w-full py-3.5 rounded-full text-[15px] font-medium active:scale-[0.99] transition-transform"
                  style={{ background: 'transparent', color: 'var(--ink)', border: '1px solid var(--border)' }}>
            Maybe later — go home
          </button>
        </div>

        <p className="text-center text-[11px] px-4 leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
          Shared by a friend · Auto-connected (businesses don't require approval)
        </p>
      </div>
    </div>
  );
};

const DeepLinkGroupSuccess = ({ group, currentUser, onGotIt, onBrowseBusinesses }) => {
  const userInitial = (currentUser?.name?.[0] || 'U').toUpperCase();
  const userColor = currentUser?.color || '#C45A3F';
  const groupName = group?.name || 'this group';
  const groupInitial = (groupName[0] || 'G').toUpperCase();
  const groupColor = group?.color || 'var(--ink)';

  return (
    <div className="flex-1 flex flex-col" style={{ background: 'var(--bg)' }}>
      <div className="flex-1 overflow-y-auto px-6 pt-10 pb-6 flex flex-col items-center">
        {/* Top pill — "Shared group invite" */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-7"
             style={{ background: '#E7EEF6', color: '#3E5B7A' }}>
          <Hash size={13} />
          <span className="text-[12px] font-medium">Shared group invite</span>
        </div>

        {/* Avatar → Group visual */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-[22px] font-medium"
               style={{ background: userColor }}>
            {userInitial}
          </div>
          <ArrowRight size={20} style={{ color: 'var(--ink-soft)' }} />
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-[22px] font-medium"
               style={{ background: groupColor }}>
            {groupInitial}
          </div>
        </div>

        {/* Title + body */}
        <h2 className="text-center mb-2.5" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.15 }}>
          Request to join sent
        </h2>
        <p className="text-center text-[14px] mb-5 px-2 leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
          An admin of <strong style={{ color: 'var(--ink)' }}>{groupName}</strong> will review and approve your request.
        </p>

        {/* Gold pending pill */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-6"
             style={{ background: '#F7EBD0', color: '#B8893A' }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#B8893A' }} />
          <span className="text-[12px] font-medium">Pending admin approval</span>
        </div>

        {/* "What happens next" card */}
        <div className="w-full max-w-sm rounded-2xl p-5 mb-7" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: 'var(--ink-soft)' }}>
            What happens next
          </div>
          {[
            ['An ', <strong key="g1" style={{ color: 'var(--ink)' }}>admin reviews your profile</strong>, ' — usually within a few hours'],
            ['They ', <strong key="g2" style={{ color: 'var(--ink)' }}>approve or decline</strong>, ' your request'],
            ["You'll get a ", <strong key="g3" style={{ color: 'var(--ink)' }}>notification</strong>, ' with the result'],
          ].map((parts, i) => (
            <div key={i} className="flex gap-3 mb-2.5 last:mb-0">
              <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[11px] font-medium"
                   style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
                {i + 1}
              </div>
              <p className="text-[13px] leading-snug" style={{ color: 'var(--ink-soft)' }}>
                {parts}
              </p>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="w-full max-w-sm space-y-2.5 mb-5">
          <button onClick={onGotIt}
                  className="w-full py-3.5 rounded-full text-[15px] font-medium active:scale-[0.99] transition-transform"
                  style={{ background: 'var(--ink)', color: '#fff' }}>
            Got it — back to Aangan
          </button>
          <button onClick={onBrowseBusinesses}
                  className="w-full py-3.5 rounded-full text-[15px] font-medium flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
                  style={{ background: 'transparent', color: 'var(--ink)', border: '1px solid var(--border)' }}>
            <Apartment size={15} /> Browse Local Businesses meanwhile
          </button>
        </div>

        <p className="text-center text-[11px] px-4 leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
          Shared by a friend · Group joins need admin approval to keep the community trusted
        </p>
      </div>
    </div>
  );
};

// =============================================================
// JOIN CHOICE / CONNECT-ID / BATCH DETAILS
// =============================================================
const JoinChoice = ({ onBack, onFind, onJoinWithCode, onCreateOwn, onRegisterBusiness, onBrowseBusinesses }) => (
  <div className="flex-1 flex flex-col">
    <TopBar title="" onBack={onBack} />
    <div className="flex-1 overflow-y-auto px-6 pt-2 sheet-bottom-safe">
      <h2 className="mb-2" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.1 }}>
        Who do you want to connect with?
      </h2>
      <p className="text-[14px] mb-7" style={{ color: 'var(--ink-soft)' }}>Pick a starting point.</p>
      <div className="space-y-3">
        {[
          // Phase 2.4 C9.4 — Ordering: solid "find" options first, then
          // dashed "create" options. This groups the choices visually
          // around intent — joining something that exists vs starting
          // something new. The earlier dashed-on-index pattern broke
          // once we had more than one dashed item, so we now carry an
          // explicit `dashed` flag per row.
          { Icon: Search, title: 'Find a batch', desc: 'Search by school, college, company, society or family', action: onFind, dashed: false },
          { Icon: Hash, title: 'I have a connect link', desc: 'Got an invite from a batchmate? Enter the link or code', action: onJoinWithCode, dashed: false },
          { Icon: Apartment, title: 'Browse local businesses', desc: 'Discover verified businesses near you — no batch needed.', action: onBrowseBusinesses, dashed: false },
          { Icon: Plus, title: 'Create my own batch', desc: "Be the first member. You'll be admin & get a shareable code.", action: onCreateOwn, dashed: false },
          { Icon: Building2, title: 'Register a business', desc: 'Sell on Aangan — accept orders from customers nearby.', action: onRegisterBusiness, dashed: false },
        ].map(({ Icon, title, desc, action, dashed }, i) => (
          <button key={i} onClick={action}
                  className="w-full text-left p-5 rounded-2xl flex items-center gap-4 active:scale-[0.99] transition-transform"
                  style={{ background: dashed ? 'var(--bg)' : 'var(--surface)',
                           border: dashed ? '1px dashed var(--border)' : '1px solid var(--border)' }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                 style={{ background: dashed ? 'var(--surface)' : 'var(--accent-soft)',
                          border: dashed ? '1px solid var(--border)' : 'none' }}>
              <Icon size={20} style={{ color: dashed ? 'var(--ink)' : 'var(--accent)' }} />
            </div>
            <div className="flex-1">
              <p className="text-[15px] font-medium mb-0.5" style={{ color: 'var(--ink)' }}>{title}</p>
              <p className="text-[12px] leading-snug" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
            </div>
            <ChevronRight size={18} style={{ color: 'var(--ink-soft)' }} />
          </button>
        ))}
      </div>
    </div>
  </div>
);

const ConnectIdEntry = ({ onBack, onJoin, existingCity }) => {
  const [code, setCode] = useState('');
  const [currentCity, setCurrentCity] = useState(existingCity || '');
  // If user already has a city saved, we don't need them to re-enter it
  const cityProvided = existingCity || currentCity.trim().length > 1;
  const valid = code.trim().length >= 6 && cityProvided;
  
  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: 0, height: '100%' }}>
      <TopBar title="" onBack={onBack} />
      <div className="flex-1 px-6 pt-2 overflow-y-auto" style={{ minHeight: 0 }}>
        <h2 className="mb-2" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.1 }}>Enter your connect link</h2>
        <p className="text-[14px] mb-7" style={{ color: 'var(--ink-soft)' }}>
          Paste the link or code your batchmate shared.
        </p>
        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Connect link</label>
        <input type="text" placeholder="MUM-7K3P" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
               className="w-full px-4 py-4 rounded-2xl text-[18px] outline-none mb-4 tracking-[0.15em] font-mono"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
        {!existingCity && (
          <div className="rounded-2xl p-4 mb-4" style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
            <label className="block text-[12px] uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: 'var(--ink)' }}>
              <MapPin size={11} /> Where do you live now?
            </label>
            <input type="text" placeholder="e.g. Bengaluru" value={currentCity} onChange={(e) => setCurrentCity(e.target.value)}
                   className="w-full px-4 py-3 rounded-xl text-[15px] outline-none"
                   style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
            <p className="text-[11px] mt-2" style={{ color: 'var(--ink-soft)' }}>Visible to your batchmates so they know where you are.</p>
          </div>
        )}
        <div className="rounded-xl p-3 flex items-start gap-2 mb-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <Clock size={13} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
          <p className="text-[12px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
            Joining via code lets you <strong style={{ color: 'var(--ink)' }}>see posts immediately</strong>. But if an admin doesn't approve you within <strong style={{ color: 'var(--ink)' }}>72 hours</strong>, you'll be auto-disconnected. To post or like, full approval is needed.
          </p>
        </div>
        
      </div>
      <div className="px-6 pt-2 sheet-bottom-safe">
        <button onClick={() => valid && onJoin({ code, currentCity, viaCode: true })} disabled={!valid}
          className="w-full h-14 rounded-2xl font-medium text-[15px] disabled:opacity-40 active:scale-[0.98] transition-transform"
          style={{ background: 'var(--ink)', color: 'var(--bg)' }}>Join</button>
      </div>
    </div>
  );
};

const BatchDetails = ({ onBack, onSearch, existingCity }) => {
  const [type, setType] = useState('alma');
  const [name, setName] = useState('');
  const [passedOut, setPassedOut] = useState(''); // year
  const [city, setCity] = useState('');
  const [tower, setTower] = useState('');
  const [flat, setFlat] = useState('');
  const [place, setPlace] = useState('');
  const [project, setProject] = useState('');
  const [currentCity, setCurrentCity] = useState(existingCity || '');
  const config = BATCH_TYPES[type];
  const isAlma = type === 'alma';
  const isWork = type === 'company';
  const isFamily = type === 'family';
  const isSociety = type === 'society';

  

  const fieldsValid = () => {
    // Name is always required
    if (!name) return false;
    // City: either from existing profile OR freshly entered (only required if no existing city)
    if (!existingCity) {
      const hasCity = currentCity.trim().length > 1;
      if (!hasCity) return false;
    }
    if (isFamily) return true;
    if (isSociety) return !!place;  // Society needs locality (the form has no City field)
    // school/college and work: only name + city required, rest optional
    return true;
  };

  const namePlaceholder = isFamily ? "e.g. Sharma Family"
    : isSociety ? "e.g. Lake View Apartments"
    : isAlma ? "e.g. St. Mary's High School / St. Xavier's College"
    : "e.g. Infosys";

  const nameLabel = isFamily ? 'Group name'
    : isSociety ? 'Society / building name'
    : isAlma ? 'School or college name'
    : 'Company name';

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: 0, height: '100%' }}>
      <TopBar title="" onBack={onBack} />
      <div className="flex-1 px-6 pt-2 overflow-y-auto" style={{ minHeight: 0 }}>
        <h2 className="mb-2" style={{ fontSize: '26px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.1 }}>What kind of batch?</h2>
        <p className="text-[14px] mb-5" style={{ color: 'var(--ink-soft)' }}>{config.subtitle}</p>

        <div className="flex gap-2 mb-5 overflow-x-auto -mx-6 px-6 pb-1" style={{ scrollbarWidth: 'none' }}>
          {Object.entries(BATCH_TYPES).map(([key, val]) => {
            const Icon = val.icon;
            const active = type === key;
            const isLocked = val.comingSoon;
            return (
              <button key={key} 
                onClick={() => isLocked ? null : setType(key)}
                disabled={isLocked}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-full shrink-0 transition-all relative"
                style={{ 
                  background: active ? 'var(--ink)' : 'var(--surface)',
                  color: active ? 'var(--bg)' : 'var(--ink)',
                  border: '1px solid var(--border)',
                  opacity: isLocked ? 0.55 : 1,
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                }}>
                <Icon size={13} strokeWidth={1.6} />
                <span className="text-[12px] font-medium">{val.label}</span>
                {isLocked && (
                  <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded ml-1"
                        style={{ background: 'var(--accent)', color: 'white' }}>
                    Soon
                  </span>
                )}
              </button>
            );
          })}
        </div>

{/* Business Connect teaser removed pre-launch; restored when feature ships in V2. */}

        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>{nameLabel}</label>
        <input type="text" placeholder={namePlaceholder}
               value={name} onChange={(e) => setName(e.target.value)}
               className="w-full px-4 py-4 rounded-2xl text-[15px] outline-none mb-4"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />

        {isSociety && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Tower (optional)</label>
                <input type="text" placeholder="A / B / C" value={tower} onChange={(e) => setTower(e.target.value)}
                       className="w-full px-4 py-3.5 rounded-2xl text-[15px] outline-none"
                       style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Flat (optional)</label>
                <input type="text" placeholder="201" value={flat} onChange={(e) => setFlat(e.target.value)}
                       className="w-full px-4 py-3.5 rounded-2xl text-[15px] outline-none"
                       style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
              </div>
            </div>
            <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Locality / area</label>
            <input type="text" placeholder="e.g. Andheri West" value={place} onChange={(e) => setPlace(e.target.value)}
                   className="w-full px-4 py-4 rounded-2xl text-[15px] outline-none mb-4"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
          </>
        )}

        {/* School/College: passed-out year + city + place(optional) — all optional */}
        {isAlma && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Passed out (optional)</label>
                <input type="text" inputMode="numeric" placeholder="2015" value={passedOut}
                       onChange={(e) => setPassedOut(e.target.value)}
                       className="w-full px-4 py-4 rounded-2xl text-[15px] outline-none"
                       style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>City (optional)</label>
                <input type="text" placeholder="Mumbai" value={city} onChange={(e) => setCity(e.target.value)}
                       className="w-full px-4 py-4 rounded-2xl text-[15px] outline-none"
                       style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
              </div>
            </div>
            <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Place (optional)</label>
            <input type="text" placeholder="e.g. Mt. Carmel Rd, Bandra" value={place}
                   onChange={(e) => setPlace(e.target.value)}
                   className="w-full px-4 py-4 rounded-2xl text-[15px] outline-none mb-4"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
          </>
        )}

        {/* Work: city(optional) + place(optional) + project(optional), no year */}
        {isWork && (
          <>
            <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>City (optional)</label>
            <input type="text" placeholder="Bengaluru" value={city} onChange={(e) => setCity(e.target.value)}
                   className="w-full px-4 py-4 rounded-2xl text-[15px] outline-none mb-4"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Place (optional)</label>
                <input type="text" placeholder="Electronic City" value={place}
                       onChange={(e) => setPlace(e.target.value)}
                       className="w-full px-4 py-3.5 rounded-2xl text-[15px] outline-none"
                       style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Project (optional)</label>
                <input type="text" placeholder="e.g. Banking team" value={project}
                       onChange={(e) => setProject(e.target.value)}
                       className="w-full px-4 py-3.5 rounded-2xl text-[15px] outline-none"
                       style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
              </div>
            </div>
          </>
        )}

        {!existingCity && (
          <div className="rounded-2xl p-4 mb-3" style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
            <label className="block text-[12px] uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: 'var(--ink)' }}>
              <MapPin size={11} /> Where do you live now?
            </label>
            <input type="text" placeholder="e.g. Bengaluru" value={currentCity} onChange={(e) => setCurrentCity(e.target.value)}
                   className="w-full px-4 py-3 rounded-xl text-[15px] outline-none"
                   style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
            <p className="text-[11px] mt-2" style={{ color: 'var(--ink-soft)' }}>Visible to your batchmates.</p>
          </div>
        )}

        
      </div>
      <div className="px-6 pt-2 sheet-bottom-safe">
        <button onClick={() => fieldsValid() && onSearch({ type, name, year: passedOut, city, place, project, currentCity })}
          disabled={!fieldsValid()}
          className="w-full h-14 rounded-2xl font-medium text-[15px] disabled:opacity-40 active:scale-[0.98] transition-transform"
          style={{ background: 'var(--ink)', color: 'var(--bg)' }}>Find my batch</button>
      </div>
    </div>
  );
};

// =============================================================
// MULTI-MATCH SCREEN
// =============================================================
const MatchesScreen = ({ matches, onBack, onSelect, onCreate, searching = false }) => (
  <div className="flex-1 flex flex-col">
    <TopBar title="" onBack={onBack} />
    <div className="flex-1 overflow-y-auto px-6 pt-2 sheet-bottom-safe">
      {searching ? (
        <>
          <h2 className="mb-2" style={{ fontSize: '26px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.15 }}>
            Looking for matches…
          </h2>
          <p className="text-[14px] mb-6" style={{ color: 'var(--ink-soft)' }}>
            Searching across batches in Aangan.
          </p>
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 rounded-full border-2 animate-spin"
                 style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
          </div>
        </>
      ) : matches.length === 0 ? (
        <>
          <h2 className="mb-2" style={{ fontSize: '26px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.15 }}>
            No matches yet.
          </h2>
          <p className="text-[14px] mb-6" style={{ color: 'var(--ink-soft)' }}>
            Aangan is new — your batch may not be here yet. Be the first member by creating it. You'll get an invite link to share with batchmates.
          </p>
        </>
      ) : (
        <>
          <h2 className="mb-2" style={{ fontSize: '26px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.15 }}>
            We found {matches.length} {matches.length === 1 ? 'match' : 'matches'}.
          </h2>
          <p className="text-[14px] mb-6" style={{ color: 'var(--ink-soft)' }}>
            Pick the one that's yours. Don't see it? You can create a new batch.
          </p>
        </>
      )}
      {!searching && matches.length > 0 && (
        <div className="space-y-3 mb-5">
          {matches.map((m) => (
            <button key={m.id} onClick={() => onSelect(m)}
              className="w-full text-left p-4 rounded-2xl flex items-center gap-3 active:scale-[0.99] transition-transform"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <GroupAvatar group={m} size={52} rounded={14} />
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-medium truncate mb-0.5" style={{ color: 'var(--ink)' }}>{m.name}</p>
                <p className="text-[12px] truncate" style={{ color: 'var(--ink-soft)' }}>
                  {[m.passedOut && `Class of ${m.passedOut}`, m.city, m.place].filter(Boolean).join(' · ') || '—'}
                </p>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-[11px] inline-flex items-center gap-1" style={{ color: 'var(--accent)' }}>
                    <Users size={10} /> {m.memberCount || 1} {(m.memberCount || 1) === 1 ? 'member' : 'members'}
                  </span>
                </div>
              </div>
              <ChevronRight size={16} style={{ color: 'var(--ink-soft)' }} />
            </button>
          ))}
        </div>
      )}
      {!searching && (
        <button onClick={onCreate}
                className="w-full text-left p-4 rounded-2xl flex items-center gap-3 active:scale-[0.99] transition-transform"
                style={{ background: 'var(--bg)', border: '1px dashed var(--border)' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <Plus size={18} style={{ color: 'var(--ink)' }} />
          </div>
          <div className="flex-1">
            <p className="text-[14px] font-medium mb-0.5" style={{ color: 'var(--ink)' }}>
              {matches.length === 0 ? 'Create my batch' : 'None of these — create new'}
            </p>
            <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>You'll be the admin and get a CONNECT LINK</p>
          </div>
          <ChevronRight size={16} style={{ color: 'var(--ink-soft)' }} />
        </button>
      )}
    </div>
  </div>
);

const MatchConfirm = ({ match, onBack, onConfirm }) => (
  <div className="flex-1 flex flex-col">
    <TopBar title="" onBack={onBack} />
    <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ background: 'var(--accent)', color: 'white' }}>
        <Check size={28} strokeWidth={2.5} />
      </div>
      <h2 className="mb-3" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.15 }}>
        Confirm your batch.
      </h2>
      <div className="rounded-2xl p-5 w-full mb-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <div className="text-left">
          <p className="text-[16px] mb-3" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>
            {match.emoji} {match.name}
          </p>
          <div className="flex gap-6 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--ink-soft)' }}>Class of</p>
              <p className="text-[16px]" style={{ color: 'var(--ink)' }}>{match.year}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--ink-soft)' }}>Location</p>
              <p className="text-[16px]" style={{ color: 'var(--ink)' }}>{match.locality}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 flex items-center gap-2" style={{ borderTop: '1px solid var(--border)' }}>
            <Users size={14} style={{ color: 'var(--accent)' }} />
            <p className="text-[13px]" style={{ color: 'var(--ink)' }}>
              <span style={{ fontWeight: 600 }}>{match.memberCount} alumni</span> already here
            </p>
          </div>
        </div>
      </div>
      <div className="rounded-xl p-3 w-full mb-4 flex items-start gap-2" style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
        <Clock size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
        <p className="text-[12px] text-left leading-relaxed" style={{ color: 'var(--ink)' }}>
          Until an admin verifies you, <strong>you'll only see this batch's name</strong>. Posts unlock once approved.
        </p>
      </div>
      <button onClick={onConfirm}
        className="w-full h-14 rounded-2xl font-medium text-[15px] active:scale-[0.98] transition-transform"
        style={{ background: 'var(--ink)', color: 'var(--bg)' }}>Request to join</button>
    </div>
  </div>
);

// =============================================================
// BATCH CREATED + PHOTO UPLOAD
// =============================================================
const BatchCreatedCelebration = ({ batch, onContinue, onCopy, onShare, onPhotoUpload }) => {
  const fileInputRef = useRef(null);
  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onPhotoUpload(ev.target.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="flex-1 flex flex-col px-6 sheet-bottom-safe relative overflow-hidden profile-header-safe">
      <div className="absolute top-0 left-0 right-0 h-1/2 opacity-30"
           style={{ background: 'radial-gradient(ellipse at top, var(--gold) 0%, transparent 60%)' }} />
      <div className="relative flex-1 flex flex-col items-center justify-center text-center">
        <button onClick={() => fileInputRef.current?.click()}
                className="relative mb-3 active:scale-[0.97] transition-transform">
          {batch.photoUrl ? (
            <img src={batch.photoUrl} alt={batch.name}
                 className="w-24 h-24 rounded-2xl object-cover"
                 style={{ boxShadow: '0 6px 18px rgba(0,0,0,0.15)' }} />
          ) : (
            <div className="w-24 h-24 rounded-2xl flex items-center justify-center"
                 style={{ background: 'var(--surface)', border: '1px solid var(--border)', fontSize: 50 }}>
              {batch.emoji}
            </div>
          )}
          <div className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center"
               style={{ background: 'var(--accent)', boxShadow: '0 4px 10px rgba(196,90,63,0.35)' }}>
            <ImagePlus size={16} color="white" strokeWidth={2} />
          </div>
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
        <p className="text-[10px] mb-4 uppercase tracking-[0.2em]" style={{ color: 'var(--ink-soft)' }}>
          {batch.photoUrl ? 'Tap to change photo' : 'Tap to add a group photo'}
        </p>
        <h1 className="mb-2" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.1 }}>
          Your batch is live! 🎉
        </h1>
        <p className="text-[14px] mb-6 max-w-[280px]" style={{ color: 'var(--ink-soft)' }}>
          You created <strong style={{ color: 'var(--ink)' }}>{batch.name}</strong>. You're admin. Here's the magic part —
        </p>
        <div className="rounded-2xl p-5 w-full mb-3 relative" style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
          <p className="text-[10px] uppercase tracking-[0.2em] opacity-70 mb-2">Your Invite Link</p>
          <p className="text-[13px] font-mono break-all opacity-90 mb-3" style={{ wordBreak: 'break-all' }}>
            {AANGAN_SHARE_BASE}/join/{batch.connectId}
          </p>
          <p className="text-[12px] opacity-80 leading-relaxed">
            Share this with friends — one tap and they're in.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full mb-5">
          <button onClick={() => onCopy(`${AANGAN_SHARE_BASE}/join/${batch.connectId}`)}
            className="h-12 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <Copy size={15} style={{ color: 'var(--ink)' }} />
            <span className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>Copy link</span>
          </button>
          <button onClick={() => onShare(batch)}
            className="h-12 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <Share2 size={15} style={{ color: 'var(--ink)' }} />
            <span className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>Share</span>
          </button>
        </div>
      </div>
      <button onClick={onContinue}
        className="w-full h-14 rounded-2xl font-medium text-[15px] active:scale-[0.98] transition-transform"
        style={{ background: 'var(--ink)', color: 'var(--bg)' }}>Take me to the batch</button>
    </div>
  );
};

// =============================================================
// PENDING BANNER
// =============================================================
const PendingBanner = ({ onCheckStatus, viaCode, checking }) => (
  <div className="px-5 py-3 flex items-start gap-3" style={{ background: 'var(--accent-soft)', borderBottom: '1px solid var(--border)' }}>
    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'var(--accent)' }}>
      <Clock size={14} color="white" />
    </div>
    <div className="flex-1">
      <p className="text-[13px] font-medium mb-0.5" style={{ color: 'var(--ink)' }}>
        {viaCode ? 'Provisional access — admin approval pending' : 'Awaiting admin approval'}
      </p>
      <p className="text-[12px] leading-relaxed mb-2" style={{ color: 'var(--ink-soft)' }}>
        {viaCode
          ? <>You can browse posts because you used a connect link. If an admin doesn't approve within <strong style={{ color: 'var(--ink)' }}>72 hours</strong>, you'll be auto-removed.</>
          : <>You can see the batch name & members. Posts, likes, comments unlock once an admin verifies you.</>}
      </p>
      <button onClick={onCheckStatus} disabled={checking}
        className="text-[11px] font-medium px-2.5 py-1 rounded-full inline-flex items-center gap-1 disabled:opacity-50"
        style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
        {checking ? (
          <>
            <div className="w-2.5 h-2.5 rounded-full border animate-spin"
                 style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
            Checking…
          </>
        ) : (
          <>🔄 Check status</>
        )}
      </button>
    </div>
  </div>
);

// =============================================================
// POST CARD + SUB TABS
// =============================================================
const PostCard = ({ post, onTap, onLike, currentUser, isApproved, onBlocked, getUser, onDelete, canDelete, onShare, onReport, onTapPhoto }) => {
  const author = post.authorId === 'me' ? currentUser : getUser(post.authorId);
  const [showMenu, setShowMenu] = useState(false);
  // Can the current user report this post? Not if it's their own.
  // In a real-identity platform (Community Guidelines #4), reporting a post
  // is equivalent to reporting its author — same target, same review flow.
  const canReport = onReport && author && post.authorId !== currentUser?.id && post.authorId !== 'me';
  return (
    <article style={{ borderBottom: '1px solid var(--border)' }}>
      {/* Header: avatar + name + meta */}
      <header className="flex items-center gap-3 px-5 pt-4 pb-3">
        <button
          onClick={(e) => { e.stopPropagation(); if (author && onTapPhoto) onTapPhoto(author); }}
          className="shrink-0 active:scale-95 transition-transform"
          aria-label={`View ${author?.name || 'member'} photo`}>
          <Avatar user={author} size={40} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-[14px] font-medium truncate" style={{ color: 'var(--ink)' }}>{author.name}</p>
            {author.isAdmin && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                                     style={{ background: 'var(--ink)', color: 'var(--bg)' }}>Admin</span>}
          </div>
          <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
            <MapPin size={9} className="inline -mt-0.5 mr-0.5" />{author.currentCity || '—'} · {post.timestamp}
          </p>
        </div>
        <button onClick={() => setShowMenu(true)} className="w-8 h-8 flex items-center justify-center rounded-full active:bg-black/5">
          <MoreHorizontal size={16} style={{ color: 'var(--ink-soft)' }} />
        </button>
      </header>

      {/* Photo (full-bleed, clean — no overlay) */}
      {post.photo && (
        <div className="px-5 pb-1">
          <PhotoBlock photo={post.photo} />
        </div>
      )}

      {/* Action row — between photo and caption (Instagram style) */}
      <div className="flex items-center gap-1 px-3 pt-2">
        <button onClick={(e) => { e.stopPropagation(); isApproved ? onLike(post.id) : onBlocked(); }}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-full active:bg-black/5 active:scale-90 transition-transform"
          style={{ opacity: isApproved ? 1 : 0.55 }}>
          {isApproved ? (
            <Zap size={20} style={{ color: post.likedByMe ? 'var(--accent)' : 'var(--ink)' }}
                 fill={post.likedByMe ? 'var(--accent)' : 'none'} strokeWidth={1.6} />
          ) : (<Lock size={16} style={{ color: 'var(--ink-soft)' }} />)}
          <span className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>{post.likes}</span>
        </button>
        <button onClick={() => onTap(post.id)} className="flex items-center gap-1.5 px-2 py-1.5 rounded-full active:bg-black/5 active:scale-90 transition-transform">
          <MessageSquareDashed size={20} style={{ color: 'var(--ink)' }} strokeWidth={1.6} />
          <span className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>{post.commentCount || post.comments?.length || 0}</span>
        </button>
        {onShare && post.photo && (
          <button onClick={(e) => { e.stopPropagation(); onShare(post); }}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-full active:bg-black/5 active:scale-90 transition-transform">
            <Rocket size={20} style={{ color: 'var(--ink)' }} strokeWidth={1.6} />
          </button>
        )}
      </div>

      {/* Caption — body text below photo, with author name prefix */}
      {post.text && (
        <div className="px-5 pb-4 pt-1">
          <p className="text-[15px] leading-relaxed" style={{ color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>
            {linkifyText(post.text)}
          </p>
          {(post.commentCount || post.comments?.length || 0) > 0 && (
            <button onClick={() => onTap(post.id)} className="text-[13px] mt-1 active:opacity-70" style={{ color: 'var(--ink-soft)' }}>
              View {(post.commentCount || post.comments?.length || 0) === 1 ? '1 comment' : `all ${post.commentCount || post.comments?.length || 0} comments`}
            </button>
          )}
        </div>
      )}

      {/* If photo-only post (no text), show comments link below action row */}
      {!post.text && (post.commentCount || post.comments?.length || 0) > 0 && (
        <div className="px-5 pb-4">
          <button onClick={() => onTap(post.id)} className="text-[13px] active:opacity-70" style={{ color: 'var(--ink-soft)' }}>
            View {(post.commentCount || post.comments?.length || 0) === 1 ? '1 comment' : `all ${post.commentCount || post.comments?.length || 0} comments`}
          </button>
        </div>
      )}

      {/* Bottom padding for text-only posts (no photo) */}
      {!post.photo && <div className="pb-2" />}
      
      {/* Actions menu */}
      {showMenu && (
        <div className="absolute inset-0 z-40 flex items-end" style={{ background: 'rgba(0,0,0,0.5)' }}
             onClick={() => setShowMenu(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full rounded-t-3xl sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
            <div className="w-12 h-1 rounded-full mx-auto my-3" style={{ background: 'var(--border)' }} />
            {canDelete && (
              <button onClick={() => { setShowMenu(false); onDelete(post); }}
                      className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5">
                <Trash2 size={18} style={{ color: '#B33A3A' }} />
                <span className="text-[14px]" style={{ color: '#B33A3A' }}>Delete post</span>
              </button>
            )}
            {canReport && (
              <button onClick={() => { setShowMenu(false); onReport(author); }}
                      className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5"
                      style={canDelete ? { borderTop: '1px solid var(--border)' } : {}}>
                <Flag size={18} style={{ color: '#B33A3A' }} />
                <span className="text-[14px]" style={{ color: '#B33A3A' }}>Report post</span>
              </button>
            )}
            <button onClick={() => setShowMenu(false)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5"
                    style={{ borderTop: '1px solid var(--border)' }}>
              <X size={18} style={{ color: 'var(--ink-soft)' }} />
              <span className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Cancel</span>
            </button>
          </div>
        </div>
      )}
    </article>
  );
};
const SubTabPills = ({ active, onChange }) => (
  <div className="flex gap-2 px-5 py-3 overflow-x-auto" style={{
    background: 'var(--bg)', borderBottom: '1px solid var(--border)', scrollbarWidth: 'none'
  }}>
    {SUB_TABS.map(({ key, label, icon: Icon }) => {
      const isActive = active === key;
      return (
        <button key={key} onClick={() => onChange(key)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full shrink-0 transition-all"
          style={{ background: isActive ? 'var(--ink)' : 'var(--surface)',
                   color: isActive ? 'var(--bg)' : 'var(--ink)',
                   border: '1px solid var(--border)' }}>
          <Icon size={12} strokeWidth={1.8} />
          <span className="text-[12px] font-medium whitespace-nowrap">{label}</span>
        </button>
      );
    })}
  </div>
);
// =============================================================
// WORD OF MOUTH — text-only posts for job referrals,
// recommendations, neighbourhood notifications, etc.
// Shares the underlying post infrastructure with Wall.
// =============================================================
const CATEGORIES = [
  { key: null, label: 'All', emoji: '🌐' },
  { key: 'job', label: 'Jobs', emoji: '💼' },
  { key: 'rec', label: 'Recommend', emoji: '👍' },
  { key: 'heads-up', label: 'Heads-up', emoji: '📢' },
  { key: 'other', label: 'Other', emoji: '💬' },
];

const WordOfMouthView = ({ posts, currentUser, isApproved, onCompose, onTapPost, onLike,
                            onBlocked, getUser, onDeletePost, onReport, currentUserIsAdmin, currentUserId, onTapPhoto }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const filtered = activeCategory ? posts.filter(p => p.category === activeCategory) : posts;
  
  return (
    <>
      {/* Composer trigger */}
      <div className="px-5 py-4 flex items-center gap-3" style={{ borderBottom: '1px solid var(--border)' }}>
        <Avatar user={currentUser} size={36} />
        <button onClick={isApproved ? onCompose : onBlocked}
          className="flex-1 text-left text-[14px] py-2.5 px-4 rounded-full flex items-center gap-2"
          style={{ background: 'var(--surface)', color: 'var(--ink-soft)', border: '1px solid var(--border)', opacity: isApproved ? 1 : 0.6 }}>
          {!isApproved && <Lock size={13} />}
          {isApproved ? 'Share a referral, recommendation or heads-up…' : 'Posting unlocks after approval'}
        </button>
      </div>
      
      {/* Category filter pills */}
      <div className="flex gap-1.5 px-4 py-3 overflow-x-auto" style={{
        background: 'var(--bg)', borderBottom: '1px solid var(--border)', scrollbarWidth: 'none'
      }}>
        {CATEGORIES.map(({ key, label, emoji }) => {
          const isActive = activeCategory === key;
          return (
            <button key={key || 'all'} onClick={() => setActiveCategory(key)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full shrink-0 transition-all"
              style={{ background: isActive ? 'var(--ink)' : 'var(--surface)',
                       color: isActive ? 'var(--bg)' : 'var(--ink)',
                       border: '1px solid var(--border)' }}>
              <span className="text-[12px]">{emoji}</span>
              <span className="text-[12px] font-medium whitespace-nowrap">{label}</span>
            </button>
          );
        })}
      </div>
      
      {/* Posts list — same PostCard as Wall, just different filter */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 px-6">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <ThumbsUp size={20} style={{ color: 'var(--ink-soft)' }} />
          </div>
          <p className="text-[14px] mb-1" style={{ color: 'var(--ink)' }}>
            {activeCategory ? 'Nothing in this category yet' : 'Nothing here yet'}
          </p>
          <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
            Share a job opening, a recommendation, or a heads-up for your batch.
          </p>
        </div>
      ) : (
        filtered.map((post) => (
          <PostCard key={post.id} post={post} onTap={onTapPost} onLike={onLike} getUser={getUser}
                    currentUser={currentUser} isApproved={isApproved} onBlocked={onBlocked}
                    onDelete={onDeletePost}
                    onReport={onReport}
                    onTapPhoto={onTapPhoto}
                    canDelete={post.authorId === currentUserId || currentUserIsAdmin} />
        ))
      )}
    </>
  );
};
const SubTabPlaceholder = ({ tab }) => (
  <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--accent-soft)' }}>
      <tab.icon size={26} style={{ color: 'var(--accent)' }} strokeWidth={1.6} />
    </div>
    <h3 className="mb-2" style={{ fontSize: '22px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>{tab.label}</h3>
    <p className="text-[13px] mb-5 max-w-[260px]" style={{ color: 'var(--ink-soft)' }}>{tab.desc}</p>
    <div className="rounded-xl px-3 py-1.5 inline-flex items-center gap-1.5"
         style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <Sparkles size={11} style={{ color: 'var(--accent)' }} />
      <span className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--ink-soft)' }}>Launching soon</span>
    </div>
  </div>
);

// =============================================================
// GROUP SWITCHER SHEET
// =============================================================
const GroupSwitcherSheet = ({ groups, activeId, onSwitch, onClose, onAddGroup }) => {
  const sorted = Object.values(groups).sort((a, b) => (a.id === activeId ? -1 : b.id === activeId ? 1 : 0));
  return (
    <div className="absolute inset-0 z-40 flex items-end" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full rounded-t-3xl px-6 pt-6 sheet-bottom-safe" style={{ background: 'var(--bg)', maxHeight: '85%' }}>
        <div className="w-12 h-1 rounded-full mx-auto mb-4" style={{ background: 'var(--border)' }} />
        <h2 className="text-[20px] mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
          Switch batch
        </h2>
        <p className="text-[12px] mb-5" style={{ color: 'var(--ink-soft)' }}>
          You're in {Object.keys(groups).length} batches.
        </p>
        <div className="space-y-2 mb-4">
          {sorted.map((g) => {
            const isActive = g.id === activeId;
            return (
              <button key={g.id} onClick={() => { onSwitch(g.id); onClose(); }}
                className="w-full flex items-center gap-3 p-3 rounded-2xl transition-all"
                style={{
                  background: isActive ? 'var(--accent-soft)' : 'var(--surface)',
                  border: '1px solid var(--border)',
                }}>
                <GroupAvatar group={g} size={44} rounded={12} />
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[14px] font-medium truncate" style={{ color: 'var(--ink)' }}>{g.name}</p>
                    {isActive && <Check size={13} style={{ color: 'var(--accent)' }} strokeWidth={2.5} />}
                  </div>
                  <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
                    {g.memberCount} {g.memberCount === 1 ? 'member' : 'members'} · {g.lastActive}
                  </p>
                </div>
                {!isActive && g.unread > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0"
                        style={{ background: 'var(--accent)', color: 'white' }}>
                    {g.unread} new
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button onClick={() => { onClose(); onAddGroup(); }}
                className="w-full rounded-2xl p-3 flex items-center justify-center gap-2"
                style={{ border: '1px dashed var(--border)' }}>
          <Plus size={15} style={{ color: 'var(--accent)' }} />
          <span className="text-[13px] font-medium" style={{ color: 'var(--accent)' }}>Add another batch</span>
        </button>
      </div>
    </div>
  );
};

// =============================================================
// MISSED-POSTS STRIP (cross-group indicator)
// =============================================================


// =============================================================
// TIME CAPSULE — photo grid (no video, no download)
// =============================================================
const TimeCapsuleView = ({ memories, isApproved, currentUser, onAddMemory, onTapMemory, onBlocked, getUser }) => {
  const fileInputRef = useRef(null);
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      e.target.value = '';
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
  // 25 MB cap on raw file size before compression
  alert('Photo too large — please pick one under 25 MB');
      e.target.value = '';
      return;
    }
    onAddMemory(file);  // pass raw File — handler will upload to Storage
    e.target.value = '';
  };

  return (
    <div className="px-4 pt-4 pb-6">
      <div className="rounded-2xl px-4 py-3 mb-4 flex items-start gap-3"
           style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
        <Heart size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
        <div className="flex-1">
          <p className="text-[12px] font-medium mb-0.5" style={{ color: 'var(--ink)' }}>Time Capsule</p>
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
            Saved memories from this batch. Photos only for now — videos coming later.
          </p>
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />

      {memories.length === 0 ? (
        <div className="text-center py-12 px-6 rounded-2xl"
             style={{ background: 'var(--surface)', border: '1px dashed var(--border)' }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
               style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <ImageIcon size={20} style={{ color: 'var(--ink-soft)' }} />
          </div>
          <p className="text-[13px] mb-2" style={{ color: 'var(--ink)' }}>No memories saved yet</p>
          <p className="text-[11px] mb-4" style={{ color: 'var(--ink-soft)' }}>
            Add the first one — a school photo, an old group pic, a trophy.
          </p>
          <button
            onClick={isApproved ? () => fileInputRef.current?.click() : onBlocked}
            className="text-[12px] font-medium px-4 py-2 rounded-full inline-flex items-center gap-1.5"
            style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
            {isApproved ? <><Plus size={13} /> Add a photo</> : <><Lock size={11} /> Approval needed</>}
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={isApproved ? () => fileInputRef.current?.click() : onBlocked}
              className="aspect-square rounded-xl flex flex-col items-center justify-center gap-1.5 active:scale-[0.97] transition-transform"
              style={{ background: 'var(--surface)', border: '1.5px dashed var(--border)' }}>
              {isApproved ? (
                <>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'var(--accent)' }}>
                    <Plus size={16} color="white" strokeWidth={2.5} />
                  </div>
                  <span className="text-[10px] font-medium" style={{ color: 'var(--ink)' }}>Add photo</span>
                </>
              ) : (
                <>
                  <Lock size={14} style={{ color: 'var(--ink-soft)' }} />
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft)' }}>Approval needed</span>
                </>
              )}
            </button>

            {memories.map((m) => {
              const author = m.addedById === 'me' ? currentUser : getUser(m.addedById);
              return (
                <button key={m.id} onClick={() => onTapMemory(m)}
                  className="aspect-square rounded-xl overflow-hidden relative active:scale-[0.97] transition-transform"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                  {m.photoUrl ? (
                    <img src={m.photoUrl} alt={m.label} 
                         className="absolute inset-0 w-full h-full object-cover"
                         draggable={false}
                         onContextMenu={(e) => e.preventDefault()}
                         style={{ userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }} />
                  ) : (
                    <PhotoBlock photo={{ tone: m.tone, label: '' }} />
                  )}
                  <div className="absolute inset-x-0 bottom-0 px-1.5 py-1.5"
                       style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
                    <p className="text-[9px] text-white/95 truncate font-medium">{m.label}</p>
                    {author && <p className="text-[8px] text-white/70 truncate">{author.name?.split(' ')[0]}</p>}
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] mt-4 text-center" style={{ color: 'var(--ink-soft)' }}>
            {memories.length} {memories.length === 1 ? 'memory' : 'memories'} · saved forever
          </p>
        </>
      )}
    </div>
  );
};

const MemoryViewerModal = ({ memory, currentUser, onClose, getUser, onDelete, canDelete }) => {
  if (!memory) return null;
  const author = memory.addedById === 'me' ? currentUser : getUser(memory.addedById);
  return (
    <div className="absolute inset-0 z-40 flex flex-col" style={{ background: 'rgba(10,10,15,0.95)' }} onClick={onClose}>
      <div className="flex items-center justify-between px-4 shrink-0 topbar-safe" style={{ minHeight: '56px' }}>
        <div className="flex items-center gap-2 min-w-0">
          {author && <Avatar user={author} size={32} />}
          <div className="min-w-0">
            <p className="text-[13px] font-medium truncate text-white">{memory.label}</p>
            <p className="text-[10px] truncate" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {author?.name || 'Unknown'} · {memory.addedAt}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Delete button: visible only to the memory's author OR a group admin. */}
          {/* Stops the click from bubbling to the backdrop (which would close the modal). */}
          {canDelete && (
            <button onClick={(e) => { e.stopPropagation(); onDelete(memory); }}
                    className="w-9 h-9 flex items-center justify-center rounded-full"
                    style={{ background: 'rgba(179,58,58,0.85)' }}
                    aria-label="Delete memory">
              <Trash2 size={16} color="white" />
            </button>
          )}
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full"
                  style={{ background: 'rgba(255,255,255,0.1)' }}>
            <X size={18} color="white" />
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-12 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {memory.photoUrl ? (
          <img src={memory.photoUrl} alt={memory.label} 
               className="max-w-full max-h-full object-contain rounded-xl cursor-zoom-in"
               draggable={false}
               onContextMenu={(e) => e.preventDefault()}
               onClick={(e) => {
                 // Double-tap-to-zoom on touch + click-to-zoom on desktop.
                 // Toggle a scale class via inline style each tap.
                 const el = e.currentTarget;
                 const isZoomed = el.dataset.zoomed === 'true';
                 el.style.transform = isZoomed ? 'scale(1)' : 'scale(2.2)';
                 el.style.transition = 'transform 200ms ease-out';
                 el.dataset.zoomed = isZoomed ? 'false' : 'true';
                 el.style.cursor = isZoomed ? 'zoom-in' : 'zoom-out';
               }}
               style={{ userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', transformOrigin: 'center center' }} />
        ) : (
          <div className="w-full" style={{ maxWidth: 320 }}>
            <PhotoBlock photo={{ tone: memory.tone, label: memory.label }} />
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================
// HOME — with switcher + missed strip + notif bell
// =============================================================
// =============================================================
// HomeHeaderActionsV2 — U1
//
// Two icon buttons that sit beside the notification bell in V1's Home
// header, for V2-enabled users:
//   🔍  Search  → opens the V2 local-business directory (everyone)
//   🔁  Switch  → opens the merchant surface (ONLY if this user owns a
//                 business)
//
// This REPLACES the old HomeStripV2 "Local Businesses" strip, which
// duplicated two entry points already available elsewhere: the directory
// (now this search icon) and "My Business" (already a full card in the
// Profile's "Switch to business mode"). Rather than thread an isOwner
// prop + handlers through Home's already-long signature, the ownership
// check lives here, self-contained — the same getBusinessesByOwners
// lookup the strip used.
//
// Navigation matches how the strip navigated: direct window.location.search,
// which the V2 layer parses. The switch button is simply not rendered for
// non-owners, so there is never a dead control.
// =============================================================
const HomeHeaderActionsV2 = ({ currentUser }) => {
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    const uid = currentUser?.id || currentUser?.uid;
    if (!uid) { setIsOwner(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const ownersMap = await getBusinessesByOwners([uid], { includeUnverified: true });
        if (!cancelled) setIsOwner(ownersMap.has(uid));
      } catch (err) {
        console.warn('[HomeHeaderActionsV2] owner check failed:', err?.message);
        if (!cancelled) setIsOwner(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id, currentUser?.uid]);

  return (
    <>
      <button
        onClick={() => { window.location.search = '?v2=biz-directory'; }}
        className="w-9 h-9 flex items-center justify-center rounded-full active:bg-black/5"
        aria-label="Local businesses"
      >
        <Search size={18} style={{ color: 'var(--ink)' }} />
      </button>
      {isOwner && (
        <button
          onClick={() => { window.location.search = '?v2=merchant'; }}
          className="w-9 h-9 flex items-center justify-center rounded-full active:bg-black/5"
          aria-label="Switch to business mode"
        >
          <Repeat size={18} style={{ color: 'var(--ink)' }} />
        </button>
      )}
    </>
  );
};

const Home = ({ posts, currentUser, group, groups, subTab, onSubTab, onTapPost, onLike,
                onOpenGroupInfo, onOpenSwitcher, onCompose, onOpenNotifs, unreadNotifs,
                onOpenChats, unreadChats,
                timeCapsule, onAddMemory, onTapMemory,
                isApproved, onCheckStatus, onBlocked, viaCode, onSwitchGroup,
                onOpenCalendar, events, checkingStatus, getUser, onDeletePost, isAdminOfActiveGroup, onShare, onReport, onTapPhoto, v2Enabled }) => (
  <div className="flex-1 flex flex-col overflow-hidden">
    <div className="px-5 pb-3 profile-header-safe" style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Logo size={18} color="var(--ink-soft)" />
          <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--ink-soft)' }}>{BATCH_TYPES[group?.type]?.headerLabel || 'Your group'}</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onOpenChats} className="w-9 h-9 flex items-center justify-center rounded-full active:bg-black/5 relative">
            <MessageSquare size={18} style={{ color: 'var(--ink)' }} />
            {unreadChats > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center text-[9px] font-medium"
                    style={{ background: 'var(--accent)', color: 'white' }}>
                {unreadChats > 9 ? '9+' : unreadChats}
              </span>
            )}
          </button>
          {/* Item #13 Phase A — Bell icon was wired through props
              (onOpenNotifs, unreadNotifs) but never rendered. Adding
              it here matches the chats-button styling so the two
              icons sit side by side. Badge shows the same count
              shape as chats (number up to 9, then "9+"). */}
          <button onClick={onOpenNotifs} className="w-9 h-9 flex items-center justify-center rounded-full active:bg-black/5 relative">
            <Bell size={18} style={{ color: 'var(--ink)' }} />
            {unreadNotifs > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center text-[9px] font-medium"
                    style={{ background: 'var(--accent)', color: 'white' }}>
                {unreadNotifs > 9 ? '9+' : unreadNotifs}
              </span>
            )}
          </button>
          {v2Enabled && <HomeHeaderActionsV2 currentUser={currentUser} />}
        </div>
      </div>
      {/* Session C3 — the group avatar and the batch switcher are SIBLINGS,
          not nested. U7 made GroupAvatar render its own <button> when onTap is
          passed, and this call site had it inside the switcher's <button> —
          which React flags ("<button> cannot be a descendant of <button>") and
          which browsers resolve inconsistently. Chrome only warns; the build
          that ships to Play runs in Android's WebView, so "it looks fine here"
          was not worth relying on.

          Both tap targets are preserved exactly:
            photo    → avatar is its own button, opens the fullscreen viewer
            no photo → avatar is a plain div INSIDE the switcher button, so
                       tapping the initials still opens the switcher as before. */}
      <div className="w-full flex items-center gap-3">
        {group.photoUrl && (
          <GroupAvatar group={group} size={48} rounded={12}
            onTap={() => onTapPhoto?.({
              id: group.id, name: group.name, photoUrl: group.photoUrl,
              color: '#7A4FC4', initials: (group.name || 'G').slice(0, 1).toUpperCase(),
            })} />
        )}
        <button onClick={onOpenSwitcher} className="flex-1 min-w-0 text-left active:opacity-80 flex items-center gap-3">
          {!group.photoUrl && <GroupAvatar group={group} size={48} rounded={12} />}
          <div className="flex-1 min-w-0">
            <h1 className="text-[19px] leading-tight truncate" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)', fontWeight: 500 }}>
              {group.name}
            </h1>
            <p className="text-[12px] truncate" style={{ color: 'var(--ink-soft)' }}>
              {group.year && `Class of ${group.year} · `}{group.city} · <span style={{ color: 'var(--accent)' }}>{group.memberCount} members</span>
            </p>
          </div>
          <ChevronsUpDown size={16} style={{ color: 'var(--ink-soft)' }} />
        </button>
      </div>
      <button onClick={onOpenGroupInfo} className="text-[11px] mt-2" style={{ color: 'var(--accent)' }}>
        See group details →
      </button>
    </div>
    <SubTabPills active={subTab} onChange={onSubTab} />
    
    <div className="flex-1 overflow-y-auto feed-bottom-safe" style={{ background: 'var(--bg)' }}>
      {!isApproved && <PendingBanner onCheckStatus={onCheckStatus} viaCode={viaCode} checking={checkingStatus} />}
      {subTab === 'wall' ? (
        <>
          <div className="px-5 py-4 flex items-center gap-3" style={{ borderBottom: '1px solid var(--border)' }}>
            <Avatar user={currentUser} size={36} />
            <button onClick={isApproved ? onCompose : onBlocked}
              className="flex-1 text-left text-[14px] py-2.5 px-4 rounded-full flex items-center gap-2"
              style={{ background: 'var(--surface)', color: 'var(--ink-soft)', border: '1px solid var(--border)', opacity: isApproved ? 1 : 0.6 }}>
              {!isApproved && <Lock size={13} />}
              {isApproved ? 'Share a memory…' : 'Posting unlocks after approval'}
            </button>
            <button onClick={isApproved ? onOpenCalendar : onBlocked}
              className="w-10 h-10 flex items-center justify-center rounded-full active:scale-[0.95] transition-transform"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', opacity: isApproved ? 1 : 0.6 }}
              aria-label="Open calendar">
              <CalendarDays size={18} style={{ color: 'var(--ink)' }} />
            </button>
          </div>
          {v2Enabled && <HomeConnectListV2 currentUser={currentUser} />}
          {getTodaysHolidays().map((h) => (
            <AanganHolidayCard key={h.date + h.name} holiday={h} />
          ))}
          {(() => {
            // Group today's events by type so multiple birthdays/anniversaries/events
            // on the same day combine into one card per type.
            const todays = getTodaysEvents(events);
            const byType = todays.reduce((acc, ev) => {
              (acc[ev.type] = acc[ev.type] || []).push(ev);
              return acc;
            }, {});
            // Render one card per type, in a stable order
            return ['birthday', 'anniversary', 'event']
              .filter(type => byType[type]?.length > 0)
              .map(type => (
                <AanganEventCard key={type} events={byType[type]} />
              ));
          })()}
          {posts.map((post) => (
            <PostCard key={post.id} post={post} onTap={onTapPost} onLike={onLike} getUser={getUser}
                      currentUser={currentUser} isApproved={isApproved} onBlocked={onBlocked}
                      onDelete={onDeletePost}
                      onShare={onShare}
                      onReport={onReport}
                      onTapPhoto={onTapPhoto}
                      canDelete={post.authorId === currentUser?.id || isAdminOfActiveGroup} />
          ))}
        </>
      ) : subTab === 'capsule' ? (
        <TimeCapsuleView memories={timeCapsule} isApproved={isApproved}
                         currentUser={currentUser}
                         onAddMemory={onAddMemory}
                         onTapMemory={onTapMemory}
                         onBlocked={onBlocked}
                         getUser={getUser} />
      ) : subTab === 'picks' ? (
        <WordOfMouthView posts={posts} currentUser={currentUser}
                         isApproved={isApproved}
                         onCompose={onCompose}
                         onTapPost={onTapPost}
                         onLike={onLike}
                         onBlocked={onBlocked}
                         getUser={getUser}
                         onDeletePost={onDeletePost}
                         onReport={onReport}
                         onTapPhoto={onTapPhoto}
                         currentUserIsAdmin={isAdminOfActiveGroup}
                         currentUserId={currentUser?.id} />
      ) : (
        <SubTabPlaceholder tab={SUB_TABS.find(t => t.key === subTab)} />
      )}
    </div>
  </div>
);

// =============================================================
// NOTIFICATIONS SCREEN
// =============================================================
// Item #13 Phase A — Firestore subscription transforms Timestamp
// createdAt → relative-time string. Mirrors WallPostCard's ageString
// but with shorter labels (the notification row is dense, so we drop
// the trailing 'ago').
function formatNotifTimestamp(ts) {
  if (!ts) return 'just now';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return date.toLocaleDateString();
}

const notifIcon = (kind) => {
  switch (kind) {
    // V1 group kinds (existing) ----------------------------------
    case 'like': return { Icon: Heart, color: '#C45A3F' };
    case 'comment': return { Icon: MessageSquare, color: '#3F6BC4' };
    // Phase B uses hyphenated 'new-comment' to match the new-connect /
    // new-post-biz / new-member / new-post convention. Same visual as
    // the legacy 'comment' since the event is the same.
    case 'new-comment': return { Icon: MessageSquare, color: '#3F6BC4' };
    case 'admin-request': return { Icon: ShieldCheck, color: '#7A4FC4' };
    case 'new-post': return { Icon: Megaphone, color: '#3F8E5C' };
    case 'new-member': return { Icon: UserPlus, color: '#B8893A' };
    case 'approval': return { Icon: Check, color: '#3F8E5C' };
    case 'warning': return { Icon: AlertTriangle, color: '#C45A3F' };
    case 'auto-removed': return { Icon: UserMinus, color: '#6E6F84' };
    // V2 business kinds (Item #13 Phase A) -----------------------
    case 'new-connect': return { Icon: Link2, color: '#3F8E5C' };
    case 'new-post-biz': return { Icon: Building2, color: '#3F6BC4' };
    case 'order-status': return { Icon: FileText, color: '#C45A3F' };
    case 'business-opened': return { Icon: Clock, color: '#3F8E5C' };
    default: return { Icon: BellRing, color: '#6E6F84' };
  }
};

const NotificationRow = ({ notif, groups, onTap, getUser }) => {
  // V1 group notifications: get actor from in-memory user cache.
  // V2 business notifications: actor info is already denormalised onto
  // the notif doc (actorName, actorPhoto), so we don't need a lookup.
  const lookedUpActor = notif.actorId ? getUser(notif.actorId) : null;
  // Combined "do we have an avatar to show" — either V1 lookup or V2
  // denormalised photo. The render below uses either.
  const actor = lookedUpActor || (notif.actorName ? {
    id: notif.actorId,
    name: notif.actorName,
    photoUrl: notif.actorPhoto || null,
    // Avatar component falls back to initials if photoUrl is null.
    initials: notif.actorName.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase().slice(0, 2),
    color: '#C45A3F',
  } : null);
  // Item #13: V2 notification doc text already includes the actor's
  // name (e.g. "Home connected with you"). Don't prefix actor.name
  // again or we get "Home Home connected with you". Detect this by
  // presence of the denormalised actorName field on the notif itself.
  const textHasActorPrefix = !notif.actorName;
  const group = groups[notif.groupId];
  const { Icon, color } = notifIcon(notif.kind);
  return (
    <button onClick={() => onTap(notif)}
      className="w-full flex items-start gap-3 px-5 py-3 active:bg-black/5"
      style={{ borderBottom: '1px solid var(--border)', background: notif.unread ? 'var(--accent-soft)' : 'var(--bg)' }}>
      <div className="relative shrink-0">
        {actor ? (
          <Avatar user={actor} size={40} />
        ) : (
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <Icon size={16} style={{ color }} />
          </div>
        )}
        {actor && (
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
               style={{ background: 'var(--bg)' }}>
            <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: color }}>
              <Icon size={9} color="white" strokeWidth={2.5} />
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="text-[13px] leading-snug mb-0.5" style={{ color: 'var(--ink)' }}>
          {actor && textHasActorPrefix && <span style={{ fontWeight: 600 }}>{actor.name} </span>}
          {notif.text}
        </p>
        {notif.subtext && (
          <p className="text-[12px] italic mb-1" style={{ color: 'var(--ink-soft)' }}>{notif.subtext}</p>
        )}
        <div className="flex items-center gap-1.5">
          {group && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ background: 'var(--surface)', color: 'var(--ink-soft)', border: '1px solid var(--border)' }}>
              <span>{group.emoji}</span> {group.name}
            </span>
          )}
          <span className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>{notif.timestamp}</span>
        </div>
      </div>
      {notif.unread && (
        <div className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ background: 'var(--accent)' }} />
      )}
    </button>
  );
};

const NotificationsScreen = ({ notifications, groups, onBack, onMarkAllRead, onTap, getUser }) => {
  const unread = notifications.filter(n => n.unread);
  const earlier = notifications.filter(n => !n.unread);
  return (
    <div className="flex-1 flex flex-col">
      <TopBar title="Notifications" onBack={onBack}
              right={unread.length > 0 ? (
                <button onClick={onMarkAllRead} className="text-[12px]" style={{ color: 'var(--accent)' }}>Mark read</button>
              ) : null} />
      <div className="flex-1 overflow-y-auto sheet-bottom-safe">
        {notifications.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                 style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <Inbox size={26} style={{ color: 'var(--ink-soft)' }} strokeWidth={1.4} />
            </div>
            <h3 className="mb-2" style={{ fontSize: '20px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
              All quiet here.
            </h3>
            <p className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>
              When someone likes, comments or joins your batches — you'll see it here.
            </p>
          </div>
        ) : (
          <>
            {unread.length > 0 && (
              <>
                <p className="px-5 pt-4 pb-1 text-[11px] uppercase tracking-wider" style={{ color: 'var(--ink-soft)' }}>New</p>
                {unread.map(n => <NotificationRow key={n.id} notif={n} groups={groups} onTap={onTap} getUser={getUser} />)}
              </>
            )}
            {earlier.length > 0 && (
              <>
                <p className="px-5 pt-5 pb-1 text-[11px] uppercase tracking-wider" style={{ color: 'var(--ink-soft)' }}>Earlier</p>
                {earlier.map(n => <NotificationRow key={n.id} notif={n} groups={groups} onTap={onTap} getUser={getUser} />)}
              </>
            )}
            <div className="h-6" />
          </>
        )}
      </div>
    </div>
  );
};

// =============================================================
// MEMBERS / MEMBER SHEET
// =============================================================
const Members = ({ group, isApproved, onTapMember, currentUserIsAdmin, currentUser, onTapSelf, members = [] }) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  // Build the display list: real members from Firestore, with current user marked as _isMe.
  // Members already includes the current user (since they're an approved member of the group).
  const all = members.map(m => ({
    ...m,
    _isMe: m.id === currentUser?.id,
    currentCity: (m.currentCity && m.currentCity.trim() && m.currentCity !== '—') ? m.currentCity : 'Unknown',
  }));
  const filtered = all.filter(u => {
    if (filter === 'admins' && !u.isAdmin && !u._isMe) return false;
    if (query) {
      const q = query.toLowerCase();
      return u.name.toLowerCase().includes(q) || (u.currentCity || '').toLowerCase().includes(q);
    }
    return true;
  });
  const byCity = filtered.reduce((acc, u) => {
    (acc[u.currentCity] = acc[u.currentCity] || []).push(u);
    return acc;
  }, {});
  const sortedCities = Object.keys(byCity).sort();
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-5 pb-3 profile-header-safe" style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
        <p className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: 'var(--ink-soft)' }}>Members of {group.name}</p>
        <h1 className="text-[24px] leading-tight mb-3" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)', fontWeight: 500 }}>
          {group.memberCount} batchmates
        </h1>
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-soft)' }} />
          <input type="text" placeholder="Search by name or city" value={query} onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-full text-[14px] outline-none"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
        </div>
        <div className="flex gap-2 mt-3">
          {(currentUserIsAdmin ? [['all', 'All'], ['admins', 'Admins']] : [['all', 'All']]).map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)} className="px-3 py-1.5 rounded-full text-[12px]"
              style={{ background: filter === key ? 'var(--ink)' : 'var(--surface)',
                       color: filter === key ? 'var(--bg)' : 'var(--ink)',
                       border: '1px solid var(--border)' }}>{label}</button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto feed-bottom-safe" style={{ background: 'var(--bg)' }}>
        {!isApproved && (
          <div className="px-5 py-2.5 flex items-center gap-2" style={{ background: 'var(--accent-soft)', borderBottom: '1px solid var(--border)' }}>
            <EyeOff size={12} style={{ color: 'var(--accent)' }} />
            <p className="text-[11px]" style={{ color: 'var(--ink)' }}>View-only until approved. Personal details are never shown.</p>
          </div>
        )}
        {sortedCities.length === 0 ? (
          all.length === 0 ? (
            <div className="text-center py-16 px-8">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <Users size={20} style={{ color: 'var(--ink-soft)' }} />
              </div>
              <p className="text-[14px] mb-1" style={{ color: 'var(--ink)' }}>You're the only one here yet</p>
              <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
                Share your connect code so batchmates can join.
              </p>
            </div>
          ) : (
            <div className="text-center py-12 px-6"><p className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>No matches.</p></div>
          )
        ) : sortedCities.map((cityName) => (
          <div key={cityName}>
            <div className="px-5 pt-4 pb-2 flex items-center gap-1.5">
              <MapPin size={11} style={{ color: 'var(--accent)' }} />
              <p className="text-[11px] uppercase tracking-wider font-medium" style={{ color: 'var(--ink-soft)' }}>
                {cityName} · {byCity[cityName].length}
              </p>
            </div>
            {byCity[cityName].map((u) => (
              <button key={u.id} onClick={() => u._isMe ? onTapSelf?.() : onTapMember(u)}
                className="w-full flex items-center gap-3 px-5 py-3 active:bg-black/5"
                style={{ borderBottom: '1px solid var(--border)' }}>
                <Avatar user={u} size={44} />
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[14px] font-medium truncate" style={{ color: 'var(--ink)' }}>{u.name}</p>
                    {u._isMe && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
                                       style={{ background: 'var(--accent)', color: 'white' }}>You</span>}
                    {u.isAdmin && !u._isMe && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
                                        style={{ background: 'var(--ink)', color: 'var(--bg)' }}>Admin</span>}
                  </div>
                  {/* City removed — already shown in the cluster header above. */}
                  {/* Status is the conversation hook; show it most prominently below the name. */}
                  {u.status && (
                    <p className="text-[12px] mt-0.5 truncate italic" style={{ color: 'var(--accent)', fontFamily: 'var(--font-display)' }}>
                      "{u.status}"
                    </p>
                  )}
                  {u.profession && (
                    <div className="mt-1">
                      <ProfessionPill profession={u.profession} />
                    </div>
                  )}
                </div>
                <ChevronRight size={16} style={{ color: 'var(--ink-soft)' }} />
              </button>
            ))}
          </div>
        ))}
        <div className="h-6" />
      </div>
    </div>
  );
};

const MemberDetail = ({ member, onBack, onChatRequested, posts, getUser, group, currentUserId, onTapPost, onLike, onReport, onRequestChat, isPending, onBlock, onUnblock, blocked, onTapPhoto, chatState, onOpenChat, onMakeAdmin, onRemove, currentUserIsAdmin }) => {
  // chatState: 'none' | 'pending' | 'active'
  const [showActions, setShowActions] = useState(false);
  const memberPosts = posts.filter(p => p.authorId === member.id && p.groupId === group.id);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 sticky top-0 z-10 topbar-safe"
           style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)', minHeight: '56px' }}>
        <button onClick={onBack} className="w-9 h-9 flex items-center justify-center -ml-2 rounded-full active:bg-black/5">
          <ArrowLeft size={20} style={{ color: 'var(--ink)' }} />
        </button>
        <h1 className="text-base font-medium" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>Profile</h1>
        <button onClick={() => setShowActions(true)} className="w-9 h-9 flex items-center justify-center -mr-2 rounded-full active:bg-black/5">
          <MoreVertical size={20} style={{ color: 'var(--ink)' }} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ background: 'var(--bg)' }}>
        <div className="px-5 pt-4 pb-5" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-start gap-4 mb-3">
            <button onClick={() => onTapPhoto?.(member)} className="active:scale-95 transition-transform shrink-0">
              <Avatar user={member} size={100} />
            </button>
            <div className="flex-1 pt-2">
              <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                <h2 className="text-[22px] leading-tight" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
                  {member.name}
                </h2>
                {member.isAdmin && (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"
                        style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
                    <Crown size={9} /> Admin
                  </span>
                )}
              </div>
              {member.work && <p className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>{member.work}</p>}
              <p className="text-[12px] mt-1 flex items-center gap-1" style={{ color: 'var(--ink)' }}>
                <MapPin size={11} style={{ color: 'var(--accent)' }} /> {member.currentCity}
              </p>
              {member.profession && (
                <div className="mt-1.5">
                  <ProfessionPill profession={member.profession} size="lg" />
                </div>
              )}
            </div>
          </div>

          {member.status && (
            <p className="text-[14px] italic px-3.5 py-2.5 rounded-2xl mb-4"
               style={{ color: 'var(--ink)', background: 'var(--accent-soft)', fontFamily: 'var(--font-display)' }}>
              "{member.status}"
            </p>
          )}

          <div className="flex gap-2">
            {chatState === 'active' ? (
              <button onClick={() => onOpenChat(member.id)}
                className="flex-1 h-11 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
                <MessageSquare size={16} /> <span className="text-[13px] font-medium">Open chat</span>
              </button>
            ) : chatState === 'pending' ? (
              <button disabled
                className="flex-1 h-11 rounded-2xl flex items-center justify-center gap-2"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink-soft)' }}>
                <Clock size={14} /> <span className="text-[13px]">Chat request sent · waiting</span>
              </button>
            ) : (
              <button onClick={() => onRequestChat(member)}
                className="flex-1 h-11 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
                <MessageSquare size={16} /> <span className="text-[13px] font-medium">Send a message</span>
              </button>
            )}
          </div>
          <p className="text-[11px] mt-2 text-center flex items-center justify-center gap-1" style={{ color: 'var(--ink-soft)' }}>
            <MessageCircle size={10} /> Recent messages only — older messages stay archived
          </p>
        </div>

        <div className="rounded-xl mx-5 my-4 p-3 flex items-start gap-2"
             style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <ShieldCheck size={13} className="mt-0.5 shrink-0" style={{ color: 'var(--ink-soft)' }} />
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
            Phone, email & other personal details are private. Member info is shown only to verified batchmates.
          </p>
        </div>

        <div className="px-5 pb-2 pt-2 flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--ink-soft)' }}>
            Posts in {group.name}
          </p>
          <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>{memberPosts.length}</p>
        </div>

        {memberPosts.length === 0 ? (
          <div className="text-center py-10 px-6">
            <p className="text-[13px] italic" style={{ color: 'var(--ink-soft)' }}>
              {member.name.split(' ')[0]} hasn't posted in this batch yet.
            </p>
          </div>
        ) : (
          memberPosts.map((post) => (
            <article key={post.id} className="px-5 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
              <p className="text-[11px] mb-2" style={{ color: 'var(--ink-soft)' }}>{post.timestamp}</p>
              <div className="text-[14px] leading-relaxed mb-2" style={{ color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{linkifyText(post.text)}</div>
              {post.photo && <div className="mb-2"><PhotoBlock photo={post.photo} /></div>}
              <div className="flex items-center gap-3 text-[12px]" style={{ color: 'var(--ink-soft)' }}>
                <span className="inline-flex items-center gap-1"><Zap size={12} /> {post.likes}</span>
                <span className="inline-flex items-center gap-1"><MessageSquareDashed size={12} /> {post.comments.length}</span>
              </div>
            </article>
          ))
        )}
        <div className="h-6" />
      </div>

      {showActions && (
        <div className="absolute inset-0 z-40 flex items-end" style={{ background: 'rgba(0,0,0,0.5)' }}
             onClick={() => setShowActions(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full rounded-t-3xl pb-8" style={{ background: 'var(--bg)' }}>
            <div className="w-12 h-1 rounded-full mx-auto my-3" style={{ background: 'var(--border)' }} />
            <p className="text-[11px] uppercase tracking-wider px-5 pb-2" style={{ color: 'var(--ink-soft)' }}>
              {member.name}
            </p>
            {currentUserIsAdmin && !member.isAdmin && (
              <button onClick={() => { setShowActions(false); onMakeAdmin(member); }}
                      className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5">
                <Crown size={18} style={{ color: 'var(--gold)' }} />
                <span className="text-[14px]" style={{ color: 'var(--ink)' }}>Make admin</span>
              </button>
            )}
            {currentUserIsAdmin && (
              <button onClick={() => { setShowActions(false); onRemove(member); }}
                      className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5">
                <UserMinus size={18} style={{ color: '#B33A3A' }} />
                <span className="text-[14px]" style={{ color: '#B33A3A' }}>Remove from group</span>
              </button>
            )}
            <button onClick={() => { setShowActions(false); onReport(member); }}
                    className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5">
              <Flag size={18} style={{ color: '#B33A3A' }} />
              <span className="text-[14px]" style={{ color: '#B33A3A' }}>Report this member</span>
            </button>
            {/* Session C2b — Block. onBlock/blocked were already in this
                component's signature but never passed and never used: the
                original design intended a block here and it was never wired.
                Now real. A member's profile is the natural place to block
                someone you have never messaged. */}
            {member.id !== currentUserId && (
              <button onClick={() => { setShowActions(false); blocked ? onUnblock?.(member) : onBlock?.(member); }}
                      className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5">
                <Ban size={18} style={{ color: blocked ? 'var(--ink-soft)' : '#B33A3A' }} />
                <span className="text-[14px]" style={{ color: blocked ? 'var(--ink)' : '#B33A3A' }}>
                  {blocked ? `Unblock ${member.name?.split(' ')[0] || 'member'}` : `Block ${member.name?.split(' ')[0] || 'member'}`}
                </span>
              </button>
            )}
            <button onClick={() => setShowActions(false)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5"
                    style={{ borderTop: '1px solid var(--border)' }}>
              <X size={18} style={{ color: 'var(--ink-soft)' }} />
              <span className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Cancel</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================
// STATUS EDITOR
// =============================================================
const StatusEditor = ({ status, onSave, onClose }) => {
  const [draft, setDraft] = useState(status || '');
  return (
    <div className="absolute inset-0 z-40 flex items-end" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full rounded-t-3xl p-6 pb-8" style={{ background: 'var(--bg)' }}>
        <div className="w-12 h-1 rounded-full mx-auto mb-5" style={{ background: 'var(--border)' }} />
        <h2 className="text-[20px] mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
          Set your status
        </h2>
        <p className="text-[12px] mb-4" style={{ color: 'var(--ink-soft)' }}>
          A short line your batchmates will see on your profile. Empty to clear.
        </p>
        <textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value.slice(0, 80))}
          placeholder="e.g. Visiting Mumbai next week — let's catch up!"
          rows={3}
          className="w-full p-4 rounded-2xl text-[14px] outline-none resize-none mb-2"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
        <p className="text-[11px] text-right mb-4" style={{ color: 'var(--ink-soft)' }}>{draft.length}/80</p>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-12 rounded-2xl font-medium text-[14px]"
                  style={{ background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--border)' }}>
            Cancel
          </button>
          <button onClick={() => onSave(draft.trim())} className="flex-1 h-12 rounded-2xl font-medium text-[14px]"
                  style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
            Save status
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================
// COMPOSE / POST DETAIL
// =============================================================
const Compose = ({ onBack, onPost, currentUser, group, defaultSubTab = 'wall' }) => {
  const [text, setText] = useState('');
  const [photoUrl, setPhotoUrl] = useState(null);   // base64 data URL of the chosen photo
  const [photoLabel, setPhotoLabel] = useState(''); // caption
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [postSubTab, setPostSubTab] = useState(defaultSubTab);
  const [category, setCategory] = useState(null);
  const fileInputRef = useRef(null);
  const isPicks = postSubTab === 'picks';

  const MAX_BYTES = 25 * 1024 * 1024; // 25 MB — modern phone photos

  const [pendingFile, setPendingFile] = useState(null); // raw File, kept for upload on Post

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');

    if (!file.type.startsWith('image/')) {
      setError('Photos only — videos aren\'t supported yet.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Photo must be under 25 MB. Try a smaller one.');
      return;
    }

    setBusy(true);
    setPendingFile(file); // keep the raw file for upload-on-post
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoUrl(ev.target.result); // local preview
      setBusy(false);
    };
    reader.onerror = () => {
      setError('Couldn\'t read that photo.');
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };

  const handlePost = async () => {
    if (!text.trim() && !photoUrl) return;
    
    // No photo? Just post the text. (No limit check needed — text posts are unlimited.)
    if (!pendingFile) {
      onPost({
        text: text.trim(),
        photo: null,
        subTab: postSubTab,
        category: isPicks ? category : null,
      });
      return;
    }
    
    // Photo post — check the user's monthly limit BEFORE uploading
    setBusy(true);
    setError('');
    try {
      const limitCheck = await checkPostPhotoLimit(currentUser?.id);
      if (!limitCheck.allowed) {
        setError(`You've reached your monthly photo limit of ${limitCheck.limit}. New month resets in a few days.`);
        setBusy(false);
        return;
      }
      // Show remaining count if low (warning)
      if (limitCheck.remaining <= 5) {
        // Still allow, but warn
        console.log(`Heads up — ${limitCheck.remaining} photo posts remaining this month`);
      }
    } catch (e) {
      // Fail open if check fails — don't block the user
      console.warn('Limit check failed, proceeding:', e);
    }
    
    // Has photo — upload to Firebase Storage first, then submit post with real URL.
    // (busy state already set above for limit check)
    setError('');
    try {
      // Generate a unique post ID upfront so we can name the photo file
      const postId = 'p_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      // Upload compressed photo to Firebase Storage
     const { url: firebaseUrl, aspect } = await uploadPostPhoto(group.id, postId, pendingFile, currentUser?.id);
      // Submit post with real Firebase URL + aspect for proper display framing
      onPost({
        id: postId,
        text: text.trim(),
        photo: { url: firebaseUrl, aspect },
        subTab: postSubTab,
        category: isPicks ? category : null,
      });
    } catch (err) {
      console.error('Photo upload failed:', err);
      setError('Could not upload photo — please try again');
      setBusy(false);
    }
  };

  const canPost = (text.trim() || photoUrl) && !busy;

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex items-center justify-between px-4 sticky top-0 z-10 topbar-safe"
           style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)', minHeight: '56px' }}>
        <button onClick={onBack} className="w-9 h-9 flex items-center justify-center -ml-2 rounded-full active:bg-black/5">
          <X size={20} style={{ color: 'var(--ink)' }} />
        </button>
        <h1 className="text-base font-medium" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>New post</h1>
        <button onClick={handlePost} disabled={!canPost}
          className="px-4 py-1.5 rounded-full text-[13px] font-medium disabled:opacity-40"
          style={{ background: 'var(--ink)', color: 'var(--bg)' }}>Post</button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        style={{ display: 'none' }}
      />

      <div className="flex-1 px-5 pt-4 overflow-y-auto" style={{ paddingBottom: 60 }}>
        <div className="flex items-start gap-3 mb-4">
          <Avatar user={currentUser} size={40} />
          <div className="flex-1">
            <p className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>{currentUser.name}</p>
            <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>Posting in {group.name}</p>
          </div>
        </div>
        
        {/* Destination picker */}
        <div className="flex gap-2 mb-3">
          <button onClick={() => setPostSubTab('wall')}
            className="flex-1 py-2 rounded-xl text-[12px] font-medium flex items-center justify-center gap-1.5"
            style={{ 
              background: postSubTab === 'wall' ? 'var(--ink)' : 'var(--surface)',
              color: postSubTab === 'wall' ? 'var(--bg)' : 'var(--ink)',
              border: '1px solid var(--border)',
            }}>
            <MessageCircle size={13} /> Wall
          </button>
          <button onClick={() => setPostSubTab('picks')}
            className="flex-1 py-2 rounded-xl text-[12px] font-medium flex items-center justify-center gap-1.5"
            style={{ 
              background: postSubTab === 'picks' ? 'var(--ink)' : 'var(--surface)',
              color: postSubTab === 'picks' ? 'var(--bg)' : 'var(--ink)',
              border: '1px solid var(--border)',
            }}>
            <ThumbsUp size={13} /> Word of Mouth
          </button>
        </div>
        
        {/* Category picker (Word of Mouth only) */}
        {isPicks && (
          <div className="flex gap-1.5 mb-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {[
              { key: 'job', label: 'Job', emoji: '💼' },
              { key: 'rec', label: 'Recommend', emoji: '👍' },
              { key: 'heads-up', label: 'Heads-up', emoji: '📢' },
              { key: 'other', label: 'Other', emoji: '💬' },
            ].map(({ key, label, emoji }) => (
              <button key={key} onClick={() => setCategory(category === key ? null : key)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full shrink-0"
                style={{
                  background: category === key ? 'var(--accent)' : 'var(--surface)',
                  color: category === key ? 'white' : 'var(--ink)',
                  border: '1px solid var(--border)',
                }}>
                <span className="text-[11px]">{emoji}</span>
                <span className="text-[11px] font-medium">{label}</span>
              </button>
            ))}
          </div>
        )}

        <textarea autoFocus placeholder="Write a caption…"
          value={text} onChange={(e) => setText(e.target.value)} rows={6}
          className="w-full bg-transparent outline-none text-[16px] leading-relaxed resize-none"
          style={{ color: 'var(--ink)' }} />

        {error && (
          <p className="text-[12px] mb-3" style={{ color: '#B33A3A' }}>{error}</p>
        )}

        {photoUrl && (
          <div className="relative mt-3">
            <img src={photoUrl} alt="Post preview"
                 className="w-full object-cover rounded-xl"
                 draggable={false}
                 onContextMenu={(e) => e.preventDefault()}
                 style={{ 
                   maxHeight: 360, 
                   border: '1px solid var(--border)',
                   userSelect: 'none',
                   WebkitUserSelect: 'none',
                   WebkitTouchCallout: 'none',
                 }} />
            <button onClick={() => { setPhotoUrl(null); setPhotoLabel(''); setPendingFile(null); }}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.6)' }}>
              <X size={16} color="white" />
            </button>
          </div>
        )}

        
      </div>

      <div className="sticky bottom-0 px-5 py-3 flex items-center gap-2"
           style={{ 
             borderTop: '1px solid var(--border)', 
             background: 'var(--bg)',
             paddingBottom: 'max(calc(0.75rem + env(safe-area-inset-bottom)), 28px)',
           }}>
        {!isPicks && (
          <button onClick={() => fileInputRef.current?.click()} disabled={busy}
            className="flex items-center gap-2 px-3 py-2 rounded-full disabled:opacity-50"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <ImageIcon size={16} style={{ color: 'var(--accent)' }} />
            <span className="text-[13px]" style={{ color: 'var(--ink)' }}>
              {busy ? 'Loading…' : photoUrl ? 'Change photo' : 'Add photo'}
            </span>
          </button>
        )}
        <span className="text-[11px] ml-auto" style={{ color: 'var(--ink-soft)' }}>
          {text.length}/{isPicks ? 1000 : 500}{!isPicks && ' · photos only · max 25 MB'}
        </span>
      </div>
    </div>
  );
};
// =============================================================
// COMMENT ROW — own-comment edit support
// Tap pencil → inline edit → Save writes to Firestore, Cancel reverts.
// Edited comments show "· edited" next to timestamp.
// =============================================================
const CommentRow = ({ comment, author, isMine, postId, groupId }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.text);
  const [saving, setSaving] = useState(false);

  // Reset draft if comment text changes from outside (e.g. real-time update)
  useEffect(() => {
    if (!editing) setDraft(comment.text);
  }, [comment.text, editing]);

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (trimmed === comment.text) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await editPostComment({ groupId, postId, commentId: comment.id, newText: trimmed });
      setEditing(false);
    } catch (err) {
      console.error('Edit comment failed:', err);
      alert('Could not save edit — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(comment.text);
    setEditing(false);
  };

  return (
    <div className="flex gap-3">
      <Avatar user={author} size={32} />
      <div className="flex-1 min-w-0">
        <div className="rounded-2xl px-3.5 py-2.5" style={{ background: 'var(--surface)' }}>
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <p className="text-[12px] font-medium truncate" style={{ color: 'var(--ink)' }}>{author.name}</p>
            {isMine && !editing && (
              <button onClick={() => setEditing(true)}
                className="w-6 h-6 flex items-center justify-center rounded active:bg-black/5 shrink-0">
                <Pencil size={11} style={{ color: 'var(--ink-soft)' }} />
              </button>
            )}
          </div>
          {editing ? (
            <>
              <textarea
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, 500))}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleCancel();
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSave(); }
                }}
                rows={2}
                className="w-full bg-transparent outline-none text-[14px] resize-none"
                style={{ color: 'var(--ink)', borderBottom: '1px solid var(--accent)' }}
              />
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-[10px]" style={{ color: 'var(--ink-soft)' }}>{draft.length}/500</p>
                <div className="flex gap-1.5">
                  <button onClick={handleCancel} disabled={saving}
                    className="text-[11px] px-2 py-1" style={{ color: 'var(--ink-soft)' }}>
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving || !draft.trim()}
                    className="text-[11px] px-2.5 py-1 rounded font-medium disabled:opacity-40"
                    style={{ background: 'var(--accent)', color: 'white' }}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <p className="text-[14px]" style={{ color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{linkifyText(comment.text)}</p>
          )}
        </div>
        <p className="text-[11px] mt-1 ml-3" style={{ color: 'var(--ink-soft)' }}>
          {comment.timestamp}
          {comment.editedAt && <span> · edited</span>}
        </p>
      </div>
    </div>
  );
};

const PostDetail = ({ post, onBack, onComment, onLike, currentUser, isApproved, onBlocked, getUser, hasMoreComments, onLoadOlderComments, loadingOlderComments }) => {
  const [commentText, setCommentText] = useState('');
  const author = post.authorId === 'me' ? currentUser : getUser(post.authorId);
  const commentsEndRef = useRef(null);
  const submit = () => {
    if (!commentText.trim()) return;
    onComment(post.id, commentText.trim());
    setCommentText('');
    // Auto-scroll to top so user sees their new comment (newest first)
    setTimeout(() => {
      const scrollContainer = document.querySelector('.overflow-y-auto');
      if (scrollContainer) scrollContainer.scrollTop = 0;
    }, 100);
  };
  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <TopBar title="Post" onBack={onBack} />
      <div className="flex-1 overflow-y-auto min-h-0">
        <article className="px-5 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <header className="flex items-center gap-3 mb-3">
            <Avatar user={author} size={44} />
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <p className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>{author.name}</p>
                {author.isAdmin && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                                         style={{ background: 'var(--ink)', color: 'var(--bg)' }}>Admin</span>}
              </div>
              <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
                <MapPin size={9} className="inline -mt-0.5 mr-0.5" />{author.currentCity || '—'} · {post.timestamp}
              </p>
            </div>
          </header>
          <div className="text-[16px] leading-relaxed mb-3" style={{ color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{linkifyText(post.text)}</div>
          {post.photo && <div className="mb-3"><PhotoBlock photo={post.photo} /></div>}
          <div className="flex items-center gap-4 pt-1">
            <button onClick={() => isApproved ? onLike(post.id) : onBlocked()}
                    className="flex items-center gap-1.5 active:scale-90 transition-transform" style={{ opacity: isApproved ? 1 : 0.55 }}>
              {isApproved ? (
                <Zap size={18} style={{ color: post.likedByMe ? 'var(--accent)' : 'var(--ink-soft)' }}
                     fill={post.likedByMe ? 'var(--accent)' : 'none'} />
              ) : <Lock size={16} style={{ color: 'var(--ink-soft)' }} />}
              <span className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>{post.likes} likes</span>
            </button>
            <span className="text-[13px] flex items-center gap-1" style={{ color: 'var(--ink-soft)' }}>
              <MessageSquareDashed size={14} /> {post.comments.length} comments
            </span>
          </div>
        </article>
        <div className="px-5 py-3">
          <p className="text-[11px] uppercase tracking-wider mb-3" style={{ color: 'var(--ink-soft)' }}>Comments</p>
          {post.comments.length === 0 ? (
            <p className="text-[13px] italic py-4" style={{ color: 'var(--ink-soft)' }}>Be the first to comment.</p>
          ) : (
            <div className="space-y-4">
              {post.comments.map((c) => {
                const cAuthor = c.authorId === 'me' ? currentUser : getUser(c.authorId);
                const isMyComment = c.authorId === currentUser?.id;
                return (
                  <CommentRow
                    key={c.id}
                    comment={c}
                    author={cAuthor}
                    isMine={isMyComment}
                    postId={post.id}
                    groupId={post.groupId}
                  />
                );
              })}
            </div>
          )}
          {hasMoreComments && (
            <button onClick={onLoadOlderComments} disabled={loadingOlderComments}
              className="w-full text-[12px] py-2.5 mt-3 mb-2 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-50"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink-soft)' }}>
              {loadingOlderComments ? (
                <>
                  <div className="w-3 h-3 rounded-full border animate-spin"
                       style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
                  Loading earlier comments…
                </>
              ) : (
                <>↓ View earlier comments</>
              )}
            </button>
          )}
        </div>
        <div ref={commentsEndRef} />
      </div>
      <div className="px-4 pt-3 flex items-center gap-2 shrink-0" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)', paddingBottom: 'max(calc(var(--safe-bottom) + 12px), 28px)' }}>
        <Avatar user={currentUser} size={32} />
        {isApproved ? (
          <>
            <input type="text" value={commentText} onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()} placeholder="Add a comment…"
              className="flex-1 px-4 py-2.5 rounded-full text-[14px] outline-none"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
            <button onClick={submit} disabled={!commentText.trim()}
              className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40"
              style={{ background: 'var(--ink)' }}><Send size={16} color="white" /></button>
          </>
        ) : (
          <button onClick={onBlocked} className="flex-1 flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px]"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink-soft)' }}>
            <Lock size={13} /> Commenting unlocks after admin approval
          </button>
        )}
      </div>
    </div>
  );
};

// =============================================================
// PROFILE — with status + multi-group list
// =============================================================
const Profile = ({ currentUser, posts, group, groups, onLogout, onEditStatus, onSwitchGroup, onAddGroup, onOpenSettings, onPrivacyData, onCommunityRules, isAdmin, onAdminQueue, pendingCount = 0 }) => {
  return (
    <div className="flex-1 flex flex-col overflow-y-auto feed-bottom-safe">
      <div className="px-5 pb-6 relative profile-header-safe" style={{ background: 'var(--surface)' }}>
        {/* U6 (revised): the gear is part of the avatar row, right-aligned and
            vertically aligned with the avatar and the "Member since" block —
            NOT absolutely positioned. Absolute positioning floated it into the
            status-bar / notch zone regardless of padding; keeping it in normal
            flow means the header's safe-area top padding carries it clear of
            the notch like every other element. */}
        <div className="flex items-end justify-between mb-4">
          <div className="flex items-end gap-4">
            <Avatar user={currentUser} size={88} />
            <div className="pb-2">
              <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--ink-soft)' }}>Member since</p>
            <p className="text-[13px]" style={{ color: 'var(--ink)' }}>
              {(() => {
                const raw = currentUser?.onboardedAt;
                if (!raw) return 'recently';
                // Firestore Timestamp object
                if (raw.toMillis && typeof raw.toMillis === 'function') {
                  return new Date(raw.toMillis()).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
                }
                // ISO string or millis number
                const d = new Date(raw);
                if (isNaN(d.getTime())) return 'recently';
                return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
              })()}
              </p>
            </div>
          </div>
          <button
            onClick={onOpenSettings}
            className="w-9 h-9 flex items-center justify-center rounded-full active:bg-black/5 shrink-0"
            aria-label="Settings"
          >
            <Settings size={18} style={{ color: 'var(--ink)' }} />
          </button>
        </div>
        <h1 className="text-[26px] leading-tight mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
          {currentUser.name}
        </h1>
        {currentUser.bio && <p className="text-[14px] mb-2" style={{ color: 'var(--ink-soft)' }}>{currentUser.bio}</p>}
        {currentUser.profession && (
          <div className="mb-2">
            <ProfessionPill profession={currentUser.profession} size="lg" />
          </div>
        )}
        <button onClick={onEditStatus}
                className="w-full text-left flex items-center gap-2 mt-2 mb-3 p-3 rounded-2xl"
                style={{ background: 'var(--bg)', border: '1px dashed var(--border)' }}>
          {currentUser.status ? (
            <>
              <Sparkles size={13} style={{ color: 'var(--accent)' }} />
              <p className="text-[13px] flex-1 italic" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>
                "{currentUser.status}"
              </p>
              <Pencil size={13} style={{ color: 'var(--ink-soft)' }} />
            </>
          ) : (
            <>
              <Pencil size={13} style={{ color: 'var(--ink-soft)' }} />
              <p className="text-[13px] flex-1" style={{ color: 'var(--ink-soft)' }}>
                Set a status — visible to your batchmates
              </p>
            </>
          )}
        </button>
        {currentUser.currentCity && (
          <p className="text-[13px] flex items-center gap-1.5" style={{ color: 'var(--ink)' }}>
            <MapPin size={12} style={{ color: 'var(--accent)' }} /> Currently in {currentUser.currentCity}
          </p>
        )}
      </div>
      <div className="px-5 py-5">
        <p className="text-[11px] uppercase tracking-wider mb-3" style={{ color: 'var(--ink-soft)' }}>
          Your batches · {Object.keys(groups).length}
        </p>
        <div className="space-y-2">
          {Object.values(groups).map((g) => {
            const isActive = g.id === group.id;
            return (
              <button key={g.id} onClick={() => onSwitchGroup(g.id)}
                className="w-full rounded-2xl p-3 flex items-center gap-3 active:scale-[0.99] transition-transform"
                style={{ background: isActive ? 'var(--accent-soft)' : 'var(--surface)', border: '1px solid var(--border)' }}>
                <GroupAvatar group={g} size={44} rounded={12} />
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[14px] font-medium truncate" style={{ color: 'var(--ink)' }}>{g.name}</p>
                    {isActive && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                                       style={{ background: 'var(--accent)', color: 'white' }}>Now</span>}
                  </div>
                  <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
                    {g.memberCount} {g.memberCount === 1 ? 'member' : 'members'}{g.unread > 0 && !isActive ? ` · ${g.unread} new` : ''}
                  </p>
                </div>
                <ChevronRight size={16} style={{ color: 'var(--ink-soft)' }} />
              </button>
            );
          })}
        </div>
        <button onClick={onAddGroup}
                className="w-full rounded-2xl p-4 flex items-center gap-3 mt-2"
                style={{ border: '1px dashed var(--border)' }}>
          <Plus size={16} style={{ color: 'var(--accent)' }} />
          <span className="text-[14px]" style={{ color: 'var(--accent)' }}>Add another batch</span>
        </button>
      </div>
      
      {/* Moderation queue moved to Chats → Requests tab (admin sees join requests + reports there) */}
      <div className="px-5 py-3 space-y-1" style={{ borderTop: '1px solid var(--border)' }}>
        <button onClick={onLogout} className="w-full flex items-center justify-between py-3">
          <span className="flex items-center gap-3 text-[14px]" style={{ color: '#B33A3A' }}>
            <LogOut size={16} /> Sign out
          </span>
        </button>
      </div>
    </div>
  );
};

// =============================================================
// GROUP INFO SHEET (admin photo upload)
// =============================================================
const GroupInfoSheet = ({ group, onClose, onCopy, onPhotoUpload, isAdmin, onUpdateInfo, onShare, onLeaveGroup, currentUser, onTapPhoto }) => {
  const handleShareFromInfoSheet = (g) => onShare?.(g);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Crop modal state for new cover photo
  const [cropSrc, setCropSrc] = useState(null);
  // Inline edit state for name and bio (admin only)
  const [editingName, setEditingName] = useState(false);
  const [editingBio, setEditingBio] = useState(false);
  const [nameDraft, setNameDraft] = useState(group.name);
  const [bioDraft, setBioDraft] = useState(group.bio || '');

  // Re-sync drafts when group changes
  useEffect(() => {
    setNameDraft(group.name);
    setBioDraft(group.bio || '');
  }, [group.id, group.name, group.bio]);

  const saveName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === group.name) {
      setEditingName(false);
      setNameDraft(group.name);
      return;
    }
    if (trimmed.length > 60) return;
    onUpdateInfo({ name: trimmed });
    setEditingName(false);
  };

  const saveBio = async () => {
    const trimmed = bioDraft.trim();
    if (trimmed === (group.bio || '')) {
      setEditingBio(false);
      return;
    }
    if (trimmed.length > 200) return;
    onUpdateInfo({ bio: trimmed });
    setEditingBio(false);
  };

  const cancelName = () => {
    setNameDraft(group.name);
    setEditingName(false);
  };
  const cancelBio = () => {
    setBioDraft(group.bio || '');
    setEditingBio(false);
  };
  // Step 1: Admin picks a file → check limit → open crop modal
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    
    // Pre-check the monthly limit BEFORE opening crop
    try {
      const limitCheck = await checkGroupCoverLimit(group.id);
      if (!limitCheck.allowed) {
        alert(`Group photo has been changed ${limitCheck.limit} times this month. Try again next month.`);
        return;
      }
    } catch (e) { /* fail open */ }
    
    // Open crop modal
    const localPreview = URL.createObjectURL(file);
    setCropSrc(localPreview);
  };

  // Step 2: Admin finished cropping — upload cropped blob as the new cover
  const handleCropDone = async (croppedFile) => {
    const previousCropSrc = cropSrc;
    setCropSrc(null);
    
    // Optimistic preview from cropped file
    const reader = new FileReader();
    reader.onload = (ev) => onPhotoUpload(ev.target.result);
    reader.readAsDataURL(croppedFile);
    
    setUploading(true);
    try {
      // Compress + upload to Firebase Storage (same pipeline as before)
      const url = await uploadGroupCover(group.id, croppedFile, currentUser?.id);
      // Save URL to Firestore so it persists across refreshes
      await updateGroupCoverUrl(group.id, url);
      // Replace local preview with real Firebase URL
      onPhotoUpload(url);
      // Photo counter is now incremented server-side by the onPhotoUpload Cloud Function
    } catch (err) {
      console.error('Group cover upload failed:', err);
      alert('Could not upload group photo — please try again');
      onPhotoUpload(group.photoUrl || null);
    } finally {
      setUploading(false);
      if (previousCropSrc) URL.revokeObjectURL(previousCropSrc);
    }
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };
  return (
    <div className="absolute inset-0 z-40 flex items-end" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full rounded-t-3xl px-6 pt-6 sheet-bottom-safe" style={{ background: 'var(--bg)', maxHeight: '85%', overflowY: 'auto' }}>
        <div className="w-12 h-1 rounded-full mx-auto mb-5" style={{ background: 'var(--border)' }} />
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => {
              if (isAdmin) {
                fileInputRef.current?.click();
              } else if (group?.photoUrl && onTapPhoto) {
                // Non-admin tap on group photo opens fullscreen viewer.
                // We synthesize a "user-like" object so FullscreenPhotoModal
                // can render the group photo (the modal uses .photoUrl and .name).
                onTapPhoto({ id: group.id, name: group.name, photoUrl: group.photoUrl, color: '#7A4FC4', initials: 'G' });
              }
            }}
            className="relative shrink-0 active:scale-[0.97] transition-transform">
            <GroupAvatar group={group} size={56} rounded={14} />
            {isAdmin && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
                   style={{ background: 'var(--accent)', boxShadow: '0 2px 6px rgba(196,90,63,0.35)' }}>
                {uploading ? (
                  <div className="w-2.5 h-2.5 rounded-full border-2 animate-spin"
                       style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
                ) : (
                  <ImagePlus size={11} color="white" />
                )}
              </div>
            )}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-1.5">
                <input 
                  type="text" 
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value.slice(0, 60))}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') cancelName(); }}
                  autoFocus
                  className="flex-1 min-w-0 text-[18px] outline-none px-2 py-1 rounded-lg"
                  style={{ background: 'var(--surface)', border: '1px solid var(--accent)', color: 'var(--ink)', fontFamily: 'var(--font-display)', fontWeight: 500 }}
                />
                <button onClick={saveName} className="text-[11px] px-2 py-1 rounded font-medium" style={{ background: 'var(--accent)', color: 'white' }}>Save</button>
                <button onClick={cancelName} className="text-[11px] px-2 py-1" style={{ color: 'var(--ink-soft)' }}>Cancel</button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <h2 className="text-[20px] leading-tight truncate" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
                  {group.name}
                </h2>
                {isAdmin && (
                  <button onClick={() => setEditingName(true)} className="w-6 h-6 flex items-center justify-center rounded active:bg-black/5 shrink-0">
                    <Pencil size={11} style={{ color: 'var(--ink-soft)' }} />
                  </button>
                )}
              </div>
            )}
            <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
              {group.year && `Class of ${group.year} · `}{group.city}
            </p>
          </div>
        </div>
        {editingBio ? (
          <div className="mb-4">
            <textarea 
              value={bioDraft}
              onChange={(e) => setBioDraft(e.target.value.slice(0, 200))}
              onKeyDown={(e) => { if (e.key === 'Escape') cancelBio(); }}
              autoFocus
              rows={3}
              placeholder="A short description of this batch…"
              className="w-full p-3 rounded-xl text-[14px] outline-none resize-none mb-2"
              style={{ background: 'var(--surface)', border: '1px solid var(--accent)', color: 'var(--ink)', fontFamily: 'var(--font-display)' }}
            />
            <div className="flex items-center justify-between">
              <p className="text-[10px]" style={{ color: 'var(--ink-soft)' }}>{bioDraft.length}/200</p>
              <div className="flex gap-2">
                <button onClick={cancelBio} className="text-[12px] px-3 py-1" style={{ color: 'var(--ink-soft)' }}>Cancel</button>
                <button onClick={saveBio} className="text-[12px] px-3 py-1 rounded-lg font-medium" style={{ background: 'var(--accent)', color: 'white' }}>Save</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 mb-4">
            <p className="text-[14px] leading-relaxed italic flex-1" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>
              {group.bio ? `"${group.bio}"` : <span style={{ color: 'var(--ink-soft)' }}>No description yet</span>}
            </p>
            {isAdmin && (
              <button onClick={() => setEditingBio(true)} className="w-6 h-6 flex items-center justify-center rounded active:bg-black/5 shrink-0 mt-1">
                <Pencil size={11} style={{ color: 'var(--ink-soft)' }} />
              </button>
            )}
          </div>
        )}
        {/* Invite link section — copy + share buttons */}
        <div className="rounded-2xl p-4 mb-4" style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
          <p className="text-[10px] uppercase tracking-[0.2em] opacity-70 mb-1.5">Invite link</p>
          <p className="text-[12px] font-mono break-all opacity-90 mb-3" style={{ wordBreak: 'break-all' }}>
            {AANGAN_SHARE_BASE}/join/{group.connectId}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button 
              onClick={() => onCopy(`${AANGAN_SHARE_BASE}/join/${group.connectId}`)}
              className="h-10 rounded-xl flex items-center justify-center gap-1.5 active:scale-[0.97] transition-transform"
              style={{ background: 'rgba(255,255,255,0.15)' }}>
              <Copy size={13} />
              <span className="text-[12px] font-medium">Copy link</span>
            </button>
            <button 
              onClick={() => {
                onClose();
                handleShareFromInfoSheet(group);
              }}
              className="h-10 rounded-xl flex items-center justify-center gap-1.5 active:scale-[0.97] transition-transform"
              style={{ background: 'var(--accent)' }}>
              <Share2 size={13} />
              <span className="text-[12px] font-medium">Share</span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--ink-soft)' }}>Members</p>
            <p className="text-[20px]" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>{group.memberCount}</p>
          </div>
          <div className="rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--ink-soft)' }}>Founded</p>
            <p className="text-[14px] mt-1" style={{ color: 'var(--ink)' }}>
              {group.createdAt
                ? new Date(group.createdAt.toMillis ? group.createdAt.toMillis() : group.createdAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                : (group.founded || '—')}
            </p>
          </div>
        </div>
        <div className="rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2 mb-1">
            <Shield size={13} style={{ color: 'var(--accent)' }} />
            <p className="text-[12px] font-medium" style={{ color: 'var(--ink)' }}>Group rules</p>
          </div>
          <p className="text-[12px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
            Be kind. No spam. New members are read-only until verified by an admin. Reports are reviewed within 24 hours. Code-joiners get auto-removed if not approved in 72 hours.
          </p>
        </div>
        
        {/* Leave group — at the bottom so it's not the first thing users see */}
        <button onClick={() => {
          if (window.confirm(`Leave ${group.name}? You'll lose access to posts, members, and chats. You can re-join via the invite link.`)) {
            onLeaveGroup();
          }
        }}
          className="w-full mt-5 py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <LogOut size={14} style={{ color: '#B33A3A' }} />
          <span className="text-[13px] font-medium" style={{ color: '#B33A3A' }}>Leave this batch</span>
        </button>
      </div>
      <PhotoCropModal
        open={!!cropSrc}
        imageSrc={cropSrc}
        aspect={1}
        cropShape="round"
        title="Position the group photo"
        onClose={handleCropCancel}
        onDone={handleCropDone}
      />
    </div>
  );
};

// =============================================================
// TAB BAR / FAB / TOAST
// =============================================================
// =============================================================
// CHAT REQUEST FLOW
// =============================================================
const InstallPromptModal = ({ onClose, onInstall }) => (
  <div className="absolute inset-0 z-40 flex items-center justify-center p-5"
       style={{ background: 'rgba(0,0,0,0.6)' }}
       onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="w-full rounded-3xl overflow-hidden relative"
         style={{ background: 'var(--bg)', maxWidth: 340 }}>
      {/* Dismiss X in the top right */}
      <button onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center active:bg-black/5 z-10"
              style={{ color: 'var(--ink-soft)' }}>
        <X size={18} />
      </button>
      
      {/* Decorative header with logo */}
      <div className="px-5 pt-7 pb-3 text-center" style={{ background: 'var(--accent-soft)' }}>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3"
             style={{ background: 'white', boxShadow: '0 4px 12px rgba(196, 90, 63, 0.15)' }}>
          <img src="/aangan-192.png" alt="Aangan" style={{ width: 48, height: 48, borderRadius: 12 }} />
        </div>
        <h2 className="text-[20px] leading-tight" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
          Install Aangan
        </h2>
        <p className="text-[12px] mt-1.5" style={{ color: 'var(--ink-soft)' }}>
          For a better experience, install Aangan on your device
        </p>
      </div>
      
      {/* Benefit bullets */}
      <div className="px-5 pt-4 pb-5">
        <div className="space-y-2.5 mb-5">
          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                 style={{ background: 'var(--accent-soft)' }}>
              <Check size={12} style={{ color: 'var(--accent)' }} strokeWidth={3} />
            </div>
            <p className="text-[13px]" style={{ color: 'var(--ink)' }}>
              Get push notifications when batchmates message you
            </p>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                 style={{ background: 'var(--accent-soft)' }}>
              <Check size={12} style={{ color: 'var(--accent)' }} strokeWidth={3} />
            </div>
            <p className="text-[13px]" style={{ color: 'var(--ink)' }}>
              One-tap launch from your home screen
            </p>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                 style={{ background: 'var(--accent-soft)' }}>
              <Check size={12} style={{ color: 'var(--accent)' }} strokeWidth={3} />
            </div>
            <p className="text-[13px]" style={{ color: 'var(--ink)' }}>
              Fullscreen, no browser bars
            </p>
          </div>
        </div>
        
        {/* CTA */}
        <button onClick={onInstall}
                className="w-full h-12 rounded-2xl text-[14px] font-medium active:scale-[0.98] transition-transform"
                style={{ background: 'var(--accent)', color: 'white' }}>
          Install Now
        </button>
        <button onClick={onClose}
                className="w-full mt-2 py-2 text-[12px]"
                style={{ color: 'var(--ink-soft)' }}>
          Maybe later
        </button>
      </div>
    </div>
  </div>
);

// =============================================================
// iOS STANDALONE WELCOME BANNER
// Shown on first launch of the iOS home-screen PWA when user is signed
// out. Sets honest expectations: iOS treats this as a fresh app install,
// so the user needs to sign in here once (Apple's design, not our bug).
// Dismiss via localStorage flag so it doesn't repeat on subsequent launches.
// =============================================================
const IOSStandaloneWelcome = ({ onDismiss }) => (
  <div className="absolute inset-x-0 top-0 z-30 mx-3 mt-3 rounded-2xl shadow-lg"
       style={{ background: 'var(--accent-soft)', border: '1px solid var(--accent)' }}>
    <div className="px-4 py-3 flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
           style={{ background: 'white' }}>
        <img src="/aangan-192.png" alt="Aangan" style={{ width: 28, height: 28, borderRadius: 8 }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium mb-0.5" style={{ color: 'var(--ink)' }}>
          Welcome to Aangan on iOS
        </p>
        <p className="text-[11px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
          Sign in once here — iOS keeps your home-screen app separate from Safari, so this is a one-time step.
        </p>
      </div>
      <button onClick={onDismiss}
              className="w-7 h-7 flex items-center justify-center rounded-full shrink-0 active:bg-black/5"
              style={{ color: 'var(--ink-soft)' }}
              aria-label="Dismiss">
        <X size={14} />
      </button>
    </div>
  </div>
);

const ChatRequestModal = ({ member, onClose, onSend }) => {
  const [text, setText] = useState('');
  return (
    <div className="absolute inset-0 z-40 flex items-end" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full rounded-t-3xl sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
        <div className="w-12 h-1 rounded-full mx-auto my-3" style={{ background: 'var(--border)' }} />
        <div className="px-5 pb-4">
          <div className="flex items-center gap-3 mb-4">
            <Avatar user={member} size={44} />
            <div className="flex-1">
              <p className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>Send a message</p>
              <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>to {member.name}</p>
            </div>
          </div>
          <textarea autoFocus rows={4} placeholder="Hi! Say hello so they know it's you…"
            value={text} onChange={(e) => setText(e.target.value)}
            className="w-full p-4 rounded-2xl text-[14px] outline-none resize-none mb-3"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
          <div className="rounded-xl p-3 flex items-start gap-2 mb-4"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <MessageCircle size={12} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
            <p className="text-[11px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
              Only you and {member.name?.split(' ')[0] || 'they'} can see this conversation. Recent messages stay visible — older messages can be loaded with "View earlier".
            </p>
          </div>
          <button onClick={() => onSend(member, text.trim())} disabled={!text.trim()}
            className="w-full h-12 rounded-2xl font-medium text-[14px] disabled:opacity-40 active:scale-[0.98] transition-transform"
            style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
            Send message
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================
// CHATS LIST (active + pending)
// =============================================================

// Path Z: BusinessConnect row secondary line — order status → label.
// Mirrors the merchant-side MerchantChatList's STATUS_LABEL but reads
// from the customer's vantage ('Confirmed' is the order being
// accepted by the business, not the customer doing anything).
const BUSINESS_STATUS_LABEL = {
  placed: 'Waiting for business',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const ChatsList = ({ 
  chats, requests, onBack, onOpenChat, onAcceptRequest, onDeclineRequest, getUser,
  // NEW: chats loading state — distinguishes 'still fetching' from
  // 'actually empty', so re-mounts after V2 round-trip don't briefly
  // flash 'No chats yet' before real chats arrive.
  chatsLoaded = true,
  // NEW: unread tracking
  unreadByChatId = {},
  // NEW props for admin items (passed only when admin of active group):
  isAdminOfActiveGroup = false,
  activeGroupName = '',
  pendingJoins = [],
  pendingJoinsLoading = false,
  reports = [],
  onApproveJoin = () => {},
  onDeclineJoin = () => {},
  onResolveReport = () => {},
  // Path Z (Jun 16): BusinessConnect tab data + handlers.
  // businessConnects is the raw orders array (already limited to 50 by
  // the parent subscription). Dedupe-by-businessId happens here so the
  // parent doesn't need to know about UI grouping.
  businessConnects = [],
  businessConnectsLoaded = false,
  businessCache = {},
  onOpenBusinessChat = () => {},
}) => {
  // Path Z: initial tab can be restored from sessionStorage when the
  // user comes back from a V2 order chat. Read once at mount, clear
  // immediately so it doesn't stick across screen visits.
  const [tab, setTab] = useState(() => {
    try {
      const saved = sessionStorage.getItem('v1_chatTab');
      if (saved === 'active' || saved === 'business' || saved === 'requests') {
        sessionStorage.removeItem('v1_chatTab');
        return saved;
      }
    } catch (err) { /* storage disabled */ }
    return 'active';
  });
  const [confirmReject, setConfirmReject] = useState(null); // pending-join userId being confirmed
  
  // Sort chats: unread first (most recent unread on top), then read chats by most recent.
  // Each item's keys: chat_<otherId> → { id, otherId, lastMessage, lastMessageAt, ... }
  const activeChats = Object.entries(chats)
    .map(([key, chat]) => ({ key, ...chat, _unread: !!unreadByChatId[key] }))
    .sort((a, b) => {
      // Unread chats always sort above read ones
      if (a._unread && !b._unread) return -1;
      if (!a._unread && b._unread) return 1;
      // Within each group, sort by lastMessageAt descending (newest first)
      const aMs = a.lastMessageAt?.toMillis ? a.lastMessageAt.toMillis() : (a.lastMessageAt || 0);
      const bMs = b.lastMessageAt?.toMillis ? b.lastMessageAt.toMillis() : (b.lastMessageAt || 0);
      return bMs - aMs;
    });

  // Path Z: dedupe businessConnects by businessId. Input is sorted
  // newest-first (parent uses orderBy placedAt desc), so the first
  // occurrence per businessId is the latest order — which is what
  // each row represents.
  const businessChats = (() => {
    const seen = new Set();
    const rows = [];
    for (const o of businessConnects) {
      if (!o.businessId) continue;
      if (seen.has(o.businessId)) continue;
      seen.add(o.businessId);
      rows.push(o);
    }
    return rows;
  })();

  // Requests tab badge count:
  // - For regular users: just chat-request count (currently always 0 in V1)
  // - For admins: chat requests + pending joins + reports
  const requestsCount = isAdminOfActiveGroup
    ? requests.length + pendingJoins.length + reports.length
    : requests.length;

  // Render a single pending-join row (admin only)
  const renderJoinRequest = (pj) => {
    const userForAvatar = {
      name: pj.name,
      initials: pj.initials,
      photoUrl: pj.photoUrl,
      color: '#C45A3F',
    };
    return (
      <div key={'join_' + pj.userId} className="px-5 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-start gap-3 mb-3">
          <div className="relative shrink-0">
            <Avatar user={userForAvatar} size={48} />
            {/* Type icon badge — bottom-right corner */}
            <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                 style={{ background: 'var(--bg)' }}>
              <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent)' }}>
                <UserPlus size={9} color="white" strokeWidth={2.5} />
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>{pj.name}</p>
            <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
              {pj.currentCity ? `📍 ${pj.currentCity} · ` : ''}wants to join {activeGroupName}
            </p>
            {pj.viaCode && (
              <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px]"
                    style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
                <Hash size={9} /> Via invite link
              </span>
            )}
            {pj.bio && (
              <p className="text-[12px] italic mt-2 px-3 py-2 rounded-xl"
                 style={{ background: 'var(--surface)', color: 'var(--ink)' }}>
                "{pj.bio}"
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setConfirmReject(pj.userId)}
            className="flex-1 h-10 rounded-xl text-[13px] font-medium"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
            Decline
          </button>
          <button onClick={() => onApproveJoin(pj.userId)}
            className="flex-1 h-10 rounded-xl text-[13px] font-medium"
            style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
            Approve
          </button>
        </div>
      </div>
    );
  };

  // Map machine-readable reason keys to human-readable labels.
  // Keys must match the modal in ReportUserSheet.
  const reasonLabel = (key) => {
    const labels = {
      abuse: 'Abuse or harassment',
      spam: 'Spam or scam',
      hate: 'Hate speech',
      impersonation: 'Impersonation',
      inappropriate: 'Inappropriate content',
      other: 'Something else',
    };
    return labels[key] || key;
  };

  // Render a single report row (admin only).
  // Real Firestore reports have: { id, reportedId, reporterId, reason, detail, createdAt }
  // where createdAt is a Firestore Timestamp. The reporter's identity is shown to
  // admins so they can spot abuse patterns, but is never shown publicly.
  const renderReport = (r) => {
    const reported = getUser(r.reportedId);
    if (!reported) return null;
    const reporter = getUser(r.reporterId);
    const reporterName = reporter?.name || 'A member';
    const when = formatTimestamp(r.createdAt);
    return (
      <div key={'rep_' + r.id} className="px-5 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-start gap-3 mb-2">
          <div className="relative shrink-0">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
                 style={{ background: '#FBE3E3' }}>
              <Flag size={18} style={{ color: '#B33A3A' }} />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                 style={{ background: 'var(--bg)' }}>
              <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: '#B33A3A' }}>
                <Flag size={9} color="white" strokeWidth={2.5} />
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px]" style={{ color: 'var(--ink)' }}>
              <strong>{reported.name}</strong> reported for <strong>{reasonLabel(r.reason)}</strong>
            </p>
            <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
              by {reporterName} · {when}
            </p>
          </div>
        </div>
        {r.detail && (
          <p className="text-[12px] italic px-3 py-2 rounded-xl mb-3"
             style={{ background: 'var(--surface)', color: 'var(--ink)' }}>
            "{r.detail}"
          </p>
        )}
        <div className="grid grid-cols-3 gap-2">
          <button onClick={() => onResolveReport(r.id, 'dismiss')}
            className="h-9 rounded-xl text-[12px] font-medium"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
            Dismiss
          </button>
          <button onClick={() => onResolveReport(r.id, 'warn')}
            className="h-9 rounded-xl text-[12px] font-medium"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
            Warn
          </button>
          <button onClick={() => onResolveReport(r.id, 'remove')}
            className="h-9 rounded-xl text-[12px] font-medium"
            style={{ background: '#B33A3A', color: 'white' }}>
            Remove
          </button>
        </div>
      </div>
    );
  };

  // Render a single chat-request row (existing pattern, dormant in V1)
  const renderChatRequest = (req) => {
    const from = getUser(req.fromId);
    if (!from) return null;
    return (
      <div key={'chat_' + req.id} className="px-5 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-start gap-3 mb-3">
          <div className="relative shrink-0">
            <Avatar user={from} size={44} />
            <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                 style={{ background: 'var(--bg)' }}>
              <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: '#3F6BC4' }}>
                <MessageSquare size={9} color="white" strokeWidth={2.5} />
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>{from.name}</p>
            <p className="text-[11px] mb-1.5" style={{ color: 'var(--ink-soft)' }}>
              {from.currentCity} · wants to chat
            </p>
            <p className="text-[13px] italic px-3 py-2 rounded-xl"
               style={{ background: 'var(--surface)', color: 'var(--ink)' }}>
              "{req.message}"
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onDeclineRequest(req.id)}
            className="flex-1 h-10 rounded-xl text-[13px] font-medium"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
            Decline
          </button>
          <button onClick={() => onAcceptRequest(req.id)}
            className="flex-1 h-10 rounded-xl text-[13px] font-medium"
            style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
            Accept
          </button>
        </div>
      </div>
    );
  };

  // Combined list for Requests tab
  // Order: chat requests → join requests → reports (chat first since most personal)
  // V1 reality: chat requests array always empty, so admin really sees joins + reports
  const hasAnyRequest = requests.length > 0 || pendingJoins.length > 0 || reports.length > 0;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <TopBar title="Chats" onBack={onBack} />
      <div className="flex gap-1 px-5 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
        {[
          // Path Z: renamed 'Active' → 'Regular' to make room for the
          // BusinessConnect tab as a parallel category, not a sibling
          // of 'Active'. Tab keys are kept simple ('active' is still
          // the internal key for peer chats to minimise blast radius).
          ['active', 'Regular', activeChats.length],
          ['business', 'BusinessConnect', businessChats.length],
          ['requests', 'Requests', requestsCount],
        ].map(([key, label, n]) => (
          <button key={key} onClick={() => setTab(key)}
            className="flex-1 py-2 rounded-xl text-[12px] font-medium flex items-center justify-center gap-1.5"
            style={{ background: tab === key ? 'var(--ink)' : 'transparent',
                     color: tab === key ? 'var(--bg)' : 'var(--ink)' }}>
            {label}
            {n > 0 && (
              <span className="text-[10px] px-1.5 rounded-full"
                    style={{ background: tab === key ? 'var(--bg)' : 'var(--accent)',
                             color: tab === key ? 'var(--ink)' : 'white' }}>{n}</span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
        {tab === 'active' ? (
          !chatsLoaded ? (
            // Path Z: brief loading state on V1 re-mount after V2 round-trip.
            // Without this, the empty state flashes for ~100-300ms before
            // the chats subscription returns data.
            <div className="text-center py-16 px-8">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <MessageSquare size={20} style={{ color: 'var(--ink-soft)' }} />
              </div>
              <p className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Loading…</p>
            </div>
          ) : activeChats.length === 0 ? (
            <div className="text-center py-16 px-8">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <MessageSquare size={20} style={{ color: 'var(--ink-soft)' }} />
              </div>
              <p className="text-[14px] mb-1" style={{ color: 'var(--ink)' }}>No chats yet</p>
              <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
                Tap a member from your batch to send a chat request.
              </p>
            </div>
          ) : (
            activeChats.map((chat) => {
              const other = getUser(chat.otherId);
              if (!other) return null;
              const lastTime = chat.lastMessageAt ? formatTimestamp(chat.lastMessageAt) : '';
              const isUnread = chat._unread;
              return (
                <button key={chat.id} onClick={() => onOpenChat(chat.otherId)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5"
                  style={{ borderBottom: '1px solid var(--border)' }}>
                  <Avatar user={other} size={48} />
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p
                        className="text-[14px] truncate"
                        style={{
                          color: 'var(--ink)',
                          fontWeight: isUnread ? 700 : 500,
                        }}
                      >
                        {other.name}
                      </p>
                      <p
                        className="text-[10px] shrink-0"
                        style={{
                          color: isUnread ? 'var(--accent)' : 'var(--ink-soft)',
                          fontWeight: isUnread ? 600 : 400,
                        }}
                      >
                        {lastTime}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className="text-[12px] truncate"
                        style={{
                          color: isUnread ? 'var(--ink)' : 'var(--ink-soft)',
                          fontWeight: isUnread ? 600 : 400,
                        }}
                      >
                        {chat.lastMessage || '—'}
                      </p>
                      {isUnread && (
                        <span
                          className="shrink-0 w-2.5 h-2.5 rounded-full"
                          style={{ background: 'var(--accent)' }}
                          aria-label="Unread"
                        />
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )
        ) : tab === 'business' ? (
          // Path Z: BusinessConnect tab — customer's order chats,
          // deduped to one row per business (latest order surfaced).
          // Empty state covers both 'still loading' and 'no orders'
          // — first-load shimmer would be over-engineered for a list
          // that's typically very short and reads quickly.
          businessChats.length === 0 ? (
            <div className="text-center py-16 px-8">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <MessageSquare size={20} style={{ color: 'var(--ink-soft)' }} />
              </div>
              <p className="text-[14px] mb-1" style={{ color: 'var(--ink)' }}>
                {businessConnectsLoaded ? 'No business chats yet' : 'Loading…'}
              </p>
              <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
                {businessConnectsLoaded
                  ? 'Place an order with a local business to start a chat.'
                  : ' '}
              </p>
            </div>
          ) : (
            businessChats.map(o => {
              const biz = businessCache[o.businessId];
              const bizName = biz?.name || 'Loading…';
              const bizLogo = biz?.logo || '🏪';
              const status = o.status || 'placed';
              const statusLabel = BUSINESS_STATUS_LABEL[status] || status;
              const placedMs = o.placedAt?.toMillis?.() ?? 0;
              const timeStr = placedMs ? formatTimestamp({ toMillis: () => placedMs }) : '';
              const orderId = o.displayNumber || o.id;
              return (
                <button
                  key={o.businessId}
                  onClick={() => onOpenBusinessChat(o.id)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5"
                  style={{ borderBottom: '1px solid var(--border)' }}
                >
                  <div
                    className="shrink-0 flex items-center justify-center"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 14,
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      fontSize: 22,
                    }}
                  >
                    {bizLogo}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="text-[14px] truncate font-medium" style={{ color: 'var(--ink)' }}>
                        {bizName}
                      </p>
                      <p className="text-[10px] shrink-0" style={{ color: 'var(--ink-soft)' }}>
                        {timeStr}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[12px] truncate" style={{ color: 'var(--ink-soft)' }}>
                        {statusLabel} · #{orderId}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })
          )
        ) : (
          // Requests tab
          <>
            {/* Admin context banner — only when admin and there are admin items */}
            {isAdminOfActiveGroup && activeGroupName && (pendingJoins.length > 0 || reports.length > 0) && (
              <div className="px-5 py-2.5 flex items-center gap-2" 
                   style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                <ShieldCheck size={12} style={{ color: 'var(--accent)' }} />
                <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
                  Admin actions for <strong style={{ color: 'var(--ink)' }}>{activeGroupName}</strong>
                </p>
              </div>
            )}
            
            {/* Loading state for pending joins */}
            {isAdminOfActiveGroup && pendingJoinsLoading && pendingJoins.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 px-8 text-center">
                <div className="w-8 h-8 rounded-full border-2 animate-spin mb-2"
                     style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
                <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>Loading requests…</p>
              </div>
            )}
            
            {!hasAnyRequest && !pendingJoinsLoading ? (
              <div className="text-center py-16 px-8">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                     style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                  <Inbox size={20} style={{ color: 'var(--ink-soft)' }} />
                </div>
                <p className="text-[14px] mb-1" style={{ color: 'var(--ink)' }}>No pending requests</p>
                <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
                  {isAdminOfActiveGroup 
                    ? `You're all caught up for ${activeGroupName || 'this batch'}.`
                    : 'Anything that needs your action will show up here.'}
                </p>
              </div>
            ) : (
              <>
                {/* Chat requests (currently dormant — array always empty in V1) */}
                {requests.map(renderChatRequest)}
                {/* Join requests (admin only) */}
                {isAdminOfActiveGroup && pendingJoins.map(renderJoinRequest)}
                {/* Reports (admin only) */}
                {isAdminOfActiveGroup && reports.map(renderReport)}
              </>
            )}
          </>
        )}
        <div className="h-6" />
      </div>

      {/* Confirm decline modal — for join requests (admin) */}
      {confirmReject && (() => {
        const pj = pendingJoins.find(p => p.userId === confirmReject);
        if (!pj) return null;
        return (
          <div className="absolute inset-0 z-40 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.6)' }}
               onClick={() => setConfirmReject(null)}>
            <div onClick={(e) => e.stopPropagation()} className="w-full rounded-3xl overflow-hidden" 
                 style={{ background: 'var(--bg)', maxWidth: 340 }}>
              <div className="p-5 pb-4 text-center" style={{ background: '#FBE3E3' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                     style={{ background: '#B33A3A' }}>
                  <UserMinus size={20} color="white" />
                </div>
                <h2 className="text-[18px]" style={{ fontFamily: 'var(--font-display)', color: '#B33A3A', fontWeight: 500 }}>
                  Decline {pj.name}?
                </h2>
              </div>
              <div className="p-5">
                <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
                  They won't be added to <strong>{activeGroupName}</strong>. They'll need to ask you directly to be added later.
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setConfirmReject(null)}
                    className="flex-1 h-11 rounded-xl text-[13px] font-medium"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                    Cancel
                  </button>
                  <button onClick={() => { onDeclineJoin(confirmReject); setConfirmReject(null); }}
                    className="flex-1 h-11 rounded-xl text-[13px] font-medium"
                    style={{ background: '#B33A3A', color: 'white' }}>
                    Decline
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// =============================================================
// CHAT SCREEN — shows last 10 messages, no auto-delete
// =============================================================
// =====================================================================
// FULLSCREEN PHOTO MODAL — WhatsApp-style profile photo viewer
// Shows the full photo on dark backdrop. Tap outside or X to close.
// Falls back gracefully when the user has no photoUrl (initials only).
// =====================================================================
const FullscreenPhotoModal = ({ user, onClose }) => {
  if (!user) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
         style={{ background: 'rgba(0,0,0,0.92)' }}
         onClick={onClose}>
      <button onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center active:scale-95 transition-transform z-10"
        style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }}>
        <X size={22} style={{ color: '#fff' }} />
      </button>
      <div className="absolute top-0 left-0 right-0 px-5 pt-4 pb-3 flex items-center gap-3"
           style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)' }}>
        <p className="text-[15px] font-medium" style={{ color: '#fff', fontFamily: 'var(--font-display)' }}>
          {user.name || 'Member'}
        </p>
      </div>
      <div onClick={(e) => e.stopPropagation()} className="px-6 overflow-hidden">
        {user.photoUrl ? (
          <img src={user.photoUrl} alt={user.name}
               className="rounded-3xl object-contain cursor-zoom-in"
               onClick={(e) => {
                 // Tap-to-zoom toggle. Two states: fit-in-frame vs 2x.
                 const el = e.currentTarget;
                 const isZoomed = el.dataset.zoomed === 'true';
                 el.style.transform = isZoomed ? 'scale(1)' : 'scale(2)';
                 el.style.transition = 'transform 200ms ease-out';
                 el.dataset.zoomed = isZoomed ? 'false' : 'true';
                 el.style.cursor = isZoomed ? 'zoom-in' : 'zoom-out';
               }}
               style={{
                 maxWidth: 'min(420px, 80vw)',
                 maxHeight: '80vh',
                 boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                 transformOrigin: 'center center',
               }} />
        ) : (
          <div className="rounded-full flex items-center justify-center"
               style={{
                 width: 'min(280px, 70vw)',
                 height: 'min(280px, 70vw)',
                 maxWidth: '320px',
                 maxHeight: '320px',
                 background: user.color || '#C45A3F',
                 boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
               }}>
            <span style={{
              color: '#fff',
              fontSize: 'clamp(72px, 20vw, 120px)',
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
            }}>{user.initials || (user.name || 'M').charAt(0).toUpperCase()}</span>
          </div>
        )}
      </div>
    </div>
  );
};
const ChatBubble = ({ msg, isMine, otherUser }) => {
  return (
    <div className={`flex gap-2 mb-3 ${isMine ? 'flex-row-reverse' : ''}`}>
      {!isMine && <Avatar user={otherUser} size={28} />}
      <div className={`flex flex-col max-w-[78%] ${isMine ? 'items-end' : 'items-start'}`}>
        <div className="px-3.5 py-2.5 rounded-2xl"
             style={{
               background: isMine ? 'var(--ink)' : 'var(--surface)',
               color: isMine ? 'var(--bg)' : 'var(--ink)',
               border: isMine ? 'none' : '1px solid var(--border)',
               borderTopRightRadius: isMine ? 6 : 16,
               borderTopLeftRadius: isMine ? 16 : 6,
             }}>
          <p className="text-[14px] leading-relaxed">
            {msg.text}
          </p>
        </div>
      </div>
    </div>
  );
};

const ChatScreen = ({ chat, otherUser, onBack, onSend, onDelete, currentUser, hasMoreMessages, onLoadOlderMessages, loadingOlderMessages, onTapPhoto, onBlock, onUnblock, blocked = false }) => {
  const [text, setText] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const submit = () => {
    if (!text.trim()) return;
    onSend(otherUser.id, text.trim());
    setText('');
    // Auto-scroll to bottom after sending
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between px-3 shrink-0 topbar-safe"
           style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)', minHeight: '56px' }}>
        <div className="flex items-center gap-2 flex-1">
          <button onClick={onBack} className="w-9 h-9 flex items-center justify-center rounded-full active:bg-black/5">
            <ArrowLeft size={20} style={{ color: 'var(--ink)' }} />
          </button>
          <button onClick={() => onTapPhoto?.(otherUser)} className="active:scale-95 transition-transform">
            <Avatar user={otherUser} size={32} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-medium truncate" style={{ color: 'var(--ink)' }}>{otherUser.name}</p>
            <p className="text-[10px] flex items-center gap-1" style={{ color: 'var(--ink-soft)' }}>
              <MessageCircle size={10} /> Recent messages only
            </p>
          </div>
        </div>
        <button onClick={() => setShowMenu(true)} className="w-9 h-9 flex items-center justify-center rounded-full active:bg-black/5">
          <MoreVertical size={18} style={{ color: 'var(--ink)' }} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 px-4 pt-4 pb-2" style={{ background: 'var(--bg)' }}>
        <div className="rounded-2xl p-3 mb-4 flex items-start gap-2"
             style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
          <MessageCircle size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--ink)' }}>
            Showing the last 10 messages. Tap "View earlier messages" to load older ones.
          </p>
        </div>
        {hasMoreMessages && (
          <button onClick={onLoadOlderMessages} disabled={loadingOlderMessages}
            className="w-full text-[12px] py-2.5 mb-3 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-50"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink-soft)' }}>
            {loadingOlderMessages ? (
              <>
                <div className="w-3 h-3 rounded-full border animate-spin"
                     style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
                Loading earlier messages…
              </>
            ) : (
              <>↑ View earlier messages</>
            )}
          </button>
        )}
        {chat.messages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>No messages yet — say hi!</p>
          </div>
        ) : chat.messages.map((m) => (
          <ChatBubble key={m.id} msg={m} isMine={m.fromId === currentUser?.id} otherUser={otherUser} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Session C2b — if I blocked them, replace the composer. Every send
          would be denied by the rules anyway, and an input that always
          silently fails is worse than no input. Only fires for MY OWN
          blocks: if THEY blocked ME this is false, the composer stays, and
          the send fails with V1's existing neutral "try again" toast. */}
      {blocked ? (
        <div className="px-5 pt-3 shrink-0"
             style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)', paddingBottom: 'max(calc(var(--safe-bottom) + 12px), 28px)' }}>
          <p className="text-[13px] text-center leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
            You blocked {otherUser.name?.split(' ')[0] || 'this person'}.{' '}
            <button onClick={() => onUnblock?.(otherUser)}
                    className="font-medium underline"
                    style={{ color: 'var(--accent)' }}>
              Unblock to message
            </button>
          </p>
        </div>
      ) : (
      <div className="px-3 pt-3 flex items-center gap-2 shrink-0"
           style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)', paddingBottom: 'max(calc(var(--safe-bottom) + 12px), 28px)' }}>
        <input type="text" value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="Type a message…"
          className="flex-1 px-4 py-2.5 rounded-full text-[14px] outline-none"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
        <button onClick={submit} disabled={!text.trim()}
          className="w-11 h-11 rounded-full flex items-center justify-center disabled:opacity-40 shrink-0"
          style={{ background: 'var(--ink)' }}>
          <Send size={16} color="white" />
        </button>
      </div>
      )}

      {showMenu && (
        <div className="absolute inset-0 z-40 flex items-end" style={{ background: 'rgba(0,0,0,0.5)' }}
             onClick={() => setShowMenu(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full rounded-t-3xl sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
            <div className="w-12 h-1 rounded-full mx-auto my-3" style={{ background: 'var(--border)' }} />
            <button onClick={() => { setShowMenu(false); onDelete(otherUser.id); }}
                    className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5">
              <Trash2 size={18} style={{ color: '#B33A3A' }} />
              <span className="text-[14px]" style={{ color: '#B33A3A' }}>Delete this conversation</span>
            </button>
            {/* Session C2b — Block from the chat itself, the surface Play
                cares about most. Same handler as the member profile, so the
                live subscription updates both at once. */}
            <button onClick={() => { setShowMenu(false); blocked ? onUnblock?.(otherUser) : onBlock?.(otherUser); }}
                    className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5">
              <Ban size={18} style={{ color: blocked ? 'var(--ink-soft)' : '#B33A3A' }} />
              <span className="text-[14px]" style={{ color: blocked ? 'var(--ink)' : '#B33A3A' }}>
                {blocked ? `Unblock ${otherUser.name?.split(' ')[0] || ''}`.trim() : `Block ${otherUser.name?.split(' ')[0] || ''}`.trim()}
              </span>
            </button>
            <button onClick={() => setShowMenu(false)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5"
                    style={{ borderTop: '1px solid var(--border)' }}>
              <X size={18} style={{ color: 'var(--ink-soft)' }} />
              <span className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Cancel</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
// =============================================================
// Thank You Modal — shown once after a user's first batch action
// (create or join). Warm welcome for early supporters.
// Triggered once per user; flag stored at /users/{uid}.celebratedAt
// =============================================================
const ThankYouModal = ({ open, onClose }) => {
  if (!open) return null;
  
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center p-5"
         style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full rounded-3xl overflow-hidden flex flex-col"
           style={{ 
             background: 'var(--bg)', 
             maxWidth: 360,
             maxHeight: '85vh',
             boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
           }}>
        
        {/* Header with celebration */}
        <div className="px-6 pt-7 pb-5 text-center relative overflow-hidden"
             style={{ background: 'var(--accent-soft)' }}>
          <div className="absolute -top-8 -left-8 w-32 h-32 rounded-full opacity-30"
               style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)' }} />
          <div className="absolute -bottom-12 -right-8 w-32 h-32 rounded-full opacity-25"
               style={{ background: 'radial-gradient(circle, var(--gold) 0%, transparent 70%)' }} />
          <div className="relative">
            <div className="text-[42px] mb-1">🎉</div>
            <h2 className="leading-tight" 
                style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
              Welcome
            </h2>
          </div>
        </div>
        
        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          <p className="text-[14px] leading-relaxed mb-3" style={{ color: 'var(--ink)' }}>
            You're one of our <strong>early supporters</strong> — and that makes you truly special.
          </p>
          <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink-soft)' }}>
            Thank you for joining <strong style={{ color: 'var(--ink)' }}>AapSaathi</strong> in its early days and being part of this journey from the beginning. Your support, feedback, and trust help us improve every single day.
          </p>
          
          <div className="space-y-2 mb-4">
            <div className="flex items-start gap-2.5">
              <span className="text-[16px]">✨</span>
              <p className="text-[13px] leading-relaxed flex-1" style={{ color: 'var(--ink)' }}>
                More exciting features are on the way
              </p>
            </div>
            <div className="flex items-start gap-2.5">
              <span className="text-[16px]">🚀</span>
              <p className="text-[13px] leading-relaxed flex-1" style={{ color: 'var(--ink)' }}>
                Faster updates and improvements coming soon
              </p>
            </div>
            <div className="flex items-start gap-2.5">
              <span className="text-[16px]">🤝</span>
              <p className="text-[13px] leading-relaxed flex-1" style={{ color: 'var(--ink)' }}>
                Built for the community, together with the community
              </p>
            </div>
          </div>
          
          <p className="text-[13px] leading-relaxed mb-1" style={{ color: 'var(--ink-soft)' }}>
            This is just the beginning — and we're glad you're here with us <span style={{ color: 'var(--accent)' }}>❤️</span>
          </p>
        </div>
        
        {/* Footer with AapSaathi branding */}
        <div className="px-6 py-4 flex items-center justify-center gap-2"
             style={{ borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
          <img src="/aap-saathi-logo.png" alt="AapSaathi"
               style={{ height: 22, width: 'auto' }}
               onError={(e) => { e.target.style.display = 'none'; }} />
          <p className="text-[12px]" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
            Team AapSaathi
          </p>
        </div>
        
        {/* Continue button */}
        <div className="px-5 pb-5 pt-2">
          <button onClick={onClose}
            className="w-full h-12 rounded-2xl font-medium text-[14px] active:scale-[0.98] transition-transform"
            style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================
// PWA Install instructions modal (iOS) — for Safari users where
// the native install prompt isn't available. Shows step-by-step.
// =============================================================
const InstallInstructionsModal = ({ open, onClose, platform = 'ios' }) => {
  if (!open) return null;
  
  return (
    <div className="absolute inset-0 z-40 flex items-end" 
         style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} 
           className="w-full rounded-t-3xl flex flex-col" 
           style={{ background: 'var(--bg)', maxHeight: '80%' }}>
        <div className="w-12 h-1 rounded-full mx-auto my-3 shrink-0" style={{ background: 'var(--border)' }} />
        
        <div className="px-5 pb-3">
          <h2 className="text-[20px] mb-1" 
              style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
            Install Aangan
          </h2>
          <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
            {platform === 'ios' 
              ? 'On iPhone or iPad, follow these steps:'
              : 'Use your browser\'s menu to install:'}
          </p>
        </div>
        
        <div className="px-5 pb-6 overflow-y-auto">
          {platform === 'ios' ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-xl" 
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-medium shrink-0" 
                     style={{ background: 'var(--ink)', color: 'var(--bg)' }}>1</div>
                <div className="flex-1">
                  <p className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>
                    Tap the Share button
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--ink-soft)' }}>
                    The square with an arrow at the bottom of Safari (or top on iPad).
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 rounded-xl" 
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-medium shrink-0" 
                     style={{ background: 'var(--ink)', color: 'var(--bg)' }}>2</div>
                <div className="flex-1">
                  <p className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>
                    Scroll down and tap "Add to Home Screen"
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--ink-soft)' }}>
                    You'll see Aangan with our logo in the preview.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 rounded-xl" 
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-medium shrink-0" 
                     style={{ background: 'var(--ink)', color: 'var(--bg)' }}>3</div>
                <div className="flex-1">
                  <p className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>
                    Tap "Add" in the top right
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--ink-soft)' }}>
                    Aangan will appear on your home screen like any other app.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="p-3 rounded-xl" 
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <p className="text-[13px] font-medium mb-1" style={{ color: 'var(--ink)' }}>Chrome / Edge</p>
                <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
                  Tap the menu (⋮) → "Install app" or "Add to Home screen"
                </p>
              </div>
              <div className="p-3 rounded-xl" 
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <p className="text-[13px] font-medium mb-1" style={{ color: 'var(--ink)' }}>Firefox</p>
                <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
                  Tap the menu → "Install" (Android) or pin to Home Screen
                </p>
              </div>
              <div className="p-3 rounded-xl" 
                   style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
                <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
                  After installing, Aangan will work like a native app — even offline for cached content.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================
// REPORT USER SHEET
// =============================================================
const ReportUserSheet = ({ member, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const reasons = [
    { key: 'abuse', label: 'Abuse or harassment', icon: AlertTriangle },
    { key: 'spam', label: 'Spam or scam', icon: Ban },
    { key: 'hate', label: 'Hate speech', icon: ShieldAlert },
    { key: 'impersonation', label: 'Impersonation', icon: UserIcon },
    { key: 'inappropriate', label: 'Inappropriate content', icon: EyeOff },
    { key: 'other', label: 'Something else', icon: MoreHorizontal },
  ];

  return (
    <div className="absolute inset-0 z-40 flex items-end" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full rounded-t-3xl sheet-bottom-safe" style={{ background: 'var(--bg)', maxHeight: '85%', overflowY: 'auto' }}>
        <div className="w-12 h-1 rounded-full mx-auto my-3" style={{ background: 'var(--border)' }} />
        <div className="px-5 pb-4">
          <h2 className="text-[20px] mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
            Report {member.name}
          </h2>
          <p className="text-[12px] mb-4" style={{ color: 'var(--ink-soft)' }}>
            Admins of this batch will review within 24 hours. Your identity stays private.
          </p>

          <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Reason</p>
          <div className="space-y-2 mb-4">
            {reasons.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setReason(key)}
                className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{
                  background: reason === key ? 'var(--accent-soft)' : 'var(--surface)',
                  border: reason === key ? '1.5px solid var(--accent)' : '1px solid var(--border)',
                }}>
                <Icon size={16} style={{ color: reason === key ? 'var(--accent)' : 'var(--ink-soft)' }} />
                <span className="text-[13px] flex-1" style={{ color: 'var(--ink)' }}>{label}</span>
                {reason === key && <Check size={14} style={{ color: 'var(--accent)' }} />}
              </button>
            ))}
          </div>

          <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>
            More detail <span style={{ textTransform: 'none', fontWeight: 'normal' }}>(optional)</span>
          </p>
          <textarea rows={3} placeholder="What happened?" value={detail}
            onChange={(e) => setDetail(e.target.value)}
            className="w-full p-3 rounded-xl text-[13px] outline-none resize-none mb-4"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />

          <button onClick={() => onSubmit({ reportedId: member.id, reason, detail })}
            disabled={!reason}
            className="w-full h-12 rounded-2xl font-medium text-[14px] disabled:opacity-40 active:scale-[0.98] transition-transform"
            style={{ background: '#B33A3A', color: 'white' }}>
            Submit report
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================
// SETTINGS SCREEN
// =============================================================
const SettingsRow = ({ icon: Icon, label, sublabel, onClick, danger = false, rightSlot = null }) => (
  <button onClick={onClick} className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5 text-left"
          style={{ borderBottom: '1px solid var(--border)' }}>
    <div className="w-9 h-9 rounded-xl flex items-center justify-center"
         style={{ background: danger ? '#FBE3E3' : 'var(--surface)', border: '1px solid var(--border)' }}>
      <Icon size={16} style={{ color: danger ? '#B33A3A' : 'var(--ink)' }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[14px]" style={{ color: danger ? '#B33A3A' : 'var(--ink)', fontWeight: 500 }}>{label}</p>
      {sublabel && <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>{sublabel}</p>}
    </div>
    {rightSlot || <ChevronRight size={16} style={{ color: 'var(--ink-soft)' }} />}
  </button>
);

const SettingsScreen = ({ onBack, onRefer, onDelete, onLogout, onAdminQueue, isAdmin, currentUser,
                          onEditProfile, onNotifPrefs, onPrivacyData, onBlockedUsers, blockedCount = 0,
                          onHelp, onCommunityRules, onPrivacyPolicy, onTerms,
                          onInstallApp, isInstalled }) => (
  <div className="flex-1 flex flex-col" style={{ minHeight: 0, height: '100%' }}>
    <TopBar title="Settings" onBack={onBack} />
    <div className="flex-1 overflow-y-auto sheet-bottom-safe" style={{ background: 'var(--bg)', minHeight: 0 }}>
      <div className="px-5 py-3 flex items-center gap-3" style={{ borderBottom: '1px solid var(--border)' }}>
        <Avatar user={currentUser} size={48} />
        <div className="flex-1">
          <p className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>{currentUser.name}</p>
          <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>{currentUser.currentCity}</p>
        </div>
      </div>

      <p className="text-[11px] uppercase tracking-wider px-5 pt-5 pb-2" style={{ color: 'var(--ink-soft)' }}>Account</p>
      <SettingsRow icon={UserIcon} label="Edit profile" onClick={onEditProfile} />
      <SettingsRow icon={Bell} label="Notifications" sublabel="Posts, comments, chats" onClick={onNotifPrefs} />
      <SettingsRow icon={Shield} label="Privacy & data" sublabel="What's visible · Download my data" onClick={onPrivacyData} />
      <SettingsRow icon={Ban} label="Blocked users" sublabel={`${blockedCount} blocked`} onClick={onBlockedUsers} />

    

      {(() => {
        // Defensive triple-check — Capacitor.isNativePlatform() can be
        // unreliable depending on render-time bootstrap state. Belt-and-
        // suspenders ensures the "Install Aangan" PWA prompt never appears
        // inside the native Android wrapper.
        try {
          if (Capacitor?.isNativePlatform?.()) return null;
          if (typeof window !== 'undefined') {
            if (window.Capacitor?.isNativePlatform?.()) return null;
            const platform = window.Capacitor?.getPlatform?.();
            if (platform && platform !== 'web') return null;
          }
        } catch (e) { /* fall through */ }
        if (isInstalled) return null;
        return (
          <>
            <p className="text-[11px] uppercase tracking-wider px-5 pt-5 pb-2" style={{ color: 'var(--ink-soft)' }}>Get the app</p>
            <div className="bg-white">
              <button onClick={onInstallApp} className="w-full flex items-center justify-between px-5 py-3.5">
                <span className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-soft)' }}>
                    <Download size={14} style={{ color: 'var(--accent)' }} />
                  </div>
                  <div className="text-left">
                    <p className="text-[14px]" style={{ color: 'var(--ink)' }}>Install Aangan</p>
                    <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>Add to your home screen</p>
                  </div>
                </span>
                <ChevronRight size={16} style={{ color: 'var(--ink-soft)' }} />
              </button>
            </div>
          </>
        );
      })()}
        
        <p className="text-[11px] uppercase tracking-wider px-5 pt-5 pb-2" style={{ color: 'var(--ink-soft)' }}>Aangan</p>
      <SettingsRow icon={Gift} label="Refer a friend" sublabel="Invite people to your batches" onClick={onRefer} />
      <SettingsRow icon={HelpCircle} label="Help & support" onClick={onHelp} />
      <SettingsRow icon={FileText} label="Community guidelines" onClick={onCommunityRules} />
      <SettingsRow icon={Shield} label="Privacy policy" onClick={onPrivacyPolicy} />
      <SettingsRow icon={FileText} label="Terms of use" onClick={onTerms} />

      <p className="text-[11px] uppercase tracking-wider px-5 pt-5 pb-2" style={{ color: 'var(--ink-soft)' }}>Danger zone</p>
      <SettingsRow icon={LogOut} label="Sign out" onClick={onLogout} danger />
      <SettingsRow icon={Trash2} label="Delete my account" sublabel="Permanently remove all my data" onClick={onDelete} danger />

      <div className="px-5 py-6 flex flex-col items-center gap-1.5">
        <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>v1.0 · Made in India</p>
        <a href="https://aapsaathi.com" target="_blank" rel="noreferrer"
           className="flex items-center gap-1.5 mt-1">
          <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: 'var(--ink-soft)' }}>From</p>
          <img src="/aap-saathi-logo.png" alt="AapSaathi"
               style={{ height: 18, width: 'auto' }}
               onError={(e) => { e.target.style.display = 'none'; }} />
          <p className="text-[12px]" style={{ fontFamily: 'var(--font-display)', color: 'var(--accent)', fontWeight: 500 }}>
            AapSaathi
          </p>
        </a>
      </div>
    </div>
  </div>
);
// =============================================================
// WARNING ACKNOWLEDGMENT MODAL
// Shown when an admin has issued a warning. User cannot dismiss
// without tapping "I understand" — which calls onAcknowledge to
// clear /users/{uid}.pendingWarning in Firestore.
// =============================================================
const WarningAcknowledgmentModal = ({ warning, groupName, onAcknowledge }) => {
  const [busy, setBusy] = useState(false);
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center p-5"
         style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full rounded-3xl overflow-hidden"
           style={{ background: 'var(--bg)', maxWidth: 360 }}>
        <div className="p-5 pb-4 text-center" style={{ background: '#FBE3E3' }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
               style={{ background: '#B33A3A' }}>
            <AlertTriangle size={22} color="white" strokeWidth={2} />
          </div>
          <h2 className="text-[20px] mb-1" style={{ fontFamily: 'var(--font-display)', color: '#B33A3A', fontWeight: 500 }}>
            Warning from admin
          </h2>
          <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>
            from {groupName}
          </p>
        </div>
        <div className="p-5">
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
            {warning.text}
          </p>
          <p className="text-[12px] italic mb-5" style={{ color: 'var(--ink-soft)' }}>
            Tap "I understand" to continue using Aangan. This acknowledgment is logged.
          </p>
          <button
            onClick={async () => {
              if (busy) return;
              setBusy(true);
              await onAcknowledge();
              // Parent unmounts us when pendingWarning becomes null
            }}
            disabled={busy}
            className="w-full h-12 rounded-2xl font-medium text-[14px] disabled:opacity-50 active:scale-[0.98] transition-transform"
            style={{ background: '#B33A3A', color: 'white' }}>
            {busy ? 'Acknowledging…' : 'I understand'}
          </button>
        </div>
      </div>
    </div>
  );
};
// =============================================================
// DELETE POST CONFIRM MODAL
// =============================================================
const DeletePostConfirm = ({ post, onClose, onConfirm }) => (
  <div className="absolute inset-0 z-40 flex items-center justify-center p-5" 
       style={{ background: 'rgba(0,0,0,0.6)' }}
       onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="w-full rounded-3xl overflow-hidden" 
         style={{ background: 'var(--bg)', maxWidth: 340 }}>
      <div className="p-5 pb-4 text-center" style={{ background: '#FBE3E3' }}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
             style={{ background: '#B33A3A' }}>
          <Trash2 size={20} color="white" />
        </div>
        <h2 className="text-[18px]" style={{ fontFamily: 'var(--font-display)', color: '#B33A3A', fontWeight: 500 }}>
          Delete this post?
        </h2>
      </div>
      <div className="p-5">
        <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
          The post, all its likes, and all its comments will be permanently removed. This can't be undone.
        </p>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 h-11 rounded-xl text-[13px] font-medium"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            className="flex-1 h-11 rounded-xl text-[13px] font-medium"
            style={{ background: '#B33A3A', color: 'white' }}>
            Delete forever
          </button>
        </div>
      </div>
    </div>
  </div>
);
// =============================================================
// DELETE MEMORY CONFIRM MODAL
// Same shape as DeletePostConfirm, just memory-specific copy.
// =============================================================
const DeleteMemoryConfirm = ({ memory, onClose, onConfirm }) => (
  <div className="absolute inset-0 z-50 flex items-center justify-center p-5"
       style={{ background: 'rgba(0,0,0,0.6)' }}
       onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="w-full rounded-3xl overflow-hidden"
         style={{ background: 'var(--bg)', maxWidth: 340 }}>
      <div className="p-5 pb-4 text-center" style={{ background: '#FBE3E3' }}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
             style={{ background: '#B33A3A' }}>
          <Trash2 size={20} color="white" />
        </div>
        <h2 className="text-[18px]" style={{ fontFamily: 'var(--font-display)', color: '#B33A3A', fontWeight: 500 }}>
          Delete this memory?
        </h2>
      </div>
      <div className="p-5">
        <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
          The memory and its photo will be permanently removed from the Time Capsule. This can't be undone.
        </p>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 h-11 rounded-xl text-[13px] font-medium"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            className="flex-1 h-11 rounded-xl text-[13px] font-medium"
            style={{ background: '#B33A3A', color: 'white' }}>
            Delete forever
          </button>
        </div>
      </div>
    </div>
  </div>
);
// =============================================================
// ADD MEMORY CAPTION MODAL
// After the user picks a file, prompt for a short caption before uploading.
// "Skip" leaves it untitled — the parent decides what placeholder to use.
// =============================================================
const AddMemoryCaptionModal = ({ file, busy, onCancel, onSubmit }) => {
  const [caption, setCaption] = useState('');
  if (!file) return null;
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center p-5"
         style={{ background: 'rgba(0,0,0,0.6)' }}
         onClick={busy ? undefined : onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full rounded-3xl overflow-hidden"
           style={{ background: 'var(--bg)', maxWidth: 360 }}>
        <div className="p-5 pb-3">
          <h2 className="text-[18px] mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
            Add a caption?
          </h2>
          <p className="text-[12px] mb-3" style={{ color: 'var(--ink-soft)' }}>
            A short title helps batchmates remember the moment. Optional — skip to leave it untitled.
          </p>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 60))}
            placeholder="e.g. Farewell dinner 2009"
            maxLength={60}
            autoFocus
            disabled={busy}
            className="w-full h-11 px-3 rounded-xl text-[14px] outline-none"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
          <p className="text-[10px] mt-1 text-right" style={{ color: 'var(--ink-soft)' }}>
            {caption.length}/60
          </p>
        </div>
        <div className="p-5 pt-2 flex gap-2">
          <button onClick={onCancel} disabled={busy}
            className="flex-1 h-11 rounded-xl text-[13px] font-medium disabled:opacity-50"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
            Cancel
          </button>
          <button onClick={() => onSubmit(caption.trim() || null)} disabled={busy}
            className="flex-1 h-11 rounded-xl text-[13px] font-medium disabled:opacity-50"
            style={{ background: 'var(--accent)', color: 'white' }}>
            {busy ? 'Uploading…' : (caption.trim() ? 'Add memory' : 'Skip & upload')}
          </button>
        </div>
      </div>
    </div>
  );
};
// =============================================================
// DELETE ACCOUNT MODAL — two-step confirmation
// =============================================================
const DeleteAccountModal = ({ onClose, onConfirm }) => {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState('');
  const canConfirm = confirmText === 'DELETE';

  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full rounded-3xl overflow-hidden" style={{ background: 'var(--bg)', maxWidth: 360 }}>
        {step === 1 ? (
          <>
            <div className="p-6 pb-4 text-center" style={{ background: '#FBE3E3' }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                   style={{ background: '#B33A3A' }}>
                <AlertTriangle size={22} color="white" />
              </div>
              <h2 className="text-[20px]" style={{ fontFamily: 'var(--font-display)', color: '#B33A3A', fontWeight: 500 }}>
                Delete your account?
              </h2>
            </div>
            <div className="p-5">
              <p className="text-[13px] mb-3" style={{ color: 'var(--ink)' }}>
                This will permanently remove:
              </p>
              <ul className="text-[12px] space-y-1.5 mb-4 pl-5" style={{ color: 'var(--ink-soft)', listStyle: 'disc' }}>
                <li>Your profile, photo, and status</li>
                <li>All your posts, comments, and reactions</li>
                <li>Your memberships in every batch</li>
                <li>All your direct messages</li>
                <li>Any batches where you are the only admin (others need admin first)</li>
              </ul>
              <div className="rounded-xl p-3 mb-4 flex items-start gap-2"
                   style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <Clock size={12} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
                <p className="text-[11px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
                  Your account will be deactivated immediately and permanently deleted after 14 days. Sign in within those 14 days to cancel.
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={onClose}
                  className="flex-1 h-11 rounded-xl text-[13px] font-medium"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                  Cancel
                </button>
                <button onClick={() => setStep(2)}
                  className="flex-1 h-11 rounded-xl text-[13px] font-medium"
                  style={{ background: '#B33A3A', color: 'white' }}>
                  Continue
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="p-5">
              <h2 className="text-[18px] mb-2" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
                One last check.
              </h2>
              <p className="text-[13px] mb-4" style={{ color: 'var(--ink-soft)' }}>
                Type <strong style={{ color: '#B33A3A', fontFamily: 'monospace' }}>DELETE</strong> below to confirm.
              </p>
              <input type="text" autoFocus value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="DELETE"
                className="w-full p-3 rounded-xl text-[14px] outline-none mb-4 font-mono tracking-widest"
                style={{ background: 'var(--surface)', border: `1.5px solid ${canConfirm ? '#B33A3A' : 'var(--border)'}`, color: 'var(--ink)' }} />
              <div className="flex gap-2">
                <button onClick={() => { setStep(1); setConfirmText(''); }}
                  className="flex-1 h-11 rounded-xl text-[13px] font-medium"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                  Back
                </button>
                <button onClick={onConfirm} disabled={!canConfirm}
                  className="flex-1 h-11 rounded-xl text-[13px] font-medium disabled:opacity-40"
                  style={{ background: '#B33A3A', color: 'white' }}>
                  Delete forever
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// =============================================================
// REFER A FRIEND MODAL — native share or copy
// =============================================================
const ReferFriendModal = ({ onClose, onCopy, currentUser, inviteUrl: customUrl, inviteText: customText, title, subtitle, emailSubject }) => {
  // If a custom URL/text isn't provided, fall back to the generic "Refer a friend" behavior
  const inviteUrl = customUrl || (`${AANGAN_SHARE_BASE}/invite/` + (currentUser?.id || 'me'));
  const inviteText = customText || `Hey, I'm reconnecting with old batchmates on Aangan — you should join. ${inviteUrl}`;
  const headingText = title || 'Invite your old gang';
  const subText = subtitle || 'Aangan is more fun the more people from your batch are here.';
  const mailSubject = emailSubject || 'Join me on Aangan';

  // Detect if we're inside the native Android app. If so, use Capacitor's Share
  // plugin to open the REAL Android system share sheet with actual installed
  // app icons (WhatsApp green, Gmail red, Telegram blue, etc). On web/PWA,
  // fall back to navigator.share (mobile browsers) or copy-to-clipboard (desktop).
  const isNative = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();

  const handleNativeShare = async () => {
    // Native path: import Capacitor Share dynamically so web bundle isn't bloated.
    if (isNative) {
      try {
        const { Share } = await import('@capacitor/share');
        await Share.share({
          title: mailSubject,
          text: inviteText,
          url: inviteUrl,
          dialogTitle: 'Invite to Aangan',
        });
      } catch (e) {
        // User cancelled the share sheet, or plugin error. Silent fail is fine.
        console.warn('Native share cancelled or failed:', e);
      }
      return;
    }
    // Web path: navigator.share works on mobile browsers; falls back to copy on desktop.
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: mailSubject, text: inviteText, url: inviteUrl });
      } catch (e) { /* user cancelled */ }
    } else {
      onCopy(inviteUrl);
    }
  };

  // On native, all 4 channel buttons open the system share sheet (which shows
  // real installed app icons). On web, keep the existing direct deep-links.
  const channels = isNative ? [
    { key: 'share', label: 'Share via…', color: 'var(--ink)', emoji: '📤', onClick: handleNativeShare, fullWidth: true },
  ] : [
    { key: 'whatsapp', label: 'WhatsApp', color: '#25D366', emoji: '💬',
      url: `https://wa.me/?text=${encodeURIComponent(inviteText)}` },
    { key: 'sms', label: 'SMS', color: '#3F8E5C', emoji: '📱',
      url: `sms:?body=${encodeURIComponent(inviteText)}` },
    { key: 'email', label: 'Email', color: '#3F6BC4', emoji: '✉️',
      url: `mailto:?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(inviteText)}` },
    { key: 'more', label: 'More apps', color: 'var(--ink)', emoji: '⋯', onClick: handleNativeShare },
  ];

  return (
    <div className="absolute inset-0 z-40 flex items-end" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full rounded-t-3xl sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
        <div className="w-12 h-1 rounded-full mx-auto my-3" style={{ background: 'var(--border)' }} />
        <div className="px-5 pb-2 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
               style={{ background: 'var(--ink)' }}>
            <Logo size={28} color="var(--bg)" />
          </div>
          <h2 className="text-[22px] mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
            {headingText}
          </h2>
          <p className="text-[13px] mb-5" style={{ color: 'var(--ink-soft)' }}>
            {subText}
          </p>
        </div>

        <div className="px-5">
          <div className="rounded-2xl p-4 mb-4 flex items-center justify-between gap-3"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <p className="text-[12px] font-mono truncate flex-1" style={{ color: 'var(--ink)' }}>{inviteUrl}</p>
            <button onClick={() => onCopy(inviteUrl)}
              className="px-3 py-1.5 rounded-full text-[12px] font-medium flex items-center gap-1.5 shrink-0"
              style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
              <Copy size={12} /> Copy
            </button>
          </div>

          <div className={isNative ? "w-full" : "grid grid-cols-4 gap-3"}>
            {channels.map((c) => {
              const Wrapper = c.url ? 'a' : 'button';
              const props = c.url ? { href: c.url, target: '_blank', rel: 'noreferrer' } : { onClick: c.onClick };
              if (c.fullWidth) {
                // Native single-button layout — full-width "Share via..." opens system sheet
                return (
                  <Wrapper key={c.key} {...props}
                    className="w-full h-12 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                    style={{ background: 'var(--ink)', color: 'var(--bg)', textDecoration: 'none' }}>
                    <Share2 size={16} />
                    <span className="text-[14px] font-medium">{c.label}</span>
                  </Wrapper>
                );
              }
              // Web 4-grid layout — original emoji icons
              return (
                <Wrapper key={c.key} {...props}
                  className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
                  style={{ textDecoration: 'none' }}>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
                       style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    {c.emoji}
                  </div>
                  <p className="text-[11px]" style={{ color: 'var(--ink)' }}>{c.label}</p>
                </Wrapper>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================
// ADMIN MODERATION QUEUE
// =============================================================
const AdminQueueScreen = ({ pendingJoins, reports, group, onBack, onApproveJoin, onDeclineJoin, onResolveReport, pendingJoinsLoading, getUser }) => {
  const [tab, setTab] = useState('joins');
  const [confirmReject, setConfirmReject] = useState(null); // userId pending confirmation
  
  return (
    <div className="flex-1 flex flex-col">
      <TopBar title="Moderation" onBack={onBack} />
      <div className="px-5 py-3" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--ink-soft)' }}>Admin tools for</p>
        <p className="text-[15px]" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)', fontWeight: 500 }}>
          {group.name}
        </p>
      </div>
      <div className="flex gap-1 px-5 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
        {[['joins', 'Pending joins', pendingJoins.length], ['reports', 'Reports', reports.length]].map(([key, label, n]) => (
          <button key={key} onClick={() => setTab(key)}
            className="flex-1 py-2 rounded-xl text-[13px] font-medium flex items-center justify-center gap-1.5"
            style={{ background: tab === key ? 'var(--ink)' : 'transparent',
                     color: tab === key ? 'var(--bg)' : 'var(--ink)' }}>
            {label}
            {n > 0 && (
              <span className="text-[10px] px-1.5 rounded-full"
                    style={{ background: tab === key ? 'var(--bg)' : 'var(--accent)',
                             color: tab === key ? 'var(--ink)' : 'white' }}>{n}</span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
        {tab === 'joins' ? (
          pendingJoinsLoading ? (
            <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
              <div className="w-10 h-10 rounded-full border-2 animate-spin mb-3"
                   style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
              <p className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>Loading pending requests…</p>
            </div>
          ) : pendingJoins.length === 0 ? (
            <div className="text-center py-16 px-8">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                   style={{ background: 'var(--accent-soft)' }}>
                <Check size={20} style={{ color: 'var(--accent)' }} />
              </div>
              <p className="text-[14px] mb-1" style={{ color: 'var(--ink)' }}>All caught up 🎉</p>
              <p className="text-[12px]" style={{ color: 'var(--ink-soft)' }}>No pending join requests right now.</p>
            </div>
          ) : pendingJoins.map((pj) => {
            // Build a user-like object for Avatar component
            const userForAvatar = {
              name: pj.name,
              initials: pj.initials,
              photoUrl: pj.photoUrl,
              color: '#C45A3F',
            };
            return (
              <div key={pj.userId} className="px-5 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
                <div className="flex items-start gap-3 mb-3">
                  <Avatar user={userForAvatar} size={48} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>{pj.name}</p>
                    {pj.currentCity && (
                      <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
                        📍 {pj.currentCity}
                      </p>
                    )}
                    {pj.viaCode && (
                      <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px]"
                            style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
                        <Hash size={9} /> Via invite link
                      </span>
                    )}
                    {pj.bio && (
                      <p className="text-[12px] italic mt-2 px-3 py-2 rounded-xl"
                         style={{ background: 'var(--surface)', color: 'var(--ink)' }}>
                        "{pj.bio}"
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setConfirmReject(pj.userId)}
                    className="flex-1 h-10 rounded-xl text-[13px] font-medium"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                    Decline
                  </button>
                  <button onClick={() => onApproveJoin(pj.userId)}
                    className="flex-1 h-10 rounded-xl text-[13px] font-medium"
                    style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
                    Approve
                  </button>
                </div>
              </div>
            );
          })
        ) : (
        // Reports tab. The banner that used to sit here said "Reports system
        // coming soon — for now, this shows demo data". Both halves were false:
        // subscribeToGroupReports feeds this list with real reports, and the
        // dismiss/warn/remove buttons below call the real resolveReport. Batch
        // admins were being told their moderation tools were fake, which is the
        // worst possible lie for the one surface that keeps members safe.
          <>
            {reports.length === 0 ? (
              <div className="text-center py-16 px-8">
                <p className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>No open reports.</p>
              </div>
            ) : reports.map((r) => {
              const reported = getUser(r.reportedId);
      const reporter = getUser(r.reporterId);
              if (!reported || !reporter) return null;
              return (
                <div key={r.id} className="px-5 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                         style={{ background: '#FBE3E3' }}>
                      <Flag size={13} style={{ color: '#B33A3A' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px]" style={{ color: 'var(--ink)' }}>
                        <strong>{reported.name}</strong> reported for <strong>{r.reason}</strong>
                      </p>
                      <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
                        by {reporter.name} · {r.timestamp} ago
                      </p>
                    </div>
                  </div>
                  {r.detail && (
                    <p className="text-[12px] italic px-3 py-2 rounded-xl mb-3"
                       style={{ background: 'var(--surface)', color: 'var(--ink)' }}>
                      "{r.detail}"
                    </p>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => onResolveReport(r.id, 'dismiss')}
                      className="h-9 rounded-xl text-[12px] font-medium"
                      style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                      Dismiss
                    </button>
                    <button onClick={() => onResolveReport(r.id, 'warn')}
                      className="h-9 rounded-xl text-[12px] font-medium"
                      style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
                      Warn
                    </button>
                    <button onClick={() => onResolveReport(r.id, 'remove')}
                      className="h-9 rounded-xl text-[12px] font-medium"
                      style={{ background: '#B33A3A', color: 'white' }}>
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
        <div className="h-6" />
      </div>

      {/* Confirm reject modal */}
      {confirmReject && (() => {
        const pj = pendingJoins.find(p => p.userId === confirmReject);
        return (
          <div className="absolute inset-0 z-40 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.6)' }}
               onClick={() => setConfirmReject(null)}>
            <div onClick={(e) => e.stopPropagation()} className="w-full rounded-3xl overflow-hidden" 
                 style={{ background: 'var(--bg)', maxWidth: 340 }}>
              <div className="p-5 pb-4 text-center" style={{ background: '#FBE3E3' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                     style={{ background: '#B33A3A' }}>
                  <UserMinus size={20} color="white" />
                </div>
                <h2 className="text-[18px]" style={{ fontFamily: 'var(--font-display)', color: '#B33A3A', fontWeight: 500 }}>
                  Decline {pj?.name}?
                </h2>
              </div>
              <div className="p-5">
                <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
                  They won't be added to <strong>{group.name}</strong>. They'll need to ask you directly to be added later.
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setConfirmReject(null)}
                    className="flex-1 h-11 rounded-xl text-[13px] font-medium"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                    Cancel
                  </button>
                  <button onClick={() => { onDeclineJoin(confirmReject); setConfirmReject(null); }}
                    className="flex-1 h-11 rounded-xl text-[13px] font-medium"
                    style={{ background: '#B33A3A', color: 'white' }}>
                    Decline
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// =============================================================
// PENDING APPROVAL SCREEN — full screen, no tabs, while non-code join is reviewed
// =============================================================
const PendingApprovalScreen = ({ batch, onSimulateApproval, onLogout, onTryAnotherBatch }) => {
  const admins = []; // V1: real admin profiles loaded via member subscriptions in Phase 2
  return (
    <div className="flex-1 flex flex-col px-6 sheet-bottom-safe relative overflow-hidden profile-header-safe">
      <div className="absolute top-0 left-0 right-0 h-1/2 opacity-20"
           style={{ background: 'radial-gradient(ellipse at top, var(--accent) 0%, transparent 60%)' }} />
      <div className="flex items-center gap-2 relative">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--ink)' }}>
          <Logo size={20} color="var(--bg)" />
        </div>
        <p className="text-[14px]" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>{APP_NAME}</p>
      </div>

      <div className="relative flex-1 flex flex-col items-center justify-center text-center">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
             style={{ background: 'var(--accent-soft)', border: '1.5px solid var(--accent)' }}>
          <Clock size={32} style={{ color: 'var(--accent)' }} />
        </div>
        <h1 className="mb-3" style={{ fontSize: '28px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, lineHeight: 1.15 }}>
          You're in the waiting room.
        </h1>
        <p className="text-[14px] leading-relaxed mb-6 max-w-[300px]" style={{ color: 'var(--ink-soft)' }}>
          Your request to join <strong style={{ color: 'var(--ink)' }}>{batch?.name || 'this batch'}</strong> is with the admins.
          We'll send you a notification the moment you're approved.
        </p>

        <div className="rounded-2xl p-4 w-full mb-3"
             style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <p className="text-[11px] uppercase tracking-wider mb-3" style={{ color: 'var(--ink-soft)' }}>
            Reviewing your request
          </p>
          <div className="flex items-center justify-center gap-3 mb-2">
            {admins.map((a) => (
              <div key={a.id} className="flex flex-col items-center">
                <Avatar user={a} size={44} />
                <p className="text-[11px] mt-1.5" style={{ color: 'var(--ink)' }}>{a.name.split(' ')[0]}</p>
                <p className="text-[10px]" style={{ color: 'var(--ink-soft)' }}>Admin</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[11px] mb-6" style={{ color: 'var(--ink-soft)' }}>
          Most batches review within a few hours.
        </p>

        <button onClick={onSimulateApproval}
          className="text-[12px] font-medium px-4 py-2 rounded-full mb-2"
          style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
          ✨ Simulate admin approval (demo)
        </button>
      </div>

      <div className="relative flex flex-col gap-2">
        <button onClick={onTryAnotherBatch}
          className="w-full h-12 rounded-2xl text-[13px] font-medium"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
          Try a different batch
        </button>
        <button onClick={onLogout}
          className="w-full h-12 rounded-2xl text-[13px]"
          style={{ color: 'var(--ink-soft)' }}>
          Sign out
        </button>
      </div>
    </div>
  );
};

// =============================================================
// EDIT PROFILE SCREEN
// =============================================================
const EditProfileScreen = ({ currentUser, onBack, onSave, authEmail }) => {
  const [name, setName] = useState(currentUser?.name || '');
  const [bio, setBio] = useState(currentUser?.bio || '');
  const [currentCity, setCurrentCity] = useState(currentUser?.currentCity || '');
  const [phone, setPhone] = useState(currentUser?.phone || '');
  const [profession, setProfession] = useState(currentUser?.profession || '');
  const [photoUrl, setPhotoUrl] = useState(currentUser?.photoUrl || null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Crop modal state for new photo selection
  const [cropSrc, setCropSrc] = useState(null);
  // Step 1: User picks a file → check monthly limit → open crop modal
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    
    // Pre-check the monthly limit BEFORE opening crop (don't waste user's time)
    try {
      const limitCheck = await checkProfilePhotoLimit(currentUser.id);
      if (!limitCheck.allowed) {
        alert(`You've changed your profile photo ${limitCheck.limit} times this month. Try again next month.`);
        return;
      }
    } catch (e) { /* fail open */ }
    
    // Open crop modal
    const localPreview = URL.createObjectURL(file);
    setCropSrc(localPreview);
  };

  // Step 2: User finished cropping — upload the cropped blob to Storage
  const handleCropDone = async (croppedFile) => {
    // Close crop modal first
    const previousCropSrc = cropSrc;
    setCropSrc(null);
    
    // Show optimistic preview from the cropped file
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoUrl(ev.target.result);
    reader.readAsDataURL(croppedFile);
    
    setUploading(true);
    try {
      // Compress + upload to Firebase Storage (same pipeline as before)
      const url = await uploadProfilePhoto(currentUser.id, croppedFile);
      setPhotoUrl(url);
      // Photo counter is now incremented server-side by the onPhotoUpload Cloud Function
    } catch (err) {
      console.error('Photo upload failed:', err);
      alert('Could not upload photo — please try again');
      setPhotoUrl(currentUser?.photoUrl || null);
    } finally {
      setUploading(false);
      if (previousCropSrc) URL.revokeObjectURL(previousCropSrc);
    }
  };

  const handleCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };
  const initials = name.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between px-4 shrink-0 topbar-safe"
           style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)', minHeight: '56px' }}>
        <button onClick={onBack} className="w-9 h-9 flex items-center justify-center -ml-2 rounded-full active:bg-black/5">
          <ArrowLeft size={20} style={{ color: 'var(--ink)' }} />
        </button>
        <h1 className="text-base font-medium" style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>Edit profile</h1>
        <button onClick={() => onSave({ name, bio, currentCity, phone, profession, photoUrl, initials })}
                disabled={!name || uploading}
                className="text-[13px] font-medium disabled:opacity-40"
                style={{ color: 'var(--accent)' }}>{uploading ? 'Uploading…' : 'Save'}</button>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0 px-6 pt-6 pb-8" style={{ background: 'var(--bg)' }}>
        <div className="flex flex-col items-center mb-6">
          <button onClick={() => fileInputRef.current?.click()}
                  className="relative mb-2 active:scale-[0.97] transition-transform">
            {photoUrl ? (
              <img src={photoUrl} alt="profile" className="w-24 h-24 rounded-full object-cover" />
            ) : (
              <div className="w-24 h-24 rounded-full flex items-center justify-center text-white font-medium"
                   style={{ background: currentUser?.color || '#C45A3F', fontSize: 36, fontFamily: 'var(--font-display)' }}>
                {initials}
              </div>
            )}
            <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center"
                 style={{ background: 'var(--ink)' }}>
              {uploading ? (
                <div className="w-3.5 h-3.5 rounded-full border-2 animate-spin"
                     style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />
              ) : (
                <Camera size={14} color="white" />
              )}
            </div>
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
          {photoUrl && (
            <button 
              onClick={() => setPhotoUrl(null)} 
              disabled={uploading}
              className="text-[12px] disabled:opacity-40" 
              style={{ color: 'var(--accent)' }}>
              Remove photo
            </button>
          )}
        </div>
        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
               className="w-full p-4 rounded-2xl text-[15px] outline-none mb-4"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>One line about you</label>
        <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={2}
                  className="w-full p-4 rounded-2xl text-[15px] outline-none resize-none mb-4"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Current city</label>
        <input type="text" value={currentCity} onChange={(e) => setCurrentCity(e.target.value)}
               className="w-full p-4 rounded-2xl text-[15px] outline-none mb-4"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />

        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Profession <span style={{ textTransform: 'none', fontWeight: 'normal' }}>(optional)</span></label>
        <input type="text" value={profession} onChange={(e) => setProfession(e.target.value)}
               placeholder="Add profession (e.g. Designer)"
               className="w-full p-4 rounded-2xl text-[15px] outline-none mb-4"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />

        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Email</label>
        <input type="email" value={authEmail || ''} readOnly
               className="w-full p-4 rounded-2xl text-[15px] outline-none mb-2 cursor-not-allowed"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink-soft)' }} />
        <p className="text-[11px] mb-4 px-1" style={{ color: 'var(--ink-soft)' }}>
          From your sign-in account. To change, contact support.
        </p>

        <label className="block text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Phone <span style={{ textTransform: 'none', fontWeight: 'normal' }}>(optional)</span></label>
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel"
               placeholder="e.g. 9876543210"
               className="w-full p-4 rounded-2xl text-[15px] outline-none mb-2"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
        <p className="text-[11px] mb-4 px-1" style={{ color: 'var(--ink-soft)' }}>
          Used only for account recovery. Never shown to other members.
        </p>

        <div className="rounded-xl p-3 flex items-start gap-2"
             style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <Lock size={12} className="mt-0.5 shrink-0" style={{ color: 'var(--ink-soft)' }} />
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
            Your phone, email and exact address are private — never shown to other members.
          </p>
        </div>
      </div>
      <PhotoCropModal
        open={!!cropSrc}
        imageSrc={cropSrc}
        aspect={1}
        cropShape="round"
        title="Position your profile photo"
        onClose={handleCropCancel}
        onDone={handleCropDone}
      />
    </div>
  );
};

// =============================================================
// NOTIFICATIONS SETTINGS — toggles
// =============================================================
const ToggleRow = ({ label, sublabel, value, onChange }) => (
  <button onClick={() => onChange(!value)}
          className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-black/5 text-left"
          style={{ borderBottom: '1px solid var(--border)' }}>
    <div className="flex-1 min-w-0">
      <p className="text-[14px]" style={{ color: 'var(--ink)' }}>{label}</p>
      {sublabel && <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>{sublabel}</p>}
    </div>
    <div className="w-11 h-6 rounded-full relative shrink-0 transition-colors"
         style={{ background: value ? 'var(--ink)' : 'var(--border)' }}>
      <div className="absolute top-0.5 w-5 h-5 rounded-full transition-transform"
           style={{ background: 'white', left: 2, transform: value ? 'translateX(20px)' : 'translateX(0)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  </button>
);

const NotificationsSettingsScreen = ({ onBack }) => {
  const [prefs, setPrefs] = useState({
    posts: true, comments: true, likes: false, newMembers: true,
    chatRequests: true, chatMessages: true, adminPings: true, weeklyDigest: false,
  });
  const setPref = (k) => (v) => setPrefs(p => ({ ...p, [k]: v }));

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <TopBar title="Notifications" onBack={onBack} />
      <div className="flex-1 overflow-y-auto min-h-0 sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
        <div className="m-5 rounded-2xl p-4 flex items-start gap-3" 
             style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
          <BellRing size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
          <div>
            <p className="text-[12px] font-medium mb-0.5" style={{ color: 'var(--ink)' }}>Chat push notifications are on</p>
            <p className="text-[11px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
              You'll get a push when someone messages you. Notifications for posts, comments, and likes are coming in the next update — these toggles will start working then.
            </p>
          </div>
        </div>
        <p className="text-[11px] uppercase tracking-wider px-5 pt-2 pb-2" style={{ color: 'var(--ink-soft)' }}>In your batches</p>
        <ToggleRow label="New posts" sublabel="When someone posts on the Wall" value={prefs.posts} onChange={setPref('posts')} />
        <ToggleRow label="Comments on my posts" value={prefs.comments} onChange={setPref('comments')} />
        <ToggleRow label="Likes on my posts" value={prefs.likes} onChange={setPref('likes')} />
        <ToggleRow label="New members joined" value={prefs.newMembers} onChange={setPref('newMembers')} />

        <p className="text-[11px] uppercase tracking-wider px-5 pt-5 pb-2" style={{ color: 'var(--ink-soft)' }}>Chats</p>
        <ToggleRow label="Chat requests" value={prefs.chatRequests} onChange={setPref('chatRequests')} />
        <ToggleRow label="New messages" value={prefs.chatMessages} onChange={setPref('chatMessages')} />

        <p className="text-[11px] uppercase tracking-wider px-5 pt-5 pb-2" style={{ color: 'var(--ink-soft)' }}>Admin</p>
        <ToggleRow label="Pending join requests" sublabel="Only if you're an admin" value={prefs.adminPings} onChange={setPref('adminPings')} />

        <p className="text-[11px] uppercase tracking-wider px-5 pt-5 pb-2" style={{ color: 'var(--ink-soft)' }}>Email</p>
        <ToggleRow label="Weekly digest" sublabel="A summary of what you missed" value={prefs.weeklyDigest} onChange={setPref('weeklyDigest')} />
        <div className="h-6" />
      </div>
    </div>
  );
};

// =============================================================
// PRIVACY & DATA SCREEN
// =============================================================
const PrivacyDataScreen = ({ onBack, onDownload }) => (
  <div className="flex-1 flex flex-col h-full overflow-hidden">
    <TopBar title="Privacy & data" onBack={onBack} />
    <div className="flex-1 overflow-y-auto min-h-0 px-5 pt-5 sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
      <div className="rounded-2xl p-4 mb-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={16} style={{ color: 'var(--accent)' }} />
          <p className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>India DPDPA 2023 compliant</p>
        </div>
        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
          You have the right to access, correct, and delete your personal data — and to know what we collect.
        </p>
      </div>

      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>What we collect</p>
      <div className="rounded-2xl p-4 mb-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <ul className="text-[13px] leading-relaxed space-y-1.5" style={{ color: 'var(--ink)' }}>
          <li>• Your name & profile photo</li>
          <li>• Your phone or email (used to sign in only)</li>
          <li>• The city you say you live in now</li>
          <li>• The batches you create or join</li>
          <li>• Posts, comments, and reactions you create</li>
          <li>• Chat messages — between you and one other batchmate, kept until either of you clears the chat</li>
          <li>• Device info needed to keep you signed in</li>
        </ul>
      </div>

      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Who can see what</p>
      <div className="rounded-2xl p-4 mb-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <p className="text-[13px] leading-relaxed mb-2" style={{ color: 'var(--ink)' }}>
          <strong>Your name, photo, current city, status, and posts</strong> are seen only by verified members of the same batch.
        </p>
        <p className="text-[13px] leading-relaxed" style={{ color: 'var(--ink)' }}>
          <strong>Your phone, email, exact address, and DMs</strong> are never shown to anyone, ever — not even to admins.
        </p>
      </div>

      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Your rights</p>
      <button onClick={onDownload}
              className="w-full text-left rounded-2xl p-4 mb-2 flex items-center gap-3 active:scale-[0.99]"
              style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
        <FileText size={18} />
        <div className="flex-1">
          <p className="text-[14px] font-medium">Download my data</p>
          <p className="text-[11px] opacity-80">Get a JSON copy of everything</p>
        </div>
        <ChevronRight size={16} />
      </button>
      <p className="text-[11px] leading-relaxed mb-3" style={{ color: 'var(--ink-soft)' }}>
        To delete your account or correct anything, go to Settings → Delete account or Edit profile.
      </p>
    </div>
  </div>
);

// =============================================================
// BLOCKED USERS SCREEN
// =============================================================
// =============================================================
// BLOCKED USERS SCREEN — Session C2b
//
// Was a stub: it took only onBack and always rendered "No one is
// blocked", while Settings showed a hardcoded "0 blocked". Now reads the
// live list from /users/{uid}/blockedUsers.
//
// The old empty-state copy said blocking means "they can't see your posts
// or message you". The first half was never true and is not true now:
// block is CONTACT-only. Their posts, listings and profile stay visible.
// Hiding content would mean client-side filtering at every read surface
// forever, and it could never be complete, because Firestore cannot filter
// "not in this other collection" server-side. Better to do one thing and
// say so than to promise disappearance and leak.
// =============================================================
const BlockedUsersScreen = ({ onBack, blockedUsers = [], onUnblock }) => {
  // Name/photo are denormalised onto the block doc at write time, so the
  // common case renders with ZERO extra reads. But a doc can lack them —
  // written by hand, written before this screen existed, or a partial write —
  // and a row reading "Someone" is useless: a block you cannot identify is a
  // block you cannot confidently undo.
  //
  // /users/{uid} is `allow read: if signedIn()`, so fill the gaps on demand.
  // Only for rows actually missing a name, only while this screen is open, and
  // cached in state after — a handful of reads on a rarely opened screen, and
  // zero once names are denormalised properly by the Block button.
  //
  // Misses are cached too (as an empty name), so a deleted user doc doesn't
  // re-trigger the lookup on every render.
  const [hydrated, setHydrated] = useState({});   // uid -> { name, photoUrl, color }

  useEffect(() => {
    const missing = blockedUsers.filter((u) => !u.name && !hydrated[u.id]);
    if (missing.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      const found = {};
      await Promise.all(missing.map(async (u) => {
        try {
          const snap = await getDoc(doc(db, 'users', u.id));
          found[u.id] = snap.exists()
            ? { name: snap.data().name || '', photoUrl: snap.data().photoUrl || null, color: snap.data().color }
            : { name: '' };
        } catch (err) {
          console.warn('[BlockedUsers] name lookup failed for', u.id, err?.code || err);
          found[u.id] = { name: '' };
        }
      }));
      if (!cancelled) setHydrated((prev) => ({ ...prev, ...found }));
    })();
    return () => { cancelled = true; };
  }, [blockedUsers, hydrated]);

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <TopBar title="Blocked users" onBack={onBack} />
      {blockedUsers.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" style={{ background: 'var(--bg)' }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
               style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <Ban size={20} style={{ color: 'var(--ink-soft)' }} />
          </div>
          <p className="text-[14px] mb-1" style={{ color: 'var(--ink)' }}>No one is blocked</p>
          <p className="text-[12px] max-w-[260px]" style={{ color: 'var(--ink-soft)' }}>
            When you block someone, neither of you can message the other. Block from any member's profile or from a chat.
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
          <div className="px-5 pt-4 pb-3">
            <p className="text-[12px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
              Neither of you can message the other, and they can't place new orders with your businesses.
              They can still see your posts and batches.
            </p>
          </div>
          {blockedUsers.map((u) => {
            const name = u.name || hydrated[u.id]?.name || '';
            const photoUrl = u.photoUrl || hydrated[u.id]?.photoUrl || null;
            const known = !!name;
            return (
              <div key={u.id} className="flex items-center gap-3 px-5 py-3"
                   style={{ borderTop: '1px solid var(--border)' }}>
                <Avatar user={{ name: name || 'Someone', photoUrl, id: u.id, color: hydrated[u.id]?.color }} size={40} />
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium truncate" style={{ color: 'var(--ink)' }}>
                    {name || 'Someone'}
                  </p>
                  <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>
                    {known ? 'Blocked' : 'Account no longer available'}
                  </p>
                </div>
                <button onClick={() => onUnblock?.({ ...u, name: name || u.name })}
                        className="px-3 h-8 rounded-full text-[12px] font-medium active:scale-95 transition-transform shrink-0"
                        style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                  Unblock
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =============================================================
// HELP & SUPPORT — placeholder until owner email configured
// =============================================================
const HelpSupportScreen = ({ onBack, onChatSupport, chatOpening }) => (
  <div className="flex-1 flex flex-col h-full overflow-hidden">
    <TopBar title="Help & support" onBack={onBack} />
    <div className="flex-1 overflow-y-auto px-5 pt-5 sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Common questions</p>
      <div className="space-y-2 mb-5">
        {[
          { q: 'My batch wasn\'t found in search', a: 'Be the first member — tap "Create my own" from the join screen. You\'ll get a connect code to share.' },
          { q: 'How long until my join request is approved?', a: 'Most admins review within a few hours. If 72 hours pass with no response, code-based provisional access is auto-removed.' },
          { q: 'Why can\'t I see posts yet?', a: 'You\'re probably awaiting admin approval. Until then, you can see the batch name and members but not posts.' },
          { q: 'What happens to my messages?', a: 'Chats are private to you and the other person. Each chat shows the last 10 messages with a button to load older ones. Either side can clear the chat anytime — that only clears it for them, not the other person.' },
          { q: 'How do I leave a batch?', a: 'Profile → tap the batch → Leave.' },
        ].map((item, i) => (
          <details key={i} className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <summary className="text-[13px] font-medium cursor-pointer flex items-center justify-between"
                     style={{ color: 'var(--ink)', listStyle: 'none' }}>
              {item.q}
              <ChevronDown size={14} style={{ color: 'var(--ink-soft)' }} />
            </summary>
            <p className="text-[12px] leading-relaxed mt-2" style={{ color: 'var(--ink-soft)' }}>
              {item.a}
            </p>
          </details>
        ))}
      </div>

      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>Still stuck?</p>

      {/* Session A — direct line to the Aangan team. Reuses the support-chat
          plumbing built for merchants in Session 5a; the Firestore create rule
          has no business-owner gate, so any signed-in member can open one.
          Placed above the email block because a reply arrives in-app rather
          than in an inbox the member may not check. */}
      <button onClick={onChatSupport} disabled={chatOpening}
        className="w-full rounded-2xl p-4 mb-3 flex items-center gap-3 text-left active:scale-[0.99] transition-transform disabled:opacity-60"
        style={{ background: 'var(--ink)', border: '1px solid var(--ink)' }}>
        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
             style={{ background: 'rgba(255,255,255,0.12)' }}>
          <MessageCircle size={16} color="white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium" style={{ color: 'var(--bg)' }}>
            {chatOpening ? 'Opening…' : 'Chat with Aangan support'}
          </p>
          <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
            Message us in the app · we reply here
          </p>
        </div>
        <ChevronRight size={16} style={{ color: 'rgba(255,255,255,0.65)' }} />
      </button>

      <div className="rounded-2xl p-4 mb-3" style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
        <p className="text-[13px] mb-3" style={{ color: 'var(--ink)' }}>
          Prefer email? We're a small team and we read every message:
        </p>
        <div className="space-y-2.5">
          <div className="flex items-start gap-2.5">
            <Mail size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
            <div>
              <a href="mailto:aangansupport@gmail.com" className="text-[13px] font-medium" style={{ color: 'var(--accent)' }}>
                aangansupport@gmail.com
              </a>
              <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>For general support questions</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
            <div>
              <a href="mailto:aangangrievance@gmail.com" className="text-[13px] font-medium" style={{ color: 'var(--accent)' }}>
                aangangrievance@gmail.com
              </a>
              <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>Grievance officer · DPDPA 2023 compliance</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <Building2 size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
            <div>
              <a href="mailto:info@aapsaathi.com" className="text-[13px] font-medium" style={{ color: 'var(--accent)' }}>
                info@aapsaathi.com
              </a>
              <p className="text-[11px]" style={{ color: 'var(--ink-soft)' }}>For business or partnership enquiries</p>
            </div>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-center" style={{ color: 'var(--ink-soft)' }}>
        We typically reply within 24 hours.
      </p>
    </div>
  </div>
);

// =============================================================
// COMMUNITY GUIDELINES SCREEN
// =============================================================
const COMMUNITY_RULES = [
  { title: 'Be kind to old friends', body: 'This is a place to reconnect, not relive grudges. Treat people the way you would in person, at a reunion.' },
  { title: 'No abuse, harassment, or hate', body: 'Slurs, threats, or targeting based on caste, religion, gender, sexuality, region or anything else gets you removed immediately.' },
  { title: 'No spam or scams', body: 'Network marketing, crypto pitches, "earn from home" stuff — keep it out. For genuine business referrals or recommendations, use Word of Mouth.' },
  { title: 'Use your real identity', body: 'Use your real name and a recognizable photo. Old friends should be able to find you. Impersonation gets you banned.' },
  { title: 'Respect privacy', body: 'Don\'t share screenshots of someone\'s profile, status, or DMs without permission. Treat private chats with the same care you\'d use offline.' },
  { title: 'Reports go to your batch admins first', body: 'Most things should be handled inside your batch. If admins don\'t respond in 48 hours, escalate via Settings → Help.' },
  { title: 'Aangan is strictly 18+', body: 'Sexual content involving minors, grooming behaviour, or any exploitation of children is forbidden and will result in immediate ban and report to authorities.' },
];

const CommunityGuidelinesScreen = ({ onBack }) => (
  <div className="flex-1 flex flex-col h-full overflow-hidden">
    <TopBar title="Community guidelines" onBack={onBack} />
    <div className="flex-1 overflow-y-auto min-h-0 px-5 pt-5 sheet-bottom-safe" style={{ background: 'var(--bg)' }}>  
      <div className="rounded-2xl p-4 mb-5" style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
        <p className="text-[14px] leading-relaxed" style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic' }}>
          "Aangan is a courtyard. Behave like you would in someone's home."
        </p>
      </div>
      {COMMUNITY_RULES.map((r, i) => (
        <div key={i} className="rounded-2xl p-4 mb-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium"
                 style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>{i + 1}</div>
            <p className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>{r.title}</p>
          </div>
          <p className="text-[12px] leading-relaxed pl-8" style={{ color: 'var(--ink-soft)' }}>
            {r.body}
          </p>
        </div>
      ))}
      <p className="text-[11px] mt-2 text-center" style={{ color: 'var(--ink-soft)' }}>
        Last updated April 2026 · Subject to change with notice.
      </p>
    </div>
  </div>
);

// =============================================================
// PRIVACY POLICY (short MVP version)
// =============================================================
const PrivacyPolicyScreen = ({ onBack }) => (
  <div className="flex-1 flex flex-col h-full overflow-hidden">
    <TopBar title="Privacy policy" onBack={onBack} />
    <div className="flex-1 overflow-y-auto min-h-0 px-5 pt-5 sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
      <div className="rounded-2xl p-4 mb-5" style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2 mb-1">
          <Shield size={14} style={{ color: 'var(--accent)' }} />
          <p className="text-[12px] font-medium" style={{ color: 'var(--ink)' }}>Plain-English summary</p>
        </div>
        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
          We keep what we need to make Aangan work, never sell your data, and let you delete it any time.
        </p>
      </div>

      <p className="text-[13px] leading-relaxed mb-5" style={{ color: 'var(--ink)' }}>
        Disputes are governed by the laws of India. For any concerns, contact us at <a href="mailto:info@aapsaathi.com" style={{ color: 'var(--accent)' }}>info@aapsaathi.com</a>.
      </p>

      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>1. Who we are</p>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
        Aangan is a private, real-identity social platform for reconnecting with the people you grew up with — your school batch, college mates, old colleagues, neighbours, and family. Aangan is operated by AapSaathi, based in India.
      </p>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
        This policy explains what data we collect, why, who we share it with, and the rights you have over your data.
      </p>

      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>2. What we collect & why</p>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
        Name, photo, phone or email (sign-in), batches you join, posts/comments, current city. Chats are stored to support multi-device access and persist until you clear them.
      </p>

      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>3. Who we share with</p>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
        Nobody. Your data is not sold or shared with advertisers. Profile visibility is restricted to verified members of the same batch.
      </p>

      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>4. Your rights</p>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
        Access, correct, delete, or download your data — Settings → Privacy & data, or Delete account.
      </p>

      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>5. Data retention</p>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
        Posts, comments, and chat messages stay until you delete them. Each chat shows the last 10 messages by default with a button to load older ones. Either party can clear a chat for themselves any time. After account deletion, all your data is gone after a 14-day grace period.
      </p>

      <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--ink-soft)' }}>6. Contact</p>
      <p className="text-[13px] leading-relaxed mb-2" style={{ color: 'var(--ink)' }}>
        Privacy questions: <a href="mailto:aangangrievance@gmail.com" style={{ color: 'var(--accent)' }}>aangangrievance@gmail.com</a> (Grievance Officer per DPDPA 2023).
      </p>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
        General queries: <a href="mailto:info@aapsaathi.com" style={{ color: 'var(--accent)' }}>info@aapsaathi.com</a>.
      </p>

      <p className="text-[11px] mt-4 text-center" style={{ color: 'var(--ink-soft)' }}>
        v1.0 · May 2026 · Aangan from AapSaathi
      </p>
    </div>
  </div>
);

// =============================================================
// TERMS OF USE (short MVP version)
// =============================================================
const TermsScreen = ({ onBack }) => (
  <div className="flex-1 flex flex-col h-full overflow-hidden">
    <TopBar title="Terms of use" onBack={onBack} />
    <div className="flex-1 overflow-y-auto min-h-0 px-5 pt-5 sheet-bottom-safe" style={{ background: 'var(--bg)' }}>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
        By using Aangan you agree to be 18+, to follow the Community Guidelines, and to not misuse the platform for spam, scams, harassment, or illegal activity.
      </p>

      {/* Child safety callout — highlighted box for visibility. Mirrors the */}
      {/* child-safety section on the public terms.html page exactly. */}
      <div className="rounded-2xl p-4 mb-4" style={{ background: 'var(--accent-soft)', border: '1px solid var(--accent)' }}>
        <p className="text-[13px] font-medium mb-1" style={{ color: 'var(--accent)' }}>Child safety</p>
        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--ink)' }}>
          Aangan strictly prohibits child sexual abuse and exploitation material (CSAM), grooming behaviour, and any content that sexualises minors. Any account found violating this will be permanently banned and reported to the relevant authorities. Aangan is an 18+ platform.
        </p>
      </div>

      <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
        We provide Aangan "as-is" — we'll do our best to keep it running but can't guarantee uptime. Your account may be suspended for violations of our Community Guidelines.
      </p>
      <p className="text-[13px] leading-relaxed mb-4" style={{ color: 'var(--ink)' }}>
        Disputes are governed by the laws of India. For any concerns, contact us at <a href="mailto:info@aapsaathi.com" style={{ color: 'var(--accent)' }}>info@aapsaathi.com</a>.
      </p>
      <p className="text-[11px] mt-4 text-center" style={{ color: 'var(--ink-soft)' }}>
        v1.0 · May 2026 · Aangan from AapSaathi
      </p>
    </div>
  </div>
);

const TabBar = ({ active, onChange }) => {
  const tabs = [
    { key: 'home', label: 'Home', Icon: HomeIcon },
    { key: 'members', label: 'Members', Icon: Users },
    { key: 'profile', label: 'Profile', Icon: UserIcon },
  ];
  return (
    <div className="absolute bottom-0 left-0 right-0 flex items-center justify-around z-30 tabbar-safe"
         style={{ 
           background: 'var(--bg)', 
           borderTop: '1px solid var(--border)',
         }}>
      {tabs.map(({ key, label, Icon }) => (
        <button key={key} onClick={() => onChange(key)} className="flex flex-col items-center gap-0.5 px-6 py-2 flex-1">
          <Icon size={20} style={{ color: active === key ? 'var(--ink)' : 'var(--ink-soft)' }}
                fill={active === key ? 'var(--ink)' : 'none'} strokeWidth={1.5} />
          <span className="text-[10px]" style={{ color: active === key ? 'var(--ink)' : 'var(--ink-soft)' }}>{label}</span>
        </button>
      ))}
    </div>
  );
};

const FAB = ({ onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled}
    className="absolute z-20 w-14 h-14 rounded-full flex items-center justify-center active:scale-95 transition-transform fab-safe"
    style={{ right: 18,
             background: disabled ? 'var(--ink-soft)' : 'var(--accent)',
             boxShadow: disabled ? 'none' : '0 8px 20px rgba(196, 90, 63, 0.35)',
             opacity: disabled ? 0.6 : 1 }}>
    {disabled ? <Lock size={20} color="white" /> : <Plus size={26} color="white" strokeWidth={2.5} />}
  </button>
);

const Toast = ({ message, onDone }) => {
  React.useEffect(() => { const t = setTimeout(onDone, 2400); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-40 px-4 py-2.5 rounded-full flex items-center gap-2"
         style={{ background: 'var(--ink)', color: 'var(--bg)', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', maxWidth: '90%' }}>
      <span className="text-[12px]">{message}</span>
    </div>
  );
};

// =============================================================
// ROOT
// =============================================================
export default function App() {
  const [screen, setScreen] = useState(() => {
    // Path Z: lazy init from sessionStorage. This runs ONCE during the
    // first render — synchronously, before any useEffect or auth
    // listener fires. That makes it race-proof against watchAuth's
    // 'welcome' → 'home' transition (the guard there leaves anything
    // non-welcome/non-auth-* alone, but only if it sees the value).
    //
    // CRITICAL: skip restoration when V2 is about to render (URL has
    // ?v2=...). App re-mounts on every V1↔V2 URL transition, so this
    // initializer fires on V1→V2 too. Consuming the flag here would
    // burn it before V2 exits, leaving nothing for the V2→V1 return
    // trip to find. We only want to consume on the V2→V1 leg, when
    // V1 is actually about to render.
    if (typeof window !== 'undefined') {
      const v2Param = new URLSearchParams(window.location.search).get('v2');
      if (v2Param) return 'welcome';
    }
    try {
      const returnScreen = sessionStorage.getItem('v1_returnScreen');
      if (returnScreen === 'chats') {
        sessionStorage.removeItem('v1_returnScreen');
        return 'chats';
      }
    } catch (err) { /* storage disabled */ }
    return 'welcome';
  });
  const [tab, setTab] = useState('home');
  const [subTab, setSubTab] = useState('wall');

  // Multi-group state
  const [groups, setGroups] = useState({});
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [adminOf, setAdminOf] = useState([]); // group ids user created
  const [groupsLoaded, setGroupsLoaded] = useState(false); // true once initial group load completes

  const [currentUser, setCurrentUser] = useState(null);
  const v2Enabled = useV2Flag(currentUser);

  const [posts, setPosts] = useState([]);
  const [activePostId, setActivePostId] = useState(null);
  // Per-user like state: { postId: true/false }
  // Loaded asynchronously when posts arrive from Firestore.
  const [myLikes, setMyLikes] = useState({});
  // Cache of user profiles (UID → profile object) — loaded on demand from Firestore
  // Used by getUser() to display authors of posts, comments, chats, etc.
  const [userCache, setUserCache] = useState({});
  
  // Track which UIDs need fetching. Updated during render is safe (it's a ref-like ref-via-state pattern with effect drain).
  const pendingFetchesRef = useRef(new Set());
  
  // PURE reader — does NOT call setState during render.
  // If user not cached, returns a placeholder AND queues a fetch in a ref.
  // The fetch is drained by an effect (next tick).
  const getUser = (userId) => {
    if (!userId) return null;
    if (userId === 'me') return currentUser;
    if (userCache[userId]) return userCache[userId];
    // Queue for fetching (no setState during render)
    pendingFetchesRef.current.add(userId);
    return { id: userId, name: 'Loading…', initials: '·', color: '#999' };
  };
  
  // Drain pending user fetches after render completes.
  useEffect(() => {
    const pending = Array.from(pendingFetchesRef.current);
    if (pending.length === 0) return;
    pendingFetchesRef.current.clear();
    pending.forEach(userId => {
      // Skip if we already started loading
      if (userCache[userId]) return;
      // Mark as loading so duplicates don't re-fetch
      setUserCache(prev => prev[userId] ? prev : { ...prev, [userId]: { id: userId, name: 'Loading…', initials: '·', color: '#999', _loading: true } });
      // CRITICAL: handle BOTH success AND failure paths. If loadUserProfile
      // throws or rejects (network glitch, deleted user, permission denied),
      // the cache must still settle to a non-loading state — otherwise the
      // placeholder "Loading…" sticks forever and the report row shows
      // "Loading… reported for X" indefinitely. Same fallback used by both
      // resolve and reject paths so the UX is consistent.
      const fallback = { id: userId, name: 'Unknown user', initials: '?', color: '#999' };
      loadUserProfile(userId)
        .then(profile => {
          setUserCache(prev => ({
            ...prev,
            [userId]: profile || fallback,
          }));
        })
        .catch(err => {
          console.warn('User profile fetch failed for', userId, err);
          setUserCache(prev => ({
            ...prev,
            [userId]: fallback,
          }));
        });
    });
  });  // No dependency array — runs after every render to drain queue. Cheap because it bails early when queue is empty.
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [showStatusEditor, setShowStatusEditor] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showCalendarList, setShowCalendarList] = useState(false);
  // Invite link state — captured from URL on first load, processed after auth resolves
  const [pendingInviteCode, setPendingInviteCode] = useState(null);
  const [pendingInviteGroup, setPendingInviteGroup] = useState(null);
  const [inviteResolving, setInviteResolving] = useState(false);
  // ---- Phase 2.4 C8 — biz deep link state ----
  // pendingBusinessId: captured from /biz/CODE on first load; consumed by
  //   the resolution effect below. Behaves like pendingInviteCode but for
  //   businesses (auto-connect, no admin approval).
  // pendingBusinessForSuccess: resolved business doc held for the success
  //   screen to render. Set right before setScreen('biz-deep-link-success').
  // pendingBusinessAlreadyConnected: variant flag for the success screen
  //   — repeat clicks of the same biz link show "Already connected" copy
  //   instead of the celebratory "You're connected!" first-time copy.
  // groupInviteSuccessGroup: parallel state for the redesigned group
  //   success screen. processInviteJoin used to do setScreen('home') +
  //   toast; now it sets this group + screen to 'group-invite-success'.
  const [pendingBusinessId, setPendingBusinessId] = useState(null);
  // Phase 2.5 / C10c — Post deep link. When user arrives via
  // /biz/CODE/post/POSTID, both pendingBusinessId AND pendingPostId are
  // set. processBizDeepLink uses the presence of pendingPostId to route
  // to wall-post detail instead of biz-profile.
  const [pendingPostId, setPendingPostId] = useState(null);
  const [pendingBusinessForSuccess, setPendingBusinessForSuccess] = useState(null);
  const [pendingBusinessAlreadyConnected, setPendingBusinessAlreadyConnected] = useState(false);
  const [bizDeepLinkResolving, setBizDeepLinkResolving] = useState(false);
  // C8.2 — tracks whether the user arrived at /biz/CODE while unauthenticated.
  // When true, post-signup we show Screen F as the onboarding ceremony.
  // When false (user was already signed in when they clicked the link),
  // we silently auto-connect and route straight to the biz profile — no
  // intermediate confirmation screen. Set in the resolution effect when
  // we bounce to auth-options; cleared when Screen F is dismissed.
  const [bizDeepLinkArrivedUnauth, setBizDeepLinkArrivedUnauth] = useState(false);
  // C8.6 — true only when the biz effect saw the user as NOT onboarded
  // on its first run (i.e. signup happened AS PART of this deep-link
  // flow). Distinguishes "freshly-onboarded new user, deserves Screen
  // F celebratory ceremony" from "existing user who happened to be
  // logged out, just wants to land on the biz profile directly".
  const [bizDeepLinkTriggeredOnboarding, setBizDeepLinkTriggeredOnboarding] = useState(false);
  const [groupInviteSuccessGroup, setGroupInviteSuccessGroup] = useState(null);
  // C8.7 — Ref kept in lockstep with pendingBusinessId / pendingInviteCode
  // so that the watchAuth callback (declared with deps=[], stable closure)
  // can read the LATEST values. Without this, watchAuth's auto-advance
  // from 'welcome' → 'home' fires synchronously on session restore
  // BEFORE the React commit propagates pendingBusinessId into its
  // closure — and we end up on V1 home for any existing user clicking
  // a deep link while signed in.
  const pendingDeepLinkRef = useRef({ biz: null, invite: null, post: null });
  useEffect(() => {
    pendingDeepLinkRef.current = {
      biz: pendingBusinessId,
      invite: pendingInviteCode,
      post: pendingPostId,
    };
  }, [pendingBusinessId, pendingInviteCode, pendingPostId]);
  const [eventBusy, setEventBusy] = useState(false);
  const [eventsByGroup, setEventsByGroup] = useState({}); // { groupId: [events...] }
  const [activeMember, setActiveMember] = useState(null);
  const [isApproved, setIsApproved] = useState(true); // default: assume approved (Firestore rules enforce real approval)
  const [viaCode, setViaCode] = useState(false);
  const [toast, setToast] = useState(null);

  const [searchQuery, setSearchQuery] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [returnAfterJoin, setReturnAfterJoin] = useState(null); // tracks where to return after join-choice flow

  const [notifications, setNotifications] = useState([]);
  const unreadNotifs = notifications.filter(n => n.unread).length;

  // BATCH 3 — chats / requests / moderation / settings modals
  const [chats, setChats] = useState({});
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const [chatRequests, setChatRequests] = useState([]);
  const [sentChatRequests, setSentChatRequests] = useState([]); // userIds we've sent requests to
  const [activeChatOtherId, setActiveChatOtherId] = useState(null);
  const [activeChatMessages, setActiveChatMessages] = useState([]);
  const [olderMessages, setOlderMessages] = useState([]);
  // Path Z (Jun 16): customer-side BusinessConnect chats. Subscribes to
  // /orders where customerId == current user, deduped client-side by
  // businessId so the V1 ChatsList BusinessConnect tab shows one row
  // per business (latest order surfaced). businessCache hydrates name
  // + logo lazily on first sight, parallel to V1's userCache pattern.
  const [businessConnects, setBusinessConnects] = useState([]);
  const [businessConnectsLoaded, setBusinessConnectsLoaded] = useState(false);
  const [businessCache, setBusinessCache] = useState({});
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  // Real pending join requests from Firestore (per active group). 
  // Falls back to seed data only when not on a real Firestore group.
  // Real-time list of approved members in the active group
  const [groupMembers, setGroupMembers] = useState([]);
  const [pendingJoins, setPendingJoins] = useState([]);
  const [pendingJoinsLoading, setPendingJoinsLoading] = useState(false);
  const [reports, setReports] = useState([]);

  const [showChatRequest, setShowChatRequest] = useState(null); // member object
  const [showReport, setShowReport] = useState(null); // member object
  const [pendingWarning, setPendingWarning] = useState(null); // active admin warning, blocks app until acknowledged
  // Session C2b — users I have blocked. Live, because this list drives UI in
  // several places at once (Settings count, Blocked users screen, and from
  // step 3 the Block/Unblock label in chat + member menus). A stale "Block"
  // label on someone you just unblocked is the kind of small lie that makes a
  // safety feature feel untrustworthy. Empty array until auth resolves — NOT
  // null, so `.length` is always safe to read.
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [showRefer, setShowRefer] = useState(false);
  // Session A — V1 support chat. supportChatId is the deterministic chat doc
  // id returned by openOrCreateSupportChat; non-null means the modal is open.
  const [supportChatId, setSupportChatId] = useState(null);
  const [supportChatOpening, setSupportChatOpening] = useState(false);
  const [shareGroupInvite, setShareGroupInvite] = useState(null); // group object — opens share sheet
  const [showDelete, setShowDelete] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null); // post object pending delete confirmation

  // Time Capsule — real-time list of memories for the active group only
  // (we don't keep memories from other groups in memory; they're loaded fresh on switch)
  const [groupMemories, setGroupMemories] = useState([]);
  const [activeMemory, setActiveMemory] = useState(null);
  const [memoryToDelete, setMemoryToDelete] = useState(null); // memory pending delete-confirmation
  const [pendingMemoryFile, setPendingMemoryFile] = useState(null); // file staged after picker, awaiting caption
  const [memoryUploading, setMemoryUploading] = useState(false);
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null); // user object whose photo to show

  // === UNREAD CHAT DERIVATION (WhatsApp-style) ===
  // A chat is "unread" for me if:
  //   - It has a lastMessageAt timestamp (someone has messaged)
  //   - I have a lastReadAt[me] timestamp earlier than lastMessageAt
  //     (OR I have no lastReadAt[me] entry at all — never opened this chat)
  // We compute a map { chatKey -> true } so the list can render badges/bold styling.
  

  // Live group reference — uses memberCount directly from groups state,
  // which is kept in sync by the subscribeToGroupMembers effect above.
  // For the ACTIVE group, prefer the in-memory groupMembers length (fastest update).
  const rawGroup = groups[activeGroupId];
  const group = rawGroup ? {
    ...rawGroup,
    memberCount: groupMembers.length || rawGroup.memberCount || 1,
  } : null;
  // NOTE: groupsWithLiveCounts removed. The groups state itself is now the
  // single source of truth — counts are written directly into it by the
  // subscribeToGroupMembers effect. Just pass `groups` everywhere.
  
  // All posts for this group, with my-like state merged in
  const allGroupPosts = posts
    .filter(p => p.groupId === activeGroupId)
    .map(p => ({ ...p, likedByMe: myLikes[p.id] || false }));
  
  // Wall posts: posts without subTab='picks' (default to wall for legacy posts)
  const wallPosts = allGroupPosts.filter(p => (p.subTab || 'wall') === 'wall');
  // Word of Mouth posts
  const picksPosts = allGroupPosts.filter(p => p.subTab === 'picks');
  
  // The current visible feed depends on subTab
  const groupPosts = subTab === 'picks' ? picksPosts : wallPosts;
  
  
  // Time Capsule for the active group — sourced from Firestore subscription.
  // Format messages so they match what TimeCapsuleView expects.
  const groupTimeCapsule = groupMemories.map(m => ({
    id: m.id,
    photoUrl: m.photoUrl,
    label: m.label,
    addedById: m.addedById,
    addedAt: m.addedAt ? formatTimestamp(m.addedAt) : 'just now',
    aspect: m.aspect,
  }));

  // Stage 1: user picked a file. Validate limits/auth, then stage the file
  // and open the caption prompt. Upload doesn't happen yet — that's stage 2.
  const handleAddMemory = (file) => {
    if (!authUser?.uid || !activeGroupId) {
      setToast('Sign in and pick a batch first');
      return;
    }
    if (!isApproved) {
      setToast('🔒 Awaiting admin approval to add memories');
      return;
    }
    // V1 limit: 10 memories per group
    const MEMORY_LIMIT = 10;
    if (groupMemories.length >= MEMORY_LIMIT) {
      setToast(`Time Capsule is full (${MEMORY_LIMIT} memories max). Delete one to add another.`);
      return;
    }
    // Stage the file for the caption modal. Actual upload happens on confirm.
    setPendingMemoryFile(file);
  };

  // Stage 2: user typed a caption (or chose to skip) in AddMemoryCaptionModal.
  // Now we actually upload to Storage + write the Firestore doc.
  const handleConfirmAddMemory = async (caption) => {
    if (!pendingMemoryFile || !authUser?.uid || !activeGroupId) return;
    const file = pendingMemoryFile;
    setMemoryUploading(true);
    try {
      const memoryId = 'mem_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      const { url: photoUrl, aspect } = await uploadMemoryPhoto(activeGroupId, memoryId, file, authUser.uid);
      await addGroupMemory({
        groupId: activeGroupId,
        memoryId,
        addedById: authUser.uid,
        photoUrl,
        label: caption || 'Untitled memory',
        aspect,
      });
      setToast(caption ? `✓ "${caption}" saved` : '✓ Memory added to Time Capsule');
      setPendingMemoryFile(null); // close the caption modal
    } catch (err) {
      console.error('Failed to add memory:', err);
      setToast('Could not add memory — try again');
      // Keep the modal open so the user can retry without re-picking the file
    } finally {
      setMemoryUploading(false);
    }
  };

  // === Capture invite code from URL on first load ===
  // URLs like /join/CUG-6Z5C — extract the code, store it, clean the URL
  // Phase 2.4 C8 — also handles /biz/XXX-XXXX deep links to business profiles.
  // Both run on first mount only; both clean the URL to /'/' so a refresh
  // doesn't re-trigger. Group invites need admin approval (existing flow);
  // business deep links auto-connect (businesses don't require approval).
  //
  // C8.3 — biz ID regex relaxed to `[A-Za-z0-9-]{2,40}` (was {4,12} all
  // caps). The original assumed every business uses the XXX-XXXX 8-char
  // code, but seeded businesses with slug IDs ("anjali-sarees",
  // "sharma-sweets") legitimately exceed 12 chars AND are lowercase.
  // Don't toUpperCase() the captured value — Firestore doc IDs are
  // case-sensitive and uppercasing a slug ID would produce a 404.
  useEffect(() => {
    const path = window.location.pathname;
    const joinMatch = path.match(/^\/join\/([A-Z0-9-]{4,12})\/?$/i);
    if (joinMatch) {
      const code = joinMatch[1].toUpperCase();
      setPendingInviteCode(code);
      window.history.replaceState({}, '', '/');
      return;
    }
    // Phase 2.5 / C10c — /biz/CODE/post/POSTID deep link MUST be checked
    // BEFORE the bare /biz/CODE pattern below, since the bare regex would
    // also match the prefix of the longer URL and consume only the code.
    // POSTID format: Firestore-generated 20-char doc IDs, but Firestore
    // can technically produce 16-28 char IDs depending on collision retry,
    // so use a tolerant range. Same case sensitivity as /biz/.
    const bizPostMatch = path.match(/^\/biz\/([A-Za-z0-9-]{2,40})\/post\/([A-Za-z0-9_-]{12,40})\/?$/);
    if (bizPostMatch) {
      const code = bizPostMatch[1];
      const postIdFromUrl = bizPostMatch[2];
      setPendingBusinessId(code);
      setPendingPostId(postIdFromUrl);
      window.history.replaceState({}, '', '/');
      return;
    }
    const bizMatch = path.match(/^\/biz\/([A-Za-z0-9-]{2,40})\/?$/);
    if (bizMatch) {
      // Preserve case — biz IDs are stored exactly as the seed/registration
      // wrote them. 'anjali-sarees' is a different doc from 'ANJALI-SARES'.
      const code = bizMatch[1];
      setPendingBusinessId(code);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // === Real Firebase auth state (Phase 4) ===
  // PWA install prompt — Chrome/Android fires this when install is possible
  const [installPrompt, setInstallPrompt] = useState(null);
  // iOS standalone welcome banner — shown once on first launch of the
  // home-screen PWA when the user is signed out. iOS isolates PWA storage
  // from Safari, so users who logged in via browser must sign in again here.
  // This banner sets the expectation honestly. Dismissed via localStorage.
  const [showIOSWelcome, setShowIOSWelcome] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // ===========================================================
  // Status bar styling (native only)
  // ===========================================================
  // On Capacitor Android, the WebView renders edge-to-edge by default.
  // ===== STORAGE VERSION MIGRATION =====
  // Detects when the installed app version doesn't match the last-stored version
  // (e.g. fresh install where Android auto-restored old localStorage from cloud
  // backup, or update from V5 -> V6 -> V7 with breaking state changes).
  // Action: wipe stale local state and force a clean sign-out so the user lands
  // on the Welcome screen instead of seeing broken stale UI.
  useEffect(() => {
    const STORAGE_VERSION = '1.0.6';  // bump on every release with breaking state
    try {
      const stored = localStorage.getItem('aangan_storage_version');
      if (stored !== STORAGE_VERSION) {
        console.info(`[Aangan] Storage version mismatch (was ${stored}, now ${STORAGE_VERSION}) — clearing local state`);
        // Preserve nothing — full wipe.
        try { localStorage.clear(); } catch (e) {}
        try { sessionStorage.clear(); } catch (e) {}
        // Mark new version BEFORE reload (so we don't loop)
        try { localStorage.setItem('aangan_storage_version', STORAGE_VERSION); } catch (e) {}
        // Force fresh auth state — if the user has a restored Firebase token
        // from auto-backup, this clears it so they go back to Welcome.
        (async () => {
          try {
            const { signOut } = await import('firebase/auth');
            const { auth } = await import('./lib/firebase');
            await signOut(auth);
          } catch (e) {
            console.warn('signOut during version migration failed:', e);
          }
          // Force a clean reload so React state initializes fresh
          window.location.reload();
        })();
      }
    } catch (e) {
      // localStorage unavailable — proceed without migration
      console.warn('Version check failed:', e);
    }
  }, []);

  // We tell the system the bar should not overlay the WebView, and we
  // match the bar to Aangan's background color (cream) with dark icons.
  // Runs once on mount.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    (async () => {
      try {
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setStyle({ style: Style.Light }); // Light = dark icons (for light background)
        await StatusBar.setBackgroundColor({ color: '#F7F0E5' }); // matches Aangan cream/beige
      } catch (e) {
        console.warn('StatusBar init failed:', e);
      }
    })();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    
    // Detect if already installed (running in standalone mode)
    const checkInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
      setIsInstalled(isStandalone);
    };
    checkInstalled();
    
    // Also listen for app being installed during this session
    window.addEventListener('appinstalled', () => {
      setInstallPrompt(null);
      setIsInstalled(true);
    });
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // === iOS standalone welcome banner detection ===
  // Show on first launch of the iOS home-screen PWA. Decision factors:
  //   - Must be iOS (iPhone/iPad/iPod userAgent)
  //   - Must be in standalone mode (launched from home screen, not Safari)
  //   - User must not have dismissed it before (localStorage flag)
  // We don't gate on signed-in state here — the banner will hide itself
  // automatically when the user signs in, since it only renders on the
  // signed-out Welcome screen (see render condition below).
  useEffect(() => {
    try {
      const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
      const alreadyDismissed = localStorage.getItem('aangan_ios_welcome_dismissed') === '1';
      if (isIOS && isStandalone && !alreadyDismissed) {
        setShowIOSWelcome(true);
      }
    } catch (e) {
      // matchMedia / localStorage may be unavailable in rare contexts; silently skip
    }
  }, []);

  // Show the early-supporter thank you modal once per user.
  // Called after a user's first batch action (create or join).
  // Flag stored at /users/{uid}.celebratedAt — persistent across sessions.
  const maybeShowThankYou = async () => {
    if (!authUser?.uid) return;
    if (currentUser?.celebratedAt) return; // already seen
    
    setShowThankYou(true);
    
    // Persist to Firestore so we never show it again
    try {
      await updateUserProfile(authUser.uid, { celebratedAt: new Date().toISOString() });
      setCurrentUser(prev => ({ ...prev, celebratedAt: new Date().toISOString() }));
    } catch (e) {
      console.warn('Could not save celebrated flag:', e);
      // Even if save fails, still show the modal in this session
    }
  };
  const handleInstallApp = async () => {
    // iOS Safari: show instructions modal (no native prompt available)
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      setShowIOSInstall(true);
      return;
    }
    
    // Android Chrome / Desktop Chrome: trigger native prompt
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        setToast('Installing Aangan…');
      }
      setInstallPrompt(null); // can only fire once
    } else {
      // No prompt available — show generic instructions
      setShowGenericInstall(true);
    }
  };
  
  const [showIOSInstall, setShowIOSInstall] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [showGenericInstall, setShowGenericInstall] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [authUser, setAuthUser] = useState(undefined); // undefined=loading, null=signed-out, object=signed-in
  
  // Auto-popup install prompt when:
  // - User is signed in (don't bother visitors who haven't joined yet)
  // - App is NOT already installed (no point asking installed users)
  // - User hasn't dismissed the prompt in this browser before
  // Shown 4 seconds after the user reaches the home screen, so it doesn't
  // interrupt initial onboarding.
  useEffect(() => {
    if (!authUser?.uid) return;
    if (isInstalled) return;
    if (screen !== 'home') return;
    // Don't show PWA install prompt when running inside Capacitor native app —
    // user has already installed the real app, prompting again is confusing.
    if (window.Capacitor?.isNativePlatform?.()) return;
    
    const dismissedKey = `aangan_install_dismissed_${authUser.uid}`;
    if (localStorage.getItem(dismissedKey)) return;
    
    const timer = setTimeout(() => {
      setShowInstallModal(true);
    }, 4000);
    
    return () => clearTimeout(timer);
  }, [authUser?.uid, isInstalled, screen]);
  
// ===========================================================
  // Android hardware back button handling
  // ===========================================================
  // Closes modals first, then walks back through screens, then bottom tabs,
  // and finally exits the app only when nothing else is open. Without this
  // wiring, every back-button press would exit Aangan entirely.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    const sub = CapacitorApp.addListener('backButton', () => {
      // 1) Close any open overlays/modals first
      if (showInstallModal) return setShowInstallModal(false);
      if (showIOSInstall) return setShowIOSInstall(false);
      if (showGenericInstall) return setShowGenericInstall(false);
      if (showThankYou) return setShowThankYou(false);
      if (showDelete) return setShowDelete(false);
      if (showRefer) return setShowRefer(false);
      if (showReport) return setShowReport(null);
      if (showChatRequest) return setShowChatRequest(null);
      if (showCalendarList) return setShowCalendarList(false);
      if (showAddEvent) return setShowAddEvent(false);
      if (showStatusEditor) return setShowStatusEditor(false);
      if (showSwitcher) return setShowSwitcher(false);
      if (showGroupInfo) return setShowGroupInfo(false);
      if (activeMemory) return setActiveMemory(null);
      
      // 2) Screen-level back navigation (mirror existing back behaviors)
      if (screen === 'privacy') return setScreen('welcome');
      if (screen === 'auth-options') return setScreen('welcome');
      if (screen === 'auth-google') return setScreen('auth-options');
      if (screen === 'auth-phone') return setScreen('auth-options');
      if (screen === 'auth-email') return setScreen('auth-options');
      if (screen === 'chat') return setScreen('chats');
      if (screen === 'chats') return setScreen('home');
      
      // Settings and its sub-screens (Settings → home, sub-screens → Settings)
      if (screen === 'settings') { setScreen('home'); setTab('profile'); return; }
      if (screen === 'edit-profile') return setScreen('settings');
      if (screen === 'notif-prefs') return setScreen('settings');
      if (screen === 'privacy-data') return setScreen('settings');
      if (screen === 'privacy-data-from-profile') { setScreen('home'); setTab('profile'); return; }
      if (screen === 'blocked') return setScreen('settings');
      if (screen === 'help') return setScreen('settings');
      if (screen === 'community-rules') return setScreen('settings');
      if (screen === 'community-rules-from-profile') { setScreen('home'); setTab('profile'); return; }
      if (screen === 'privacy-policy') return setScreen('settings');
      if (screen === 'terms') return setScreen('settings');
      
      // Other screens
      if (screen === 'compose') return setScreen('home');
      if (screen === 'post') return setScreen('home');
      if (screen === 'notifications') return setScreen('home');
      if (screen === 'member-detail') { setScreen('home'); setTab('members'); return; }
      if (screen === 'connect-id') return setScreen('join-choice');
      if (screen === 'join-choice') return setScreen('home');
      if (screen === 'batch-details') return setScreen('matches');
      if (screen === 'matches') return setScreen('home');
      if (screen === 'match-confirm') return setScreen('matches');
      if (screen === 'profile-setup') return setScreen('privacy');
      
      // 3a) If on home but not on Wall sub-tab → back to Wall first.
      // This prevents Time Capsule / Word of Mouth from exiting the app —
      // user must explicitly land on Wall before back-pressing again to exit.
      if (tab === 'home' && subTab !== 'wall') return setSubTab('wall');
      
      // 3b) Tab-level back: any tab other than home → home
      if (tab !== 'home') return setTab('home');
      
      // 4) On home + wall sub-tab with nothing open → exit app
      CapacitorApp.exitApp();
    });
    
    return () => { sub.then(s => s.remove()); };
  }, [
    screen, tab, subTab,
    showInstallModal, showIOSInstall, showGenericInstall, showThankYou,
    showDelete, showRefer, showReport, showChatRequest,
    showCalendarList, showAddEvent, showStatusEditor, showSwitcher, showGroupInfo,
    activeMemory
  ]);

  const handleDismissInstallModal = () => {
    if (authUser?.uid) {
      localStorage.setItem(`aangan_install_dismissed_${authUser.uid}`, '1');
    }
    setShowInstallModal(false);
  };
  
  const handleInstallFromModal = async () => {
    setShowInstallModal(false);
    await handleInstallApp();
  };

  // === UNREAD CHAT DERIVATION (WhatsApp-style) ===
  // A chat is "unread" for me if:
  //   - It has a lastMessageAt timestamp (someone has messaged)
  //   - I have a lastReadAt[me] timestamp earlier than lastMessageAt
  //     (OR I have no lastReadAt[me] entry at all — never opened this chat)
  // We compute a map { chatKey -> true } so the list can render badges/bold styling.
  const unreadByChatId = React.useMemo(() => {
    const map = {};
    if (!authUser?.uid) return map;
    Object.entries(chats).forEach(([chatKey, chat]) => {
      const lastMsgAt = chat.lastMessageAt;
      if (!lastMsgAt) return;
      const lastMsgMs = lastMsgAt.toMillis ? lastMsgAt.toMillis() : lastMsgAt;
      const myReadAt = chat.lastReadAt?.[authUser.uid];
      const myReadMs = myReadAt
        ? (myReadAt.toMillis ? myReadAt.toMillis() : myReadAt)
        : 0;
      if (lastMsgMs > myReadMs) {
        map[chatKey] = true;
      }
    });
    return map;
  }, [chats, authUser?.uid]);
  
  // Count of unread chats (= conversations with new messages, WhatsApp-style).
  const unreadChatCount = Object.keys(unreadByChatId).length;
  
  // Chats-icon badge: ONLY unread chats. Admin items live on the Requests tab label.
  const unreadChats = unreadChatCount;
  
  // Admin-items count for the Requests tab label (pending joins + reports).
  // Only counts items if user is admin of the currently-active group.
  const adminItemsCount = adminOf.includes(activeGroupId)
    ? pendingJoins.length + reports.length
    : 0;

  // Listen for Firebase auth changes (sign-in, sign-out, page reload)
  useEffect(() => {
    return watchAuth(async (fbUser) => {
      setAuthUser(fbUser);
      
      if (!fbUser) {
        setCurrentUser(null);
        setGroupsLoaded(false);
        return;
      }
      
      // === Ask for notification permission on every sign-in ===
      // requestNotificationPermission handles all states:
      //   - 'granted' → fetches FCM token + saves to user's Firestore doc
      //   - 'denied' (browser-blocked) → returns gracefully, won't ask again
      //   - 'default' (user dismissed) → next sign-in will ask again
      //   - 'unsupported' (Safari < 16.4, in-app browser) → returns silently
      // We fire-and-forget — the rest of sign-in flow shouldn't wait on this.
      requestNotificationPermission(fbUser.uid).catch(err => {
        console.warn('Notification permission flow failed:', err);
      });
      
      // === Subscribe to foreground push notifications ===
      // When app is open and active, pushes don't auto-display as system notifications.
      // We show a toast so the user still gets a visual cue. The background SW
      // (firebase-messaging-sw.js) handles closed/backgrounded states automatically.
      onForegroundMessage((payload) => {
        const title = payload.data?.title || payload.notification?.title || 'Aangan';
        const body = payload.data?.body || payload.notification?.body || '';
        setToast(`💬 ${title}: ${body}`);
      });
      
      // Auto-advance from any auth screen
      // C8.7 — Skip the auto-advance when a deep link is pending. The
      // deep-link resolution effect (pendingBusinessId or
      // pendingInviteCode) drives routing in those cases. Reading from
      // pendingDeepLinkRef (not state) because this callback's closure
      // is created once at mount with deps=[] and never re-creates —
      // so closing over state would give us stale null values. The ref
      // is updated synchronously by a sibling effect every time the
      // pending state changes.
      const pdl = pendingDeepLinkRef.current;
      if (!pdl.biz && !pdl.invite) {
        setScreen(prev => (prev === 'welcome' || prev === 'auth-options' || prev === 'auth-google' || prev === 'auth-email' || prev === 'auth-phone') ? 'home' : prev);
      }
      
        // Set minimal currentUser from Firebase Auth immediately — UI no longer null.
        // We deliberately DO NOT use fbUser.displayName or fbUser.photoURL here,
        // because those could differ from the user's Firestore profile (e.g. user
        // updated their Google name but their Aangan profile should stay as-is).
        // Show a placeholder name + the actual Firestore data overlays moments later.
        //
        // IMPORTANT: watchAuth re-fires on token refresh (~every hour) AND on the
        // same user signing in twice in quick succession. If we ALWAYS reset to
        // "Loading…" we'll briefly flash the placeholder mid-session. So if we
        // already have a hydrated currentUser for the same uid, just keep it —
        // the upcoming loadWithRetry will refresh it in the background anyway.
        setCurrentUser(prev => {
          const sameUserAlreadyHydrated = prev && prev.id === fbUser.uid && prev.name && prev.name !== 'Loading…';
          if (sameUserAlreadyHydrated) {
            return prev;
          }
          return {
            id: fbUser.uid,
            name: prev?.name || 'Loading…',
            initials: prev?.initials || 'U',
            color: prev?.color || '#C45A3F',
            photoUrl: prev?.photoUrl || null,
            bio: prev?.bio || '',
            status: prev?.status || '',
            currentCity: prev?.currentCity || '',
            onboardedAt: prev?.onboardedAt || null,
          };
        });
      
      // Load Firestore data with retry for token-propagation issues
      const loadWithRetry = async () => {
        for (let attempt = 0; attempt < 5; attempt++) {
          try {
            const profile = await loadUserDoc(fbUser.uid);
            // Treat "profile exists but name is blank" as a partial/cold read
            // (same as profile being null) — retry instead of falling back to
            // fbUser.displayName, which would leak the user's Google name into
            // Aangan's UI. Aangan's name is set by the user in ProfileSetup /
            // Edit Profile; Google's displayName must never appear here.
            const hasUsableProfile = profile && (profile.name || '').trim().length > 0;
            if (hasUsableProfile) {
              setCurrentUser(prev => ({
                ...(prev || { id: fbUser.uid }),
                name: profile.name,
                photoUrl: profile.photoUrl || null,
                bio: profile.bio || '',
                status: profile.status || '',
                profession: profile.profession || '',
                currentCity: profile.currentCity || '',
                color: profile.color || prev?.color || '#C45A3F',
                initials: profile.name
                  ? profile.name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase().slice(0, 2)
                  : (prev?.initials || 'U'),
                onboardedAt: profile.onboardedAt || null,
              }));
            } else if (attempt < 4) {
              // Either profile is null OR profile.name is empty (cold/partial
              // Firestore read). Retry with increasing backoff. 4 attempts:
              // 600ms, 1200ms, 1800ms, 2400ms — total worst case ~6 seconds.
              await new Promise(r => setTimeout(r, 600 * (attempt + 1)));
              continue;
            } else if (profile) {
              // Final fallback: profile loaded but name is genuinely empty in
              // Firestore (very rare — implies a data corruption edge case).
              // Show "Member" as a safe placeholder rather than the Google name.
              setCurrentUser(prev => ({
                ...(prev || { id: fbUser.uid }),
                name: 'Member',
                photoUrl: profile.photoUrl || null,
                bio: profile.bio || '',
                status: profile.status || '',
                profession: profile.profession || '',
                currentCity: profile.currentCity || '',
                color: profile.color || prev?.color || '#C45A3F',
                initials: 'M',
                onboardedAt: profile.onboardedAt || null,
              }));
            }
            
            let myGroups = await loadUserGroups(fbUser.uid);
            
            // If we got 0 groups on first attempt, retry up to 2 more times — this catches
            // the case where the user's /users doc was just created or Firestore read
            // replicas haven't propagated yet. Without this, on first refresh the user
            // sees a "Welcome to Aangan, you haven't joined any batches" flash even
            // though they DO have batches. Doubles the wait each attempt: 800ms, 1600ms.
            if (myGroups.length === 0 && attempt < 2) {
              await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
              continue; // re-enter the for loop, which will try loadUserGroups again
            }
            
            // Self-heal feature disabled for V1 — Firestore collection group rule needs debugging.
            // For now, lost memberships are fixed manually via Firebase Console.
            // TODO: Re-enable in V2 after rules debug.
            
            if (myGroups.length > 0) {
              setGroups(prev => {
                const merged = { ...prev };
                myGroups.forEach(g => {
                  merged[g.id] = {
                    ...g,
                    unread: 0,
                    lastActive: 'just now',
                    emoji: g.emoji || BATCH_TYPES[g.type]?.emoji || '🏷️',
                    photoUrl: g.coverUrl || g.photoUrl || null,
                  };
                });
                return merged;
              });
              const myAdminGroupIds = myGroups
                .filter(g => g.adminIds?.includes(fbUser.uid))
                .map(g => g.id);
              if (myAdminGroupIds.length > 0) {
                setAdminOf(prev => Array.from(new Set([...prev, ...myAdminGroupIds])));
              }
              setActiveGroupId(prev => {
                // If no active group set, OR active group is a deleted seed group, pick the first real group
                if (!prev || !myGroups.find(g => g.id === prev)) {
                  return myGroups[0].id;
                }
                return prev;
              });
              // NOW mark groups as loaded — after activeGroupId is set, so empty-state doesn't flash
              setGroupsLoaded(true);
              
              // Check approval status of the active group from Firestore
              // (so isApproved reflects real state on refresh, not a stale default)
              const targetGroupId = (!activeGroupId || !myGroups.find(g => g.id === activeGroupId))
                ? myGroups[0].id 
                : activeGroupId;
              try {
                const membership = await getMyMembership(targetGroupId, fbUser.uid);
                setIsApproved(membership.isApproved);
                setViaCode(membership.viaCode);
                
                // If user was rejected from this group, clean up their local + Firestore state
                if (membership.status === 'rejected') {
                  console.log('User was rejected from active group — cleaning up');
                  setGroups(prev => {
                    const next = { ...prev };
                    delete next[targetGroupId];
                    return next;
                  });
                  // Pick another group to be active, or fall back to a seed group
                  const remaining = myGroups.filter(g => g.id !== targetGroupId);
                  if (remaining.length > 0) {
                    setActiveGroupId(remaining[0].id);
                  } else {
                    setActiveGroupId('g1'); // fall back to seed
                  }
                  setToast(`You're no longer a member of this group`);
                  // Clean up the user's memberOfGroupIds in Firestore (silent, fire-and-forget)
                  removeGroupFromUserMembership({ userId: fbUser.uid, groupId: targetGroupId })
                    .catch(e => console.warn('Cleanup failed:', e));
                }
              } catch (e) {
                console.warn('Could not check membership status:', e);
              }
            } else {
              // User has no groups — still need to mark loaded so the Welcome empty state shows
              setGroupsLoaded(true);
            }
            return;
          } catch (err) {
            if (err?.code === 'permission-denied' && attempt < 4) {
              const wait = (attempt + 1) * 800;
              console.warn(`Auth token not ready, retrying in ${wait}ms (attempt ${attempt + 1}/5)...`);
              await new Promise(r => setTimeout(r, wait));
            } else {
              console.error('Failed to load user data:', err);
              return;
            }
          }
        }
      };
      loadWithRetry();
    });
  }, []);
      
  // === Process pending invite code once auth + groups have loaded ===
  // Strategy: just navigate to auth flow if not signed in. After full onboarding
  // (auth → privacy → profile-setup → markOnboarded), the join-choice screen
  // checks for a pending invite and joins directly instead of asking user to find/create.
  useEffect(() => {
    if (!pendingInviteCode) return;
    if (authUser === undefined) return; // still loading auth
    if (!authUser) {
      // Not signed in yet — send to auth screen but keep the code so we resolve after sign-in
      setScreen('auth-options');
      return;
    }
    if (authUser && !currentUser) return; // signed in but profile not loaded yet — wait
    if (inviteResolving) return;
    
    (async () => {
      setInviteResolving(true);
      try {
        const found = await findGroupByConnectId(pendingInviteCode);
        if (!found) {
          setToast('Invite link is invalid or expired');
          setPendingInviteCode(null);
          setInviteResolving(false);
          return;
        }
        
        // Save the resolved group so the rest of the flow can use it
        setPendingInviteGroup(found);
        
        // Not signed in → start auth flow. We'll come back here after.
        if (!authUser) {
          setScreen('auth-options');
          setInviteResolving(false);
          return;
        }
        
        // Already a member? Silent navigation in.
        const myGroupIds = Object.keys(groups);
        if (myGroupIds.includes(found.id)) {
          setActiveGroupId(found.id);
          setScreen('home');
          setTab('home');
          setIsApproved(true);
          setPendingInviteCode(null);
          setPendingInviteGroup(null);
          setInviteResolving(false);
          return;
        }
        
        // Signed in but maybe not onboarded — fetch profile directly to avoid React state race.
        // If profile says they're onboarded, skip privacy/profile setup entirely.
        let isOnboarded = false;
        try {
          const freshProfile = await loadUserDoc(authUser.uid);
          isOnboarded = !!freshProfile?.onboardedAt;
        } catch (e) {
          console.warn('Could not check onboarding status:', e);
        }
        
        if (!isOnboarded) {
          setScreen('privacy');
          setInviteResolving(false);
          return;
        }
        
        // Signed in + onboarded + not yet a member → process the join now
        await processInviteJoin(found);
        setPendingInviteCode(null);
        setPendingInviteGroup(null);
        setInviteResolving(false);
      } catch (err) {
        console.error('Invite processing failed:', err);
        console.error('Error code:', err?.code);
        console.error('Error message:', err?.message);
        // If permission denied (likely because user signed out or session expired), 
        // keep the invite code and route to auth.
        if (err?.code === 'permission-denied') {
          setScreen('auth-options');
        } else {
          setToast(`Invite failed: ${err?.code || err?.message || 'unknown'}`);
          setPendingInviteCode(null);
          setPendingInviteGroup(null);
        }
        setInviteResolving(false);
      }
    })();
  }, [pendingInviteCode, authUser, currentUser, groups]);

  // ---- Item #13 Phase A — V1 NotificationsScreen subscription ----
  // The screen + bell badge UI were built but unwired (notifications
  // was useState([]) with no Firestore feed). This effect plugs that
  // in: subscribe to /notifications/{uid}/items ordered by createdAt
  // desc, limit 50, transform docs to the shape NotificationRow
  // expects (unread:bool, timestamp:string, ...denormalised fields).
  //
  // Both V1 server-side notification kinds ('warning', 'new-post',
  // 'auto-removed') and V2 kinds ('new-connect', 'new-post-biz',
  // 'order-status', 'business-opened') live in the same collection
  // and render through the same row component. The schemas overlap;
  // notifIcon() picks the right icon by kind.
  //
  // Limit 50 is generous given the 10-day retention from the cleanup
  // function (Phase A Step 7, not yet shipped). Once that lands the
  // collection size will stay well below 50 in steady state.
  //
  // Important: this effect MUST live after the authUser state
  // declaration (line ~7355) — earlier placement causes temporal-
  // dead-zone errors at render. Cluster with other auth-dependent
  // effects for readability.
  useEffect(() => {
    if (!authUser?.uid) {
      setNotifications([]);
      return;
    }
    const itemsRef = collection(db, 'notifications', authUser.uid, 'items');
    const q = query(itemsRef, orderBy('createdAt', 'desc'), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        // Item #13 Phase A — audience filter.
        // V1 NotificationsScreen renders the CUSTOMER inbox. Business-
        // audience notifications (e.g. "New order from X", "Customer
        // connected") belong in the MerchantApp bell — they don't
        // pollute the V1 home feed even when the same user is both a
        // customer AND a business owner (which happens for any test
        // account that connects to its own business). V1 legacy
        // notifications without an audience field (warning, new-post,
        // auto-removed) pass through because they predate the schema.
        .filter(d => {
          const aud = d.data().audience;
          return aud !== 'business';
        })
        .map(d => {
          const data = d.data();
          return {
            id: d.id,
            kind: data.kind || 'default',
            actorId: data.actorId || null,
            actorName: data.actorName || null,   // V2 denormalised — V1 docs won't have this
            actorPhoto: data.actorPhoto || null,
            text: data.text || '',
            subtext: data.subtext || '',
            groupId: data.groupId || null,
            businessId: data.businessId || null,
            postId: data.postId || null,
            orderId: data.orderId || null,
            deepLink: data.deepLink || null,
            // unread is derived from readAt — null/missing == unread.
            // Server writes readAt as a Timestamp when the user marks-read.
            unread: !data.readAt,
            // Pre-compute display string so NotificationRow stays a pure
            // render. The strings won't "tick" without a re-render, but
            // since notifications are dense + recent that's acceptable.
            timestamp: formatNotifTimestamp(data.createdAt),
          };
        });
      setNotifications(docs);
    }, (err) => {
      console.error('Notifications subscription failed:', err);
    });
    return () => unsub();
  }, [authUser?.uid]);

  // ---- Phase 2.4 C8 — biz deep link resolution effect ----
  // Mirrors the pendingInviteCode flow above but for businesses. Key
  // differences: (1) no admin approval — businesses auto-connect via
  // connectToBusiness(), (2) the success screen is the celebratory
  // "You're connected!" Screen F, not the "pending admin approval" one.
  //
  // Runs in two waves: when the user is NOT signed in, sends them to
  // auth-options and waits; the post-onboarding hook re-triggers this
  // effect once authUser + currentUser are set. When the user IS signed
  // in + onboarded, fetches the biz, calls connectToBusiness (idempotent),
  // and routes to the success screen.
  useEffect(() => {
    if (!pendingBusinessId) return;
    if (authUser === undefined) return; // still loading auth
    if (!authUser) {
      // Not signed in — send to auth, keep pendingBusinessId so we
      // resolve after sign-in completes. Mark this as an unauth arrival
      // so processBizDeepLink later shows Screen F as the celebratory
      // post-signup ceremony.
      setBizDeepLinkArrivedUnauth(true);
      setScreen('auth-options');
      return;
    }
    if (authUser && !currentUser) return; // signed in, profile loading
    // C8.7 — Also wait if currentUser is just the watchAuth placeholder
    // ({ name: 'Loading…', onboardedAt: null }). Running the biz effect
    // against the placeholder makes us see !isOnboarded for EVERY user
    // and incorrectly route existing users into the privacy gate. The
    // real Firestore profile lands via loadWithRetry within a few
    // hundred ms; we just need to wait for it.
    if (authUser && currentUser?.name === 'Loading…') return;
    if (bizDeepLinkResolving) return;

    (async () => {
      setBizDeepLinkResolving(true);
      try {
        const biz = await getBusiness(pendingBusinessId);
        if (!biz) {
          setToast('Business link is invalid or no longer available');
          setPendingBusinessId(null);
          setBizDeepLinkResolving(false);
          return;
        }
        // Check onboarding status the same way the invite flow does.
        // If the user just signed up via the auth flow, they may not
        // be fully onboarded yet (privacy + profile-setup) — let that
        // flow run, and we'll re-fire here when onboardedAt lands on
        // currentUser.
        let isOnboarded = false;
        try {
          isOnboarded = !!currentUser?.onboardedAt;
        } catch (e) { /* keep false */ }
        if (!isOnboarded) {
          // C8.6 — mark that this deep-link flow CAUSED the user to
          // onboard. When the effect re-fires post-onboarding, this
          // flag tells processBizDeepLink to show Screen F (the
          // celebratory ceremony for freshly-signed-up users). Without
          // it, we can't distinguish a brand-new user from an existing
          // user who simply logged out and re-logged-in via the link.
          setBizDeepLinkTriggeredOnboarding(true);
          setScreen('privacy');
          setBizDeepLinkResolving(false);
          return;
        }
        await processBizDeepLink(biz);
        setPendingBusinessId(null);
        setBizDeepLinkResolving(false);
      } catch (err) {
        console.error('Biz deep link resolution failed:', err);
        if (err?.code === 'permission-denied') {
          // Session expired or rules denied — back to auth.
          setScreen('auth-options');
        } else {
          setToast(`Couldn't load business: ${err?.code || err?.message || 'unknown'}`);
          setPendingBusinessId(null);
        }
        setBizDeepLinkResolving(false);
      }
    })();
  }, [pendingBusinessId, authUser, currentUser]);

  // Auto-connect the user to the business. The post-connect routing
  // depends on how the user arrived:
  //
  //   - Unauth arrival (clicked link while signed-out, completed signup
  //     in between) → show Screen F as the celebratory ceremony, then
  //     "Go to {biz}" routes onward
  //   - Already-signed-in arrival → silent auto-connect + route directly
  //     to the V2 biz profile via window.location.search. Skip Screen F
  //     entirely.
  //
  // C8.4 edge cases handled:
  //   1. OWNER clicks own /biz/CODE link → skip connect (Firestore rules
  //      deny connect-to-own-business). Just route to the biz profile;
  //      the merchant view will surface the "Manage this business"
  //      shortcut from there.
  //   2. connectToBusiness FAILS for any reason → swallow the error and
  //      still route to the biz profile. The user lands somewhere
  //      sensible and can retry connect from the profile UI. Dumping
  //      them on V1 home with a toast was the C8.3-and-earlier behavior
  //      and was what produced the "first click works, later clicks
  //      don't" symptom (since the toast scrolled past unnoticed).
  //   3. getBusiness throws (rules deny read, network) → re-thrown to
  //      outer catch, which still redirects to ?v2=biz-profile&id=X.
  //      V2App's BusinessProfile renders its own not-found state if
  //      the doc truly can't be loaded.
  const processBizDeepLink = async (biz) => {
    if (!authUser?.uid) return;

    const isOwner = biz.ownerId === authUser.uid;

    try {
      let alreadyConnected = false;
      if (!isOwner) {
        // Read existing connection doc to decide if we need to connect.
        // Owners skip this entirely — they're not in their own connect list.
        const connDocId = `${authUser.uid}_${biz.id}`;
        const { doc: fsDoc, getDoc } = await import('firebase/firestore');
        try {
          const connSnap = await getDoc(fsDoc(db, 'connections', connDocId));
          alreadyConnected = connSnap.exists() && connSnap.data()?.status === 'active';
        } catch (readErr) {
          // Connection read failure isn't fatal — assume not connected,
          // proceed to connect (which is itself resilient below).
          console.warn('Connection read failed (assuming not connected):', readErr);
        }

        if (!alreadyConnected) {
          try {
            await connectToBusiness(biz.id);
          } catch (connectErr) {
            // Connect failure shouldn't block the deep link. The biz
            // profile UI can surface its own connect button for retry.
            console.warn('Auto-connect failed (proceeding to biz profile):', connectErr);
          }
        }
      }

      if (bizDeepLinkArrivedUnauth && bizDeepLinkTriggeredOnboarding && !isOwner) {
        // C8.6 — Show Screen F ONLY for genuinely-new users who
        // completed signup as part of this deep-link flow. Existing
        // users who happened to be logged out (bizDeepLinkArrivedUnauth
        // is true but they were already onboarded when biz effect first
        // ran) skip the ceremony — they just want to land on the biz
        // profile, matching the signed-in path.
        setPendingBusinessForSuccess(biz);
        setPendingBusinessAlreadyConnected(alreadyConnected);
        setScreen('biz-deep-link-success');
        return;
      }

      // Silent path: signed-in non-owner OR owner. Surface a toast for
      // the new-connect case so the user gets feedback. Owners just go
      // straight to their merchant-view-of-the-profile.
      if (!isOwner && !alreadyConnected) {
        setToast(`Connected to ${biz.name}`);
      }
      // Phase 2.5 / C10c — if user came via /biz/CODE/post/POSTID, route
      // to the wall-post detail screen instead of the biz profile.
      // pendingPostId is consumed (cleared) before navigation; the
      // detail screen reads postId from the v2= URL params.
      const targetPostId = pendingPostId;
      setPendingBusinessId(null);
      setPendingPostId(null);
      if (typeof window !== 'undefined') {
        if (targetPostId) {
          window.location.search =
            `?v2=wall-post&id=${encodeURIComponent(biz.id)}&postId=${encodeURIComponent(targetPostId)}`;
        } else {
          window.location.search = `?v2=biz-profile&id=${encodeURIComponent(biz.id)}`;
        }
      }
    } catch (err) {
      // Last-resort fallback. Whatever went wrong, still try to land
      // the user somewhere sensible. The V2 biz profile renders its
      // own loading/not-found states, so worst case the user sees a
      // clear error in context (instead of mysteriously landing on V1
      // home with a toast).
      console.error('processBizDeepLink failed (falling through to biz profile):', err);
      const targetPostId = pendingPostId;
      setPendingBusinessId(null);
      setPendingPostId(null);
      if (typeof window !== 'undefined') {
        if (targetPostId) {
          window.location.search =
            `?v2=wall-post&id=${encodeURIComponent(biz.id)}&postId=${encodeURIComponent(targetPostId)}`;
        } else {
          window.location.search = `?v2=biz-profile&id=${encodeURIComponent(biz.id)}`;
        }
      }
    }
  };

  const processInviteJoin = async (group) => {
    if (!authUser?.uid) return;
    
    // Business batches auto-approve. Other types need admin approval.
    const isBusinessBatch = group.type === 'Business Connect';
    
    try {
  // Step 1: Write member doc (always pending — admin approves manually)
  const result = await joinGroupAsPendingMember({
    groupId: group.id,
    userId: authUser.uid,
  });
  
  // If user is already a member (e.g., admin/creator opening their own invite link),
  // skip the rest and just navigate them in.
  if (result?.alreadyMember) {
    setGroups(prev => ({
      ...prev,
      [group.id]: {
        ...group,
        unread: 0,
        lastActive: 'just now',
        emoji: group.emoji || BATCH_TYPES[group.type]?.emoji || '🏷️',
      },
    }));
    setActiveGroupId(group.id);
    setIsApproved(true);
    setScreen('home');
    setTab('home');
    setToast(`You're already in ${group.name}`);
    return;
  }
      
      // Step 2: Add group to user's memberOfGroupIds (with retry for timing race)
      try {
        await addGroupToUserMembership({
          userId: authUser.uid,
          groupId: group.id,
        });
      } catch (e) {
        console.warn('User doc update failed, retrying...', e);
        await new Promise(r => setTimeout(r, 600));
        await addGroupToUserMembership({
          userId: authUser.uid,
          groupId: group.id,
        });
      }
      
      // Update local state — group appears in batches list
      setGroups(prev => ({
        ...prev,
        [group.id]: {
          ...group,
          unread: 0,
          lastActive: 'just now',
          emoji: group.emoji || BATCH_TYPES[group.type]?.emoji || '🏷️',
        },
      }));
      setActiveGroupId(group.id);
      setIsApproved(false); // pending until admin approves// business → fully approved; others → pending
      // Phase 2.4 C8 — route to redesigned success screen instead of
      // setScreen('home') + toast. The screen reads groupInviteSuccessGroup
      // and handles its own onGotIt / onBrowseBusinesses CTAs. Toast
      // is kept only for the re-request case, since the screen copy is
      // the same regardless of first/re-request and the re-request
      // distinction matters for the user.
      setGroupInviteSuccessGroup(group);
      setScreen('group-invite-success');

      const isReRequest = result?.reRequest === true;
      if (isReRequest) {
        setToast(`Request resent to admin of ${group.name}`);
      }
      setIsApproved(false); // not yet approved — pending banner shows
      // Show the early-supporter thank you (once per user)
      maybeShowThankYou();
    } catch (err) {
      console.error('Join request failed:', err);
      if (err?.code === 'permission-denied' || err?.message?.includes('insufficient permissions')) {
        setToast('Could not send request. Try refreshing the page.');
      } else {
        setToast('Could not send join request — try again');
      }
    }
  };
  // === Subscribe to user's chat list from Firestore in real-time ===
  // Whenever the OTHER user sends you a message, the chat doc's lastMessageAt
  // updates and this subscription pushes the new state to the UI immediately.
  // Re-runs when authUser changes (sign in/out).
  useEffect(() => {
    if (!authUser?.uid) {
      setChats({});
      setChatsLoaded(false);
      return;
    }
    
    const unsub = subscribeToUserChats(authUser.uid, (list) => {
      // Convert array to keyed-by-otherId map for the UI's existing shape.
      // Skip chats the user has cleared (where lastMessageAt < their clearedAt).
      const chatMap = {};
      list.forEach(chat => {
        const otherId = chat.participants.find(p => p !== authUser.uid);
        if (!otherId) return;
        
        // Hide chat if user cleared it AND no new messages have arrived since
        const myClearedAt = chat.clearedAt?.[authUser.uid];
        if (myClearedAt && chat.lastMessageAt) {
          const clearedMs = myClearedAt.toMillis ? myClearedAt.toMillis() : myClearedAt;
          const lastMs = chat.lastMessageAt.toMillis ? chat.lastMessageAt.toMillis() : chat.lastMessageAt;
          if (lastMs <= clearedMs) return; // user cleared, no new messages — skip
        }
        
        chatMap['chat_' + otherId] = {
          id: chat.id,
          otherId,
          chatId: chat.id,
          acceptedAt: chat.acceptedAt,
          lastMessage: chat.lastMessage || '',
          lastMessageAt: chat.lastMessageAt,
          lastReadAt: chat.lastReadAt || {},
          clearedAt: chat.clearedAt || {},
          messages: [],
        };
      });
      setChats(chatMap);
      setChatsLoaded(true);
    });
    
    return () => unsub();
  }, [authUser?.uid]);

  // === Path Z: Subscribe to my orders (customer side) ===
  // Customer-facing equivalent of MerchantApp's orders subscription.
  // Powers the V1 ChatsList BusinessConnect tab. Limited to 50 most
  // recent so old order history doesn't bloat the read budget on
  // every chat-list visit (50 docs/visit cap). Customers with more
  // than 50 past orders won't see the oldest in BusinessConnect;
  // they're still reachable via the business profile anyway.
  useEffect(() => {
    if (!authUser?.uid) {
      setBusinessConnects([]);
      setBusinessConnectsLoaded(false);
      return undefined;
    }
    const q = query(
      collection(db, 'orders'),
      where('customerId', '==', authUser.uid),
      orderBy('placedAt', 'desc'),
      limit(50),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const orders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setBusinessConnects(orders);
        setBusinessConnectsLoaded(true);
      },
      (err) => {
        console.warn('[App] customer orders subscription failed:', err);
        setBusinessConnects([]);
        setBusinessConnectsLoaded(true);
      },
    );
    return () => unsub();
  }, [authUser?.uid]);

  // === Path Z: Hydrate businessCache for new businessIds ===
  // When orders update with a businessId not yet in cache, fetch the
  // business doc once. Fire-and-forget — failures fall through to a
  // placeholder display. Parallel to MerchantApp's customerCache
  // pattern.
  useEffect(() => {
    const toFetch = new Set();
    businessConnects.forEach(o => {
      if (o.businessId && !(o.businessId in businessCache)) {
        toFetch.add(o.businessId);
      }
    });
    if (toFetch.size === 0) return undefined;
    let cancelled = false;
    (async () => {
      const updates = {};
      await Promise.all(Array.from(toFetch).map(async (bizId) => {
        try {
          const snap = await getDoc(doc(db, 'businesses', bizId));
          if (snap.exists()) {
            const d = snap.data();
            updates[bizId] = {
              id: bizId,
              name: d.name || 'Business',
              logo: d.logo || '🏪',
              category: d.category || 'other',
              locality: d.locality || '',
              city: d.city || '',
              verified: !!d.verified,
              status: d.status,
            };
          } else {
            updates[bizId] = {
              id: bizId,
              name: '(business removed)',
              logo: '🏪',
              category: 'other',
              locality: '',
              city: '',
              verified: false,
              status: 'suspended',
            };
          }
        } catch (err) {
          console.warn('[App] business fetch failed for', bizId, err);
        }
      }));
      if (!cancelled && Object.keys(updates).length > 0) {
        setBusinessCache(prev => ({ ...prev, ...updates }));
      }
    })();
    return () => { cancelled = true; };
  }, [businessConnects, businessCache]);

  // === Path Z: V1 ChatsList tab restoration is in ChatsList's
  // useState lazy init (reads sessionStorage v1_chatTab). The
  // screen-level restoration (v1_returnScreen) is in the lazy init
  // of `screen` above — both run synchronously on first render so
  // there's no race with watchAuth's welcome → home transition.

  // === Subscribe to LAST 10 messages of the active chat ===
  // Only runs when a chat is open. Unsubscribes when chat closes.
  // Cost: 10 reads on open + 1 read per new message in that chat.
  useEffect(() => {
    if (!activeChatOtherId || !authUser?.uid) {
      setActiveChatMessages([]);
      setOlderMessages([]);
      setHasMoreMessages(false);
      return;
    }
    
    // Compute chatId deterministically — doesn't need chats state
    const chatId = makeChatId(authUser.uid, activeChatOtherId);
    
    // Reset older messages when switching chats
    setOlderMessages([]);
    setHasMoreMessages(false);
    
    // Read clearedAt once from current chats state. We deliberately don't
    // depend on `chats` because that would resubscribe on every state update,
    // causing an infinite loop. clearedAt is set once when chat is cleared,
    // and the effect resubscribes when activeChatOtherId changes anyway.
    const chatRecord = chats['chat_' + activeChatOtherId];
    const myClearedAt = chatRecord?.clearedAt?.[authUser.uid] || null;
    
    const unsub = subscribeToLatestMessages(chatId, 10, (messages) => {
      setActiveChatMessages(messages);
      // If we got 10 messages, there are likely older ones
      setHasMoreMessages(messages.length === 10);
      
      // Auto-mark-read: if I'm sitting in this chat and a new message arrives
      // from the other person, mark the chat as read so it doesn't show as unread.
      // Optimistically update local state + sync to Firestore.
      const lastMsg = messages[messages.length - 1];
      if (lastMsg && lastMsg.fromId !== authUser.uid) {
        const chatKey = 'chat_' + activeChatOtherId;
        const now = Date.now();
        setChats(prev => {
          if (!prev[chatKey]) return prev;
          return {
            ...prev,
            [chatKey]: {
              ...prev[chatKey],
              lastReadAt: {
                ...(prev[chatKey]?.lastReadAt || {}),
                [authUser.uid]: { toMillis: () => now }
              }
            }
          };
        });
        markChatRead({ chatId, userId: authUser.uid });
      }
    }, myClearedAt);
    return () => unsub();
  }, [activeChatOtherId, authUser?.uid]);
  
  // === Subscribe to memories in the active group (real-time Time Capsule) ===
  // Cost: ~10 reads per group switch (cap of 10 memories per group).
  useEffect(() => {
    if (!activeGroupId || !authUser?.uid) {
      setGroupMemories([]);
      return;
    }
    const unsub = subscribeToGroupMemories(activeGroupId, (memories) => {
      setGroupMemories(memories);
    });
    return () => unsub();
  }, [activeGroupId, authUser]);
  // === Subscribe to members of the active group (real-time) ===
  // So the Members tab shows the actual batchmate list, including the current user.
  useEffect(() => {
    if (!activeGroupId || !authUser?.uid) {
      setGroupMembers([]);
      return;
    }
    const unsub = subscribeToGroupMembers(activeGroupId, (members) => {
      setGroupMembers(members);
    });
    return () => unsub();
  }, [activeGroupId, authUser]);

  // === Subscribe to OPEN reports for the active group (admin only) ===
  // Reports surface in the Requests tab (ChatsList) where admins triage them.
  // Per Community Guidelines #6: reports go to batch admins first.
  // We derive admin status from `adminOf` inline here because the named
  // const `isAdminOfActiveGroup` is declared much later in this file
  // (Rule 19/31 TDZ class of bug). Firestore rules block this query for
  // non-admins, so we gate the subscription to avoid permission-denied noise.
  // Cost: ~1 read per open report on first load + 1 per change. Negligible.
  useEffect(() => {
    if (!activeGroupId || !authUser?.uid) {
      setReports([]);
      return;
    }
    const amIAdmin = adminOf.includes(activeGroupId);
    if (!amIAdmin) {
      setReports([]);
      return;
    }
    const unsub = subscribeToGroupReports(activeGroupId, (openReports) => {
      setReports(openReports);
    });
    return () => unsub();
  }, [activeGroupId, authUser, adminOf]);

  // === Subscribe to my own pendingWarning ===
  // If an admin issues a warning via the Reports queue, the warned user's
  // /users/{uid}.pendingWarning field gets populated. We subscribe so the
  // blocking acknowledgment modal appears immediately (mid-session) AND on
  // next app load. The user must tap "I understand" — which clears the
  // field via acknowledgePendingWarning — before continuing.
  useEffect(() => {
    if (!authUser?.uid) {
      setPendingWarning(null);
      return;
    }
    const unsub = subscribeToPendingWarning(authUser.uid, (warning) => {
      setPendingWarning(warning);
    });
    return () => unsub();
  }, [authUser]);

  // === Session C2b: subscribe to the users I have blocked ===
  // Cost is negligible — blocked lists are single digits for almost everyone,
  // so this is one tiny subscription for the whole session. Clears on sign-out
  // so a second account on the same device never inherits the first one's list.
  useEffect(() => {
    if (!authUser?.uid) {
      setBlockedUsers([]);
      return;
    }
    const unsub = subscribeToBlockedUsers(authUser.uid, (list) => {
      setBlockedUsers(list || []);
    });
    return () => unsub();
  }, [authUser]);

  // === Session C2b: block / unblock handlers ===
  // Defined once here and passed down, so every entry point (Settings,
  // member profile, chat menu) writes through the same path and the live
  // subscription above updates all of them at once.
  const handleBlockUser = async (user) => {
    if (!authUser?.uid || !user?.id) return;
    if (user.id === authUser.uid) return;   // rules reject this too; don't bother the server
    try {
      await blockUser({
        blockerId: authUser.uid,
        blockedId: user.id,
        name: user.name || '',
        photoUrl: user.photoUrl || null,
      });
      setToast(`${(user.name || 'They').split(' ')[0]} is blocked`);
    } catch (err) {
      console.error('Block failed:', err);
      setToast('Could not block — try again');
    }
  };

  const handleUnblockUser = async (user) => {
    if (!authUser?.uid || !user?.id) return;
    try {
      await unblockUser({ blockerId: authUser.uid, blockedId: user.id });
      setToast(`${(user.name || 'They').split(' ')[0]} is unblocked`);
    } catch (err) {
      console.error('Unblock failed:', err);
      setToast('Could not unblock — try again');
    }
  };

  // Is this uid on my block list? Reads the live state, so callers re-render
  // when it changes rather than holding a stale answer.
  const isBlocked = (uid) => blockedUsers.some((b) => b.id === uid);

  // === Subscribe to member counts for ALL of the user's batches ===
  // Writes live counts DIRECTLY onto the groups state. Single source of truth.
  // ALSO: derives adminOf from member subcollection's isAdmin field. This is
  // the canonical truth (the Members tab admin badge uses this), so even if
  // group.adminIds is stale/missing/out-of-sync, admin detection still works.
  // Cost: ~1 subscription per batch the user is in. For 10 batches with 50 members
  // each, that's ~500 reads on first load, then 1 read per change. Acceptable.
  useEffect(() => {
    if (!authUser?.uid) return;
    const groupIds = Object.keys(groups);
    if (groupIds.length === 0) return;
    
    const unsubs = groupIds.map(gid => {
      return subscribeToGroupMembers(gid, (members) => {
        // Update memberCount on the groups state
        setGroups(prev => {
          if (!prev[gid]) return prev;
          if (prev[gid].memberCount === members.length) return prev;
          return {
            ...prev,
            [gid]: { ...prev[gid], memberCount: members.length },
          };
        });
        
        // Derive admin status from member subcollection (canonical truth).
        // If I appear in this group's members with isAdmin: true, add this
        // group to adminOf. If I'm not admin, remove it.
        const meInMembers = members.find(m => m.id === authUser.uid);
        const iAmAdminHere = !!meInMembers?.isAdmin;
        setAdminOf(prev => {
          const alreadyIn = prev.includes(gid);
          if (iAmAdminHere && !alreadyIn) {
            return [...prev, gid];
          }
          if (!iAmAdminHere && alreadyIn) {
            return prev.filter(id => id !== gid);
          }
          return prev;
        });
      });
    });
    
    return () => unsubs.forEach(u => u && u());
  }, [authUser?.uid, Object.keys(groups).sort().join(',')]);
  // === Subscribe to posts in the active group (real-time) ===
  // When group changes, unsubscribe from old, subscribe to new.
  // Posts auto-update when anyone in the group adds/edits/deletes.
  useEffect(() => {
    if (!activeGroupId || !authUser?.uid) return;
    
    const unsub = subscribeToGroupPosts(activeGroupId, 30, (firestorePosts) => {
      // Map Firestore posts to the format our UI expects
      const formatted = firestorePosts.map(p => ({
        id: p.id,
        groupId: activeGroupId,
        authorId: p.authorId,
        text: p.text || '',
        photo: p.photo || null,
        subTab: p.subTab || 'wall',           // NEW: which tab this post belongs to
        category: p.category || null,         // NEW: optional category tag
        likes: p.likeCount || 0,
        likedByMe: false,
        comments: [],
        commentCount: p.commentCount || 0,
        createdAt: p.createdAt,
        timestamp: formatTimestamp(p.createdAt),
      }));
      // Replace posts for this group only — keep posts from other groups untouched
      setPosts(prev => {
        const otherGroups = prev.filter(p => p.groupId !== activeGroupId);
        return [...formatted, ...otherGroups];
      });
    });
    
    return () => unsub();
  }, [activeGroupId, authUser]);
  // === Load per-user "did I like this?" state when posts arrive ===
  // Runs after subscription delivers posts. Cheap: 1 read per post we don't already have a like state for.
  useEffect(() => {
    if (!authUser?.uid) return;
    const groupPostsList = posts.filter(p => p.groupId === activeGroupId);
    if (groupPostsList.length === 0) return;
    
    // Only check posts we haven't fetched the like state for yet
    const toCheck = groupPostsList.filter(p => !(p.id in myLikes));
    if (toCheck.length === 0) return;
    
    (async () => {
      const updates = {};
      for (const post of toCheck) {
        try {
          const liked = await hasUserLikedPost({ groupId: activeGroupId, postId: post.id, userId: authUser.uid });
          updates[post.id] = liked;
        } catch (e) {
          updates[post.id] = false;
        }
      }
      setMyLikes(prev => ({ ...prev, ...updates }));
    })();
  }, [posts, activeGroupId, authUser]);
// === Live subscription to pending join requests for the active group ===
  // Live so that B2 (onJoinRequest) notifications land in the Chats→Requests
  // tab immediately, rather than requiring a group-switch or hard refresh to
  // re-run the (previously) one-shot getPendingMembers fetch. Same guards as
  // before: bail unless authed + a group is active + we're an admin of it +
  // the group is real (not a seed like g1/g2/g3).
  useEffect(() => {
    if (!authUser?.uid || !activeGroupId) return;
    if (!adminOf.includes(activeGroupId)) {
      setPendingJoins([]); // not admin of this group → nothing to show
      return;
    }
    if (activeGroupId.startsWith('g') && activeGroupId.length <= 3) {
      setPendingJoins([]); // seed group, no Firestore data
      return;
    }

    const unsub = subscribeToPendingMembers(activeGroupId, (list) => {
      setPendingJoins(list);
    });
    return () => unsub();
  }, [authUser, activeGroupId, adminOf]);

  // === Load events for the active batch (calendar + auto-cards on Wall) ===
  useEffect(() => {
    if (!authUser?.uid || !activeGroupId) return;
    
    if (eventsByGroup[activeGroupId]) return;
    (async () => {
      try {
        const events = await getGroupEvents(activeGroupId);
        setEventsByGroup(prev => ({ ...prev, [activeGroupId]: events }));
      } catch (err) {
        // Permission denied is expected if user isn't an approved member yet
        if (err?.code !== 'permission-denied') {
          console.error('Failed to load events for group:', err);
        }
      }
    })();
  }, [authUser, activeGroupId]);


  // === Handlers ===
  const handleLike = async (id) => {
    if (!authUser?.uid || !activeGroupId) return;
    
    // Optimistic UI update — feels instant
    const wasLiked = myLikes[id] || false;
    setMyLikes(prev => ({ ...prev, [id]: !wasLiked }));
    setPosts(prev => prev.map(p => p.id === id ? {
      ...p, likes: wasLiked ? Math.max(0, p.likes - 1) : p.likes + 1,
    } : p));
    
    try {
      await togglePostLike({ groupId: activeGroupId, postId: id, userId: authUser.uid });
    } catch (err) {
      console.error('Failed to toggle like:', err);
      // Revert optimistic update on failure
      setMyLikes(prev => ({ ...prev, [id]: wasLiked }));
      setPosts(prev => prev.map(p => p.id === id ? {
        ...p, likes: wasLiked ? p.likes + 1 : Math.max(0, p.likes - 1),
      } : p));
      setToast('Could not save like — try again');
    }
  };

  const handleComment = async (postId, text) => {
    if (!authUser?.uid || !activeGroupId) {
      setToast('Sign in first');
      return;
    }
    if (!text?.trim()) return;
    
    try {
      await addPostComment({
        groupId: activeGroupId,
        postId,
        authorId: authUser.uid,
        text: text.trim(),
      });
      // Subscription will pick it up — no manual state update needed
      // Update commentCount on the local post object so the count badge updates immediately
      setPosts(prev => prev.map(p => p.id === postId 
        ? { ...p, commentCount: (p.commentCount || 0) + 1 } 
        : p
      ));
    } catch (err) {
      console.error('Failed to add comment:', err);
      setToast('Could not post comment — try again');
    }
  };

  const handleNewPost = async ({ id, text, photo, subTab: postSubTab, category }) => {
    if (!authUser?.uid || !activeGroupId) {
      setToast('Sign in and pick a batch first');
      return;
    }
    
    // Navigate back to wherever they were composing from
    setScreen('home'); 
    setTab('home'); 
    setSubTab(postSubTab || 'wall');
    
    try {
      await createPost({
        groupId: activeGroupId,
        authorId: authUser.uid,
        text,
        photo,
        postId: id,
        subTab: postSubTab || 'wall',
        category: category || null,
      });
      // Photo counter is now incremented server-side by the onPhotoUpload Cloud Function
      // when the photo actually lands in Storage. No client-side bump needed.
      // The subscription will pick up the new post and add it to the feed automatically.
      setToast('✓ Posted');
    } catch (err) {
      console.error('Failed to create post:', err);
      setToast('Could not post — try again');
    }
  };

  const handleBlocked = () => setToast('🔒 Awaiting admin approval to interact');
  const [checkingStatus, setCheckingStatus] = useState(false);
  
  const handleCheckStatus = async () => {
    if (!authUser?.uid || !activeGroupId) return;
    
    setCheckingStatus(true);
    try {
      const membership = await getMyMembership(activeGroupId, authUser.uid);
      if (membership.status === 'rejected') {
        // Got rejected while waiting — clean up and bail
        setGroups(prev => {
          const next = { ...prev };
          delete next[activeGroupId];
          return next;
        });
        setActiveGroupId('g1');
        setToast(`Your request was declined`);
        removeGroupFromUserMembership({ userId: authUser.uid, groupId: activeGroupId })
          .catch(e => console.warn('Cleanup failed:', e));
      } else if (membership.isApproved) {
        setIsApproved(true);
        setToast('✓ You are approved! Welcome.');
      } else {
        setToast('Still waiting on admin approval');
      }
    } catch (err) {
      console.error('Status check failed:', err);
      setToast('Could not check status — try again');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleCopy = (text) => {
    // Clipboard API often fails in sandboxed iframes; try it then fall back to a textarea trick.
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text);
        copied = true;
      }
    } catch (e) { /* ignore */ }
    if (!copied) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        copied = true;
      } catch (e) { /* ignore */ }
    }
    setToast(copied ? `Copied ${text}` : `Long-press to copy: ${text}`);
  };

  const handleSharePost = async (post) => {
    if (!group?.connectId) {
      setToast('Share link unavailable');
      return;
    }
    const url = `${AANGAN_SHARE_BASE}/join/${group.connectId}`;
    const message = `Check out this post on Aangan from ${group.name}: ${url}`;
    
    try {
      if (navigator.share) {
        await navigator.share({ title: group.name, text: message, url });
      } else {
        await navigator.clipboard.writeText(url);
        setToast('Invite link copied');
      }
    } catch (err) {
      // User cancelled share — silent. Other errors:
      if (err.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          setToast('Invite link copied');
        } catch (e) {
          setToast('Could not share — try copy from group info');
        }
      }
    }
  };
  const handleShare = (batch) => {
    // Opens the WhatsApp/SMS/Email/More share sheet for this specific batch.
    // Reuses ReferFriendModal with batch-specific invite text and URL.
    setShareGroupInvite(batch);
  };
  const handleEditStatus = async (newStatus) => {
  setCurrentUser(prev => ({ ...prev, status: newStatus }));
  setShowStatusEditor(false);
  setToast(newStatus ? '✓ Status updated' : '✓ Status cleared');
  // Persist to Firestore
  try {
    if (authUser?.uid) {
      await updateUserProfile(authUser.uid, { status: newStatus });
    }
  } catch (err) {
    console.error('Failed to save status:', err);
    setToast('Could not save status — try again');
  }
};

  const handleSwitchGroup = async (gid) => {
    setActiveGroupId(gid);
    setGroups(prev => ({ ...prev, [gid]: { ...prev[gid], unread: 0, lastActive: 'just now' } }));
    setSubTab('wall');
    setTab('home');
    setScreen('home');
    
    // Check real approval status from Firestore for this group
    if (authUser?.uid) {
      // Real Firestore group — check actual membership
      try {
        const membership = await getMyMembership(gid, authUser.uid);
        if (membership.status === 'rejected') {
          // Rejected — remove group and bail
          setGroups(prev => {
            const next = { ...prev };
            delete next[gid];
            return next;
          });
          setActiveGroupId('g1');
          setToast(`You're no longer a member of this group`);
          removeGroupFromUserMembership({ userId: authUser.uid, groupId: gid })
            .catch(e => console.warn('Cleanup failed:', e));
          return;
        }
        setIsApproved(membership.isApproved);
        setViaCode(membership.viaCode);
      } catch (e) {
        console.warn('Could not check membership:', e);
        setIsApproved(false);
      }
    } 
    
    setToast(`Switched to ${groups[gid].name}`);
  };

 const handleAddEvent = async (eventData) => {
    if (!authUser?.uid || !activeGroupId) {
      setToast('Sign in and pick a batch first');
      return;
    }
    setEventBusy(true);
    try {
      const newEvent = await addGroupEvent({
        groupId: activeGroupId,
        creatorId: authUser.uid,
        ...eventData,
      });
      // Add to local cache so the new event shows up immediately
      setEventsByGroup(prev => ({
        ...prev,
        [activeGroupId]: [...(prev[activeGroupId] || []), newEvent],
      }));
      setShowAddEvent(false);
      setEventBusy(false);
      setToast('✓ Added to calendar');
    } catch (err) {
      console.error('Failed to add event:', err);
      setEventBusy(false);
      setToast('Could not add event — try again');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!activeGroupId) return;
    try {
      await deleteGroupEvent({ groupId: activeGroupId, eventId });
      setEventsByGroup(prev => ({
        ...prev,
        [activeGroupId]: (prev[activeGroupId] || []).filter(e => e.id !== eventId),
      }));
      setToast('Event removed');
    } catch (err) {
      console.error('Failed to delete event:', err);
      setToast('Could not delete event');
    }
  };

  const handleGroupPhotoUpload = (dataUrl) => {
    setGroups(prev => ({ ...prev, [activeGroupId]: { ...prev[activeGroupId], photoUrl: dataUrl } }));
    setToast('✓ Group photo updated');
  };
  const handleUpdateGroupInfo = async (updates) => {
    // Optimistic UI update — feels instant
    setGroups(prev => ({
      ...prev,
      [activeGroupId]: { ...prev[activeGroupId], ...updates },
    }));
    setToast('✓ Saved');
    // Persist to Firestore (async, non-blocking)
    try {
      await updateGroupInfo(activeGroupId, updates);
    } catch (err) {
      console.error('Failed to update group info:', err);
      setToast('Could not save — try again');
    }
  };

  const handleMarkAllRead = async () => {
    if (!authUser?.uid) return;
    const unreadIds = notifications.filter(n => n.unread).map(n => n.id);
    if (unreadIds.length === 0) return;

    setToast('All caught up');

    // Item #13 Phase A — persist via writeBatch. The limit-50 query
    // means we're well under writeBatch's 500-op cap. The onSnapshot
    // subscription will reflect the updated readAt values on the
    // next tick — no need to optimistically update local state.
    const batch = writeBatch(db);
    unreadIds.forEach(id => {
      batch.update(doc(db, 'notifications', authUser.uid, 'items', id), {
        readAt: serverTimestamp(),
      });
    });
    try {
      await batch.commit();
    } catch (err) {
      console.error('mark-all-read failed:', err);
      setToast('Could not mark all read');
    }
  };

  const handleNotifTap = async (notif) => {
    // Item #13 Phase A — persist the read state in Firestore. Done
    // before navigation so the round-trip starts in parallel with
    // the user's perceived "tap → navigate" transition.
    if (notif.unread && authUser?.uid) {
      try {
        await updateDoc(
          doc(db, 'notifications', authUser.uid, 'items', notif.id),
          { readAt: serverTimestamp() }
        );
      } catch (err) {
        console.warn('mark-read failed for', notif.id, err);
        // Non-fatal — proceed with navigation regardless. The bell
        // badge will catch up on next snapshot.
      }
    }

    // Item #13 Phase B polish — resolve postId once. Top-level per
    // the locked Phase A schema; deepLink.postId is a defensive fallback
    // in case any trigger stashed it there. Only new-comment / new-post /
    // like carry a postId; other kinds (approval, new-member, etc.) leave
    // this null and fall through to the group-home behaviour below.
    const targetPostId = notif.postId || notif.deepLink?.postId || null;

    // V1 group notifications — route to the specific post when the
    // notification carries a postId; otherwise fall back to group home.
    // Covers two previously-broken paths:
    //   (a) different group + postId (old code switched but ignored postId)
    //   (b) same group + postId (old code dropped through to setScreen('home'))
    if (notif.groupId) {
      if (notif.groupId !== activeGroupId) {
        // Fire-and-forget: handleSwitchGroup's synchronous setState calls
        // (including its own setScreen('home')) batch with ours below;
        // React commits once with the last setScreen winning.
        handleSwitchGroup(notif.groupId);
      }
      if (targetPostId) {
        setActivePostId(targetPostId);
        setScreen('post');
      } else if (notif.groupId === activeGroupId) {
        // Same-group, no postId (e.g. approval, new-member) — close the
        // notifications overlay onto the current group's home.
        setScreen('home');
      }
      return;
    }

    // V2 deep links — push a URL change so the V2 router picks it up
    // on the next render. window.location.search assignment triggers
    // a navigation (full reload) which is acceptable since the V1
    // notification screen and the V2 destination live in separate
    // app shells in this codebase.
    if (notif.deepLink?.screen) {
      const { screen, businessId } = notif.deepLink;
      if (screen === 'merchant-connects') {
        window.location.search = '?v2=merchant';
        return;
      }
      if (screen === 'biz-profile' && businessId) {
        window.location.search = `?v2=biz-profile&id=${encodeURIComponent(businessId)}`;
        return;
      }
    }

    // Default fallback — just close the notifications screen.
    setScreen('home');
  };

  // === BATCH 3 handlers ===
  const memberChatState = (memberId) => {
    if (chats['chat_' + memberId]) return 'active';
    return 'none'; // never expose 'pending' to the requester
  };

  const handleSendChatRequest = async (member, message) => {
    if (!authUser?.uid || !member?.id) return;
    
    setShowChatRequest(null);
    
    try {
      // V1: Auto-create chat on first message — skip the request/accept gate.
      // (Request/accept flow deferred to V2.)
      const chatId = makeChatId(authUser.uid, member.id);
      await sendChatMessage({
        chatId,
        fromId: authUser.uid,
        toId: member.id,
        text: message,
      });
      
      // Add to local chat list and open the chat
      setChats(prev => ({
        ...prev,
        ['chat_' + member.id]: {
          id: chatId,
          chatId,
          otherId: member.id,
          lastMessage: message,
          lastMessageAt: { toMillis: () => Date.now() },
        },
      }));
      
      setActiveChatOtherId(member.id);
      setScreen('chat');
      setToast('Chat started');
    } catch (err) {
      console.error('Failed to start chat:', err);
      setToast('Could not send message — try again');
    }
  };

  // tap-protect: only open the request modal if no request has been sent yet
  const handleOpenChatRequest = (member) => {
    if (sentChatRequests.includes(member.id)) {
      setToast(`Already requested — waiting for ${member.name.split(' ')[0]} to respond`);
      return;
    }
    setShowChatRequest(member);
  };

  const handleAcceptRequest = (reqId) => {
    const req = chatRequests.find(r => r.id === reqId);
    if (!req) return;
    setChats(prev => ({
      ...prev,
      ['chat_' + req.fromId]: {
        id: 'chat_' + req.fromId, otherId: req.fromId, acceptedAt: Date.now(),
        messages: [{ id: 'm_' + Date.now(), fromId: req.fromId, text: req.message, sentAt: req.sentAt }],
      },
    }));
    setChatRequests(prev => prev.filter(r => r.id !== reqId));
    setToast('Chat accepted — say hi!');
  };

  const handleDeclineRequest = (reqId) => {
    setChatRequests(prev => prev.filter(r => r.id !== reqId));
    setToast('Request declined');
  };

  const handleSendMessage = async (otherId, text) => {
    if (!authUser?.uid || !otherId || !text?.trim()) return;
    
    const chatId = makeChatId(authUser.uid, otherId);
    
    try {
      await sendChatMessage({
        chatId,
        fromId: authUser.uid,
        toId: otherId,
        text: text.trim(),
      });
      
      // Make sure this chat appears in the list (in case it didn't exist yet)
      setChats(prev => ({
        ...prev,
        ['chat_' + otherId]: {
          ...(prev['chat_' + otherId] || {}),
          id: chatId,
          chatId,
          otherId,
          lastMessage: text.trim(),
          lastMessageAt: { toMillis: () => Date.now() }, // placeholder until next list reload
        },
      }));
    } catch (err) {
      console.error('Send message failed:', err);
      setToast('Could not send message — try again');
    }
  };
  const handleLoadOlderMessages = async () => {
    if (!activeChatOtherId || !authUser?.uid || loadingOlderMessages) return;
    
    const chatId = makeChatId(authUser.uid, activeChatOtherId);
    
    // Find the oldest message we currently have. With chat-style ordering
    // (oldest at top), the first item is the oldest.
    const allMessages = [...olderMessages, ...activeChatMessages];
    if (allMessages.length === 0) return;
    
    const oldestTimestamp = allMessages[0].sentAt;
    if (!oldestTimestamp) return;
    
    setLoadingOlderMessages(true);
    try {
      const { messages, hasMore } = await loadOlderMessages({
        chatId,
        beforeTimestamp: oldestTimestamp,
        pageSize: 10,
      });
      
      // Prepend older messages to the top of olderMessages array
      setOlderMessages(prev => [...messages, ...prev]);
      setHasMoreMessages(hasMore);
    } catch (err) {
      console.error('Failed to load older messages:', err);
      setToast('Could not load older messages');
    } finally {
      setLoadingOlderMessages(false);
    }
  };

  const handleDeleteChat = async (otherId) => {
    if (!authUser?.uid) return;
    
    const chatId = makeChatId(authUser.uid, otherId);
    
    // Optimistic UI: remove from local state first
    setChats(prev => {
      const next = { ...prev };
      delete next['chat_' + otherId];
      return next;
    });
    setActiveChatOtherId(null);
    setScreen('chats');
    setToast('Chat cleared from your side');
    
    // Personal deletion — only clears for current user. Other side still sees it.
    try {
      await clearChatForUser({ chatId, userId: authUser.uid });
    } catch (err) {
      console.error('Clear chat failed:', err);
      // No need to revert — UI already moved on
    }
  };

  // Session A — open (or reuse) this member's support chat with the Aangan
  // team. Idempotent: makeChatId is deterministic, so a member who is also a
  // business owner lands in the SAME thread they use from Manage Business.
  //
  // source:'v1' is only persisted on CREATE — it never downgrades a chat that
  // was already opened from the merchant surface, which would hide the admin's
  // Aangan Connect plan-card picker. See openOrCreateSupportChat in db.js.
  const handleOpenSupportChat = async () => {
    if (supportChatOpening || !authUser?.uid) return;
    setSupportChatOpening(true);
    try {
      const { chatId } = await openOrCreateSupportChat({
        ownerUid: authUser.uid,
        ownerName: currentUser?.name || '',
        source: 'v1',
      });
      setSupportChatId(chatId);
    } catch (err) {
      console.error('Could not open support chat:', err);
      setToast('Could not open support chat — try again');
    } finally {
      setSupportChatOpening(false);
    }
  };

  const handleSubmitReport = async ({ reportedId, reason, detail }) => {
    if (!authUser?.uid || !activeGroupId || !reportedId) {
      setToast('Could not submit report — try again');
      return;
    }
    
    // Optimistically close the modal — feedback feels instant
    setShowReport(null);
    
    try {
      const result = await submitReport({
        groupId: activeGroupId,
        reporterId: authUser.uid,
        reportedId,
        reason,
        detail: detail || '',
      });
      
      if (result.duplicate) {
        setToast('Already reported — admins are reviewing.');
        return;
      }
      
      setToast('Reported. Admins will review within 24 hours.');
    } catch (err) {
      console.error('Submit report failed:', err);
      setToast('Could not submit report — try again');
    }
  };
// Load pending join requests from Firestore for the active group.
  // Called when admin opens the Moderation queue.
  const loadPendingJoins = async () => {
    if (!activeGroupId) return;
    // Skip seed groups (g1, g2, g3) — they don't exist in Firestore
    if (activeGroupId.startsWith('g') && activeGroupId.length <= 3) {
      setPendingJoins([]);
      return;
    }
    setPendingJoinsLoading(true);
    try {
      const list = await getPendingMembers(activeGroupId);
      setPendingJoins(list);
    } catch (err) {
      console.error('Failed to load pending joins:', err);
      setToast('Could not load pending requests');
      setPendingJoins([]);
    } finally {
      setPendingJoinsLoading(false);
    }
  };
  const handleApproveJoin = async (userId) => {
    const pj = pendingJoins.find(p => p.userId === userId);
    if (!pj) return;
    
    // Optimistic UI: remove from pending list immediately
    setPendingJoins(prev => prev.filter(p => p.userId !== userId));
    setGroups(prev => ({ 
      ...prev, 
      [activeGroupId]: { 
        ...prev[activeGroupId],
        memberCount: (prev[activeGroupId].memberCount || 1) + 1 
      } 
    }));
    setToast(`✓ Approved ${pj.name?.split(' ')[0] || 'member'}`);
    
    // Persist to Firestore
    try {
      await approvePendingMember({ groupId: activeGroupId, userId });
    } catch (err) {
      console.error('Failed to approve member:', err);
      setToast('Could not approve — try again');
      // Revert optimistic update on failure
      setPendingJoins(prev => [pj, ...prev]);
    }
  };

  const handleDeclineJoin = async (userId) => {
    const pj = pendingJoins.find(p => p.userId === userId);
    if (!pj) return;
    
    // Optimistic UI
    setPendingJoins(prev => prev.filter(p => p.userId !== userId));
    setToast('Request declined');
    
    // Persist to Firestore
    try {
      await rejectPendingMember({ groupId: activeGroupId, userId });
    } catch (err) {
      console.error('Failed to reject member:', err);
      setToast('Could not decline — try again');
      // Revert
      setPendingJoins(prev => [pj, ...prev]);
    }
  };
const handleDeletePost = (post) => {
    setPostToDelete(post);
  };

  // Memory delete flow: tile/viewer → opens confirm modal → confirm calls
  // deleteGroupMemory which removes the Firestore doc + Storage file.
  // Permission check is at the call site (canDeleteMemory below) so the
  // delete button only renders for the author or a group admin.
  const handleDeleteMemory = (memory) => {
    setMemoryToDelete(memory);
  };
  const handleConfirmDeleteMemory = async () => {
    if (!memoryToDelete || !activeGroupId) return;
    const memory = memoryToDelete;
    setMemoryToDelete(null);
    // Close the viewer modal too if it's open on this memory
    setActiveMemory(prev => (prev?.id === memory.id ? null : prev));
    try {
      await deleteGroupMemory({
        groupId: activeGroupId,
        memoryId: memory.id,
        photoUrl: memory.photoUrl,
      });
      setToast('Memory deleted');
    } catch (err) {
      console.error('Delete memory failed:', err);
      setToast('Could not delete memory — try again');
    }
  };
const handleLoadOlderComments = async () => {
    if (!activePostId || !activeGroupId || loadingOlderComments) return;
    
    // Find the oldest comment we currently have. With newest-first ordering,
    // the oldest is the LAST item in the combined list.
    const allCurrentComments = [...activePostComments, ...olderComments];
    if (allCurrentComments.length === 0) return;
    
    const oldestTimestamp = allCurrentComments[allCurrentComments.length - 1].createdAt;
    if (!oldestTimestamp) return;
    
    setLoadingOlderComments(true);
    try {
      const { comments, hasMore } = await loadOlderComments({
        groupId: activeGroupId,
        postId: activePostId,
        beforeTimestamp: oldestTimestamp,
        pageSize: 10,
      });
      
      const formatted = comments.map(c => ({
        id: c.id,
        authorId: c.authorId,
        text: c.text,
        timestamp: formatTimestamp(c.createdAt),
        createdAt: c.createdAt,
        editedAt: c.editedAt || null,
      }));
      
      // Append older comments at the bottom (since newest is on top)
      setOlderComments(prev => [...prev, ...formatted]);
      setHasMoreComments(hasMore);
    } catch (err) {
      console.error('Failed to load older comments:', err);
      setToast('Could not load older comments');
    } finally {
      setLoadingOlderComments(false);
    }
  };
  const handleConfirmDeletePost = async () => {
    if (!postToDelete || !activeGroupId) return;
    const post = postToDelete;
    setPostToDelete(null);
    
    // Optimistic UI: remove the post immediately
    setPosts(prev => prev.filter(p => p.id !== post.id));
    
    try {
      await deletePost({ 
        groupId: activeGroupId, 
        postId: post.id,
        photoUrl: post.photo?.url || null,
      });
      setToast('✓ Post deleted');
    } catch (err) {
      console.error('Failed to delete post:', err);
      setToast('Could not delete — try again');
      // Revert optimistic update on failure: add post back
      setPosts(prev => [post, ...prev]);
    }
  };
  const handleResolveReport = async (rId, action) => {
    if (!authUser?.uid) return;
    
    // Find the report in local state so we have groupId + reportedId
    const report = reports.find(r => r.id === rId);
    if (!report) return;
    
    // Optimistic UI: remove from local list immediately. The subscription
    // will reconfirm shortly (the doc will no longer be status='open').
    setReports(prev => prev.filter(r => r.id !== rId));
    
    const successMsg = action === 'dismiss' ? 'Report dismissed' :
                       action === 'warn' ? 'Member warned' : 'Member removed from group';
    
    try {
      // 1. Update the report doc (status + reviewer audit fields)
      await resolveReport({
        reportId: rId,
        reviewerId: authUser.uid,
        action,
        groupId: report.groupId,
        reportedId: report.reportedId,
      });
      
      // 2. For 'remove' action, actually remove the member from the group.
      // resolveReport intentionally does NOT do this — it's the caller's
      // job, so audit trail and action are decoupled.
      if (action === 'remove' && report.groupId && report.reportedId) {
        try {
          await removeGroupMember({ groupId: report.groupId, userId: report.reportedId });
        } catch (removeErr) {
          console.error('Resolve marked as removed but removeGroupMember failed:', removeErr);
          setToast('Report resolved but member removal failed — try removing manually from Members tab');
          return;
        }
      }
      
      setToast(successMsg);
    } catch (err) {
      console.error('Resolve report failed:', err);
      // Roll back optimistic removal — re-fetch will sort it out, but show error now
      setToast('Could not resolve report — try again');
    }
  };

  const handleMakeAdmin = async (member) => {
    if (!authUser?.uid || !activeGroupId || !member?.id) return;
    
    // Optimistic UI: update local groupMembers so admin badge shows immediately
    setGroupMembers(prev => prev.map(m => m.id === member.id ? { ...m, isAdmin: true } : m));
    setToast(`✓ ${member.name.split(' ')[0]} is now an admin`);
    setScreen('home');
    setTab('members');
    
    // Persist to Firestore
    try {
      await makeGroupAdmin({ groupId: activeGroupId, userId: member.id });
      // adminOf locally — if this admin promotes me, I'd need to update adminOf,
      // but since this UI requires you to BE admin to use it, we don't promote ourselves.
    } catch (err) {
      console.error('Make admin failed:', err);
      setToast('Could not promote — try again');
      // Revert
      setGroupMembers(prev => prev.map(m => m.id === member.id ? { ...m, isAdmin: false } : m));
    }
  };

  const handleRemoveMember = async (member) => {
    if (!authUser?.uid || !activeGroupId || !member?.id) return;
    
    // Confirm before destructive action
    if (!window.confirm(`Remove ${member.name} from ${group.name}? They'll lose access immediately.`)) {
      return;
    }
    
    // Optimistic UI: remove from local list
    setGroupMembers(prev => prev.filter(m => m.id !== member.id));
    setToast(`${member.name.split(' ')[0]} removed from group`);
    setScreen('home');
    setTab('members');
    
    // Persist to Firestore
    try {
      await removeGroupMember({ groupId: activeGroupId, userId: member.id });
    } catch (err) {
      console.error('Remove member failed:', err);
      setToast('Could not remove — try again');
      // Revert: add back to list
      setGroupMembers(prev => [...prev, member]);
    }
  };
const handleLeaveGroup = async () => {
    if (!authUser?.uid || !activeGroupId || !group) return;
    
    // Block if user is the only admin
    if (isAdminOfActiveGroup) {
      try {
        const onlyAdmin = await isOnlyAdmin({ groupId: activeGroupId, userId: authUser.uid });
        if (onlyAdmin) {
          setToast(`You're the only admin. Make someone else an admin first.`);
          return;
        }
      } catch (e) { /* fall through */ }
    }
    
    // Capture name before we drop state
    const groupName = group.name;
    const groupId = activeGroupId;
    
    // Optimistic UI: drop locally
    setGroups(prev => {
      const next = { ...prev };
      delete next[groupId];
      return next;
    });
    setAdminOf(prev => prev.filter(id => id !== groupId));
    
    // Switch to another group if user has more, else go to empty state.
    // Track the destination so we can refresh isApproved / viaCode for it
    // — without this refresh, those state values still reflect the group we
    // just left, which (when the departed group was pending) leaked its
    // "Awaiting admin approval" banner into whichever remaining group we
    // landed on. Mirrors handleSwitchGroup's pattern for consistency.
    const remaining = Object.keys(groups).filter(id => id !== groupId);
    const nextGroupId = remaining.length > 0 ? remaining[0] : null;
    setActiveGroupId(nextGroupId);
    if (!nextGroupId) {
      // Empty state — no meaningful membership. Defensive reset so nothing
      // downstream reads stale flags from the departed group.
      setIsApproved(false);
      setViaCode(false);
    }
    
    setShowGroupInfo(false);
    setTab('home');
    setScreen('home');
    setToast(`You left ${groupName}`);
    
    // Refresh membership for the new group in parallel with the Firestore
    // leaveGroup write below. Fire-and-forget so we don't extend the
    // perceived latency of the leave action; the banner just corrects
    // itself on the next render (~200ms). Same error posture as
    // handleSwitchGroup: default to isApproved=false on failure.
    if (nextGroupId && authUser?.uid) {
      getMyMembership(nextGroupId, authUser.uid)
        .then(membership => {
          setIsApproved(membership.isApproved);
          setViaCode(membership.viaCode);
        })
        .catch(e => {
          console.warn('Could not refresh membership after leave:', e);
          setIsApproved(false);
          setViaCode(false);
        });
    }
    
    // Persist to Firestore
    try {
      await leaveGroup({ groupId, userId: authUser.uid });
    } catch (err) {
      console.error('Leave group failed:', err);
      setToast('Could not leave — try again');
    }
  };
  const handleLogout = async () => {
    try { await firebaseLogout(); } catch (e) { console.warn('Logout failed:', e); }
    setScreen('welcome');
    setCurrentUser(null);
    setTab('home');
    setSubTab('wall');
    setIsApproved(false);
    setViaCode(false);
    setGroups({});
    setActiveGroupId(null);
    setAdminOf([]);
    setNotifications([]);
    setChats({});
    setChatRequests([]);
    setSentChatRequests([]);
    setPendingJoins([]);
    setReports([]);
    setEventsByGroup({});
  };

  const handleAccountDelete = async () => {
    setShowDelete(false);
    try { localStorage.removeItem('aangan_onboarded'); } catch (e) { /* ignore */ }
    try { await firebaseLogout(); } catch (e) { console.warn('Logout failed:', e); }
    setScreen('welcome');
    setCurrentUser(null);
    setTab('home');
    setSubTab('wall');
    setIsApproved(false);
    setViaCode(false);
    setGroups({});
    setActiveGroupId(null);
    setAdminOf([]);
    setNotifications([]);
    setChats({});
    setChatRequests([]);
    setSentChatRequests([]);
    setPendingJoins([]);
    setReports([]);
    setToast('Account scheduled for deletion in 14 days');
  };

  const activePost = activePostId ? posts.find(p => p.id === activePostId) : null;
  // Per-post comment state — populated when user opens a post detail
  const [activePostComments, setActivePostComments] = useState([]);
  // Older comments loaded via "View earlier" button (separate from subscription)
  const [olderComments, setOlderComments] = useState([]);
  const [hasMoreComments, setHasMoreComments] = useState(false);
  const [loadingOlderComments, setLoadingOlderComments] = useState(false);
  
  // Subscribe to comments on the active post (real-time updates)
  useEffect(() => {
    if (!activePostId || !activeGroupId) {
      setActivePostComments([]);
      setOlderComments([]);
      setHasMoreComments(false);
      return;
    }
    // Reset older comments when switching posts
    setOlderComments([]);
    setHasMoreComments(false);
    const unsub = subscribeToPostComments(activeGroupId, activePostId, 10, (comments) => {
      // Map Firestore comments to UI format
      const formatted = comments.map(c => ({
        id: c.id,
        authorId: c.authorId,
        text: c.text,
        timestamp: formatTimestamp(c.createdAt),
        createdAt: c.createdAt,
        editedAt: c.editedAt || null,
      }));
      setActivePostComments(formatted);
      // If we got exactly 10 comments back, there might be more older ones
      // (When fewer than 10, post has fewer total — no "earlier" button needed)
      setHasMoreComments(comments.length === 10);
    });
    return () => unsub();
  }, [activePostId, activeGroupId]);
  const isAdminOfActiveGroup = adminOf.includes(activeGroupId);
  const activeChat = activeChatOtherId ? chats['chat_' + activeChatOtherId] : null;
  const activeChatOther = activeChatOtherId ? getUser(activeChatOtherId) : null;

  // After successful authentication: returning users skip the privacy/profile/join flow.
  // First-time users go through privacy → profile → join-choice as before.
  // The onboarding flag is set once a user has finished setting up their first batch.
  const onAuthSuccess = async () => {
    // Wait briefly for the auth listener to load the user profile from Firestore
    await new Promise(r => setTimeout(r, 400));
    // Check if this user has completed onboarding before
    let onboarded = false;
    try {
      if (authUser?.uid) {
        const profile = await loadUserDoc(authUser.uid);
        onboarded = !!profile?.onboardedAt;
      }
    } catch (e) {
      console.error('Couldn\'t check onboarding status:', e);
    }

    // Phase 2.4 C8.6 — if a deep link is pending, DON'T navigate to home.
    // The deep-link resolution effects (pendingBusinessId effect for biz
    // links, pendingInviteCode effect for group invites) need to drive
    // post-auth routing themselves — they have async work (fetch biz/group,
    // connect, redirect) that runs slower than setScreen('home') here, so
    // without this guard the user lands on V1 home for ~1 second, then
    // the deep-link effect tries to navigate and may lose the race.
    // We still set isApproved for onboarded users so they're in the right
    // approval state when they eventually land somewhere.
    if (pendingBusinessId || pendingInviteCode) {
      if (onboarded) setIsApproved(true);
      // For brand-new users (!onboarded), the biz effect will route them
      // to 'privacy' once currentUser loads. Existing users (onboarded)
      // get routed to either Screen F or biz-profile directly by the biz
      // effect. Either way, we don't touch screen here.
      return;
    }

    if (onboarded) {
      // Returning user — go straight to home
      setIsApproved(true);
      setScreen('home');
      setTab('home');
      setSubTab('wall');
    } else {
      // First-time user — show privacy gate then onboarding
      setScreen('privacy');
    }
  };

 const markOnboarded = async () => {
    try {
      if (authUser?.uid) {
        await markUserOnboarded(authUser.uid);
      }
      // Also keep localStorage flag as a quick-path fallback
      localStorage.setItem('aangan_onboarded', '1');
    } catch (e) {
      console.error('Failed to mark onboarded:', e);
    }
  };
  

  // === Routing ===
  const renderScreen = () => {
    // While Firebase is still resolving auth state on initial load, show a clean
    // loading screen instead of flashing the Welcome page.
    if (authUser === undefined) {
      return (
        <div className="flex-1 flex flex-col items-center justify-between py-16" style={{ background: 'var(--bg)' }}>
          {/* Top spacer */}
          <div />
          
          {/* Center: Aangan brand */}
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                 style={{ background: 'var(--ink)' }}>
              <Logo size={36} color="var(--bg)" />
            </div>
            <h1 className="leading-none mb-2"
                style={{ fontSize: '40px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500, letterSpacing: '-0.01em' }}>
              Aangan
            </h1>
            <div className="w-6 h-6 rounded-full border-2 animate-spin mt-3"
                 style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
          </div>
          
          {/* Bottom: from AapSaathi (logo + text) */}
          <div className="flex flex-col items-center gap-1.5">
            <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--ink-soft)' }}>From</p>
            <div className="flex items-center gap-2">
              <img src="/aap-saathi-logo.png" alt="AapSaathi"
                   style={{ height: 24, width: 'auto' }}
                   onError={(e) => { e.target.style.display = 'none'; }} />
              <p className="text-[14px]" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
                AapSaathi
              </p>
            </div>
          </div>
        </div>
      );
    }
    // Wrapper that overlays the iOS standalone welcome banner above the
    // Welcome / AuthOptions screens. The banner appears at the top of the
    // viewport and the underlying screen renders normally beneath it.
    const withIOSBanner = (screenContent) => (
      <>
        {screenContent}
        {showIOSWelcome && (
          <IOSStandaloneWelcome onDismiss={() => {
            try { localStorage.setItem('aangan_ios_welcome_dismissed', '1'); } catch (e) { /* ignore */ }
            setShowIOSWelcome(false);
          }} />
        )}
      </>
    );

    // Phase 2.4 C8 — deep link success screens
    if (screen === 'biz-deep-link-success' && pendingBusinessForSuccess) {
      return <DeepLinkBizSuccess
        business={pendingBusinessForSuccess}
        alreadyConnected={pendingBusinessAlreadyConnected}
        currentUser={currentUser}
        onGoToBiz={() => {
          const bizId = pendingBusinessForSuccess.id;
          setPendingBusinessForSuccess(null);
          setPendingBusinessAlreadyConnected(false);
          setBizDeepLinkArrivedUnauth(false);
          setBizDeepLinkTriggeredOnboarding(false);
          window.location.search = `?v2=biz-profile&id=${encodeURIComponent(bizId)}`;
        }}
        onGoHome={() => {
          setPendingBusinessForSuccess(null);
          setPendingBusinessAlreadyConnected(false);
          setBizDeepLinkArrivedUnauth(false);
          setBizDeepLinkTriggeredOnboarding(false);
          setScreen('home');
        }}
      />;
    }
    if (screen === 'group-invite-success' && groupInviteSuccessGroup) {
      return <DeepLinkGroupSuccess
        group={groupInviteSuccessGroup}
        currentUser={currentUser}
        onGotIt={() => {
          setGroupInviteSuccessGroup(null);
          setScreen('home');
          setTab('home');
        }}
        onBrowseBusinesses={() => {
          setGroupInviteSuccessGroup(null);
          window.location.search = '?v2=biz-directory';
        }}
      />;
    }

    if (screen === 'welcome') return withIOSBanner(<Welcome onStart={() => setScreen('auth-options')} />);
    if (screen === 'auth-options') return withIOSBanner(<AuthOptions
      onBack={() => setScreen('welcome')}
      onMethod={(m) => setScreen(m === 'google' ? 'auth-google' : m === 'phone' ? 'auth-phone' : 'auth-email')} />);
    if (screen === 'auth-google') return <GoogleAuthFlow onBack={() => setScreen('auth-options')} onContinue={onAuthSuccess} />;
    if (screen === 'auth-phone') return <PhoneAuthFlow onBack={() => setScreen('auth-options')} onContinue={onAuthSuccess} />;
    if (screen === 'auth-email') return <EmailAuthFlow onBack={() => setScreen('auth-options')} onContinue={onAuthSuccess} />;
   
// Defense: if user is already onboarded, skip privacy gate entirely
    if (screen === 'privacy' && currentUser?.onboardedAt) {
      // Side-effect navigation
      setTimeout(() => {
        // C8.7 — Pending biz deep link takes priority. The biz
        // resolution effect will handle routing (either to Screen F
        // or directly to the biz profile). Don't override here.
        if (pendingBusinessId) {
          // Stay on the loading screen below; biz effect drives routing.
          return;
        }
        if (pendingInviteGroup) {
          setScreen('join-choice'); // will trigger invite processing
        } else {
          setScreen('home');
          setTab('home');
        }
      }, 0);
      return <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <p className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Loading…</p>
      </div>;
    }
    if (screen === 'privacy') return <PrivacyGate onBack={() => setScreen('auth-options')} onAccept={() => {
      // After privacy, go to profile-setup. The invite (if any) will be processed
      // after profile-setup completes.
      setScreen('profile-setup');
    }} />;
    // Defense: if user is already onboarded, skip profile-setup entirely
    if (screen === 'profile-setup' && currentUser?.onboardedAt) {
      setTimeout(() => {
        if (pendingInviteGroup) {
          setScreen('join-choice');
        } else {
          setScreen('home');
          setTab('home');
        }
      }, 0);
      return <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <p className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Loading…</p>
      </div>;
    }
   if (screen === 'profile-setup') return <ProfileSetup
      onBack={() => setScreen('privacy')}
      onContinue={async (u) => {
        // Save profile to Firestore
        if (authUser?.uid) {
          try { 
            await updateUserProfile(authUser.uid, { 
              name: u.name, 
              bio: u.bio || '', 
              currentCity: u.currentCity || '',
              photoUrl: u.photoUrl || null,
              color: u.color || '#C45A3F',
              initials: u.initials || (u.name?.[0] || 'U').toUpperCase(),
            }); 
          } catch (e) { console.error(e); }
          try { await markUserOnboarded(authUser.uid); } catch (e) { console.error(e); }
        }
        // Update local state with onboardedAt set so invite logic doesn't loop
        setCurrentUser(prev => ({ ...prev, ...u, onboardedAt: new Date().toISOString() }));
        // If pending invite, process directly. Else go to join-choice.
        // Phase 2.4 C8 — pendingBusinessId takes priority: if the user
        // arrived via /biz/CODE, their intent was the business, not
        // batch discovery. We fetch the biz, auto-connect, and route
        // to the success screen. join-choice is the empty-handed
        // default for users who came in via the welcome flow.
        if (pendingBusinessId) {
          // Defer to the biz resolution effect — it'll fire now that
          // currentUser has onboardedAt set. No need to call
          // processBizDeepLink directly; just let the effect handle it.
        } else if (pendingInviteGroup) {
          await processInviteJoin(pendingInviteGroup);
          setPendingInviteCode(null);
          setPendingInviteGroup(null);
        } else {
          setScreen('join-choice');
        }
      }} />;
      if (screen === 'join-choice' && pendingInviteGroup) {
      // Trigger join in next tick (avoids state update during render)
      setTimeout(() => {
        processInviteJoin(pendingInviteGroup);
        setPendingInviteCode(null);
        setPendingInviteGroup(null);
      }, 0);
      // Render a brief loading state instead of the full screen
      return <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <p className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Joining {pendingInviteGroup.name}…</p>
      </div>;
    }
    if (screen === 'join-choice') return <JoinChoice
      onBack={() => {
        if (returnAfterJoin === 'profile') {
          setReturnAfterJoin(null);
          setScreen('home'); setTab('profile');
        } else {
          setScreen('profile-setup');
        }
      }}
      onFind={() => setScreen('batch-details')}
      onJoinWithCode={() => setScreen('connect-id')}
      onCreateOwn={() => setScreen('batch-details')}
      // C9.4 — route was ?v2=onboarding-v2 (Welcome v2 page), corrected
      // to ?v2=register-business so the user lands on the actual
      // 3-step business registration wizard.
      onRegisterBusiness={() => { window.location.search = '?v2=register-business'; }}
      onBrowseBusinesses={() => { window.location.search = '?v2=biz-directory'; }} />;
    if (screen === 'connect-id') return <ConnectIdEntry
      existingCity={currentUser?.currentCity || ''}
      onBack={() => setScreen('join-choice')}
      onJoin={async ({ code, currentCity }) => {
        // Save current city if user provided one (only for new users)
        if (currentCity && !currentUser?.currentCity) {
          setCurrentUser(prev => ({ ...prev, currentCity }));
          if (authUser?.uid) {
            try {
              await updateUserProfile(authUser.uid, { currentCity });
            } catch (e) {
              console.warn('Could not save city:', e);
            }
          }
        }
        
        // Extract just the code if user pasted a full URL
        // e.g. "http://localhost:5173/join/KFU-KUR7" → "KFU-KUR7"
        let cleanCode = code.trim().toUpperCase();
        const urlMatch = cleanCode.match(/\/JOIN\/([A-Z0-9-]+)/i);
        if (urlMatch) cleanCode = urlMatch[1];
        
        // Look up group by connect code
        try {
          const found = await findGroupByConnectId(cleanCode);
          if (!found) {
            setToast('Invalid or expired connect link');
            return;
          }
          
          // Already a member?
          if (groups[found.id]) {
            setActiveGroupId(found.id);
            setIsApproved(true);
            setScreen('home');
            setTab('home');
            setToast(`You're already in ${found.name}`);
            return;
          }
          
          // Mark onboarded so we don't re-ask "where do you live"
          markOnboarded();
          
          // Process the join — same flow as invite link
          // (processInviteJoin internally calls maybeShowThankYou)
          await processInviteJoin(found);
        } catch (err) {
          console.error('Connect ID join failed:', err);
          setToast('Could not join — please try again');
        }
      }} />;
    if (screen === 'batch-details') return <BatchDetails
      existingCity={currentUser?.currentCity || ''}
      onBack={() => setScreen('join-choice')}
      onSearch={async (q) => {
        // If user already has a city, use that — don't override with empty form value
        const finalQuery = {
          ...q,
          currentCity: currentUser?.currentCity || q.currentCity,
        };
        setSearchQuery(finalQuery);
        setSearching(true);
        setSearchResults([]);
        setScreen('matches');
        try {
          const results = await searchGroupsByCriteria({
            type: q.type,
            name: q.name,
            city: q.city || finalQuery.currentCity,
          });
          setSearchResults(results);
        } catch (err) {
          console.error('Batch search failed:', err);
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      }} />;

    if (screen === 'matches') return <MatchesScreen
      matches={searchResults}
      searching={searching}
      onBack={() => setScreen('batch-details')}
      onSelect={async (m) => {
        // Detect "already a member" before pushing user through the join flow.
        // If they're already in this batch (admin/member), just switch them in
        // and navigate to home instead of showing "Request to join" again.
        if (authUser?.uid) {
          try {
            const membership = await getMyMembership(m.id, authUser.uid);
            if (membership?.status === 'active') {
              // Update local groups state if this group isn't already there
              if (!groups[m.id]) {
                setGroups(prev => ({
                  ...prev,
                  [m.id]: { ...m, unread: 0, lastActive: 'just now' },
                }));
              }
              setActiveGroupId(m.id);
              setIsApproved(membership.isApproved);
              markOnboarded();
              setScreen('home');
              setTab('home');
              setToast(`Switched to ${m.name}`);
              return;
            }
          } catch (e) {
            // Fall through to normal join flow if membership check fails
            console.warn('Membership check failed, proceeding with join flow:', e);
          }
        }
        setSelectedMatch(m);
        setScreen('match-confirm');
      }}
      onCreate={async () => {
        const sq = searchQuery || {};
        const t = sq.type || 'alma';
        const tempId = 'g_new_' + Date.now();
        const localConnectId = generateConnectId();

        // Build the local group object — used by UI immediately
        const newGroup = {
          id: tempId,
          type: t,
          name: sq.name || 'New Batch',
          year: sq.year || '',
          city: sq.city || sq.place || '—',
          place: sq.place || '',
          project: sq.project || '',
          memberCount: 1,
          bio: 'Newly created — invite your batchmates!',
          founded: 'Just now',
          connectId: localConnectId,
          emoji: BATCH_TYPES[t]?.emoji || '🎓',
          photoUrl: null,
          unread: 0,
          lastActive: 'just now',
        };

        // Optimistic UI: show the group right away with temp id
        setGroups(prev => ({ ...prev, [tempId]: newGroup }));
        setActiveGroupId(tempId);
        setAdminOf(prev => [...prev, tempId]);
        setIsApproved(true);
        setCurrentUser(prev => ({ ...prev, currentCity: sq.currentCity || prev?.currentCity }));
        markOnboarded();
        setScreen('batch-created');
        // Show the early-supporter thank you (once per user)
        maybeShowThankYou();

        // Persist to Firestore in the background (non-blocking)
        try {
          if (authUser?.uid) {
            const created = await createGroup({
              creatorId: authUser.uid,
              type: t,
              name: sq.name || 'New Batch',
              passedOut: sq.year || '',
              city: sq.city || '',
              place: sq.place || '',
              project: sq.project || '',
            });
            // Replace the temp group with the real one — USE Firestore's connectId, not local
            setGroups(prev => {
              const { [tempId]: _, ...rest } = prev;
              return {
                ...rest,
                [created.id]: { 
                  ...newGroup, 
                  id: created.id, 
                  connectId: created.connectId, // Firestore's connectId — what the join URL needs
                },
              };
            });
            setActiveGroupId(created.id);
            setAdminOf(prev => prev.map(id => id === tempId ? created.id : id));
          }
        } catch (err) {
          console.error('Failed to save group to Firestore:', err);
          setToast('Group created locally — couldn\'t save to cloud');
        }
      }} />;
       

    if (screen === 'match-confirm' && selectedMatch) return <MatchConfirm
      match={selectedMatch}
      onBack={() => setScreen('matches')}
      onConfirm={async () => {
        // Sync the city the user typed during search into their profile
        if (searchQuery?.currentCity && searchQuery.currentCity !== currentUser?.currentCity) {
          setCurrentUser(prev => ({ ...prev, currentCity: searchQuery.currentCity }));
          try {
            await updateUserProfile(authUser.uid, { currentCity: searchQuery.currentCity });
          } catch (e) {
            console.warn('Failed to persist city update:', e);
          }
        }
        // Actually request to join the batch on Firestore — same path as the
        // connect-link join flow. This writes a pending member doc to
        // groups/{id}/members/{uid} which the admin then approves.
        try {
          await processInviteJoin(selectedMatch);
          markOnboarded();
          // processInviteJoin already handles screen + toast + active group
        } catch (err) {
          console.error('Match join failed:', err);
          setToast('Could not send join request — try again');
        }
      }} />;

    if (screen === 'batch-created') return <BatchCreatedCelebration
      batch={group} onCopy={handleCopy} onShare={handleShare} onPhotoUpload={handleGroupPhotoUpload}
      onContinue={() => setScreen('home')} />;

    if (screen === 'compose') return <Compose onBack={() => setScreen('home')} onPost={handleNewPost} currentUser={currentUser} group={group} defaultSubTab={subTab === 'picks' ? 'picks' : 'wall'} />;
    if (screen === 'post' && activePost) return <PostDetail
      post={{ ...activePost, comments: [...activePostComments, ...olderComments] }} 
      onBack={() => { setScreen('home'); setActivePostId(null); }}
      onComment={handleComment} onLike={handleLike}
      currentUser={currentUser} isApproved={isApproved} onBlocked={handleBlocked} getUser={getUser}
      hasMoreComments={hasMoreComments}
      onLoadOlderComments={handleLoadOlderComments}
      loadingOlderComments={loadingOlderComments} />;
    if (screen === 'notifications') return <NotificationsScreen
      notifications={notifications} groups={groups}
      onBack={() => setScreen('home')}
      onMarkAllRead={handleMarkAllRead} onTap={handleNotifTap} getUser={getUser} />;

    // Member detail (full screen)
    if (screen === 'member-detail' && activeMember) return <MemberDetail
      member={activeMember}
      onTapPhoto={(user) => setFullscreenPhoto(user)} group={group} posts={posts}
      onBack={() => { setActiveMember(null); setScreen('home'); setTab('members'); }}
      onRequestChat={(m) => handleOpenChatRequest(m)}
      onOpenChat={(otherId) => { setActiveChatOtherId(otherId); setScreen('chat'); }}
      onReport={(m) => setShowReport(m)}
      onBlock={handleBlockUser}
      onUnblock={handleUnblockUser}
      blocked={isBlocked(activeMember.id)}
      onMakeAdmin={handleMakeAdmin}
      onRemove={handleRemoveMember}
      currentUserIsAdmin={isAdminOfActiveGroup}
      chatState={memberChatState(activeMember.id)} />;

    // Chats list
    if (screen === 'chats') return <ChatsList
      chats={chats} requests={chatRequests}
      chatsLoaded={chatsLoaded}
      unreadByChatId={unreadByChatId}
      onBack={() => setScreen('home')}
      onOpenChat={(otherId) => {
        // Mark chat as read BEFORE navigating.
        // Optimistically update local state so the red dot clears instantly,
        // then sync the real timestamp to Firestore in the background.
        const chatKey = 'chat_' + otherId;
        const chatRecord = chats[chatKey];
        if (chatRecord?.chatId && authUser?.uid) {
          const now = Date.now();
          setChats(prev => ({
            ...prev,
            [chatKey]: {
              ...prev[chatKey],
              lastReadAt: {
                ...(prev[chatKey]?.lastReadAt || {}),
                [authUser.uid]: { toMillis: () => now }
              }
            }
          }));
          markChatRead({ chatId: chatRecord.chatId, userId: authUser.uid });
        }
        setActiveChatOtherId(otherId);
        setScreen('chat');
      }}
      onAcceptRequest={handleAcceptRequest}
      onDeclineRequest={handleDeclineRequest}
      getUser={getUser}
      isAdminOfActiveGroup={isAdminOfActiveGroup}
      activeGroupName={group?.name || ''}
      pendingJoins={pendingJoins}
      pendingJoinsLoading={pendingJoinsLoading}
      reports={reports}
      onApproveJoin={handleApproveJoin}
      onDeclineJoin={handleDeclineJoin}
      onResolveReport={handleResolveReport}
      // Path Z: BusinessConnect tab data + handler.
      // ChatsList dedupes businessConnects by businessId internally.
      // onOpenBusinessChat writes return-flags then jumps to V2 order
      // chat — V1 unmounts, V2 mounts, and on V2 Back the mount-time
      // useEffect restores us to this screen + business tab.
      businessConnects={businessConnects}
      businessConnectsLoaded={businessConnectsLoaded}
      businessCache={businessCache}
      onOpenBusinessChat={(orderId) => {
        try {
          sessionStorage.setItem('v1_returnScreen', 'chats');
          sessionStorage.setItem('v1_chatTab', 'business');
        } catch (err) { /* storage disabled — skip restoration */ }
        window.location.search = '?v2=order-chat&id=' + encodeURIComponent(orderId);
      }} />;

    // Active chat
    if (screen === 'chat' && activeChatOther) return <ChatScreen
      chat={{ ...activeChat, messages: [...olderMessages, ...activeChatMessages] }}
      otherUser={activeChatOther}
      onBack={() => { setActiveChatOtherId(null); setScreen('chats'); }}
      onSend={handleSendMessage}
      onDelete={handleDeleteChat}
      currentUser={currentUser}
      hasMoreMessages={hasMoreMessages}
      onLoadOlderMessages={handleLoadOlderMessages}
      loadingOlderMessages={loadingOlderMessages}
      onBlock={handleBlockUser}
      onUnblock={handleUnblockUser}
      blocked={isBlocked(activeChatOther.id)}
      onTapPhoto={(user) => setFullscreenPhoto(user)} />;

    // Settings
    // Settings
    if (screen === 'settings') return <SettingsScreen
  currentUser={currentUser}
  onBack={() => { setScreen('home'); setTab('profile'); }}
  onRefer={() => setShowRefer(true)}
  onDelete={() => setShowDelete(true)}
  onEditProfile={() => setScreen('edit-profile')}
  onNotifPrefs={() => setScreen('notif-prefs')}
  onPrivacyData={() => setScreen('privacy-data')}
  onBlockedUsers={() => setScreen('blocked')}
  blockedCount={blockedUsers.length}
  onHelp={() => setScreen('help')}
  onCommunityRules={() => setScreen('community-rules')}
  onPrivacyPolicy={() => setScreen('privacy-policy')}
  onTerms={() => setScreen('terms')}
  onInstallApp={handleInstallApp}
  isInstalled={isInstalled}
  onLogout={handleLogout} />;

    if (screen === 'edit-profile') return <EditProfileScreen
      currentUser={currentUser}
      authEmail={authUser?.email || ''}
      onBack={() => setScreen('settings')}
      onSave={async (updates) => {
        // Detect if photo was removed (user had photo before, but cleared it)
        const photoRemoved = currentUser?.photoUrl && !updates.photoUrl;
        // Optimistic UI update — feels instant
        setCurrentUser(prev => ({ ...prev, ...updates }));
        setScreen('settings');
        setToast('✓ Profile updated');
        // Persist to Firestore (async, doesn't block the UI)
        try {
          if (authUser?.uid) {
            await updateUserProfile(authUser.uid, updates);
            // If user removed their photo, NOW delete it from Storage
            if (photoRemoved) {
              try { await deleteProfilePhoto(authUser.uid); } catch (e) { console.warn('Photo delete failed:', e); }
            }
          }
        } catch (err) {
          console.error('Failed to save profile:', err);
          setToast('Could not save profile — try again');
        }
      }} />;

    if (screen === 'notif-prefs') return <NotificationsSettingsScreen
      onBack={() => setScreen('settings')} />;

    if (screen === 'privacy-data') return <PrivacyDataScreen
      onBack={() => setScreen('settings')}
      onDownload={() => setToast('A copy of your data will be emailed to you within 48 hours.')} />;

    if (screen === 'privacy-data-from-profile') return <PrivacyDataScreen
      onBack={() => { setScreen('home'); setTab('profile'); }}
      onDownload={() => setToast('A copy of your data will be emailed to you within 48 hours.')} />;

    if (screen === 'blocked') return <BlockedUsersScreen
      blockedUsers={blockedUsers}
      onUnblock={handleUnblockUser}
      onBack={() => setScreen('settings')} />;

    if (screen === 'help') return <HelpSupportScreen
      onBack={() => setScreen('settings')}
      onChatSupport={handleOpenSupportChat}
      chatOpening={supportChatOpening} />;

    if (screen === 'community-rules') return <CommunityGuidelinesScreen
      onBack={() => setScreen('settings')} />;

    if (screen === 'community-rules-from-profile') return <CommunityGuidelinesScreen
      onBack={() => { setScreen('home'); setTab('profile'); }} />;

    if (screen === 'privacy-policy') return <PrivacyPolicyScreen
      onBack={() => setScreen('settings')} />;

    if (screen === 'terms') return <TermsScreen
      onBack={() => setScreen('settings')} />;

    // Admin queue removed — moderation moved into Chats → Requests tab.
    // Old 'admin-queue' route preserved as fallback in case any stale state
    // still tries to navigate there; redirect to chats instead.
    if (screen === 'admin-queue') {
      setTimeout(() => { setScreen('chats'); }, 0);
      return <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <p className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Opening requests…</p>
      </div>;
    }
// Still loading groups? Show spinner instead of empty state
    if (!groupsLoaded) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center" style={{ background: 'var(--bg)' }}>
          <div className="w-10 h-10 rounded-full border-2 animate-spin mb-3"
               style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
          <p className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>Loading your batches…</p>
        </div>
      );
    }
    // No active group AND groups have loaded? User truly has no batches.
    // Strict check: only show "Welcome empty state" if the user has ZERO real groups.
    // If activeGroupId is set but `group` is undefined (timing issue: groups map not yet hydrated),
    // show spinner instead — the real group data will arrive on the next render.
    const realGroupsCount = Object.keys(groups).filter(id => !['g1', 'g2', 'g3'].includes(id)).length;
    if (realGroupsCount === 0) {
      // Phase 2.4 C9.1 — render BizOnlyHome (Screen H) when the user
      // has no real batches. Wrapped with TabBar so the bottom nav
      // is still present — matches the prototype.
      //
      // The previous inline JSX here ("Welcome to Aangan / You haven't
      // joined any batches yet") was the V1 default. It assumed batch-
      // joining was the only path forward; with V2 biz-only flows, an
      // onboarded user might legitimately stay on this screen using
      // the app for businesses without ever joining a batch.
      //
      // Members and Profile tabs in this state: Members shows a
      // "join a batch first" placeholder (no active group → no
      // members to render). Profile works fine without batches, so
      // it reuses the same V1/V2 Profile rendering as the main return.
      return (
        <div className="flex-1 flex flex-col overflow-hidden relative feed-bottom-safe">
          {tab === 'home' && (
            <BizOnlyHome
              currentUser={currentUser}
              onFindBatch={() => setScreen('join-choice')}
              onOpenChats={() => setScreen('chats')}
              onOpenNotifs={() => setScreen('notifications')}
              onOpenBizDirectory={() => { window.location.search = '?v2=biz-directory'; }}
              unreadNotifs={unreadNotifs}
              unreadChats={unreadChats}
            />
          )}
          {tab === 'members' && (
            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                   style={{ background: 'var(--accent-soft)', border: '1px solid var(--border)' }}>
                <Users size={26} style={{ color: 'var(--accent)' }} strokeWidth={1.5} />
              </div>
              <h2 className="mb-2" style={{ fontSize: '20px', fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
                Join a batch first
              </h2>
              <p className="text-[13px] mb-5 max-w-[280px]" style={{ color: 'var(--ink-soft)' }}>
                Members appear here once you join a batch.
              </p>
              <button onClick={() => setScreen('join-choice')}
                className="h-11 px-5 rounded-full font-medium text-[14px] active:scale-[0.98] transition-transform"
                style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
                Find or create a batch
              </button>
            </div>
          )}
          {tab === 'profile' && (
            !currentUser ? (
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                <div className="w-10 h-10 rounded-full border-2 animate-spin mb-3"
                     style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
                <p className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>Loading your profile…</p>
              </div>
            ) : (
              // Phase 2.4 C9.2 — Always use ProfileTabV2 in the zero-
              // batches state, regardless of v2Enabled flag. A user
              // here is by definition using Aangan for businesses (no
              // batches yet), so they need the V2 surface that shows
              // Connect List + My Orders + business registration —
              // which V1's Profile component lacks. Once they join a
              // batch, the main return takes over and respects the
              // v2Enabled flag normally.
              <ProfileTabV2
                currentUser={currentUser}
                asTab
                groups={groups}
                onLogout={handleLogout}
                onOpenSettings={() => setScreen('settings')}
                onEditStatus={() => setShowStatusEditor(true)}
                onSwitchGroup={handleSwitchGroup}
                onAddGroup={() => { setReturnAfterJoin('profile'); setScreen('join-choice'); }}
                onOpenConnectList={() => { window.location.search = '?v2=connect-list'; }}
                onOpenMyOrders={() => { window.location.search = '?v2=my-orders'; }}
                onOpenHelp={() => { window.location.search = '?v2=help'; }}
                onOpenBusinessReg={() => { window.location.search = '?v2=register-business'; }}
              />
            )
          )}
          <TabBar active={tab} onChange={setTab} />
        </div>
      );
    }
    
    // We have real groups but `group` (= groups[activeGroupId]) is undefined.
    // This means activeGroupId hasn't been updated yet to point to a real group.
    // Show spinner — the next render after state syncs will show home properly.
    if (!group) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center" style={{ background: 'var(--bg)' }}>
          <div className="w-10 h-10 rounded-full border-2 animate-spin mb-3"
               style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
          <p className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>Loading your batches…</p>
        </div>
      );
    }
    return (
      <div className="flex-1 flex flex-col overflow-hidden relative feed-bottom-safe">
        {tab === 'home' && (
          <Home posts={groupPosts} currentUser={currentUser} group={group} groups={groups}
                subTab={subTab} onSubTab={setSubTab}
                onTapPost={(id) => { setActivePostId(id); setScreen('post'); }}
                onLike={handleLike}
                onOpenGroupInfo={() => setShowGroupInfo(true)}
                onOpenSwitcher={() => setShowSwitcher(true)}
                onCompose={() => setScreen('compose')}
                onOpenNotifs={() => setScreen('notifications')}
                unreadNotifs={unreadNotifs}
                onOpenChats={() => setScreen('chats')}
                unreadChats={unreadChats}
                timeCapsule={groupTimeCapsule}
                onAddMemory={handleAddMemory}
                onTapMemory={(m) => setActiveMemory(m)}
                isApproved={isApproved} onCheckStatus={handleCheckStatus} onBlocked={handleBlocked} checkingStatus={checkingStatus}
                viaCode={viaCode}
                onSwitchGroup={handleSwitchGroup}
                onOpenCalendar={() => setShowCalendarList(true)}
                events={eventsByGroup[activeGroupId] || []}
                getUser={getUser}
                onDeletePost={handleDeletePost}
                onShare={handleSharePost}
                onReport={(member) => setShowReport(member)}
                onTapPhoto={(user) => setFullscreenPhoto(user)}
                isAdminOfActiveGroup={isAdminOfActiveGroup}
                v2Enabled={v2Enabled} />
        )}
        {tab === 'members' && (
          v2Enabled ? (
            <MembersTabV2
              groupId={activeGroupId}
              currentUser={currentUser}
              asTab
              members={groupMembers}
              groupName={group?.name}
              onTapSelf={() => setTab('profile')}
              onTapMember={(m) => { setActiveMember(m); setScreen('member-detail'); }}
              onOpenBusiness={(id) => { window.location.search = '?v2=biz-profile&id=' + id; }}
            />
          ) : (
            <Members group={group} isApproved={isApproved} currentUserIsAdmin={isAdminOfActiveGroup}
                     currentUser={currentUser}
                     members={groupMembers}
                     onTapSelf={() => setTab('profile')}
                     onTapMember={(m) => { setActiveMember(m); setScreen('member-detail'); }} />
          )
        )}
        {tab === 'profile' && (
          !currentUser ? (
            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
              <div className="w-10 h-10 rounded-full border-2 animate-spin mb-3"
                   style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
              <p className="text-[13px]" style={{ color: 'var(--ink-soft)' }}>Loading your profile…</p>
            </div>
          ) : v2Enabled ? (
            <ProfileTabV2
              currentUser={currentUser}
              asTab
              groups={groups}
              onLogout={handleLogout}
              onOpenSettings={() => setScreen('settings')}
              onEditStatus={() => setShowStatusEditor(true)}
              onSwitchGroup={handleSwitchGroup}
              onAddGroup={() => { setReturnAfterJoin('profile'); setScreen('join-choice'); }}
              onOpenConnectList={() => { window.location.search = '?v2=connect-list'; }}
              onOpenMyOrders={() => { window.location.search = '?v2=my-orders'; }}
              onOpenHelp={() => { window.location.search = '?v2=help'; }}
              onOpenBusinessReg={() => { window.location.search = '?v2=register-business'; }}
            />
          ) : (
            <Profile currentUser={currentUser} posts={posts} group={group} groups={groups}
                     onLogout={handleLogout}
                     onEditStatus={() => setShowStatusEditor(true)}
                     onSwitchGroup={handleSwitchGroup}
                     onAddGroup={() => { setReturnAfterJoin('profile'); setScreen('join-choice'); }}
                     onOpenSettings={() => setScreen('settings')}
                     onPrivacyData={() => setScreen('privacy-data-from-profile')}
                     onCommunityRules={() => setScreen('community-rules-from-profile')}
                     isAdmin={isAdminOfActiveGroup}
                     onAdminQueue={() => { loadPendingJoins(); setScreen('admin-queue'); }}
                     pendingCount={pendingJoins.length} />
          )
        )}
        {tab === 'home' && subTab === 'wall' && (
          <FAB onClick={isApproved ? () => setScreen('compose') : handleBlocked} disabled={!isApproved} />
        )}
        <TabBar active={tab} onChange={setTab} />
        {showGroupInfo && <GroupInfoSheet group={group} onClose={() => setShowGroupInfo(false)}
                                          onCopy={handleCopy} onPhotoUpload={handleGroupPhotoUpload}
                                          isAdmin={isAdminOfActiveGroup}
                                          onUpdateInfo={handleUpdateGroupInfo}
                                          onShare={handleShare}
                                          onLeaveGroup={handleLeaveGroup}
                                          onTapPhoto={(user) => setFullscreenPhoto(user)}
                                          currentUser={currentUser} />}
        {showSwitcher && <GroupSwitcherSheet groups={groups} activeId={activeGroupId}
                                             onSwitch={handleSwitchGroup}
                                             onClose={() => setShowSwitcher(false)}
                                             onAddGroup={() => setScreen('join-choice')} />}
        {showStatusEditor && <StatusEditor status={currentUser?.status || ''}
                                           onSave={handleEditStatus}
                                           onClose={() => setShowStatusEditor(false)} />}
      </div>
    );
  };

  // V2 URL-based screen intercept (for local testing without sign-in)
  // Visit http://localhost:5173/?v2=biz-directory to see V2 Business Directory
  // Or ?v2=biz-profile&id=b1 to jump directly to Sharma Sweets profile
  if (typeof window !== 'undefined') {
    const v2Params = new URLSearchParams(window.location.search);
    const v2ScreenParam = v2Params.get('v2');
    const v2BusinessId = v2Params.get('id');
    // Phase 2.5 / C10c — wall-post screen also reads a 'postId' param
    const v2PostId = v2Params.get('postId');
    if (v2ScreenParam) {
      // Phase 2.4 C8.5 — Unauthenticated user landing on a V2 biz
      // profile via the legacy ?v2=biz-profile&id=X URL: redirect
      // through the deep-link onboarding flow instead of letting V2App
      // mount and surface a "Missing or insufficient permissions"
      // error from BusinessProfile. The /biz/CODE URL parser handles
      // the new format at mount; this branch covers the legacy format
      // that older shared links use.
      //
      // While auth is still resolving (authUser === undefined), show
      // a loading state instead of deciding either way — otherwise we
      // could briefly flash V2App, the error, then the redirect.
      if (v2ScreenParam === 'biz-profile' && v2BusinessId) {
        if (authUser === undefined) {
          return (
            <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--bg)', minHeight: '100vh' }}>
              <p className="text-[14px]" style={{ color: 'var(--ink-soft)' }}>Loading…</p>
            </div>
          );
        }
        if (authUser === null) {
          return <UnauthBizProfileRedirect
            bizId={v2BusinessId}
            setPendingBusinessId={setPendingBusinessId}
            setBizDeepLinkArrivedUnauth={setBizDeepLinkArrivedUnauth}
          />;
        }
        // Authenticated user — fall through to V2App normally.
      }
      return <V2App
  initialScreen={v2ScreenParam}
  initialBusinessId={v2BusinessId}
  /* Phase 2.5 / C10c — for ?v2=wall-post&id=BIZ&postId=POST */
  initialPostId={v2PostId}
  onExitV2={() => { window.location.search = ''; }}
  currentUser={currentUser}
  activeGroupId={activeGroupId}
  onTapSelf={() => { setTab('profile'); window.location.search = ''; }}
  onTapMember={(m) => { setActiveMember(m); setScreen('member-detail'); window.location.search = ''; }}
  /* My Orders: reuse the same /orders subscription that feeds V1's
     BusinessConnect tab — keeps customer read budget bounded to one
     subscription regardless of how many surfaces show order data. */
  businessConnects={businessConnects}
  businessConnectsLoaded={businessConnectsLoaded}
  businessCache={businessCache}
/>;
    }
  }
  return (
    <div className="w-full" style={{
      background: window.innerWidth >= 768 ? 'linear-gradient(135deg, #2A2418 0%, #1A1812 100%)' : 'var(--bg)',
      fontFamily: 'var(--font-body)',
      minHeight: '100dvh',
      display: 'flex',
      alignItems: window.innerWidth >= 768 ? 'center' : 'stretch',
      justifyContent: 'center',
      padding: window.innerWidth >= 768 ? '32px' : '0',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400..600;1,9..144,400..600&family=DM+Sans:wght@400;500;600&display=swap');
        :root {
          --bg: #F5EFE0; --surface: #FFFCF5; --ink: #1F2438; --ink-soft: #6E6F84;
          --accent: #C45A3F; --accent-soft: #F5E2D3; --gold: #B8893A; --border: #E8DFCC;
          --font-display: 'Fraunces', Georgia, serif;
          --font-body: 'DM Sans', -apple-system, sans-serif;
        }
      `}</style>
      <div className="relative flex flex-col overflow-hidden mx-auto"
           style={{ 
             width: window.innerWidth >= 768 ? 390 : '100%',
             height: window.innerWidth >= 768 ? 'min(820px, 90vh)' : '100dvh',
             background: 'var(--bg)', 
             borderRadius: window.innerWidth >= 768 ? 44 : 0,
             border: window.innerWidth >= 768 ? '8px solid #0A0A0A' : 'none',
             boxShadow: window.innerWidth >= 768 ? '0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.03) inset' : 'none' 
           }}>
        {window.innerWidth >= 768 && (
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-50"
               style={{ width: 110, height: 26, background: '#0A0A0A',
                        borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }} />
        )}
        {renderScreen()}

        {/* Top-level modals can sit over any screen */}
        {/* Session A — support chat modal. Rendered at top level so it
            overlays whatever screen the member is on. ownerUid is just
            "this user's uid" — the prop name is a Session 5a legacy from
            when only business owners could open support chats. */}
        {supportChatId && authUser?.uid && (
          <SupportChatModal
            chatId={supportChatId}
            ownerUid={authUser.uid}
            onClose={() => setSupportChatId(null)} />
        )}
        {showChatRequest && <ChatRequestModal member={showChatRequest}
                                              onClose={() => setShowChatRequest(null)}
                                              onSend={handleSendChatRequest} />}
        {showReport && <ReportUserSheet member={showReport}
                                        onClose={() => setShowReport(null)}
                                        onSubmit={handleSubmitReport} />}
        {pendingWarning && authUser?.uid && (
          <WarningAcknowledgmentModal
            warning={pendingWarning}
            groupName={groups[pendingWarning.groupId]?.name || 'a batch'}
            onAcknowledge={async () => {
              try {
                await acknowledgePendingWarning(authUser.uid);
                // Subscription will null the state on its own, but clear
                // optimistically so the modal closes immediately.
                setPendingWarning(null);
              } catch (err) {
                console.error('Failed to acknowledge warning:', err);
                setToast('Could not acknowledge — please try again');
              }
            }} />
        )}
        {showRefer && <ReferFriendModal currentUser={currentUser}
                                        onClose={() => setShowRefer(false)}
                                        onCopy={handleCopy} />}
        {shareGroupInvite && <ReferFriendModal
          currentUser={currentUser}
          onClose={() => setShareGroupInvite(null)}
          onCopy={handleCopy}
          title={`Invite to ${shareGroupInvite.name}`}
          subtitle={`Share this link to add batchmates to ${shareGroupInvite.name}.`}
          inviteUrl={`${AANGAN_SHARE_BASE}/join/${shareGroupInvite.connectId}`}
          inviteText={`Join me on Aangan — our batch "${shareGroupInvite.name}" is here. ${AANGAN_SHARE_BASE}/join/${shareGroupInvite.connectId}`}
          emailSubject={`Join ${shareGroupInvite.name} on Aangan`}
        />}
        {postToDelete && <DeletePostConfirm post={postToDelete}
                                            onClose={() => setPostToDelete(null)}
                                            onConfirm={handleConfirmDeletePost} />}
        {showDelete && <DeleteAccountModal onClose={() => setShowDelete(false)}
                                            onConfirm={handleAccountDelete} />}
        {activeMemory && <MemoryViewerModal memory={activeMemory} currentUser={currentUser}
                                            onClose={() => setActiveMemory(null)}
                                            getUser={getUser}
                                            onDelete={handleDeleteMemory}
                                            canDelete={activeMemory.addedById === currentUser?.id || isAdminOfActiveGroup} />}
        {memoryToDelete && <DeleteMemoryConfirm memory={memoryToDelete}
                                                onClose={() => setMemoryToDelete(null)}
                                                onConfirm={handleConfirmDeleteMemory} />}
        {pendingMemoryFile && <AddMemoryCaptionModal
                                  file={pendingMemoryFile}
                                  busy={memoryUploading}
                                  onCancel={() => setPendingMemoryFile(null)}
                                  onSubmit={handleConfirmAddMemory} />}
        <AddEventModal open={showAddEvent}
                       onClose={() => setShowAddEvent(false)}
                       onSubmit={handleAddEvent}
                       busy={eventBusy} />
        <CalendarListModal open={showCalendarList}
                           onClose={() => setShowCalendarList(false)}
                           events={eventsByGroup[activeGroupId] || []}
                           isAdmin={isAdminOfActiveGroup}
                           currentUserId={authUser?.uid}
                           onDelete={async (eventId) => {
                             await handleDeleteEvent(eventId);
                           }}
                           onAddNew={() => {
                             setShowCalendarList(false);
                             setShowAddEvent(true);
                           }} />
        {fullscreenPhoto && (
          <FullscreenPhotoModal user={fullscreenPhoto} onClose={() => setFullscreenPhoto(null)} />
        )}
        <InstallInstructionsModal open={showIOSInstall} 
                                  onClose={() => setShowIOSInstall(false)} 
                                  platform="ios" />
        <InstallInstructionsModal open={showGenericInstall} 
                                  onClose={() => setShowGenericInstall(false)} 
                                  platform="generic" />
        <ThankYouModal open={showThankYou} onClose={() => setShowThankYou(false)} />
        {showInstallModal && (
          <InstallPromptModal 
            onClose={handleDismissInstallModal}
            onInstall={handleInstallFromModal} />
        )}
      </div>
      
    </div>
  );
}
